// ============================================
// RESEARCH STUDIES DATA - FULLY TRANSLATED
// 150+ Studies × 6 Languages (DE, EN, FR, NL, IT, ES)
// Years: 1984-2026
// ============================================

import { VERIFIED_STUDIES } from './verifiedStudies';
import { pubmedStudies2026 } from './pubmedStudies2026';
import { moreStudies2026 } from './moreStudies2026';
import { STUDY_TOPICS, TOPIC_ORDER, type StudyTopic } from './studyTopics';

export type Language = 'de' | 'en' | 'fr' | 'nl' | 'it' | 'es';

export interface TranslatedText {
  de: string;
  en: string;
  fr: string;
  nl: string;
  it: string;
  es: string;
}

export interface StructuredSummary {
  problemStatement: TranslatedText;
  purpose: TranslatedText;
  methods: TranslatedText;
  results: TranslatedText;
  conclusions: TranslatedText;
}

export interface Study {
  id: number;
  category: string;
  topic?: StudyTopic;
  title: TranslatedText;
  authors: string;
  journal: string;
  year: number;
  type: string;
  url: string;
  structured: StructuredSummary;
  eapRelevance?: 1 | 2 | 3 | 4 | 5; // 1 = höchste Relevanz (grün), 5 = niedrigste (rot)
}

// EAP-Relevanz Farben und Labels
export const eapRelevanceConfig = {
  1: { color: 'bg-green-500', textColor: 'text-white', label: { de: 'Höchste Relevanz', en: 'Highest Relevance', fr: 'Pertinence maximale', nl: 'Hoogste relevantie', it: 'Massima rilevanza', es: 'Máxima relevancia' } },
  2: { color: 'bg-green-300', textColor: 'text-green-900', label: { de: 'Hohe Relevanz', en: 'High Relevance', fr: 'Haute pertinence', nl: 'Hoge relevantie', it: 'Alta rilevanza', es: 'Alta relevancia' } },
  3: { color: 'bg-yellow-400', textColor: 'text-yellow-900', label: { de: 'Mittlere Relevanz', en: 'Medium Relevance', fr: 'Pertinence moyenne', nl: 'Gemiddelde relevantie', it: 'Media rilevanza', es: 'Relevancia media' } },
  4: { color: 'bg-orange-400', textColor: 'text-orange-900', label: { de: 'Geringe Relevanz', en: 'Low Relevance', fr: 'Faible pertinence', nl: 'Lage relevantie', it: 'Bassa rilevanza', es: 'Baja relevancia' } },
  5: { color: 'bg-red-500', textColor: 'text-white', label: { de: 'Minimale Relevanz', en: 'Minimal Relevance', fr: 'Pertinence minimale', nl: 'Minimale relevantie', it: 'Rilevanza minima', es: 'Relevancia mínima' } },
};

// Helper to create translated text
const t = (de: string, en: string, fr: string, nl: string, it: string, es: string): TranslatedText => ({
  de, en, fr, nl, it, es
});

// ============================================
// KATEGORIE A: KUNSTSTOFF- & HARZ-TOXIZITÄT (31 Studien)
// ============================================

export const polymerToxicityStudies: Study[] = [
  {
    id: 4,
    category: "polymer-toxicity",
    title: t(
      "Wie viel löst sich aus zahnärztlichen Materialien auf Harzbasis auf? Ein metaanalytischer Ansatz",
      "How Much Does Elute from Resin-Based Dental Materials? A Meta-Analytical Approach",
      "Combien de substances s'éluent des matériaux dentaires à base de résine ? Une approche méta-analytique",
      "Hoeveel lost er op uit harsgebaseerde tandheelkundige materialen? Een meta-analytische benadering",
      "Quanto si rilascia dai materiali dentali a base di resina? Un approccio meta-analitico",
      "¿Cuánto se libera de los materiales dentales a base de resina? Un enfoque metaanalítico"
    ),
    authors: "Van Landuyt KL, Nawrot T, Geebelen B, De Munck J, Snauwaert J, Yoshihara K, Scheers H, Godderis L, Hoet P, Van Meerbeek B",
    journal: "Dental Materials",
    year: 2011,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/21664675/",
    structured: {
      problemStatement: t(
        "Die kurz- und langfristige Freisetzung von Komponenten aus harzbasierten Zahnmaterialien ist unklar.",
        "The short- and long-term release of components from resin-based dental materials is unclear.",
        "La libération à court et long terme des composants des matériaux dentaires à base de résine n'est pas claire.",
        "De korte- en langetermijnafgifte van componenten uit harsgebaseerde tandheelkundige materialen is onduidelijk.",
        "Il rilascio a breve e lungo termine dei componenti dai materiali dentali a base di resina non è chiaro.",
        "La liberación a corto y largo plazo de los componentes de los materiales dentales a base de resina no está clara."
      ),
      purpose: t(
        "Metaanalyse zur Quantifizierung der Monomerfreisetzung aus Dentalkompositen.",
        "Meta-analysis to quantify monomer release from dental composites.",
        "Méta-analyse pour quantifier la libération de monomères des composites dentaires.",
        "Meta-analyse om monomeerafgifte uit tandheelkundige composieten te kwantificeren.",
        "Meta-analisi per quantificare il rilascio di monomeri dai compositi dentali.",
        "Metaanálisis para cuantificar la liberación de monómeros de los composites dentales."
      ),
      methods: t(
        "Systematische Analyse von 71 Studien, davon 22 in Metaanalyse einbezogen.",
        "Systematic analysis of 71 studies, 22 of which were included in meta-analysis.",
        "Analyse systématique de 71 études, dont 22 incluses dans la méta-analyse.",
        "Systematische analyse van 71 studies, waarvan 22 opgenomen in meta-analyse.",
        "Analisi sistematica di 71 studi, di cui 22 inclusi nella meta-analisi.",
        "Análisis sistemático de 71 estudios, de los cuales 22 se incluyeron en el metaanálisis."
      ),
      results: t(
        "HEMA, TEGDMA, BisGMA und UDMA werden häufig freigesetzt.",
        "HEMA, TEGDMA, BisGMA, and UDMA are frequently released.",
        "HEMA, TEGDMA, BisGMA et UDMA sont fréquemment libérés.",
        "HEMA, TEGDMA, BisGMA en UDMA worden vaak afgegeven.",
        "HEMA, TEGDMA, BisGMA e UDMA vengono rilasciati frequentemente.",
        "HEMA, TEGDMA, BisGMA y UDMA se liberan con frecuencia."
      ),
      conclusions: t(
        "Standardisierungsrichtlinien für Analyseforschung sind dringend erforderlich.",
        "Standardization guidelines for analytical research are urgently needed.",
        "Des lignes directrices de standardisation pour la recherche analytique sont urgemment nécessaires.",
        "Standaardisatierichtlijnen voor analytisch onderzoek zijn dringend nodig.",
        "Le linee guida di standardizzazione per la ricerca analitica sono urgentemente necessarie.",
        "Se necesitan urgentemente directrices de estandarización para la investigación analítica."
      ),
    },
  },
  {
    id: 5,
    category: "polymer-toxicity",
    title: t(
      "Dentalmethacrylate können über die oxidative Induktion von DNA-Doppelstrangbrüchen genotoxische Wirkungen entfalten",
      "Dental Methacrylates May Exert Genotoxic Effects via Oxidative Induction of DNA Double-Strand Breaks",
      "Les méthacrylates dentaires peuvent exercer des effets génotoxiques via l'induction oxydative de cassures double-brin de l'ADN",
      "Dentale methacrylaten kunnen genotoxische effecten uitoefenen via oxidatieve inductie van DNA-dubbelstrengsbreuken",
      "I metacrilati dentali possono esercitare effetti genotossici tramite induzione ossidativa di rotture del doppio filamento del DNA",
      "Los metacrilatos dentales pueden ejercer efectos genotóxicos mediante la inducción oxidativa de roturas de doble cadena del ADN"
    ),
    authors: "Blasiak J, Synowiec E, Tarnawska J, Czarny P, Poplawski T, Reiter RJ",
    journal: "Molecular Biology Reports",
    year: 2012,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/21681428/",
    structured: {
      problemStatement: t(
        "Der genotoxische Mechanismus von Methacrylaten ist nicht vollständig verstanden.",
        "The genotoxic mechanism of methacrylates is not fully understood.",
        "Le mécanisme génotoxique des méthacrylates n'est pas entièrement compris.",
        "Het genotoxische mechanisme van methacrylaten is niet volledig begrepen.",
        "Il meccanismo genotossico dei metacrilati non è completamente compreso.",
        "El mecanismo genotóxico de los metacrilatos no se comprende completamente."
      ),
      purpose: t(
        "Untersuchung der molekularen Mechanismen der Genotoxizität von Dentalmethacrylaten.",
        "Investigation of the molecular mechanisms of genotoxicity of dental methacrylates.",
        "Étude des mécanismes moléculaires de la génotoxicité des méthacrylates dentaires.",
        "Onderzoek naar de moleculaire mechanismen van genotoxiciteit van dentale methacrylaten.",
        "Indagine sui meccanismi molecolari della genotossicità dei metacrilati dentali.",
        "Investigación de los mecanismos moleculares de la genotoxicidad de los metacrilatos dentales."
      ),
      methods: t(
        "Analyse von DNA-Doppelstrangbrüchen und oxidativem Stress in Zellkulturen.",
        "Analysis of DNA double-strand breaks and oxidative stress in cell cultures.",
        "Analyse des cassures double-brin de l'ADN et du stress oxydatif dans les cultures cellulaires.",
        "Analyse van DNA-dubbelstrengsbreuken en oxidatieve stress in celculturen.",
        "Analisi delle rotture del doppio filamento del DNA e dello stress ossidativo nelle colture cellulari.",
        "Análisis de las roturas de doble cadena del ADN y el estrés oxidativo en cultivos celulares."
      ),
      results: t(
        "Methacrylate induzieren DNA-Doppelstrangbrüche über oxidative Mechanismen.",
        "Methacrylates induce DNA double-strand breaks via oxidative mechanisms.",
        "Les méthacrylates induisent des cassures double-brin de l'ADN via des mécanismes oxydatifs.",
        "Methacrylaten induceren DNA-dubbelstrengsbreuken via oxidatieve mechanismen.",
        "I metacrilati inducono rotture del doppio filamento del DNA tramite meccanismi ossidativi.",
        "Los metacrilatos inducen roturas de doble cadena del ADN a través de mecanismos oxidativos."
      ),
      conclusions: t(
        "Reaktive Sauerstoffspezies spielen eine zentrale Rolle bei der Genotoxizität.",
        "Reactive oxygen species play a central role in genotoxicity.",
        "Les espèces réactives de l'oxygène jouent un rôle central dans la génotoxicité.",
        "Reactieve zuurstofspecies spelen een centrale rol bij genotoxiciteit.",
        "Le specie reattive dell'ossigeno svolgono un ruolo centrale nella genotossicità.",
        "Las especies reactivas de oxígeno juegan un papel central en la genotoxicidad."
      ),
    },
  },
  {
    id: 6,
    category: "polymer-toxicity",
    title: t(
      "Zytotoxizität von Methacrylat-Monomeren bei der Formgebung von Provisorien auf Primärzellen",
      "Cytotoxicity of Methacrylate Monomers in Forming Provisionals on Primary Cells",
      "Cytotoxicité des monomères de méthacrylate dans la formation de provisoires sur les cellules primaires",
      "Cytotoxiciteit van methacrylaatmonomeren bij het vormen van provisoria op primaire cellen",
      "Citotossicità dei monomeri di metacrilato nella formazione di provvisori sulle cellule primarie",
      "Citotoxicidad de los monómeros de metacrilato en la formación de provisionales en células primarias"
    ),
    authors: "Schweikl H, Hartmann A, Ötzcan M, et al.",
    journal: "Dental Materials",
    year: 2014,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/24656739/",
    structured: {
      problemStatement: t(
        "Provisorische Restaurationen setzen Monomere frei, die toxisch auf periimplantäres Gewebe wirken können.",
        "Provisional restorations release monomers that can be toxic to peri-implant tissue.",
        "Les restaurations provisoires libèrent des monomères qui peuvent être toxiques pour les tissus péri-implantaires.",
        "Provisorische restauraties geven monomeren af die toxisch kunnen zijn voor peri-implantair weefsel.",
        "Le restaurazioni provvisorie rilasciano monomeri che possono essere tossici per i tessuti perimplantari.",
        "Las restauraciones provisionales liberan monómeros que pueden ser tóxicos para el tejido periimplantario."
      ),
      purpose: t(
        "Bewertung der Zytotoxizität freigesetzter Monomere auf Primärzellen.",
        "Evaluation of the cytotoxicity of released monomers on primary cells.",
        "Évaluation de la cytotoxicité des monomères libérés sur les cellules primaires.",
        "Evaluatie van de cytotoxiciteit van vrijgekomen monomeren op primaire cellen.",
        "Valutazione della citotossicità dei monomeri rilasciati sulle cellule primarie.",
        "Evaluación de la citotoxicidad de los monómeros liberados en células primarias."
      ),
      methods: t(
        "In-vitro-Exposition von Primärzellen gegenüber Methacrylat-Monomeren.",
        "In vitro exposure of primary cells to methacrylate monomers.",
        "Exposition in vitro de cellules primaires aux monomères de méthacrylate.",
        "In vitro blootstelling van primaire cellen aan methacrylaatmonomeren.",
        "Esposizione in vitro di cellule primarie a monomeri di metacrilato.",
        "Exposición in vitro de células primarias a monómeros de metacrilato."
      ),
      results: t(
        "Signifikante Reduktion der Zellviabilität durch Methacrylat-Exposition.",
        "Significant reduction in cell viability from methacrylate exposure.",
        "Réduction significative de la viabilité cellulaire par l'exposition aux méthacrylates.",
        "Significante vermindering van celviabiliteit door methacrylaatblootstelling.",
        "Riduzione significativa della vitalità cellulare dall'esposizione ai metacrilati.",
        "Reducción significativa de la viabilidad celular por exposición a metacrilatos."
      ),
      conclusions: t(
        "Die direkte Anformung von Provisorien kann periimplantäres Gewebe schädigen.",
        "Direct forming of provisionals can damage peri-implant tissue.",
        "La formation directe de provisoires peut endommager les tissus péri-implantaires.",
        "Directe vorming van provisoria kan peri-implantair weefsel beschadigen.",
        "La formazione diretta di provvisori può danneggiare i tessuti perimplantari.",
        "La formación directa de provisionales puede dañar el tejido periimplantario."
      ),
    },
  },
  {
    id: 7,
    category: "polymer-toxicity",
    title: t(
      "Oxidativer Stress und zelluläre Antworten auf Zahnharz-Monomere",
      "Oxidative Stress and Cellular Responses to Dental Resin Monomers",
      "Stress oxydatif et réponses cellulaires aux monomères de résine dentaire",
      "Oxidatieve stress en cellulaire reacties op tandharsmonomeren",
      "Stress ossidativo e risposte cellulari ai monomeri di resina dentale",
      "Estrés oxidativo y respuestas celulares a los monómeros de resina dental"
    ),
    authors: "Krifka S, Petzel C, Ötzcan M, et al.",
    journal: "Biomaterials",
    year: 2013,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/23602363/",
    structured: {
      problemStatement: t(
        "Zahnharz-Monomere induzieren oxidativen Stress in Zellen.",
        "Dental resin monomers induce oxidative stress in cells.",
        "Les monomères de résine dentaire induisent un stress oxydatif dans les cellules.",
        "Tandharsmonomeren induceren oxidatieve stress in cellen.",
        "I monomeri di resina dentale inducono stress ossidativo nelle cellule.",
        "Los monómeros de resina dental inducen estrés oxidativo en las células."
      ),
      purpose: t(
        "Review der zellulären Antworten auf oxidativen Stress durch Methacrylate.",
        "Review of cellular responses to oxidative stress from methacrylates.",
        "Revue des réponses cellulaires au stress oxydatif des méthacrylates.",
        "Review van cellulaire reacties op oxidatieve stress door methacrylaten.",
        "Revisione delle risposte cellulari allo stress ossidativo da metacrilati.",
        "Revisión de las respuestas celulares al estrés oxidativo por metacrilatos."
      ),
      methods: t(
        "Systematischer Review von in-vitro und in-vivo Studien.",
        "Systematic review of in vitro and in vivo studies.",
        "Revue systématique des études in vitro et in vivo.",
        "Systematische review van in vitro en in vivo studies.",
        "Revisione sistematica degli studi in vitro e in vivo.",
        "Revisión sistemática de estudios in vitro e in vivo."
      ),
      results: t(
        "Monomere aktivieren zelluläre Stressantworten und Apoptose-Signalwege.",
        "Monomers activate cellular stress responses and apoptosis pathways.",
        "Les monomères activent les réponses au stress cellulaire et les voies de l'apoptose.",
        "Monomeren activeren cellulaire stressreacties en apoptose-routes.",
        "I monomeri attivano le risposte allo stress cellulare e le vie dell'apoptosi.",
        "Los monómeros activan las respuestas al estrés celular y las vías de apoptosis."
      ),
      conclusions: t(
        "Antioxidative Schutzmechanismen sind entscheidend für die Biokompatibilität.",
        "Antioxidant protective mechanisms are crucial for biocompatibility.",
        "Les mécanismes de protection antioxydants sont cruciaux pour la biocompatibilité.",
        "Antioxidatieve beschermingsmechanismen zijn cruciaal voor biocompatibiliteit.",
        "I meccanismi di protezione antiossidante sono cruciali per la biocompatibilità.",
        "Los mecanismos de protección antioxidante son cruciales para la biocompatibilidad."
      ),
    },
  },
  {
    id: 8,
    category: "polymer-toxicity",
    title: t(
      "N-Acetylcystein reduziert DNA-Doppelstrangbrüche durch Zahnharz-Metaboliten",
      "N-Acetylcysteine Reduces DNA Double-Strand Breaks by Dental Resin Metabolites",
      "La N-acétylcystéine réduit les cassures double-brin de l'ADN par les métabolites de résine dentaire",
      "N-acetylcysteïne vermindert DNA-dubbelstrengsbreuken door tandharsenmetabolieten",
      "La N-acetilcisteina riduce le rotture del doppio filamento del DNA da metaboliti di resina dentale",
      "La N-acetilcisteína reduce las roturas de doble cadena del ADN por metabolitos de resina dental"
    ),
    authors: "Schwengberg S, Tong M, Ozer F, et al.",
    journal: "Archives of Toxicology",
    year: 2015,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/25370009/",
    structured: {
      problemStatement: t(
        "In Zellkulturversuchen wurden Zahnharz-Metaboliten mit oxidativem Stress und DNA-Schäden in Verbindung gebracht (In-vitro-Befund).",
        "In cell culture experiments, dental resin metabolites have been associated with oxidative stress and DNA damage (in vitro finding).",
        "Dans des essais sur cultures cellulaires, les métabolites de résine dentaire ont été associés à un stress oxydatif et à des dommages à l'ADN (résultat in vitro).",
        "In celkweekexperimenten werden tandharsenmetabolieten geassocieerd met oxidatieve stress en DNA-schade (in-vitrobevinding).",
        "In esperimenti su colture cellulari, i metaboliti delle resine dentali sono stati associati a stress ossidativo e danni al DNA (risultato in vitro).",
        "En ensayos con cultivos celulares, los metabolitos de resina dental se han asociado con estrés oxidativo y daño al ADN (hallazgo in vitro)."
      ),
      purpose: t(
        "Prüfung der protektiven Wirkung von N-Acetylcystein (NAC) auf DNA-Schäden.",
        "Testing the protective effect of N-acetylcysteine (NAC) on DNA damage.",
        "Test de l'effet protecteur de la N-acétylcystéine (NAC) sur les dommages à l'ADN.",
        "Testen van het beschermende effect van N-acetylcysteïne (NAC) op DNA-schade.",
        "Test dell'effetto protettivo della N-acetilcisteina (NAC) sui danni al DNA.",
        "Prueba del efecto protector de la N-acetilcisteína (NAC) sobre el daño al ADN."
      ),
      methods: t(
        "In-vitro-Studie mit humanen Gingivafibroblasten und NAC-Behandlung.",
        "In vitro study with human gingival fibroblasts and NAC treatment.",
        "Étude in vitro avec des fibroblastes gingivaux humains et traitement NAC.",
        "In vitro studie met humane gingivale fibroblasten en NAC-behandeling.",
        "Studio in vitro con fibroblasti gengivali umani e trattamento con NAC.",
        "Estudio in vitro con fibroblastos gingivales humanos y tratamiento con NAC."
      ),
      results: t(
        "NAC reduzierte signifikant die DSBs-Foci. NAC war wirksamer als Ascorbinsäure.",
        "NAC significantly reduced DSBs foci. NAC was more effective than ascorbic acid.",
        "NAC a réduit significativement les foyers de DSBs. NAC était plus efficace que l'acide ascorbique.",
        "NAC verminderde significant de DSBs-foci. NAC was effectiever dan ascorbinezuur.",
        "NAC ha ridotto significativamente i foci di DSBs. NAC era più efficace dell'acido ascorbico.",
        "NAC redujo significativamente los focos de DSBs. NAC fue más efectivo que el ácido ascórbico."
      ),
      conclusions: t(
        "In diesem In-vitro-Modell reduzierten Antioxidantien die durch Zahnharz-Metaboliten ausgelösten DNA-Schäden; klinische Übertragbarkeit nicht belegt.",
        "In this in vitro model, antioxidants reduced DNA damage triggered by dental resin metabolites; clinical transferability is not established.",
        "Dans ce modèle in vitro, les antioxydants ont réduit les dommages à l'ADN liés aux métabolites de résine dentaire ; la transposition clinique n'est pas établie.",
        "In dit in-vitromodel verminderden antioxidanten de door tandharsenmetabolieten veroorzaakte DNA-schade; klinische overdraagbaarheid is niet aangetoond.",
        "In questo modello in vitro, gli antiossidanti hanno ridotto i danni al DNA legati ai metaboliti delle resine dentali; la trasferibilità clinica non è dimostrata.",
        "En este modelo in vitro, los antioxidantes redujeron el daño al ADN causado por metabolitos de resina dental; la transferibilidad clínica no está demostrada."
      ),
    },
  },
  {
    id: 9,
    category: "polymer-toxicity",
    title: t(
      "NAC verbessert die durch Zahnverbundwerkstoffe induzierte DNA-Doppelstrangbrüche und Chromatinkondensation",
      "NAC Ameliorates Dental Composite-Induced DNA Double-Strand Breaks and Chromatin Condensation",
      "NAC améliore les cassures double-brin de l'ADN et la condensation de la chromatine induites par les composites dentaires",
      "NAC verbetert door tandheelkundige composieten geïnduceerde DNA-dubbelstrengsbreuken en chromatinecondensatie",
      "NAC migliora le rotture del doppio filamento del DNA e la condensazione della cromatina indotte dai compositi dentali",
      "NAC mejora las roturas de doble cadena del ADN y la condensación de la cromatina inducidas por los composites dentales"
    ),
    authors: "Styllou P, Styllou M, Hogg C, Schwarz S, Aringer I, Schmitz C, Hofer S, Reichl FX, Hickel R",
    journal: "Dental Materials Journal",
    year: 2017,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/28747595/",
    structured: {
      problemStatement: t(
        "Zahnverbundwerkstoffe können DNA-Integrität beeinträchtigen.",
        "Dental composites can impair DNA integrity.",
        "Les composites dentaires peuvent altérer l'intégrité de l'ADN.",
        "Tandheelkundige composieten kunnen de DNA-integriteit aantasten.",
        "I compositi dentali possono compromettere l'integrità del DNA.",
        "Los composites dentales pueden afectar la integridad del ADN."
      ),
      purpose: t(
        "Untersuchung der protektiven Wirkung von NAC auf DNA-Schäden.",
        "Investigation of the protective effect of NAC on DNA damage.",
        "Étude de l'effet protecteur du NAC sur les dommages à l'ADN.",
        "Onderzoek naar het beschermende effect van NAC op DNA-schade.",
        "Indagine sull'effetto protettivo del NAC sui danni al DNA.",
        "Investigación del efecto protector del NAC sobre el daño al ADN."
      ),
      methods: t(
        "Exposition von Gingivafibroblasten gegenüber Bis-GMA, GMA, HEMA und TEGDMA.",
        "Exposure of gingival fibroblasts to Bis-GMA, GMA, HEMA, and TEGDMA.",
        "Exposition de fibroblastes gingivaux au Bis-GMA, GMA, HEMA et TEGDMA.",
        "Blootstelling van gingivale fibroblasten aan Bis-GMA, GMA, HEMA en TEGDMA.",
        "Esposizione di fibroblasti gengivali a Bis-GMA, GMA, HEMA e TEGDMA.",
        "Exposición de fibroblastos gingivales a Bis-GMA, GMA, HEMA y TEGDMA."
      ),
      results: t(
        "Signifikante Reduktion von DNA-Doppelstrangbrüchen durch NAC-Behandlung.",
        "Significant reduction of DNA double-strand breaks by NAC treatment.",
        "Réduction significative des cassures double-brin de l'ADN par le traitement NAC.",
        "Significante vermindering van DNA-dubbelstrengsbreuken door NAC-behandeling.",
        "Riduzione significativa delle rotture del doppio filamento del DNA mediante trattamento con NAC.",
        "Reducción significativa de las roturas de doble cadena del ADN mediante tratamiento con NAC."
      ),
      conclusions: t(
        "NAC kann genotoxische Effekte von Zahnverbundmaterialien reduzieren.",
        "NAC can reduce genotoxic effects of dental composites.",
        "NAC peut réduire les effets génotoxiques des composites dentaires.",
        "NAC kan genotoxische effecten van tandheelkundige composieten verminderen.",
        "NAC può ridurre gli effetti genotossici dei compositi dentali.",
        "NAC puede reducir los efectos genotóxicos de los composites dentales."
      ),
    },
  },
  {
    id: 10,
    category: "polymer-toxicity",
    title: t(
      "Auswirkungen von Zahnverbundharzmonomeren auf Zahnfleischzellen",
      "Effects of Dental Composite Resin Monomers on Gingival Cells",
      "Effets des monomères de résine composite dentaire sur les cellules gingivales",
      "Effecten van tandheelkundige composietharsmonomeren op gingivale cellen",
      "Effetti dei monomeri di resina composita dentale sulle cellule gengivali",
      "Efectos de los monómeros de resina compuesta dental en las células gingivales"
    ),
    authors: "Schneider TR, Hakami-Tafreshi R, Teets-Perez AC",
    journal: "Dental Materials Journal",
    year: 2019,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/31092760/",
    structured: {
      problemStatement: t(
        "Methacrylatmonomere zeigen Toxizität für Zahnfleischzellen.",
        "Methacrylate monomers show toxicity to gingival cells.",
        "Les monomères de méthacrylate montrent une toxicité pour les cellules gingivales.",
        "Methacrylaatmonomeren tonen toxiciteit voor gingivale cellen.",
        "I monomeri di metacrilato mostrano tossicità per le cellule gengivali.",
        "Los monómeros de metacrilato muestran toxicidad para las células gingivales."
      ),
      purpose: t(
        "Untersuchung der Toxizität von Bis-GMA, UDMA, TEGDMA auf menschliche Zahnfleischzellen.",
        "Investigation of the toxicity of Bis-GMA, UDMA, TEGDMA on human gingival cells.",
        "Étude de la toxicité du Bis-GMA, UDMA, TEGDMA sur les cellules gingivales humaines.",
        "Onderzoek naar de toxiciteit van Bis-GMA, UDMA, TEGDMA op humane gingivale cellen.",
        "Indagine sulla tossicità di Bis-GMA, UDMA, TEGDMA sulle cellule gengivali umane.",
        "Investigación de la toxicidad de Bis-GMA, UDMA, TEGDMA en células gingivales humanas."
      ),
      methods: t(
        "In-vitro-Studie mit humanen Gingivazellen. Analyse von oxidativem Stress.",
        "In vitro study with human gingival cells. Analysis of oxidative stress.",
        "Étude in vitro avec des cellules gingivales humaines. Analyse du stress oxydatif.",
        "In vitro studie met humane gingivale cellen. Analyse van oxidatieve stress.",
        "Studio in vitro con cellule gengivali umane. Analisi dello stress ossidativo.",
        "Estudio in vitro con células gingivales humanas. Análisis del estrés oxidativo."
      ),
      results: t(
        "Toxizität in der Reihenfolge Bis-GMA > UDMA > TEGDMA.",
        "Toxicity in the order Bis-GMA > UDMA > TEGDMA.",
        "Toxicité dans l'ordre Bis-GMA > UDMA > TEGDMA.",
        "Toxiciteit in de volgorde Bis-GMA > UDMA > TEGDMA.",
        "Tossicità nell'ordine Bis-GMA > UDMA > TEGDMA.",
        "Toxicidad en el orden Bis-GMA > UDMA > TEGDMA."
      ),
      conclusions: t(
        "Bis-GMA und UDMA verursachen Glutathion-Abschöpfung und Zelltod.",
        "Bis-GMA and UDMA cause glutathione depletion and cell death.",
        "Bis-GMA et UDMA causent l'épuisement du glutathion et la mort cellulaire.",
        "Bis-GMA en UDMA veroorzaken glutathionuitputting en celdood.",
        "Bis-GMA e UDMA causano esaurimento del glutatione e morte cellulare.",
        "Bis-GMA y UDMA causan agotamiento del glutatión y muerte celular."
      ),
    },
  },
  {
    id: 11,
    category: "polymer-toxicity",
    title: t(
      "Eine integrative Überprüfung der Toxizität von Bisphenol A (BPA) aus Harzverbunden in der Zahnmedizin",
      "An Integrative Review of Bisphenol A (BPA) Toxicity from Resin Composites Used in Dentistry",
      "Une revue intégrative de la toxicité du bisphénol A (BPA) des composites de résine utilisés en dentisterie",
      "Een integratieve review van de toxiciteit van bisfenol A (BPA) uit harscomposieten gebruikt in de tandheelkunde",
      "Una revisione integrativa della tossicità del bisfenolo A (BPA) dai compositi in resina utilizzati in odontoiatria",
      "Una revisión integradora de la toxicidad del bisfenol A (BPA) de los composites de resina utilizados en odontología"
    ),
    authors: "Lopes-Rocha L, Ribeiro-Gonçalves L, Henriques B, Zöllner M, Teixeira FS, Zille A",
    journal: "Journal of Biomedical Materials Research Part B",
    year: 2021,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/33834604/",
    structured: {
      problemStatement: t(
        "Die Freisetzung von BPA aus Harz-Matrix-Verbundwerkstoffen ist besorgniserregend.",
        "The release of BPA from resin-matrix composites is concerning.",
        "La libération de BPA des composites à matrice de résine est préoccupante.",
        "De afgifte van BPA uit harsmatrixcomposieten is zorgwekkend.",
        "Il rilascio di BPA dai compositi a matrice resinosa è preoccupante.",
        "La liberación de BPA de los composites de matriz de resina es preocupante."
      ),
      purpose: t(
        "Systematische Überprüfung der BPA-Freisetzung und Toxizität.",
        "Systematic review of BPA release and toxicity.",
        "Revue systématique de la libération et de la toxicité du BPA.",
        "Systematische review van BPA-afgifte en toxiciteit.",
        "Revisione sistematica del rilascio e della tossicità del BPA.",
        "Revisión sistemática de la liberación y toxicidad del BPA."
      ),
      methods: t(
        "PubMed-Suche zu in vitro- und in vivo-Studien.",
        "PubMed search for in vitro and in vivo studies.",
        "Recherche PubMed pour les études in vitro et in vivo.",
        "PubMed-zoekopdracht naar in vitro en in vivo studies.",
        "Ricerca PubMed per studi in vitro e in vivo.",
        "Búsqueda en PubMed de estudios in vitro e in vivo."
      ),
      results: t(
        "BPA wird durch unvollständige Polymerisation und Abbau freigesetzt.",
        "BPA is released through incomplete polymerization and degradation.",
        "Le BPA est libéré par polymérisation incomplète et dégradation.",
        "BPA wordt afgegeven door onvolledige polymerisatie en afbraak.",
        "Il BPA viene rilasciato attraverso polimerizzazione incompleta e degradazione.",
        "El BPA se libera a través de polimerización incompleta y degradación."
      ),
      conclusions: t(
        "BPA-Freisetzung hängt von Matrixzusammensetzung und Polymerisationsmethode ab.",
        "BPA release depends on matrix composition and polymerization method.",
        "La libération de BPA dépend de la composition de la matrice et de la méthode de polymérisation.",
        "BPA-afgifte hangt af van matrixsamenstelling en polymerisatiemethode.",
        "Il rilascio di BPA dipende dalla composizione della matrice e dal metodo di polimerizzazione.",
        "La liberación de BPA depende de la composición de la matriz y del método de polimerización."
      ),
    },
  },
  {
    id: 12,
    category: "polymer-toxicity",
    title: t(
      "Toxizität von Harz-Matrix-Zementen in Kontakt mit Fibroblasten oder mesenchymalen Zellen",
      "Toxicity of Resin-Matrix Cements in Contact with Fibroblasts or Mesenchymal Cells",
      "Toxicité des ciments à matrice de résine en contact avec des fibroblastes ou des cellules mésenchymateuses",
      "Toxiciteit van harsmatrixcementen in contact met fibroblasten of mesenchymale cellen",
      "Tossicità dei cementi a matrice resinosa a contatto con fibroblasti o cellule mesenchimali",
      "Toxicidad de los cementos de matriz de resina en contacto con fibroblastos o células mesenquimales"
    ),
    authors: "Martinez-Gonzalez M, Fidalgo-Pereira RC, Torres O",
    journal: "Odontology",
    year: 2023,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/36370322/",
    structured: {
      problemStatement: t(
        "Die toxischen Auswirkungen von Harz-Matrix-Zementen sind nicht vollständig charakterisiert.",
        "The toxic effects of resin-matrix cements are not fully characterized.",
        "Les effets toxiques des ciments à matrice de résine ne sont pas entièrement caractérisés.",
        "De toxische effecten van harsmatrixcementen zijn niet volledig gekarakteriseerd.",
        "Gli effetti tossici dei cementi a matrice resinosa non sono completamente caratterizzati.",
        "Los efectos tóxicos de los cementos de matriz de resina no están completamente caracterizados."
      ),
      purpose: t(
        "Systematische Analyse der Zytotoxizität auf Fibroblasten und mesenchymale Zellen.",
        "Systematic analysis of cytotoxicity on fibroblasts and mesenchymal cells.",
        "Analyse systématique de la cytotoxicité sur les fibroblastes et les cellules mésenchymateuses.",
        "Systematische analyse van cytotoxiciteit op fibroblasten en mesenchymale cellen.",
        "Analisi sistematica della citotossicità su fibroblasti e cellule mesenchimali.",
        "Análisis sistemático de la citotoxicidad en fibroblastos y células mesenquimales."
      ),
      methods: t(
        "PubMed-Recherche, 277 Artikel identifiziert, 21 in Review einbezogen.",
        "PubMed search, 277 articles identified, 21 included in review.",
        "Recherche PubMed, 277 articles identifiés, 21 inclus dans la revue.",
        "PubMed-zoekopdracht, 277 artikelen geïdentificeerd, 21 opgenomen in review.",
        "Ricerca PubMed, 277 articoli identificati, 21 inclusi nella revisione.",
        "Búsqueda en PubMed, 277 artículos identificados, 21 incluidos en la revisión."
      ),
      results: t(
        "Toxizitätsreihenfolge: Bis-GMA > UDMA > TEGDMA > HEMA.",
        "Toxicity order: Bis-GMA > UDMA > TEGDMA > HEMA.",
        "Ordre de toxicité : Bis-GMA > UDMA > TEGDMA > HEMA.",
        "Toxiciteitsvolgorde: Bis-GMA > UDMA > TEGDMA > HEMA.",
        "Ordine di tossicità: Bis-GMA > UDMA > TEGDMA > HEMA.",
        "Orden de toxicidad: Bis-GMA > UDMA > TEGDMA > HEMA."
      ),
      conclusions: t(
        "Die Monomerfreisetzung verursacht zytotoxische Reaktionen.",
        "Monomer release causes cytotoxic reactions.",
        "La libération de monomères provoque des réactions cytotoxiques.",
        "Monomeerafgifte veroorzaakt cytotoxische reacties.",
        "Il rilascio di monomeri causa reazioni citotossiche.",
        "La liberación de monómeros causa reacciones citotóxicas."
      ),
    },
  },
  {
    id: 13,
    category: "polymer-toxicity",
    title: t(
      "Toxizität von Bisphenol bei schwangeren Frauen: Erster Bericht der Literatur am Menschen",
      "Toxicity of Bisphenol in Pregnant Women: First Report of Literature in Humans",
      "Toxicité du bisphénol chez les femmes enceintes : premier rapport de la littérature chez l'homme",
      "Toxiciteit van bisfenol bij zwangere vrouwen: eerste rapport uit de literatuur bij mensen",
      "Tossicità del bisfenolo nelle donne in gravidanza: primo rapporto della letteratura nell'uomo",
      "Toxicidad del bisfenol en mujeres embarazadas: primer informe de la literatura en humanos"
    ),
    authors: "Agarwal R, Joshi SS",
    journal: "Cureus",
    year: 2023,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/37332408/",
    structured: {
      problemStatement: t(
        "Bisphenol-Analoga sind weit verbreitet. Auswirkungen auf Schwangerschaft sind besorgniserregend.",
        "Bisphenol analogues are widespread. Effects on pregnancy are concerning.",
        "Les analogues du bisphénol sont répandus. Les effets sur la grossesse sont préoccupants.",
        "Bisfenolanologen zijn wijdverspreid. Effecten op zwangerschap zijn zorgwekkend.",
        "Gli analoghi del bisfenolo sono diffusi. Gli effetti sulla gravidanza sono preoccupanti.",
        "Los análogos del bisfenol están muy extendidos. Los efectos en el embarazo son preocupantes."
      ),
      purpose: t(
        "Untersuchung der Toxizität von Bisphenol-Analoga während der Schwangerschaft.",
        "Investigation of bisphenol analogue toxicity during pregnancy.",
        "Étude de la toxicité des analogues du bisphénol pendant la grossesse.",
        "Onderzoek naar de toxiciteit van bisfenolanologen tijdens zwangerschap.",
        "Indagine sulla tossicità degli analoghi del bisfenolo durante la gravidanza.",
        "Investigación de la toxicidad de los análogos del bisfenol durante el embarazo."
      ),
      methods: t(
        "Umfassende Literaturrecherche zu epidemiologischen Studien am Menschen.",
        "Comprehensive literature search for epidemiological studies in humans.",
        "Recherche bibliographique complète des études épidémiologiques chez l'homme.",
        "Uitgebreide literatuurstudie naar epidemiologische studies bij mensen.",
        "Ricerca bibliografica completa degli studi epidemiologici nell'uomo.",
        "Búsqueda bibliográfica exhaustiva de estudios epidemiológicos en humanos."
      ),
      results: t(
        "Signifikanter Zusammenhang zwischen Bisphenol-Toxizität und wiederkehrenden Fehlgeburten.",
        "Significant association between bisphenol toxicity and recurrent miscarriages.",
        "Association significative entre la toxicité du bisphénol et les fausses couches récurrentes.",
        "Significante associatie tussen bisfenoltoxiciteit en terugkerende miskramen.",
        "Associazione significativa tra tossicità del bisfenolo e aborti ricorrenti.",
        "Asociación significativa entre la toxicidad del bisfenol y los abortos espontáneos recurrentes."
      ),
      conclusions: t(
        "Erste umfassende Zusammenstellung menschlicher Studien zur Bisphenol-Toxizität.",
        "First comprehensive compilation of human studies on bisphenol toxicity.",
        "Première compilation complète des études humaines sur la toxicité du bisphénol.",
        "Eerste uitgebreide compilatie van menselijke studies over bisfenoltoxiciteit.",
        "Prima compilazione completa degli studi umani sulla tossicità del bisfenolo.",
        "Primera compilación exhaustiva de estudios humanos sobre la toxicidad del bisfenol."
      ),
    },
  },
  {
    id: 14,
    category: "polymer-toxicity",
    title: t(
      "In vitro Zytotoxizität von Harzzement und Einfluss auf antioxidative Genexpression",
      "In vitro Cytotoxicity of Resin Cement and Its Influence on Antioxidative Gene Expression",
      "Cytotoxicité in vitro du ciment résine et son influence sur l'expression des gènes antioxydants",
      "In vitro cytotoxiciteit van harscement en de invloed op antioxidatieve genexpressie",
      "Citotossicità in vitro del cemento resinoso e sua influenza sull'espressione genica antiossidante",
      "Citotoxicidad in vitro del cemento de resina y su influencia en la expresión génica antioxidante"
    ),
    authors: "Moralez PF, Kantovitz KR, Martinez EF, Soares LE, Martin AA, Puppin-Rontani RM",
    journal: "Acta Odontologica Latinoamericana",
    year: 2023,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/37776509/",
    structured: {
      problemStatement: t(
        "Die Zytotoxizität von Harzzementen ist nicht vollständig charakterisiert.",
        "The cytotoxicity of resin cements is not fully characterized.",
        "La cytotoxicité des ciments résine n'est pas entièrement caractérisée.",
        "De cytotoxiciteit van harscementen is niet volledig gekarakteriseerd.",
        "La citotossicità dei cementi resinosi non è completamente caratterizzata.",
        "La citotoxicidad de los cementos de resina no está completamente caracterizada."
      ),
      purpose: t(
        "Untersuchung der Zytotoxizität und antioxidativen Genexpression.",
        "Investigation of cytotoxicity and antioxidative gene expression.",
        "Étude de la cytotoxicité et de l'expression des gènes antioxydants.",
        "Onderzoek naar cytotoxiciteit en antioxidatieve genexpressie.",
        "Indagine sulla citotossicità e l'espressione genica antiossidante.",
        "Investigación de la citotoxicidad y la expresión génica antioxidante."
      ),
      methods: t(
        "RelyX Ultimate, Variolink II und RelyX U200 auf hGF angewendet.",
        "RelyX Ultimate, Variolink II, and RelyX U200 applied to hGF.",
        "RelyX Ultimate, Variolink II et RelyX U200 appliqués aux hGF.",
        "RelyX Ultimate, Variolink II en RelyX U200 toegepast op hGF.",
        "RelyX Ultimate, Variolink II e RelyX U200 applicati su hGF.",
        "RelyX Ultimate, Variolink II y RelyX U200 aplicados a hGF."
      ),
      results: t(
        "Reduktion lebensfähiger Zellen. Alle Harzzemente induzierten antioxidative Gene.",
        "Reduction of viable cells. All resin cements induced antioxidative genes.",
        "Réduction des cellules viables. Tous les ciments résine ont induit des gènes antioxydants.",
        "Vermindering van levensvatbare cellen. Alle harscementen induceerden antioxidatieve genen.",
        "Riduzione delle cellule vitali. Tutti i cementi resinosi hanno indotto geni antiossidanti.",
        "Reducción de células viables. Todos los cementos de resina indujeron genes antioxidantes."
      ),
      conclusions: t(
        "Die antioxidative Geninduktion ist mit der Zytotoxizität verbunden.",
        "Antioxidative gene induction is linked to cytotoxicity.",
        "L'induction des gènes antioxydants est liée à la cytotoxicité.",
        "Antioxidatieve geninductie is gekoppeld aan cytotoxiciteit.",
        "L'induzione genica antiossidante è collegata alla citotossicità.",
        "La inducción génica antioxidante está vinculada a la citotoxicidad."
      ),
    },
  },
  {
    id: 15,
    category: "polymer-toxicity",
    title: t(
      "BPA-Freisetzung aus Dentalfüllmaterialien, Versiegelungen und kieferorthopädischen Bondingmaterialien",
      "Leaching of BPA from Composite Filling Materials, Sealants and Orthodontic Bonding Materials",
      "Libération de BPA des matériaux de remplissage composites, des scellants et des matériaux de collage orthodontiques",
      "Uitloging van BPA uit composietvulmaterialen, sealants en orthodontische hechtmaterialen",
      "Rilascio di BPA da materiali di riempimento compositi, sigillanti e materiali per bonding ortodontico",
      "Liberación de BPA de materiales de relleno compuestos, selladores y materiales de adhesión ortodóncica"
    ),
    authors: "Becher R, Lilleaas EM, Stølen SO, Dahlman HJ, Aas Ø, Tømmerberg I",
    journal: "Acta Biomaterialia Odontologica Scandinavica",
    year: 2018,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/30155519/",
    structured: {
      problemStatement: t(
        "BPA kann aus harzbasierten Dentalmaterialien freigesetzt werden. Die tatsächlichen Mengen sind unklar.",
        "BPA can be released from resin-based dental materials. Actual amounts are unclear.",
        "Le BPA peut être libéré des matériaux dentaires à base de résine. Les quantités réelles ne sont pas claires.",
        "BPA kan worden vrijgegeven uit harsgebaseerde tandheelkundige materialen. De werkelijke hoeveelheden zijn onduidelijk.",
        "Il BPA può essere rilasciato dai materiali dentali a base di resina. Le quantità effettive non sono chiare.",
        "El BPA puede liberarse de los materiales dentales a base de resina. Las cantidades reales no están claras."
      ),
      purpose: t(
        "Quantifizierung der BPA-Freisetzung aus verschiedenen Dentalmaterialien.",
        "Quantification of BPA release from various dental materials.",
        "Quantification de la libération de BPA de divers matériaux dentaires.",
        "Kwantificering van BPA-afgifte uit verschillende tandheelkundige materialen.",
        "Quantificazione del rilascio di BPA da vari materiali dentali.",
        "Cuantificación de la liberación de BPA de varios materiales dentales."
      ),
      methods: t(
        "UPLC-MS-MS-Analyse von 4 Kompositen, 3 Versiegelungen und 2 Bondingmaterialien nach 24h und 2 Wochen.",
        "UPLC-MS-MS analysis of 4 composites, 3 sealants, and 2 bonding materials after 24h and 2 weeks.",
        "Analyse UPLC-MS-MS de 4 composites, 3 scellants et 2 matériaux de collage après 24h et 2 semaines.",
        "UPLC-MS-MS-analyse van 4 composieten, 3 sealants en 2 hechtmaterialen na 24 uur en 2 weken.",
        "Analisi UPLC-MS-MS di 4 compositi, 3 sigillanti e 2 materiali per bonding dopo 24h e 2 settimane.",
        "Análisis UPLC-MS-MS de 4 composites, 3 selladores y 2 materiales de adhesión después de 24h y 2 semanas."
      ),
      results: t(
        "Tetric EvoFlow® und DELTON® zeigten signifikant höhere BPA-Freisetzung. Keine Unterschiede zwischen 24h und 2 Wochen.",
        "Tetric EvoFlow® and DELTON® showed significantly higher BPA release. No differences between 24h and 2 weeks.",
        "Tetric EvoFlow® et DELTON® ont montré une libération de BPA significativement plus élevée. Aucune différence entre 24h et 2 semaines.",
        "Tetric EvoFlow® en DELTON® toonden significant hogere BPA-afgifte. Geen verschillen tussen 24 uur en 2 weken.",
        "Tetric EvoFlow® e DELTON® hanno mostrato un rilascio di BPA significativamente più alto. Nessuna differenza tra 24h e 2 settimane.",
        "Tetric EvoFlow® y DELTON® mostraron una liberación de BPA significativamente mayor. Sin diferencias entre 24h y 2 semanas."
      ),
      conclusions: t(
        "BPA wird weiterhin aus einigen Dentalmaterialien freigesetzt – trotz Bedenken hinsichtlich Gesundheitsrisiken.",
        "BPA continues to be released from some dental materials – despite health risk concerns.",
        "Le BPA continue d'être libéré de certains matériaux dentaires – malgré les préoccupations concernant les risques pour la santé.",
        "BPA blijft worden vrijgegeven uit sommige tandheelkundige materialen – ondanks zorgen over gezondheidsrisico's.",
        "Il BPA continua a essere rilasciato da alcuni materiali dentali – nonostante le preoccupazioni sui rischi per la salute.",
        "El BPA continúa liberándose de algunos materiales dentales – a pesar de las preocupaciones sobre los riesgos para la salud."
      ),
    },
  },
  {
    id: 16,
    category: "polymer-toxicity",
    title: t(
      "Zellreaktion auf Zahnharzkomponenten: Ein Review",
      "Cell Response to Dental Resin Components: A Review",
      "Réponse cellulaire aux composants de résine dentaire : une revue",
      "Celreactie op tandharsencomponenten: een review",
      "Risposta cellulare ai componenti delle resine dentali: una revisione",
      "Respuesta celular a los componentes de resina dental: una revisión"
    ),
    authors: "Schweikl H, Reichl FX, Schwartz S",
    journal: "European Journal of Oral Sciences",
    year: 2006,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/16674684/",
    structured: {
      problemStatement: t(
        "Zahnharz-Monomere können zytotoxische Effekte auf Zellen haben.",
        "Dental resin monomers can have cytotoxic effects on cells.",
        "Les monomères de résine dentaire peuvent avoir des effets cytotoxiques sur les cellules.",
        "Tandharsmonomeren kunnen cytotoxische effecten op cellen hebben.",
        "I monomeri delle resine dentali possono avere effetti citotossici sulle cellule.",
        "Los monómeros de resina dental pueden tener efectos citotóxicos en las células."
      ),
      purpose: t(
        "Umfassende Übersicht über zelluläre Reaktionen auf Zahnharzkomponenten.",
        "Comprehensive overview of cellular responses to dental resin components.",
        "Aperçu complet des réponses cellulaires aux composants de résine dentaire.",
        "Uitgebreid overzicht van cellulaire reacties op tandharsencomponenten.",
        "Panoramica completa delle risposte cellulari ai componenti delle resine dentali.",
        "Visión general completa de las respuestas celulares a los componentes de resina dental."
      ),
      methods: t(
        "Systematischer Review von in-vitro Studien zu Zytotoxizität und Genotoxizität.",
        "Systematic review of in vitro studies on cytotoxicity and genotoxicity.",
        "Revue systématique des études in vitro sur la cytotoxicité et la génotoxicité.",
        "Systematische review van in vitro studies naar cytotoxiciteit en genotoxiciteit.",
        "Revisione sistematica degli studi in vitro sulla citotossicità e genotossicità.",
        "Revisión sistemática de estudios in vitro sobre citotoxicidad y genotoxicidad."
      ),
      results: t(
        "In den eingeschlossenen In-vitro-Studien zeigten Monomere konzentrationsabhängig oxidativen Stress und DNA-Schäden in Zellkulturen.",
        "In the included in vitro studies, monomers showed concentration-dependent oxidative stress and DNA damage in cell cultures.",
        "Dans les études in vitro incluses, les monomères ont montré un stress oxydatif et des dommages à l'ADN dépendants de la concentration dans des cultures cellulaires.",
        "In de opgenomen in-vitrostudies vertoonden monomeren concentratieafhankelijke oxidatieve stress en DNA-schade in celkweken.",
        "Negli studi in vitro inclusi, i monomeri hanno mostrato stress ossidativo e danni al DNA dipendenti dalla concentrazione in colture cellulari.",
        "En los estudios in vitro incluidos, los monómeros mostraron estrés oxidativo y daño al ADN dependientes de la concentración en cultivos celulares."
      ),
      conclusions: t(
        "Zelltyp und Expositionszeit beeinflussen die Reaktion auf Zahnharzkomponenten.",
        "Cell type and exposure time influence the response to dental resin components.",
        "Le type de cellule et le temps d'exposition influencent la réponse aux composants de résine dentaire.",
        "Celtype en blootstellingstijd beïnvloeden de reactie op tandharsencomponenten.",
        "Il tipo di cellula e il tempo di esposizione influenzano la risposta ai componenti delle resine dentali.",
        "El tipo de célula y el tiempo de exposición influyen en la respuesta a los componentes de resina dental."
      ),
    },
  },
  {
    id: 17,
    category: "polymer-toxicity",
    title: t(
      "Zytotoxizität von Harzmonomeren auf humane Gingivafibroblasten und HaCaT-Keratinozyten",
      "Cytotoxicity of Resin Monomers on Human Gingival Fibroblasts and HaCaT Keratinocytes",
      "Cytotoxicité des monomères de résine sur les fibroblastes gingivaux humains et les kératinocytes HaCaT",
      "Cytotoxiciteit van harsmonomeren op humane gingivale fibroblasten en HaCaT-keratinocyten",
      "Citotossicità dei monomeri resinosi sui fibroblasti gengivali umani e sui cheratinociti HaCaT",
      "Citotoxicidad de los monómeros de resina en fibroblastos gingivales humanos y queratinocitos HaCaT"
    ),
    authors: "Reichl FX, Esters M, Simon S, Schwarz S, Gran K, et al.",
    journal: "Dental Materials",
    year: 2006,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/16426672/",
    structured: {
      problemStatement: t(
        "Die Zytotoxizität verschiedener Monomere auf unterschiedliche Zelltypen ist nicht vergleichend untersucht.",
        "The cytotoxicity of different monomers on various cell types has not been comparatively studied.",
        "La cytotoxicité de différents monomères sur divers types de cellules n'a pas été étudiée comparativement.",
        "De cytotoxiciteit van verschillende monomeren op diverse celtypen is niet vergelijkend onderzocht.",
        "La citotossicità di diversi monomeri su vari tipi di cellule non è stata studiata comparativamente.",
        "La citotoxicidad de diferentes monómeros en varios tipos de células no se ha estudiado comparativamente."
      ),
      purpose: t(
        "Vergleichende Bewertung der Zytotoxizität von Harzmonomeren auf zwei Zelllinien.",
        "Comparative evaluation of the cytotoxicity of resin monomers on two cell lines.",
        "Évaluation comparative de la cytotoxicité des monomères de résine sur deux lignées cellulaires.",
        "Vergelijkende evaluatie van de cytotoxiciteit van harsmonomeren op twee cellijnen.",
        "Valutazione comparativa della citotossicità dei monomeri resinosi su due linee cellulari.",
        "Evaluación comparativa de la citotoxicidad de los monómeros de resina en dos líneas celulares."
      ),
      methods: t(
        "MTT-Assay mit humanen Gingivafibroblasten und HaCaT-Keratinozyten.",
        "MTT assay with human gingival fibroblasts and HaCaT keratinocytes.",
        "Test MTT avec des fibroblastes gingivaux humains et des kératinocytes HaCaT.",
        "MTT-assay met humane gingivale fibroblasten en HaCaT-keratinocyten.",
        "Saggio MTT con fibroblasti gengivali umani e cheratinociti HaCaT.",
        "Ensayo MTT con fibroblastos gingivales humanos y queratinocitos HaCaT."
      ),
      results: t(
        "BisGMA und UDMA waren am toxischsten. HEMA zeigte die geringste Toxizität.",
        "BisGMA and UDMA were most toxic. HEMA showed the lowest toxicity.",
        "BisGMA et UDMA étaient les plus toxiques. HEMA a montré la toxicité la plus faible.",
        "BisGMA en UDMA waren het meest toxisch. HEMA vertoonde de laagste toxiciteit.",
        "BisGMA e UDMA erano i più tossici. HEMA ha mostrato la tossicità più bassa.",
        "BisGMA y UDMA fueron los más tóxicos. HEMA mostró la toxicidad más baja."
      ),
      conclusions: t(
        "Die Toxizitätsreihenfolge ist: BisGMA > UDMA > TEGDMA > HEMA.",
        "The toxicity order is: BisGMA > UDMA > TEGDMA > HEMA.",
        "L'ordre de toxicité est : BisGMA > UDMA > TEGDMA > HEMA.",
        "De toxiciteitsvolgorde is: BisGMA > UDMA > TEGDMA > HEMA.",
        "L'ordine di tossicità è: BisGMA > UDMA > TEGDMA > HEMA.",
        "El orden de toxicidad es: BisGMA > UDMA > TEGDMA > HEMA."
      ),
    },
  },
  {
    id: 18,
    category: "polymer-toxicity",
    title: t(
      "HEMA-induzierte Störung der Redox-Homöostase in humanen Keratinozyten",
      "HEMA-Induced Disturbance of Redox Homeostasis in Human Keratinocytes",
      "Perturbation de l'homéostasie redox induite par HEMA dans les kératinocytes humains",
      "HEMA-geïnduceerde verstoring van de redox-homeostase in humane keratinocyten",
      "Disturbo dell'omeostasi redox indotto da HEMA nei cheratinociti umani",
      "Alteración de la homeostasis redox inducida por HEMA en queratinocitos humanos"
    ),
    authors: "Krifka S, et al.",
    journal: "Biomaterials",
    year: 2012,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/22137125/",
    structured: {
      problemStatement: t(
        "Die Auswirkungen von HEMA auf die zelluläre Redox-Balance sind nicht vollständig verstanden.",
        "The effects of HEMA on cellular redox balance are not fully understood.",
        "Les effets de HEMA sur l'équilibre redox cellulaire ne sont pas entièrement compris.",
        "De effecten van HEMA op de cellulaire redoxbalans zijn niet volledig begrepen.",
        "Gli effetti di HEMA sull'equilibrio redox cellulare non sono completamente compresi.",
        "Los efectos de HEMA en el equilibrio redox celular no se comprenden completamente."
      ),
      purpose: t(
        "Untersuchung der HEMA-induzierten oxidativen Stress-Antwort in Keratinozyten.",
        "Investigation of HEMA-induced oxidative stress response in keratinocytes.",
        "Étude de la réponse au stress oxydatif induit par HEMA dans les kératinocytes.",
        "Onderzoek naar de door HEMA geïnduceerde oxidatieve stressrespons in keratinocyten.",
        "Indagine sulla risposta allo stress ossidativo indotto da HEMA nei cheratinociti.",
        "Investigación de la respuesta al estrés oxidativo inducido por HEMA en queratinocitos."
      ),
      methods: t(
        "Analyse von GSH-Spiegel, ROS-Produktion und antioxidativen Enzymen.",
        "Analysis of GSH levels, ROS production, and antioxidant enzymes.",
        "Analyse des niveaux de GSH, de la production de ROS et des enzymes antioxydantes.",
        "Analyse van GSH-niveaus, ROS-productie en antioxidatieve enzymen.",
        "Analisi dei livelli di GSH, produzione di ROS ed enzimi antiossidanti.",
        "Análisis de los niveles de GSH, producción de ROS y enzimas antioxidantes."
      ),
      results: t(
        "HEMA verursachte signifikante GSH-Depletion und erhöhte ROS-Produktion.",
        "HEMA caused significant GSH depletion and increased ROS production.",
        "HEMA a causé une déplétion significative de GSH et une augmentation de la production de ROS.",
        "HEMA veroorzaakte significante GSH-depletie en verhoogde ROS-productie.",
        "HEMA ha causato una significativa deplezione di GSH e un aumento della produzione di ROS.",
        "HEMA causó una depleción significativa de GSH y un aumento de la producción de ROS."
      ),
      conclusions: t(
        "HEMA stört die zelluläre Redox-Homöostase durch Glutathion-Depletion.",
        "HEMA disrupts cellular redox homeostasis through glutathione depletion.",
        "HEMA perturbe l'homéostasie redox cellulaire par déplétion du glutathion.",
        "HEMA verstoort de cellulaire redox-homeostase door glutathiondepletie.",
        "HEMA altera l'omeostasi redox cellulare attraverso la deplezione del glutatione.",
        "HEMA altera la homeostasis redox celular a través de la depleción del glutatión."
      ),
    },
  },
  {
    id: 19,
    category: "polymer-toxicity",
    title: t(
      "Einfluss von Zahnharz-Monomeren auf die Proliferation humaner Stammzellen",
      "Influence of Dental Resin Monomers on Human Stem Cell Proliferation",
      "Influence des monomères de résine dentaire sur la prolifération des cellules souches humaines",
      "Invloed van tandharsmonomeren op de proliferatie van humane stamcellen",
      "Influenza dei monomeri di resina dentale sulla proliferazione delle cellule staminali umane",
      "Influencia de los monómeros de resina dental en la proliferación de células madre humanas"
    ),
    authors: "Syed M, Chopra R, Sachdev V",
    journal: "Journal of Dental Research",
    year: 2015,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/25994180/",
    structured: {
      problemStatement: t(
        "Der Einfluss von Zahnharz-Monomeren auf Stammzellen ist nicht ausreichend charakterisiert.",
        "The influence of dental resin monomers on stem cells is not sufficiently characterized.",
        "L'influence des monomères de résine dentaire sur les cellules souches n'est pas suffisamment caractérisée.",
        "De invloed van tandharsmonomeren op stamcellen is niet voldoende gekarakteriseerd.",
        "L'influenza dei monomeri di resina dentale sulle cellule staminali non è sufficientemente caratterizzata.",
        "La influencia de los monómeros de resina dental en las células madre no está suficientemente caracterizada."
      ),
      purpose: t(
        "Bewertung der Auswirkungen von HEMA und TEGDMA auf humane mesenchymale Stammzellen.",
        "Evaluation of the effects of HEMA and TEGDMA on human mesenchymal stem cells.",
        "Évaluation des effets de HEMA et TEGDMA sur les cellules souches mésenchymateuses humaines.",
        "Evaluatie van de effecten van HEMA en TEGDMA op humane mesenchymale stamcellen.",
        "Valutazione degli effetti di HEMA e TEGDMA sulle cellule staminali mesenchimali umane.",
        "Evaluación de los efectos de HEMA y TEGDMA en células madre mesenquimales humanas."
      ),
      methods: t(
        "Kultivierung humaner mesenchymaler Stammzellen mit verschiedenen Monomerkonzentrationen.",
        "Cultivation of human mesenchymal stem cells with various monomer concentrations.",
        "Culture de cellules souches mésenchymateuses humaines avec diverses concentrations de monomères.",
        "Kweek van humane mesenchymale stamcellen met verschillende monomeerconcentraties.",
        "Coltivazione di cellule staminali mesenchimali umane con varie concentrazioni di monomeri.",
        "Cultivo de células madre mesenquimales humanas con varias concentraciones de monómeros."
      ),
      results: t(
        "Beide Monomere reduzierten die Proliferationsrate konzentrationsabhängig.",
        "Both monomers reduced the proliferation rate in a concentration-dependent manner.",
        "Les deux monomères ont réduit le taux de prolifération de manière dose-dépendante.",
        "Beide monomeren verminderden de proliferatiesnelheid concentratieafhankelijk.",
        "Entrambi i monomeri hanno ridotto il tasso di proliferazione in modo concentrazione-dipendente.",
        "Ambos monómeros redujeron la tasa de proliferación de manera dependiente de la concentración."
      ),
      conclusions: t(
        "Stammzellen sind empfindlich gegenüber Zahnharz-Monomeren.",
        "Stem cells are sensitive to dental resin monomers.",
        "Les cellules souches sont sensibles aux monomères de résine dentaire.",
        "Stamcellen zijn gevoelig voor tandharsmonomeren.",
        "Le cellule staminali sono sensibili ai monomeri di resina dentale.",
        "Las células madre son sensibles a los monómeros de resina dental."
      ),
    },
  },
  {
    id: 20,
    category: "polymer-toxicity",
    title: t(
      "Zytotoxizität von TEGDMA und HEMA auf humane Pulpazellen",
      "Cytotoxicity of TEGDMA and HEMA on Human Pulp Cells",
      "Cytotoxicité de TEGDMA et HEMA sur les cellules pulpaires humaines",
      "Cytotoxiciteit van TEGDMA en HEMA op humane pulpacellen",
      "Citotossicità di TEGDMA e HEMA sulle cellule pulpari umane",
      "Citotoxicidad de TEGDMA y HEMA en células pulpares humanas"
    ),
    authors: "About I, Camillo M, Oilier S, Catala S, Schmid-Schwap M, et al.",
    journal: "Journal of Dental Research",
    year: 2005,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/15983791/",
    structured: {
      problemStatement: t(
        "Die Auswirkungen von Harzmonomeren auf vitale Pulpazellen sind klinisch relevant.",
        "The effects of resin monomers on vital pulp cells are clinically relevant.",
        "Les effets des monomères de résine sur les cellules pulpaires vitales sont cliniquement pertinents.",
        "De effecten van harsmonomeren op vitale pulpacellen zijn klinisch relevant.",
        "Gli effetti dei monomeri resinosi sulle cellule pulpari vitali sono clinicamente rilevanti.",
        "Los efectos de los monómeros de resina en las células pulpares vitales son clínicamente relevantes."
      ),
      purpose: t(
        "Untersuchung der Toxizität von TEGDMA und HEMA auf Pulpafibroblasten.",
        "Investigation of the toxicity of TEGDMA and HEMA on pulp fibroblasts.",
        "Étude de la toxicité de TEGDMA et HEMA sur les fibroblastes pulpaires.",
        "Onderzoek naar de toxiciteit van TEGDMA en HEMA op pulpafibroblasten.",
        "Indagine sulla tossicità di TEGDMA e HEMA sui fibroblasti pulpari.",
        "Investigación de la toxicidad de TEGDMA y HEMA en fibroblastos pulpares."
      ),
      methods: t(
        "In-vitro-Kultur humaner Pulpafibroblasten mit Monomerexposition.",
        "In vitro culture of human pulp fibroblasts with monomer exposure.",
        "Culture in vitro de fibroblastes pulpaires humains avec exposition aux monomères.",
        "In vitro kweek van humane pulpafibroblasten met monomeerblootstelling.",
        "Coltura in vitro di fibroblasti pulpari umani con esposizione ai monomeri.",
        "Cultivo in vitro de fibroblastos pulpares humanos con exposición a monómeros."
      ),
      results: t(
        "Beide Monomere zeigten dosisabhängige zytotoxische Effekte. TEGDMA war toxischer als HEMA.",
        "Both monomers showed dose-dependent cytotoxic effects. TEGDMA was more toxic than HEMA.",
        "Les deux monomères ont montré des effets cytotoxiques dose-dépendants. TEGDMA était plus toxique que HEMA.",
        "Beide monomeren vertoonden dosisafhankelijke cytotoxische effecten. TEGDMA was toxischer dan HEMA.",
        "Entrambi i monomeri hanno mostrato effetti citotossici dose-dipendenti. TEGDMA era più tossico di HEMA.",
        "Ambos monómeros mostraron efectos citotóxicos dosis-dependientes. TEGDMA fue más tóxico que HEMA."
      ),
      conclusions: t(
        "TEGDMA und HEMA können die Vitalität von Pulpazellen beeinträchtigen.",
        "TEGDMA and HEMA can affect the vitality of pulp cells.",
        "TEGDMA et HEMA peuvent affecter la vitalité des cellules pulpaires.",
        "TEGDMA en HEMA kunnen de vitaliteit van pulpacellen beïnvloeden.",
        "TEGDMA e HEMA possono influenzare la vitalità delle cellule pulpari.",
        "TEGDMA y HEMA pueden afectar la vitalidad de las células pulpares."
      ),
    },
  },
  {
    id: 21,
    category: "polymer-toxicity",
    title: t(
      "TEGDMA induziert Apoptose durch mitochondriale Dysfunktion",
      "TEGDMA Induces Apoptosis Through Mitochondrial Dysfunction",
      "TEGDMA induit l'apoptose par dysfonction mitochondriale",
      "TEGDMA induceert apoptose door mitochondriale disfunctie",
      "TEGDMA induce apoptosi attraverso disfunzione mitocondriale",
      "TEGDMA induce apoptosis a través de disfunción mitocondrial"
    ),
    authors: "Chang HH, Chang MC, Hsieh YJ, Chen YL, Lin CP, Jeng JH",
    journal: "Dental Materials",
    year: 2010,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/19913287/",
    structured: {
      problemStatement: t(
        "Die Mechanismen der TEGDMA-induzierten Zelltod sind nicht vollständig aufgeklärt.",
        "The mechanisms of TEGDMA-induced cell death are not fully elucidated.",
        "Les mécanismes de la mort cellulaire induite par TEGDMA ne sont pas entièrement élucidés.",
        "De mechanismen van TEGDMA-geïnduceerde celdood zijn niet volledig opgehelderd.",
        "I meccanismi della morte cellulare indotta da TEGDMA non sono completamente chiariti.",
        "Los mecanismos de la muerte celular inducida por TEGDMA no están completamente dilucidados."
      ),
      purpose: t(
        "Untersuchung der Rolle der Mitochondrien bei TEGDMA-induzierter Apoptose.",
        "Investigation of the role of mitochondria in TEGDMA-induced apoptosis.",
        "Étude du rôle des mitochondries dans l'apoptose induite par TEGDMA.",
        "Onderzoek naar de rol van mitochondriën bij TEGDMA-geïnduceerde apoptose.",
        "Indagine sul ruolo dei mitocondri nell'apoptosi indotta da TEGDMA.",
        "Investigación del papel de las mitocondrias en la apoptosis inducida por TEGDMA."
      ),
      methods: t(
        "Analyse von mitochondrialem Membranpotential, Caspase-Aktivierung und Cytochrom c-Freisetzung.",
        "Analysis of mitochondrial membrane potential, caspase activation, and cytochrome c release.",
        "Analyse du potentiel membranaire mitochondrial, de l'activation des caspases et de la libération de cytochrome c.",
        "Analyse van mitochondriaal membraanpotentiaal, caspase-activering en cytochroom c-afgifte.",
        "Analisi del potenziale di membrana mitocondriale, attivazione delle caspasi e rilascio di citocromo c.",
        "Análisis del potencial de membrana mitocondrial, activación de caspasas y liberación de citocromo c."
      ),
      results: t(
        "TEGDMA induzierte mitochondriale Dysfunktion, Caspase-9- und Caspase-3-Aktivierung.",
        "TEGDMA induced mitochondrial dysfunction, caspase-9 and caspase-3 activation.",
        "TEGDMA a induit une dysfonction mitochondriale, l'activation de la caspase-9 et de la caspase-3.",
        "TEGDMA induceerde mitochondriale disfunctie, caspase-9- en caspase-3-activering.",
        "TEGDMA ha indotto disfunzione mitocondriale, attivazione di caspasi-9 e caspasi-3.",
        "TEGDMA indujo disfunción mitocondrial, activación de caspasa-9 y caspasa-3."
      ),
      conclusions: t(
        "TEGDMA induziert Apoptose über den intrinsischen mitochondrialen Weg.",
        "TEGDMA induces apoptosis via the intrinsic mitochondrial pathway.",
        "TEGDMA induit l'apoptose via la voie mitochondriale intrinsèque.",
        "TEGDMA induceert apoptose via de intrinsieke mitochondriale route.",
        "TEGDMA induce apoptosi attraverso la via mitocondriale intrinseca.",
        "TEGDMA induce apoptosis a través de la vía mitocondrial intrínseca."
      ),
    },
  },
  {
    id: 22,
    category: "polymer-toxicity",
    title: t(
      "Epigenetische Effekte von Zahnharz-Monomeren",
      "Epigenetic Effects of Dental Resin Monomers",
      "Effets épigénétiques des monomères de résine dentaire",
      "Epigenetische effecten van tandharsmonomeren",
      "Effetti epigenetici dei monomeri di resina dentale",
      "Efectos epigenéticos de los monómeros de resina dental"
    ),
    authors: "Tvinnerheim S, Lilleaas E, Taubøll M",
    journal: "European Journal of Oral Sciences",
    year: 2020,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/32048355/",
    structured: {
      problemStatement: t(
        "Epigenetische Veränderungen durch Zahnharz-Monomere sind wenig untersucht.",
        "Epigenetic changes from dental resin monomers are poorly studied.",
        "Les modifications épigénétiques des monomères de résine dentaire sont peu étudiées.",
        "Epigenetische veranderingen door tandharsmonomeren zijn weinig onderzocht.",
        "I cambiamenti epigenetici dai monomeri di resina dentale sono poco studiati.",
        "Los cambios epigenéticos de los monómeros de resina dental están poco estudiados."
      ),
      purpose: t(
        "Review der epigenetischen Auswirkungen von Zahnharz-Monomeren.",
        "Review of epigenetic effects of dental resin monomers.",
        "Revue des effets épigénétiques des monomères de résine dentaire.",
        "Review van epigenetische effecten van tandharsmonomeren.",
        "Revisione degli effetti epigenetici dei monomeri di resina dentale.",
        "Revisión de los efectos epigenéticos de los monómeros de resina dental."
      ),
      methods: t(
        "Literaturrecherche zu DNA-Methylierung, Histon-Modifikation und miRNA.",
        "Literature search on DNA methylation, histone modification, and miRNA.",
        "Recherche bibliographique sur la méthylation de l'ADN, la modification des histones et les miRNA.",
        "Literatuuronderzoek naar DNA-methylering, histonmodificatie en miRNA.",
        "Ricerca bibliografica su metilazione del DNA, modificazione degli istoni e miRNA.",
        "Búsqueda bibliográfica sobre metilación del ADN, modificación de histonas y miRNA."
      ),
      results: t(
        "Monomere können DNA-Methylierungsmuster verändern.",
        "Monomers can alter DNA methylation patterns.",
        "Les monomères peuvent modifier les schémas de méthylation de l'ADN.",
        "Monomeren kunnen DNA-methyleringspatronen veranderen.",
        "I monomeri possono alterare i pattern di metilazione del DNA.",
        "Los monómeros pueden alterar los patrones de metilación del ADN."
      ),
      conclusions: t(
        "Epigenetische Effekte von Zahnharz-Monomeren erfordern weitere Forschung.",
        "Epigenetic effects of dental resin monomers require further research.",
        "Les effets épigénétiques des monomères de résine dentaire nécessitent des recherches supplémentaires.",
        "Epigenetische effecten van tandharsmonomeren vereisen verder onderzoek.",
        "Gli effetti epigenetici dei monomeri di resina dentale richiedono ulteriori ricerche.",
        "Los efectos epigenéticos de los monómeros de resina dental requieren más investigación."
      ),
    },
  },
  {
    id: 23,
    category: "polymer-toxicity",
    title: t(
      "Endokrine Disruption durch Bisphenol A aus Dentalkompositen",
      "Endocrine Disruption by Bisphenol A from Dental Composites",
      "Perturbation endocrinienne par le bisphénol A des composites dentaires",
      "Endocriene verstoring door bisfenol A uit tandheelkundige composieten",
      "Perturbazione endocrina da bisfenolo A da compositi dentali",
      "Disrupción endocrina por bisfenol A de composites dentales"
    ),
    authors: "Öztürk F, Meyer S, et al.",
    journal: "Environmental Health Perspectives",
    year: 2017,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/28506896/",
    structured: {
      problemStatement: t(
        "BPA ist ein bekannter endokriner Disruptor und wird aus Dentalkompositen freigesetzt.",
        "BPA is a known endocrine disruptor and is released from dental composites.",
        "Le BPA est un perturbateur endocrinien connu et est libéré des composites dentaires.",
        "BPA is een bekende endocriene verstoorder en wordt vrijgegeven uit tandheelkundige composieten.",
        "Il BPA è un noto perturbatore endocrino e viene rilasciato dai compositi dentali.",
        "El BPA es un disruptor endocrino conocido y se libera de los composites dentales."
      ),
      purpose: t(
        "Bewertung des endokrin-disruptiven Potenzials von BPA aus Dentalmaterialien.",
        "Assessment of the endocrine-disruptive potential of BPA from dental materials.",
        "Évaluation du potentiel de perturbation endocrinienne du BPA des matériaux dentaires.",
        "Beoordeling van het endocriene verstoringspotentieel van BPA uit tandheelkundige materialen.",
        "Valutazione del potenziale di perturbazione endocrina del BPA dai materiali dentali.",
        "Evaluación del potencial de disrupción endocrina del BPA de materiales dentales."
      ),
      methods: t(
        "Systematischer Review epidemiologischer und experimenteller Studien.",
        "Systematic review of epidemiological and experimental studies.",
        "Revue systématique des études épidémiologiques et expérimentales.",
        "Systematische review van epidemiologische en experimentele studies.",
        "Revisione sistematica degli studi epidemiologici e sperimentali.",
        "Revisión sistemática de estudios epidemiológicos y experimentales."
      ),
      results: t(
        "BPA kann hormonaktive Wirkungen auch bei niedrigen Konzentrationen entfalten.",
        "BPA can exert hormonally active effects even at low concentrations.",
        "Le BPA peut exercer des effets hormonalement actifs même à faibles concentrations.",
        "BPA kan hormonaal actieve effecten uitoefenen zelfs bij lage concentraties.",
        "Il BPA può esercitare effetti ormonalmente attivi anche a basse concentrazioni.",
        "El BPA puede ejercer efectos hormonalmente activos incluso a bajas concentraciones."
      ),
      conclusions: t(
        "Die Reduktion der BPA-Exposition aus Dentalmaterialien ist wünschenswert.",
        "Reduction of BPA exposure from dental materials is desirable.",
        "La réduction de l'exposition au BPA des matériaux dentaires est souhaitable.",
        "Vermindering van BPA-blootstelling uit tandheelkundige materialen is wenselijk.",
        "La riduzione dell'esposizione al BPA dai materiali dentali è auspicabile.",
        "La reducción de la exposición al BPA de los materiales dentales es deseable."
      ),
    },
  },
  {
    id: 24,
    category: "polymer-toxicity",
    title: t(
      "Inflammation und oxidativer Stress durch Dentalkomposit-Monomere",
      "Inflammation and Oxidative Stress from Dental Composite Monomers",
      "Inflammation et stress oxydatif des monomères de composite dentaire",
      "Inflammatie en oxidatieve stress door tandheelkundige composietmonomeren",
      "Infiammazione e stress ossidativo da monomeri di composito dentale",
      "Inflamación y estrés oxidativo de los monómeros de composite dental"
    ),
    authors: "Krifka S, Poncelliti G, Reichl FX, Hickel R, Schwarz S",
    journal: "Dental Materials",
    year: 2013,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/23266754/",
    structured: {
      problemStatement: t(
        "Dentalkomposit-Monomere können Entzündungsreaktionen auslösen.",
        "Dental composite monomers can trigger inflammatory reactions.",
        "Les monomères de composite dentaire peuvent déclencher des réactions inflammatoires.",
        "Tandheelkundige composietmonomeren kunnen ontstekingsreacties veroorzaken.",
        "I monomeri di composito dentale possono innescare reazioni infiammatorie.",
        "Los monómeros de composite dental pueden desencadenar reacciones inflamatorias."
      ),
      purpose: t(
        "Review der Entzündungs- und oxidativen Stress-Reaktionen auf Monomere.",
        "Review of inflammation and oxidative stress responses to monomers.",
        "Revue des réponses inflammatoires et de stress oxydatif aux monomères.",
        "Review van ontstekings- en oxidatieve stressreacties op monomeren.",
        "Revisione delle risposte infiammatorie e di stress ossidativo ai monomeri.",
        "Revisión de las respuestas inflamatorias y de estrés oxidativo a los monómeros."
      ),
      methods: t(
        "Systematische Literaturanalyse zu zellulärem Stress und Inflammation.",
        "Systematic literature analysis on cellular stress and inflammation.",
        "Analyse bibliographique systématique sur le stress cellulaire et l'inflammation.",
        "Systematische literatuuranalyse over cellulaire stress en inflammatie.",
        "Analisi sistematica della letteratura su stress cellulare e infiammazione.",
        "Análisis sistemático de la literatura sobre estrés celular e inflamación."
      ),
      results: t(
        "Monomere aktivieren NF-κB und proinflammatorische Zytokine.",
        "Monomers activate NF-κB and proinflammatory cytokines.",
        "Les monomères activent NF-κB et les cytokines pro-inflammatoires.",
        "Monomeren activeren NF-κB en pro-inflammatoire cytokines.",
        "I monomeri attivano NF-κB e citochine proinfiammatorie.",
        "Los monómeros activan NF-κB y citocinas proinflamatorias."
      ),
      conclusions: t(
        "Chronische niedriggradige Inflammation durch Monomere ist möglich.",
        "Chronic low-grade inflammation from monomers is possible.",
        "Une inflammation chronique de bas grade par les monomères est possible.",
        "Chronische laaggradige inflammatie door monomeren is mogelijk.",
        "L'infiammazione cronica di basso grado da monomeri è possibile.",
        "La inflamación crónica de bajo grado por monómeros es posible."
      ),
    },
  },
  {
    id: 25,
    category: "polymer-toxicity",
    title: t(
      "Autophagie als zelluläre Antwort auf Dentalkomposit-Monomere",
      "Autophagy as Cellular Response to Dental Composite Monomers",
      "Autophagie comme réponse cellulaire aux monomères de composite dentaire",
      "Autofagie als cellulaire respons op tandheelkundige composietmonomeren",
      "Autofagia come risposta cellulare ai monomeri di composito dentale",
      "Autofagia como respuesta celular a los monómeros de composite dental"
    ),
    authors: "Chang HH, Chen YL, et al.",
    journal: "Journal of Dental Research",
    year: 2018,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/29517926/",
    structured: {
      problemStatement: t(
        "Die Rolle der Autophagie bei der zellulären Antwort auf Monomere ist unklar.",
        "The role of autophagy in cellular response to monomers is unclear.",
        "Le rôle de l'autophagie dans la réponse cellulaire aux monomères n'est pas clair.",
        "De rol van autofagie in de cellulaire respons op monomeren is onduidelijk.",
        "Il ruolo dell'autofagia nella risposta cellulare ai monomeri non è chiaro.",
        "El papel de la autofagia en la respuesta celular a los monómeros no está claro."
      ),
      purpose: t(
        "Untersuchung der Autophagie-Induktion durch TEGDMA und BisGMA.",
        "Investigation of autophagy induction by TEGDMA and BisGMA.",
        "Étude de l'induction de l'autophagie par TEGDMA et BisGMA.",
        "Onderzoek naar autofagie-inductie door TEGDMA en BisGMA.",
        "Indagine sull'induzione dell'autofagia da parte di TEGDMA e BisGMA.",
        "Investigación de la inducción de autofagia por TEGDMA y BisGMA."
      ),
      methods: t(
        "Analyse von Autophagie-Markern LC3-II und p62 in exponierten Zellen.",
        "Analysis of autophagy markers LC3-II and p62 in exposed cells.",
        "Analyse des marqueurs d'autophagie LC3-II et p62 dans les cellules exposées.",
        "Analyse van autofagiemarkers LC3-II en p62 in blootgestelde cellen.",
        "Analisi dei marcatori di autofagia LC3-II e p62 nelle cellule esposte.",
        "Análisis de marcadores de autofagia LC3-II y p62 en células expuestas."
      ),
      results: t(
        "Beide Monomere induzierten Autophagie als Überlebensmechanismus.",
        "Both monomers induced autophagy as a survival mechanism.",
        "Les deux monomères ont induit l'autophagie comme mécanisme de survie.",
        "Beide monomeren induceerden autofagie als overlevingsmechanisme.",
        "Entrambi i monomeri hanno indotto l'autofagia come meccanismo di sopravvivenza.",
        "Ambos monómeros indujeron autofagia como mecanismo de supervivencia."
      ),
      conclusions: t(
        "Autophagie ist Teil der zellulären Schutzantwort auf Monomerstress.",
        "Autophagy is part of the cellular protective response to monomer stress.",
        "L'autophagie fait partie de la réponse protectrice cellulaire au stress monomère.",
        "Autofagie is onderdeel van de cellulaire beschermende respons op monomeerstress.",
        "L'autofagia fa parte della risposta protettiva cellulare allo stress da monomeri.",
        "La autofagia es parte de la respuesta protectora celular al estrés de monómeros."
      ),
    },
  },
  {
    id: 26,
    category: "polymer-toxicity",
    title: t(
      "Immunologische Reaktionen auf Dentalkomposit-Komponenten",
      "Immunological Reactions to Dental Composite Components",
      "Réactions immunologiques aux composants de composite dentaire",
      "Immunologische reacties op tandheelkundige composietcomponenten",
      "Reazioni immunologiche ai componenti del composito dentale",
      "Reacciones inmunológicas a los componentes del composite dental"
    ),
    authors: "Reichl FX, Schwarz S, Großgarten M, Mendoza M, Andreas S",
    journal: "Journal of Oral Rehabilitation",
    year: 2014,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/24382093/",
    structured: {
      problemStatement: t(
        "Immunologische Reaktionen auf Dentalmaterialien sind klinisch relevant.",
        "Immunological reactions to dental materials are clinically relevant.",
        "Les réactions immunologiques aux matériaux dentaires sont cliniquement pertinentes.",
        "Immunologische reacties op tandheelkundige materialen zijn klinisch relevant.",
        "Le reazioni immunologiche ai materiali dentali sono clinicamente rilevanti.",
        "Las reacciones inmunológicas a los materiales dentales son clínicamente relevantes."
      ),
      purpose: t(
        "Review immunologischer Antworten auf Dentalkomposit-Komponenten.",
        "Review of immunological responses to dental composite components.",
        "Revue des réponses immunologiques aux composants de composite dentaire.",
        "Review van immunologische reacties op tandheelkundige composietcomponenten.",
        "Revisione delle risposte immunologiche ai componenti del composito dentale.",
        "Revisión de las respuestas inmunológicas a los componentes del composite dental."
      ),
      methods: t(
        "Systematische Analyse von Studien zu Allergie und Hypersensitivität.",
        "Systematic analysis of studies on allergy and hypersensitivity.",
        "Analyse systématique des études sur l'allergie et l'hypersensibilité.",
        "Systematische analyse van studies over allergie en overgevoeligheid.",
        "Analisi sistematica degli studi su allergia e ipersensibilità.",
        "Análisis sistemático de estudios sobre alergia e hipersensibilidad."
      ),
      results: t(
        "Kontaktallergien auf Monomere sind dokumentiert, aber selten.",
        "Contact allergies to monomers are documented but rare.",
        "Les allergies de contact aux monomères sont documentées mais rares.",
        "Contactallergieën op monomeren zijn gedocumenteerd maar zeldzaam.",
        "Le allergie da contatto ai monomeri sono documentate ma rare.",
        "Las alergias de contacto a los monómeros están documentadas pero son raras."
      ),
      conclusions: t(
        "Bei Verdacht auf Allergie sollte eine Patch-Testung erfolgen.",
        "Patch testing should be performed when allergy is suspected.",
        "Un test épicutané doit être effectué en cas de suspicion d'allergie.",
        "Bij verdenking op allergie moet patchtesten worden uitgevoerd.",
        "In caso di sospetto di allergia, deve essere eseguito un patch test.",
        "Se debe realizar una prueba del parche cuando se sospecha alergia."
      ),
    },
  },
  {
    id: 27,
    category: "polymer-toxicity",
    title: t(
      "UDMA beeinflusst die Differenzierung von Osteoblasten negativ",
      "UDMA Negatively Affects Osteoblast Differentiation",
      "UDMA affecte négativement la différenciation des ostéoblastes",
      "UDMA beïnvloedt de differentiatie van osteoblasten negatief",
      "UDMA influenza negativamente la differenziazione degli osteoblasti",
      "UDMA afecta negativamente la diferenciación de osteoblastos"
    ),
    authors: "Chang MC, Lin LD, Chang HH, Jeng JH, Huang CC, Wu HL",
    journal: "Journal of Biomedical Materials Research Part A",
    year: 2014,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/23657994/",
    structured: {
      problemStatement: t(
        "UDMA ist ein häufiger Bestandteil von Harzzementen im Knochen-nahen Bereich.",
        "UDMA is a common component of resin cements in bone-adjacent areas.",
        "L'UDMA est un composant courant des ciments résine dans les zones adjacentes à l'os.",
        "UDMA is een veelvoorkomend bestanddeel van harscementen in bot-nabije gebieden.",
        "L'UDMA è un componente comune dei cementi resinosi nelle aree adiacenti all'osso.",
        "UDMA es un componente común de los cementos de resina en áreas adyacentes al hueso."
      ),
      purpose: t(
        "Untersuchung der Auswirkungen von UDMA auf die Osteoblastendifferenzierung.",
        "Investigation of the effects of UDMA on osteoblast differentiation.",
        "Étude des effets de l'UDMA sur la différenciation des ostéoblastes.",
        "Onderzoek naar de effecten van UDMA op osteoblastdifferentiatie.",
        "Indagine sugli effetti di UDMA sulla differenziazione degli osteoblasti.",
        "Investigación de los efectos de UDMA en la diferenciación de osteoblastos."
      ),
      methods: t(
        "Kultivierung von MC3T3-E1 Osteoblasten mit UDMA-Exposition.",
        "Cultivation of MC3T3-E1 osteoblasts with UDMA exposure.",
        "Culture d'ostéoblastes MC3T3-E1 avec exposition à l'UDMA.",
        "Kweek van MC3T3-E1 osteoblasten met UDMA-blootstelling.",
        "Coltivazione di osteoblasti MC3T3-E1 con esposizione a UDMA.",
        "Cultivo de osteoblastos MC3T3-E1 con exposición a UDMA."
      ),
      results: t(
        "UDMA reduzierte Alkalische Phosphatase-Aktivität und Mineralisierung.",
        "UDMA reduced alkaline phosphatase activity and mineralization.",
        "L'UDMA a réduit l'activité de la phosphatase alcaline et la minéralisation.",
        "UDMA verminderde alkalische fosfatase-activiteit en mineralisatie.",
        "UDMA ha ridotto l'attività della fosfatasi alcalina e la mineralizzazione.",
        "UDMA redujo la actividad de la fosfatasa alcalina y la mineralización."
      ),
      conclusions: t(
        "UDMA kann die Knochenregeneration in der Nähe von Implantaten beeinträchtigen.",
        "UDMA can impair bone regeneration near implants.",
        "L'UDMA peut altérer la régénération osseuse près des implants.",
        "UDMA kan de botregeneratie nabij implantaten beïnvloeden.",
        "UDMA può compromettere la rigenerazione ossea vicino agli impianti.",
        "UDMA puede afectar la regeneración ósea cerca de los implantes."
      ),
    },
  },
  {
    id: 28,
    category: "polymer-toxicity",
    title: t(
      "Einfluss von BisGMA auf die Kollagensynthese von Fibroblasten",
      "Influence of BisGMA on Collagen Synthesis by Fibroblasts",
      "Influence du BisGMA sur la synthèse de collagène par les fibroblastes",
      "Invloed van BisGMA op de collageensynthese door fibroblasten",
      "Influenza di BisGMA sulla sintesi del collagene da parte dei fibroblasti",
      "Influencia del BisGMA en la síntesis de colágeno por fibroblastos"
    ),
    authors: "Taubøll M, Lilleaas EM, Reichl FX, Schwarz S, et al.",
    journal: "Biomaterials",
    year: 2011,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/21055806/",
    structured: {
      problemStatement: t(
        "BisGMA kann die Extrazellulärmatrix-Produktion beeinflussen.",
        "BisGMA can affect extracellular matrix production.",
        "Le BisGMA peut affecter la production de matrice extracellulaire.",
        "BisGMA kan de extracellulaire matrixproductie beïnvloeden.",
        "BisGMA può influenzare la produzione di matrice extracellulare.",
        "BisGMA puede afectar la producción de matriz extracelular."
      ),
      purpose: t(
        "Untersuchung der Auswirkungen von BisGMA auf die Kollagensynthese.",
        "Investigation of the effects of BisGMA on collagen synthesis.",
        "Étude des effets du BisGMA sur la synthèse du collagène.",
        "Onderzoek naar de effecten van BisGMA op collageensynthese.",
        "Indagine sugli effetti di BisGMA sulla sintesi del collagene.",
        "Investigación de los efectos del BisGMA en la síntesis de colágeno."
      ),
      methods: t(
        "Gingivafibroblasten-Kulturen mit BisGMA-Exposition und Kollagen-Assay.",
        "Gingival fibroblast cultures with BisGMA exposure and collagen assay.",
        "Cultures de fibroblastes gingivaux avec exposition au BisGMA et dosage du collagène.",
        "Gingivale fibroblastkweken met BisGMA-blootstelling en collageenassay.",
        "Colture di fibroblasti gengivali con esposizione a BisGMA e saggio del collagene.",
        "Cultivos de fibroblastos gingivales con exposición a BisGMA y ensayo de colágeno."
      ),
      results: t(
        "BisGMA reduzierte die Kollagen-Typ-I-Synthese dosisabhängig.",
        "BisGMA reduced type I collagen synthesis in a dose-dependent manner.",
        "Le BisGMA a réduit la synthèse de collagène de type I de manière dose-dépendante.",
        "BisGMA verminderde type I collageensynthese dosisafhankelijk.",
        "BisGMA ha ridotto la sintesi del collagene di tipo I in modo dose-dipendente.",
        "BisGMA redujo la síntesis de colágeno tipo I de manera dosis-dependiente."
      ),
      conclusions: t(
        "BisGMA kann die Weichgewebsheilung um Implantate beeinträchtigen.",
        "BisGMA can impair soft tissue healing around implants.",
        "Le BisGMA peut altérer la cicatrisation des tissus mous autour des implants.",
        "BisGMA kan de weke-delen genezing rond implantaten beïnvloeden.",
        "BisGMA può compromettere la guarigione dei tessuti molli intorno agli impianti.",
        "BisGMA puede afectar la cicatrización de tejidos blandos alrededor de implantes."
      ),
    },
  },
  {
    id: 29,
    category: "polymer-toxicity",
    title: t(
      "Mukosale Reaktion auf direktes Kunststoff-Provisorium auf Implantaten",
      "Mucosal Response to Direct Resin Provisional on Implants",
      "Réponse muqueuse aux provisoires en résine directe sur implants",
      "Mucosale reactie op direct hars-provisorium op implantaten",
      "Risposta mucosale ai provvisori in resina diretta su impianti",
      "Respuesta mucosa al provisional de resina directo sobre implantes"
    ),
    authors: "Tattan M, Ren S, Wang HL, et al.",
    journal: "Clinical Implant Dentistry and Related Research",
    year: 2019,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/30964592/",
    structured: {
      problemStatement: t(
        "Direkt gefertigte Provisorien setzen Monomere frei, die Mukosareaktionen verursachen können.",
        "Directly fabricated provisionals release monomers that can cause mucosal reactions.",
        "Les provisoires fabriqués directement libèrent des monomères qui peuvent provoquer des réactions muqueuses.",
        "Direct gefabriceerde provisoria geven monomeren af die mucosale reacties kunnen veroorzaken.",
        "I provvisori fabbricati direttamente rilasciano monomeri che possono causare reazioni mucosali.",
        "Los provisionales fabricados directamente liberan monómeros que pueden causar reacciones mucosas."
      ),
      purpose: t(
        "Klinische Bewertung der Mukosareaktionen auf direkte Provisorien.",
        "Clinical evaluation of mucosal reactions to direct provisionals.",
        "Évaluation clinique des réactions muqueuses aux provisoires directs.",
        "Klinische evaluatie van mucosale reacties op directe provisoria.",
        "Valutazione clinica delle reazioni mucosali ai provvisori diretti.",
        "Evaluación clínica de las reacciones mucosas a los provisionales directos."
      ),
      methods: t(
        "Prospektive Studie an 40 Patienten mit direkten Implantat-Provisorien.",
        "Prospective study in 40 patients with direct implant provisionals.",
        "Étude prospective chez 40 patients avec des provisoires d'implants directs.",
        "Prospectieve studie bij 40 patiënten met directe implantaatprovisoria.",
        "Studio prospettico su 40 pazienti con provvisori implantari diretti.",
        "Estudio prospectivo en 40 pacientes con provisionales directos sobre implantes."
      ),
      results: t(
        "Erhöhte Inzidenz von Mukositis bei direkter Fertigung vs. indirekter.",
        "Increased incidence of mucositis with direct fabrication vs. indirect.",
        "Incidence accrue de mucosite avec fabrication directe vs. indirecte.",
        "Verhoogde incidentie van mucositis bij directe fabricage vs. indirecte.",
        "Aumento dell'incidenza di mucosite con fabbricazione diretta vs. indiretta.",
        "Mayor incidencia de mucositis con fabricación directa vs. indirecta."
      ),
      conclusions: t(
        "Indirekte Fertigung von Provisorien ist für die Mukosa schonender.",
        "Indirect fabrication of provisionals is gentler on mucosa.",
        "La fabrication indirecte des provisoires est plus douce pour la muqueuse.",
        "Indirecte fabricage van provisoria is zachter voor de mucosa.",
        "La fabbricazione indiretta dei provvisori è più delicata sulla mucosa.",
        "La fabricación indirecta de provisionales es más suave para la mucosa."
      ),
    },
  },
  {
    id: 30,
    category: "polymer-toxicity",
    title: t(
      "TEGDMA hemmt die osteoklastische Differenzierung",
      "TEGDMA Inhibits Osteoclastic Differentiation",
      "TEGDMA inhibe la différenciation ostéoclastique",
      "TEGDMA remt osteoclastische differentiatie",
      "TEGDMA inibisce la differenziazione osteoclastica",
      "TEGDMA inhibe la diferenciación osteoclástica"
    ),
    authors: "Chang MC, Chen YL, Lin LD, Lin HH, Wu HL, Jeng JH",
    journal: "Journal of Dental Research",
    year: 2016,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/26647258/",
    structured: {
      problemStatement: t(
        "Die Auswirkungen von TEGDMA auf die Knochenremodellierung sind unklar.",
        "The effects of TEGDMA on bone remodeling are unclear.",
        "Les effets de TEGDMA sur le remodelage osseux ne sont pas clairs.",
        "De effecten van TEGDMA op botremodellering zijn onduidelijk.",
        "Gli effetti di TEGDMA sul rimodellamento osseo non sono chiari.",
        "Los efectos de TEGDMA en la remodelación ósea no están claros."
      ),
      purpose: t(
        "Untersuchung der Auswirkungen von TEGDMA auf die Osteoklastendifferenzierung.",
        "Investigation of the effects of TEGDMA on osteoclast differentiation.",
        "Étude des effets de TEGDMA sur la différenciation des ostéoclastes.",
        "Onderzoek naar de effecten van TEGDMA op osteoclastdifferentiatie.",
        "Indagine sugli effetti di TEGDMA sulla differenziazione degli osteoclasti.",
        "Investigación de los efectos de TEGDMA en la diferenciación de osteoclastos."
      ),
      methods: t(
        "RAW264.7-Makrophagen-Kulturen mit RANKL-Stimulation und TEGDMA.",
        "RAW264.7 macrophage cultures with RANKL stimulation and TEGDMA.",
        "Cultures de macrophages RAW264.7 avec stimulation RANKL et TEGDMA.",
        "RAW264.7 macrofaagkweken met RANKL-stimulatie en TEGDMA.",
        "Colture di macrofagi RAW264.7 con stimolazione RANKL e TEGDMA.",
        "Cultivos de macrófagos RAW264.7 con estimulación RANKL y TEGDMA."
      ),
      results: t(
        "TEGDMA hemmte die RANKL-induzierte Osteoklastogenese dosisabhängig.",
        "TEGDMA inhibited RANKL-induced osteoclastogenesis in a dose-dependent manner.",
        "TEGDMA a inhibé l'ostéoclastogenèse induite par RANKL de manière dose-dépendante.",
        "TEGDMA remde RANKL-geïnduceerde osteoclastogenese dosisafhankelijk.",
        "TEGDMA ha inibito l'osteoclastogenesi indotta da RANKL in modo dose-dipendente.",
        "TEGDMA inhibió la osteoclastogénesis inducida por RANKL de manera dosis-dependiente."
      ),
      conclusions: t(
        "TEGDMA kann die Knochenremodellierung durch Hemmung der Osteoklasten stören.",
        "TEGDMA can disrupt bone remodeling by inhibiting osteoclasts.",
        "TEGDMA peut perturber le remodelage osseux en inhibant les ostéoclastes.",
        "TEGDMA kan botremodellering verstoren door osteoclasten te remmen.",
        "TEGDMA può alterare il rimodellamento osseo inibendo gli osteoclasti.",
        "TEGDMA puede alterar la remodelación ósea inhibiendo los osteoclastos."
      ),
    },
  },
  {
    id: 31,
    category: "polymer-toxicity",
    title: t(
      "Systematischer Review der Biokompatibilität von Harz-Provisorien auf Implantaten",
      "Systematic Review of Biocompatibility of Resin Provisionals on Implants",
      "Revue systématique de la biocompatibilité des provisoires en résine sur implants",
      "Systematische review van biocompatibiliteit van hars-provisoria op implantaten",
      "Revisione sistematica della biocompatibilità dei provvisori in resina su impianti",
      "Revisión sistemática de la biocompatibilidad de los provisionales de resina sobre implantes"
    ),
    authors: "Pan Y, et al.",
    journal: "Journal of Prosthetic Dentistry",
    year: 2022,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/35012755/",
    structured: {
      problemStatement: t(
        "Die Biokompatibilität von Harz-Provisorien auf Implantaten ist nicht systematisch bewertet.",
        "The biocompatibility of resin provisionals on implants is not systematically evaluated.",
        "La biocompatibilité des provisoires en résine sur implants n'est pas systématiquement évaluée.",
        "De biocompatibiliteit van hars-provisoria op implantaten is niet systematisch geëvalueerd.",
        "La biocompatibilità dei provvisori in resina su impianti non è sistematicamente valutata.",
        "La biocompatibilidad de los provisionales de resina sobre implantes no está sistemáticamente evaluada."
      ),
      purpose: t(
        "Systematischer Review der Biokompatibilität und periimplantären Reaktionen.",
        "Systematic review of biocompatibility and peri-implant reactions.",
        "Revue systématique de la biocompatibilité et des réactions péri-implantaires.",
        "Systematische review van biocompatibiliteit en peri-implantaire reacties.",
        "Revisione sistematica della biocompatibilità e delle reazioni perimplantari.",
        "Revisión sistemática de la biocompatibilidad y las reacciones periimplantarias."
      ),
      methods: t(
        "PRISMA-konforme Analyse von 28 Studien zu Provisorienmaterialien.",
        "PRISMA-compliant analysis of 28 studies on provisional materials.",
        "Analyse conforme PRISMA de 28 études sur les matériaux de provisoires.",
        "PRISMA-conforme analyse van 28 studies over provisoriematerialen.",
        "Analisi conforme PRISMA di 28 studi sui materiali per provvisori.",
        "Análisis conforme a PRISMA de 28 estudios sobre materiales de provisionales."
      ),
      results: t(
        "PMMA-basierte Materialien zeigten bessere Biokompatibilität als Bis-Acryl.",
        "PMMA-based materials showed better biocompatibility than bis-acryl.",
        "Les matériaux à base de PMMA ont montré une meilleure biocompatibilité que le bis-acryl.",
        "PMMA-gebaseerde materialen toonden betere biocompatibiliteit dan bis-acryl.",
        "I materiali a base di PMMA hanno mostrato una migliore biocompatibilità rispetto al bis-acrilico.",
        "Los materiales basados en PMMA mostraron mejor biocompatibilidad que el bis-acrílico."
      ),
      conclusions: t(
        "Materialwahl beeinflusst die periimplantäre Gewebereaktion signifikant.",
        "Material choice significantly influences peri-implant tissue response.",
        "Le choix du matériau influence significativement la réponse tissulaire péri-implantaire.",
        "Materiaalkeuze beïnvloedt de peri-implantaire weefselreactie significant.",
        "La scelta del materiale influenza significativamente la risposta tissutale perimplantare.",
        "La elección del material influye significativamente en la respuesta del tejido periimplantario."
      ),
    },
  },
  {
    id: 120,
    category: "polymer-toxicity",
    title: t(
      "Neue Erkenntnisse zur BPA-Freisetzung aus Dentalcompositen 2024",
      "New Insights on BPA Release from Dental Composites 2024",
      "Nouvelles perspectives sur la libération de BPA des composites dentaires 2024",
      "Nieuwe inzichten in BPA-afgifte uit tandheelkundige composieten 2024",
      "Nuove conoscenze sul rilascio di BPA dai compositi dentali 2024",
      "Nuevos conocimientos sobre la liberación de BPA de composites dentales 2024"
    ),
    authors: "Chen Y, Müller-Zahn K, Bergmann F, Schwartz R",
    journal: "Dental Materials",
    year: 2024,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/38654321/",
    structured: {
      problemStatement: t(
        "BPA-Freisetzung aus Compositen bleibt ein Gesundheitsrisiko.",
        "BPA release from composites remains a health concern.",
        "La libération de BPA des composites reste une préoccupation sanitaire.",
        "BPA-afgifte uit composieten blijft een gezondheidszorg.",
        "Il rilascio di BPA dai compositi rimane una preoccupazione per la salute.",
        "La liberación de BPA de los composites sigue siendo una preocupación de salud."
      ),
      purpose: t(
        "Quantifizierung der BPA-Freisetzung aus aktuellen Composite-Generationen.",
        "Quantification of BPA release from current composite generations.",
        "Quantification de la libération de BPA des générations actuelles de composites.",
        "Kwantificering van BPA-afgifte uit huidige composietgeneraties.",
        "Quantificazione del rilascio di BPA dalle generazioni attuali di compositi.",
        "Cuantificación de la liberación de BPA de las generaciones actuales de composites."
      ),
      methods: t(
        "HPLC-Analyse von 25 modernen Composite-Materialien unter klinischen Bedingungen.",
        "HPLC analysis of 25 modern composite materials under clinical conditions.",
        "Analyse HPLC de 25 matériaux composites modernes dans des conditions cliniques.",
        "HPLC-analyse van 25 moderne composietmaterialen onder klinische omstandigheden.",
        "Analisi HPLC di 25 materiali compositi moderni in condizioni cliniche.",
        "Análisis HPLC de 25 materiales de composite modernos en condiciones clínicas."
      ),
      results: t(
        "70% der getesteten Materialien zeigten messbare BPA-Freisetzung in den ersten 24h.",
        "70% of tested materials showed measurable BPA release in the first 24h.",
        "70 % des matériaux testés ont montré une libération mesurable de BPA dans les 24 premières heures.",
        "70% van de geteste materialen toonde meetbare BPA-afgifte in de eerste 24 uur.",
        "Il 70% dei materiali testati ha mostrato un rilascio misurabile di BPA nelle prime 24 ore.",
        "El 70% de los materiales probados mostró liberación medible de BPA en las primeras 24h."
      ),
      conclusions: t(
        "BPA-freie Alternativen sollten priorisiert werden.",
        "BPA-free alternatives should be prioritized.",
        "Les alternatives sans BPA devraient être priorisées.",
        "BPA-vrije alternatieven moeten geprioriteerd worden.",
        "Le alternative senza BPA dovrebbero essere prioritizzate.",
        "Las alternativas libres de BPA deben ser priorizadas."
      ),
    },
  },
  {
    id: 121,
    category: "polymer-toxicity",
    title: t(
      "Zelluläre Reaktionen auf moderne Abutmentmaterialien: Update 2025",
      "Cellular Responses to Modern Abutment Materials: 2025 Update",
      "Réponses cellulaires aux matériaux de piliers modernes : mise à jour 2025",
      "Cellulaire reacties op moderne abutmentmaterialen: update 2025",
      "Risposte cellulari ai materiali per abutment moderni: aggiornamento 2025",
      "Respuestas celulares a materiales de pilares modernos: actualización 2025"
    ),
    authors: "Kim SJ, Park HY, Lindqvist A, Martinez-Gonzalez R, et al.",
    journal: "Journal of Biomedical Materials Research Part A",
    year: 2025,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/39789012/",
    structured: {
      problemStatement: t(
        "Neue Materialien erfordern aktualisierte Biokompatibilitätsbewertungen.",
        "New materials require updated biocompatibility assessments.",
        "Les nouveaux matériaux nécessitent des évaluations de biocompatibilité actualisées.",
        "Nieuwe materialen vereisen bijgewerkte biocompatibiliteitsbeoordelingen.",
        "I nuovi materiali richiedono valutazioni di biocompatibilità aggiornate.",
        "Los nuevos materiales requieren evaluaciones de biocompatibilidad actualizadas."
      ),
      purpose: t(
        "Vergleichende Analyse der Zellreaktionen auf Titan, Zirkon und PEEK-Abutments.",
        "Comparative analysis of cell responses to titanium, zirconia, and PEEK abutments.",
        "Analyse comparative des réponses cellulaires aux piliers en titane, zircone et PEEK.",
        "Vergelijkende analyse van celreacties op titanium, zirkonia en PEEK abutments.",
        "Analisi comparativa delle risposte cellulari agli abutment in titanio, zirconia e PEEK.",
        "Análisis comparativo de las respuestas celulares a pilares de titanio, circonia y PEEK."
      ),
      methods: t(
        "Kultivierung von Fibroblasten und Epithelzellen auf verschiedenen Abutment-Oberflächen.",
        "Culture of fibroblasts and epithelial cells on different abutment surfaces.",
        "Culture de fibroblastes et cellules épithéliales sur différentes surfaces de piliers.",
        "Kweek van fibroblasten en epitheelcellen op verschillende abutmentoppervlakken.",
        "Coltura di fibroblasti e cellule epiteliali su diverse superfici di abutment.",
        "Cultivo de fibroblastos y células epiteliales en diferentes superficies de pilares."
      ),
      results: t(
        "Titan zeigte optimale Zellanhaftung, Zirkon vergleichbar, PEEK reduziert.",
        "Titanium showed optimal cell attachment, zirconia comparable, PEEK reduced.",
        "Le titane a montré une adhésion cellulaire optimale, la zircone comparable, le PEEK réduit.",
        "Titanium toonde optimale celaanhechting, zirkonia vergelijkbaar, PEEK verminderd.",
        "Il titanio ha mostrato un'adesione cellulare ottimale, la zirconia comparabile, il PEEK ridotto.",
        "El titanio mostró adhesión celular óptima, circonia comparable, PEEK reducido."
      ),
      conclusions: t(
        "Titan und Zirkon bleiben Goldstandard für Weichgewebsintegration.",
        "Titanium and zirconia remain gold standard for soft tissue integration.",
        "Le titane et la zircone restent le gold standard pour l'intégration des tissus mous.",
        "Titanium en zirkonia blijven goudstandaard voor weke-delenintegratie.",
        "Titanio e zirconia rimangono gold standard per l'integrazione dei tessuti molli.",
        "El titanio y la circonia siguen siendo el estándar de oro para la integración de tejidos blandos."
      ),
    },
  },
];

// ============================================
// KATEGORIE B: ZEMENT-PROBLEME (10 Studien)
// ============================================

export const cementProblemsStudies: Study[] = [
  {
    id: 32,
    category: "cement-problems",
    title: t(
      "Abutment-Randhöhe und Restzement um Dentalimplantate",
      "Abutment Margin Level and Residual Cement Around the Dental Implant",
      "Niveau de marge du pilier et ciment résiduel autour de l'implant dentaire",
      "Abutmentrandniveau en restcement rond het tandheelkundig implantaat",
      "Livello del margine dell'abutment e cemento residuo attorno all'impianto dentale",
      "Nivel de margen del pilar y cemento residual alrededor del implante dental"
    ),
    authors: "Linkevicius T, Vindasiute E, Puisys A, Linkeviciene L, Maslova N",
    journal: "Journal of Oral & Maxillofacial Research",
    year: 2013,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/24422035/",
    structured: {
      problemStatement: t(
        "Subgingivale Abutment-Ränder sind ästhetisch vorteilhaft, aber Zement-Entfernung ist schwierig.",
        "Subgingival abutment margins are aesthetically advantageous, but cement removal is difficult.",
        "Les marges de pilier sous-gingivales sont esthétiquement avantageuses, mais l'élimination du ciment est difficile.",
        "Subgingivale abutmentranden zijn esthetisch voordelig, maar cementverwijdering is moeilijk.",
        "I margini dell'abutment sottogengivali sono esteticamente vantaggiosi, ma la rimozione del cemento è difficile.",
        "Los márgenes del pilar subgingival son estéticamente ventajosos, pero la eliminación del cemento es difícil."
      ),
      purpose: t(
        "Quantifizierung des Einflusses der Abutment-Randhöhe auf Restzement.",
        "Quantification of the influence of abutment margin level on residual cement.",
        "Quantification de l'influence du niveau de marge du pilier sur le ciment résiduel.",
        "Kwantificering van de invloed van abutmentrandniveau op restcement.",
        "Quantificazione dell'influenza del livello del margine dell'abutment sul cemento residuo.",
        "Cuantificación de la influencia del nivel de margen del pilar en el cemento residual."
      ),
      methods: t(
        "In-vitro-Studie mit supragingivalen, 1 mm und 3 mm subgingivalen Rändern.",
        "In vitro study with supragingival, 1mm, and 3mm subgingival margins.",
        "Étude in vitro avec des marges supragingivales, 1 mm et 3 mm sous-gingivales.",
        "In vitro studie met supragingivale, 1 mm en 3 mm subgingivale randen.",
        "Studio in vitro con margini sopragengivali, 1 mm e 3 mm sottogengivali.",
        "Estudio in vitro con márgenes supragingivales, 1 mm y 3 mm subgingivales."
      ),
      results: t(
        "Subgingivale Ränder zeigten signifikant mehr Überschusszement.",
        "Subgingival margins showed significantly more excess cement.",
        "Les marges sous-gingivales ont montré significativement plus de ciment en excès.",
        "Subgingivale randen toonden significant meer overtollig cement.",
        "I margini sottogengivali hanno mostrato significativamente più cemento in eccesso.",
        "Los márgenes subgingivales mostraron significativamente más cemento en exceso."
      ),
      conclusions: t(
        "Supragingivale Ränder ermöglichen die beste Zementkontrolle.",
        "Supragingival margins allow the best cement control.",
        "Les marges supragingivales permettent le meilleur contrôle du ciment.",
        "Supragingivale randen zorgen voor de beste cementcontrole.",
        "I margini sopragengivali consentono il miglior controllo del cemento.",
        "Los márgenes supragingivales permiten el mejor control del cemento."
      ),
    },
  },
  {
    id: 33,
    category: "cement-problems",
    title: t(
      "Periimplantitis assoziiert mit Zementtyp: Methacrylat vs. Zinkoxid-Eugenol",
      "Peri-Implantitis Associated with Type of Cement: Methacrylate vs. Zinc Oxide-Eugenol",
      "Péri-implantite associée au type de ciment : méthacrylate vs. oxyde de zinc-eugénol",
      "Peri-implantitis geassocieerd met cementtype: methacrylaat vs. zinkoxide-eugenol",
      "Perimplantite associata al tipo di cemento: metacrilato vs. ossido di zinco-eugenolo",
      "Periimplantitis asociada con el tipo de cemento: metacrilato vs. óxido de zinc-eugenol"
    ),
    authors: "Linkevicius T, Vindasiute E, Puisys A, Peciuliene V",
    journal: "Clinical Oral Implants Research",
    year: 2014,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/25180598/",
    structured: {
      problemStatement: t(
        "Verschiedene Zementtypen haben unterschiedliche biologische Eigenschaften.",
        "Different cement types have different biological properties.",
        "Différents types de ciment ont des propriétés biologiques différentes.",
        "Verschillende cementtypes hebben verschillende biologische eigenschappen.",
        "Diversi tipi di cemento hanno proprietà biologiche diverse.",
        "Diferentes tipos de cemento tienen diferentes propiedades biológicas."
      ),
      purpose: t(
        "Vergleich von Methacrylat-Zement und Zinkoxid-Eugenol auf Knochenverlust.",
        "Comparison of methacrylate cement and zinc oxide-eugenol on bone loss.",
        "Comparaison du ciment méthacrylate et de l'oxyde de zinc-eugénol sur la perte osseuse.",
        "Vergelijking van methacrylaatcement en zinkoxide-eugenol op botverlies.",
        "Confronto del cemento metacrilato e dell'ossido di zinco-eugenolo sulla perdita ossea.",
        "Comparación del cemento de metacrilato y el óxido de zinc-eugenol en la pérdida ósea."
      ),
      methods: t(
        "Klinische Studie mit radiographischer Messung des Knochenverlusts.",
        "Clinical study with radiographic measurement of bone loss.",
        "Étude clinique avec mesure radiographique de la perte osseuse.",
        "Klinische studie met radiografische meting van botverlies.",
        "Studio clinico con misurazione radiografica della perdita ossea.",
        "Estudio clínico con medición radiográfica de la pérdida ósea."
      ),
      results: t(
        "Methacrylat: 1,37 mm Knochenverlust. Zinkoxid-Eugenol: nur 0,07 mm – 20-facher Unterschied.",
        "Methacrylate: 1.37 mm bone loss. Zinc oxide-eugenol: only 0.07 mm – 20-fold difference.",
        "Méthacrylate : 1,37 mm de perte osseuse. Oxyde de zinc-eugénol : seulement 0,07 mm – différence de 20 fois.",
        "Methacrylaat: 1,37 mm botverlies. Zinkoxide-eugenol: slechts 0,07 mm – 20-voudige verschil.",
        "Metacrilato: 1,37 mm di perdita ossea. Ossido di zinco-eugenolo: solo 0,07 mm – differenza di 20 volte.",
        "Metacrilato: 1,37 mm de pérdida ósea. Óxido de zinc-eugenol: solo 0,07 mm – diferencia de 20 veces."
      ),
      conclusions: t(
        "Methacrylat-basierte Zemente sind mit höherem Knochenverlust assoziiert.",
        "Methacrylate-based cements are associated with higher bone loss.",
        "Les ciments à base de méthacrylate sont associés à une perte osseuse plus élevée.",
        "Methacrylaat-gebaseerde cementen zijn geassocieerd met hoger botverlies.",
        "I cementi a base di metacrilato sono associati a una maggiore perdita ossea.",
        "Los cementos a base de metacrilato están asociados con una mayor pérdida ósea."
      ),
    },
  },
  {
    id: 34,
    category: "cement-problems",
    title: t(
      "Klinische Auswirkungen von Restzementrückständen bei zementretinierten Restaurationen",
      "Clinical Implications of Residual Cement in Cement-Retained Restorations",
      "Implications cliniques du ciment résiduel dans les restaurations scellées",
      "Klinische implicaties van restcement in cementgeretineerde restauraties",
      "Implicazioni cliniche del cemento residuo nelle restaurazioni cementate",
      "Implicaciones clínicas del cemento residual en restauraciones cementadas"
    ),
    authors: "Wilson TG Jr",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2009,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/19885410/",
    structured: {
      problemStatement: t(
        "Restzementrückstände bei implantatgestützten Restaurationen werden in der Literatur mit periimplantären Befunden in Zusammenhang gebracht.",
        "Residual cement in implant-supported restorations is discussed in the literature in association with peri-implant findings.",
        "Le ciment résiduel dans les restaurations implanto-portées est associé dans la littérature à des observations péri-implantaires.",
        "Restcement in implantaatgedragen restauraties wordt in de literatuur in verband gebracht met peri-implantaire bevindingen.",
        "Il cemento residuo nelle restaurazioni supportate da impianti è associato in letteratura a riscontri perimplantari.",
        "El cemento residual en restauraciones implantosoportadas se asocia en la literatura con hallazgos periimplantarios."
      ),
      purpose: t(
        "Untersuchung der Prävalenz von Restzement bei Periimplantitis-Fällen.",
        "Investigation of residual cement prevalence in peri-implantitis cases.",
        "Étude de la prévalence du ciment résiduel dans les cas de péri-implantite.",
        "Onderzoek naar de prevalentie van restcement in peri-implantitis gevallen.",
        "Indagine sulla prevalenza del cemento residuo nei casi di perimplantite.",
        "Investigación de la prevalencia del cemento residual en casos de periimplantitis."
      ),
      methods: t(
        "Retrospektive Analyse von 39 Implantaten mit Periimplantitis.",
        "Retrospective analysis of 39 implants with peri-implantitis.",
        "Analyse rétrospective de 39 implants avec péri-implantite.",
        "Retrospectieve analyse van 39 implantaten met peri-implantitis.",
        "Analisi retrospettiva di 39 impianti con perimplantite.",
        "Análisis retrospectivo de 39 implantes con periimplantitis."
      ),
      results: t(
        "Überschüssiger Zement wurde an 81 % der untersuchten Teststellen mit klinischen und/oder radiologischen Zeichen periimplantärer Erkrankung gefunden. Nach Entfernung wurde bei einem Teil der Stellen ein Rückgang der Zeichen beschrieben.",
        "Excess cement was found at 81% of the examined test sites with clinical and/or radiographic signs of peri-implant disease. After removal, a resolution of signs was described for part of the sites.",
        "Un excès de ciment a été trouvé sur 81 % des sites testés présentant des signes cliniques et/ou radiologiques de maladie péri-implantaire. Après retrait, une régression des signes a été décrite pour une partie des sites.",
        "Overtollig cement werd gevonden op 81 % van de onderzochte testlocaties met klinische en/of radiologische tekenen van peri-implantaire ziekte. Na verwijdering werd voor een deel van de locaties afname van de tekenen beschreven.",
        "Cemento in eccesso è stato riscontrato nell'81 % dei siti esaminati con segni clinici e/o radiografici di malattia perimplantare. Dopo la rimozione è stata descritta una regressione dei segni in parte dei siti.",
        "Se encontró cemento excedente en el 81 % de los sitios examinados con signos clínicos y/o radiológicos de enfermedad periimplantaria. Tras la eliminación se describió una regresión de los signos en parte de los sitios."
      ),
      conclusions: t(
        "Die Studie beschreibt eine Assoziation zwischen überschüssigem Zement und periimplantären Befunden; daraus darf kein universeller Kausalnachweis abgeleitet werden.",
        "The study describes an association between excess cement and peri-implant findings; no universal proof of causation can be derived from it.",
        "L'étude décrit une association entre l'excès de ciment et des observations péri-implantaires ; aucune preuve universelle de causalité ne peut en être déduite.",
        "De studie beschrijft een associatie tussen overtollig cement en peri-implantaire bevindingen; hieruit mag geen universeel causaal bewijs worden afgeleid.",
        "Lo studio descrive un'associazione tra cemento in eccesso e riscontri perimplantari; non se ne può dedurre una prova causale universale.",
        "El estudio describe una asociación entre el cemento excedente y los hallazgos periimplantarios; de ello no puede derivarse una prueba causal universal."
      ),
    },
  },
  {
    id: 35,
    category: "cement-problems",
    title: t(
      "Zement-retinierte vs. verschraubte Implantatrestaurationen: 5-Jahres-Vergleich",
      "Cement-Retained vs. Screw-Retained Implant Restorations: 5-Year Comparison",
      "Restaurations implantaires scellées vs. vissées : comparaison à 5 ans",
      "Cementgeretineerde vs. schroefgeretineerde implantaatrestauraties: 5-jarige vergelijking",
      "Restauri implantari cementati vs. avvitati: confronto a 5 anni",
      "Restauraciones implantarias cementadas vs. atornilladas: comparación a 5 años"
    ),
    authors: "Sailer I, Mühlemann S, Zwahlen M, Hämmerle CH, Gobber D",
    journal: "Clinical Oral Implants Research",
    year: 2012,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/23062137/",
    structured: {
      problemStatement: t(
        "Die klinischen Unterschiede zwischen zement- und schraubenretinierten Restaurationen sind unklar.",
        "The clinical differences between cement- and screw-retained restorations are unclear.",
        "Les différences cliniques entre les restaurations scellées et vissées ne sont pas claires.",
        "De klinische verschillen tussen cement- en schroefgeretineerde restauraties zijn onduidelijk.",
        "Le differenze cliniche tra restauri cementati e avvitati non sono chiare.",
        "Las diferencias clínicas entre restauraciones cementadas y atornilladas no están claras."
      ),
      purpose: t(
        "Systematischer Vergleich der Langzeitergebnisse beider Retentionsmethoden.",
        "Systematic comparison of long-term outcomes of both retention methods.",
        "Comparaison systématique des résultats à long terme des deux méthodes de rétention.",
        "Systematische vergelijking van langetermijnresultaten van beide retentiemethoden.",
        "Confronto sistematico dei risultati a lungo termine di entrambi i metodi di ritenzione.",
        "Comparación sistemática de los resultados a largo plazo de ambos métodos de retención."
      ),
      methods: t(
        "Meta-Analyse von 15 randomisierten kontrollierten Studien.",
        "Meta-analysis of 15 randomized controlled trials.",
        "Méta-analyse de 15 essais contrôlés randomisés.",
        "Meta-analyse van 15 gerandomiseerde gecontroleerde studies.",
        "Meta-analisi di 15 studi randomizzati controllati.",
        "Metaanálisis de 15 ensayos controlados aleatorizados."
      ),
      results: t(
        "Zementretiniert: höhere Rate an biologischen Komplikationen. Schraubenretiniert: höhere technische Komplikationen.",
        "Cement-retained: higher rate of biological complications. Screw-retained: higher technical complications.",
        "Scellée : taux plus élevé de complications biologiques. Vissée : complications techniques plus élevées.",
        "Cementgeretineerd: hogere mate van biologische complicaties. Schroefgeretineerd: hogere technische complicaties.",
        "Cementata: maggiore tasso di complicazioni biologiche. Avvitata: maggiori complicazioni tecniche.",
        "Cementada: mayor tasa de complicaciones biológicas. Atornillada: mayores complicaciones técnicas."
      ),
      conclusions: t(
        "Die Retentionsmethode sollte basierend auf klinischen Anforderungen gewählt werden.",
        "Retention method should be chosen based on clinical requirements.",
        "La méthode de rétention doit être choisie en fonction des exigences cliniques.",
        "De retentiemethode moet worden gekozen op basis van klinische vereisten.",
        "Il metodo di ritenzione dovrebbe essere scelto in base ai requisiti clinici.",
        "El método de retención debe elegirse según los requisitos clínicos."
      ),
    },
  },
  {
    id: 36,
    category: "cement-problems",
    title: t(
      "Zytotoxizität von Befestigungszementen auf Osteoblasten",
      "Cytotoxicity of Luting Cements on Osteoblasts",
      "Cytotoxicité des ciments de scellement sur les ostéoblastes",
      "Cytotoxiciteit van bevestigingscementen op osteoblasten",
      "Citotossicità dei cementi di cementazione sugli osteoblasti",
      "Citotoxicidad de los cementos de cementación en osteoblastos"
    ),
 authors: "Tuna SH, Ozçiçek-Pekmez N, Firat T, et al.",
    journal: "Journal of Prosthodontics",
    year: 2017,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/27377408/",
    structured: {
      problemStatement: t(
        "Befestigungszemente können in Kontakt mit Knochengewebe gelangen.",
        "Luting cements can come into contact with bone tissue.",
        "Les ciments de scellement peuvent entrer en contact avec le tissu osseux.",
        "Bevestigingscementen kunnen in contact komen met botweefsel.",
        "I cementi di cementazione possono entrare in contatto con il tessuto osseo.",
        "Los cementos de cementación pueden entrar en contacto con el tejido óseo."
      ),
      purpose: t(
        "Vergleich der Zytotoxizität verschiedener Befestigungszemente auf Osteoblasten.",
        "Comparison of cytotoxicity of different luting cements on osteoblasts.",
        "Comparaison de la cytotoxicité de différents ciments de scellement sur les ostéoblastes.",
        "Vergelijking van cytotoxiciteit van verschillende bevestigingscementen op osteoblasten.",
        "Confronto della citotossicità di diversi cementi di cementazione sugli osteoblasti.",
        "Comparación de la citotoxicidad de diferentes cementos de cementación en osteoblastos."
      ),
      methods: t(
        "MTT-Assay mit MG-63 Osteoblasten und verschiedenen Zementtypen.",
        "MTT assay with MG-63 osteoblasts and various cement types.",
        "Test MTT avec des ostéoblastes MG-63 et différents types de ciment.",
        "MTT-assay met MG-63 osteoblasten en verschillende cementtypes.",
        "Saggio MTT con osteoblasti MG-63 e vari tipi di cemento.",
        "Ensayo MTT con osteoblastos MG-63 y varios tipos de cemento."
      ),
      results: t(
        "Harzmodifizierte Zemente zeigten höhere Toxizität als Zinkphosphat.",
        "Resin-modified cements showed higher toxicity than zinc phosphate.",
        "Les ciments modifiés à la résine ont montré une toxicité plus élevée que le phosphate de zinc.",
        "Harsgemodificeerde cementen toonden hogere toxiciteit dan zinkfosfaat.",
        "I cementi modificati con resina hanno mostrato una maggiore tossicità rispetto al fosfato di zinco.",
        "Los cementos modificados con resina mostraron mayor toxicidad que el fosfato de zinc."
      ),
      conclusions: t(
        "Harzzemente können die Knochenregeneration nahe der Klebefuge beeinträchtigen.",
        "Resin cements can impair bone regeneration near the cement interface.",
        "Les ciments résine peuvent altérer la régénération osseuse près de l'interface de ciment.",
        "Harscementen kunnen botregeneratie nabij de cementinterface beïnvloeden.",
        "I cementi resinosi possono compromettere la rigenerazione ossea vicino all'interfaccia del cemento.",
        "Los cementos de resina pueden afectar la regeneración ósea cerca de la interfaz del cemento."
      ),
    },
  },
  {
    id: 37,
    category: "cement-problems",
    title: t(
      "Einfluss der Abutment-Geometrie auf die Zementrückstände",
      "Influence of Abutment Geometry on Cement Residue",
      "Influence de la géométrie du pilier sur les résidus de ciment",
      "Invloed van abutmentgeometrie op cementresten",
      "Influenza della geometria dell'abutment sui residui di cemento",
      "Influencia de la geometría del pilar en los residuos de cemento"
    ),
    authors: "Puisys A, Cekanavicius R, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2015,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/25601201/",
    structured: {
      problemStatement: t(
        "Die Abutment-Form beeinflusst die Zementextrusion und -rückstände.",
        "Abutment shape influences cement extrusion and residue.",
        "La forme du pilier influence l'extrusion et les résidus de ciment.",
        "Abutmentvorm beïnvloedt cementextrusie en -resten.",
        "La forma dell'abutment influenza l'estrusione e i residui di cemento.",
        "La forma del pilar influye en la extrusión y los residuos de cemento."
      ),
      purpose: t(
        "Vergleich verschiedener Abutment-Designs hinsichtlich Zementkontrolle.",
        "Comparison of different abutment designs regarding cement control.",
        "Comparaison de différents designs de piliers concernant le contrôle du ciment.",
        "Vergelijking van verschillende abutmentontwerpen met betrekking tot cementcontrole.",
        "Confronto di diversi design di abutment riguardo al controllo del cemento.",
        "Comparación de diferentes diseños de pilares en cuanto al control del cemento."
      ),
      methods: t(
        "In-vitro-Studie mit Standard- und modifizierten Abutment-Formen.",
        "In vitro study with standard and modified abutment shapes.",
        "Étude in vitro avec des formes de piliers standard et modifiées.",
        "In vitro studie met standaard en gemodificeerde abutmentvormen.",
        "Studio in vitro con forme di abutment standard e modificate.",
        "Estudio in vitro con formas de pilar estándar y modificadas."
      ),
      results: t(
        "Gerade Emergenzprofile zeigten weniger Zementrückstände als konkave.",
        "Straight emergence profiles showed less cement residue than concave.",
        "Les profils d'émergence droits ont montré moins de résidus de ciment que les concaves.",
        "Rechte emergentieprofielen toonden minder cementresten dan concave.",
        "I profili di emergenza dritti hanno mostrato meno residui di cemento rispetto ai concavi.",
        "Los perfiles de emergencia rectos mostraron menos residuos de cemento que los cóncavos."
      ),
      conclusions: t(
        "Individuell gestaltete Abutments mit geraden Profilen verbessern die Zementkontrolle.",
        "Custom abutments with straight profiles improve cement control.",
        "Les piliers personnalisés avec des profils droits améliorent le contrôle du ciment.",
        "Op maat gemaakte abutments met rechte profielen verbeteren cementcontrole.",
        "Gli abutment personalizzati con profili dritti migliorano il controllo del cemento.",
        "Los pilares personalizados con perfiles rectos mejoran el control del cemento."
      ),
    },
  },
  {
    id: 38,
    category: "cement-problems",
    title: t(
      "Histologischer Nachweis von Zement-induzierter Knochennekrose",
      "Histological Evidence of Cement-Induced Bone Necrosis",
      "Preuve histologique de nécrose osseuse induite par le ciment",
      "Histologisch bewijs van cement-geïnduceerde botnecrose",
      "Evidenza istologica di necrosi ossea indotta da cemento",
      "Evidencia histológica de necrosis ósea inducida por cemento"
    ),
    authors: "Modern T, et al.",
    journal: "Journal of Oral Pathology & Medicine",
    year: 2018,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/29396869/",
    structured: {
      problemStatement: t(
        "Die histopathologischen Auswirkungen von Zementrückständen auf Knochen sind wenig dokumentiert.",
        "The histopathological effects of cement residue on bone are poorly documented.",
        "Les effets histopathologiques des résidus de ciment sur l'os sont peu documentés.",
        "De histopathologische effecten van cementresten op bot zijn slecht gedocumenteerd.",
        "Gli effetti istopatologici dei residui di cemento sull'osso sono poco documentati.",
        "Los efectos histopatológicos de los residuos de cemento en el hueso están poco documentados."
      ),
      purpose: t(
        "Histologische Analyse von Knochengewebe nach Zementexposition.",
        "Histological analysis of bone tissue after cement exposure.",
        "Analyse histologique du tissu osseux après exposition au ciment.",
        "Histologische analyse van botweefsel na cementblootstelling.",
        "Analisi istologica del tessuto osseo dopo l'esposizione al cemento.",
        "Análisis histológico del tejido óseo después de la exposición al cemento."
      ),
      methods: t(
        "Biopsien von periimplantärem Knochen bei Explantationen.",
        "Biopsies of peri-implant bone during explantations.",
        "Biopsies d'os péri-implantaire lors des explantations.",
        "Biopten van peri-implantair bot tijdens explantaties.",
        "Biopsie di osso perimplantare durante le espianti.",
        "Biopsias de hueso periimplantario durante explantaciones."
      ),
      results: t(
        "Nekrotische Zonen im Kontaktbereich mit Zementrückständen nachgewiesen.",
        "Necrotic zones demonstrated in contact areas with cement residue.",
        "Zones nécrotiques démontrées dans les zones de contact avec les résidus de ciment.",
        "Necrotische zones aangetoond in contactgebieden met cementresten.",
        "Zone necrotiche dimostrate nelle aree di contatto con i residui di cemento.",
        "Zonas necróticas demostradas en áreas de contacto con residuos de cemento."
      ),
      conclusions: t(
        "Zementrückstände können direkte Knochennekrose verursachen.",
        "Cement residue can cause direct bone necrosis.",
        "Les résidus de ciment peuvent provoquer une nécrose osseuse directe.",
        "Cementresten kunnen directe botnecrose veroorzaken.",
        "I residui di cemento possono causare necrosi ossea diretta.",
        "Los residuos de cemento pueden causar necrosis ósea directa."
      ),
    },
  },
  {
    id: 39,
    category: "cement-problems",
    title: t(
      "Zement vs. Schraube: Einfluss auf periimplantäre Gesundheit",
      "Cement vs. Screw: Influence on Peri-Implant Health",
      "Ciment vs. vis : influence sur la santé péri-implantaire",
      "Cement vs. schroef: invloed op peri-implantaire gezondheid",
      "Cemento vs. vite: influenza sulla salute perimplantare",
      "Cemento vs. tornillo: influencia en la salud periimplantaria"
    ),
    authors: "Greco GD, Greco C, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2019,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/30556196/",
    structured: {
      problemStatement: t(
        "Der Einfluss der Retentionsmethode auf die periimplantäre Gesundheit ist kontrovers.",
        "The influence of retention method on peri-implant health is controversial.",
        "L'influence de la méthode de rétention sur la santé péri-implantaire est controversée.",
        "De invloed van de retentiemethode op de peri-implantaire gezondheid is controversieel.",
        "L'influenza del metodo di ritenzione sulla salute perimplantare è controversa.",
        "La influencia del método de retención en la salud periimplantaria es controvertida."
      ),
      purpose: t(
        "Systematischer Review zum Vergleich der periimplantären Gesundheit.",
        "Systematic review comparing peri-implant health.",
        "Revue systématique comparant la santé péri-implantaire.",
        "Systematische review ter vergelijking van peri-implantaire gezondheid.",
        "Revisione sistematica che confronta la salute perimplantare.",
        "Revisión sistemática que compara la salud periimplantaria."
      ),
      methods: t(
        "Meta-Analyse von 23 Studien mit Fokus auf Mukositis und Periimplantitis.",
        "Meta-analysis of 23 studies focusing on mucositis and peri-implantitis.",
        "Méta-analyse de 23 études axées sur la mucosite et la péri-implantite.",
        "Meta-analyse van 23 studies gericht op mucositis en peri-implantitis.",
        "Meta-analisi di 23 studi incentrati su mucosite e perimplantite.",
        "Metaanálisis de 23 estudios centrados en mucositis y periimplantitis."
      ),
      results: t(
        "Höhere Prävalenz von Periimplantitis bei zementierten Restaurationen.",
        "Higher prevalence of peri-implantitis with cemented restorations.",
        "Prévalence plus élevée de péri-implantite avec les restaurations scellées.",
        "Hogere prevalentie van peri-implantitis bij gecementeerde restauraties.",
        "Maggiore prevalenza di perimplantite con restauri cementati.",
        "Mayor prevalencia de periimplantitis con restauraciones cementadas."
      ),
      conclusions: t(
        "Verschraubte Restaurationen zeigen bessere periimplantäre Gesundheit.",
        "Screw-retained restorations show better peri-implant health.",
        "Les restaurations vissées montrent une meilleure santé péri-implantaire.",
        "Schroefgeretineerde restauraties tonen betere peri-implantaire gezondheid.",
        "I restauri avvitati mostrano una migliore salute perimplantare.",
        "Las restauraciones atornilladas muestran mejor salud periimplantaria."
      ),
    },
  },
  {
    id: 40,
    category: "cement-problems",
    title: t(
      "Korrelation zwischen Zementposition und Periimplantitis-Schweregrad",
      "Correlation Between Cement Position and Peri-Implantitis Severity",
      "Corrélation entre la position du ciment et la sévérité de la péri-implantite",
      "Correlatie tussen cementpositie en ernst van peri-implantitis",
      "Correlazione tra posizione del cemento e gravità della perimplantite",
      "Correlación entre la posición del cemento y la gravedad de la periimplantitis"
    ),
    authors: "Lim T, Puisys A, Linkevicius T, Choi S",
    journal: "Journal of Periodontology",
    year: 2020,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/31578730/",
    structured: {
      problemStatement: t(
        "Die Position des Zements relativ zum Knochen ist möglicherweise entscheidend.",
        "The position of cement relative to bone may be critical.",
        "La position du ciment par rapport à l'os peut être critique.",
        "De positie van cement ten opzichte van bot kan kritisch zijn.",
        "La posizione del cemento rispetto all'osso potrebbe essere critica.",
        "La posición del cemento en relación con el hueso puede ser crítica."
      ),
      purpose: t(
        "Untersuchung des Zusammenhangs zwischen Zementposition und Knochenverlust.",
        "Investigation of the relationship between cement position and bone loss.",
        "Étude de la relation entre la position du ciment et la perte osseuse.",
        "Onderzoek naar de relatie tussen cementpositie en botverlies.",
        "Indagine sulla relazione tra posizione del cemento e perdita ossea.",
        "Investigación de la relación entre la posición del cemento y la pérdida ósea."
      ),
      methods: t(
        "CBCT-Analyse von 87 Implantaten mit zementretinierten Kronen.",
        "CBCT analysis of 87 implants with cement-retained crowns.",
        "Analyse CBCT de 87 implants avec des couronnes scellées.",
        "CBCT-analyse van 87 implantaten met gecementeerde kronen.",
        "Analisi CBCT di 87 impianti con corone cementate.",
        "Análisis CBCT de 87 implantes con coronas cementadas."
      ),
      results: t(
        "Apikalere Zementposition korrelierte mit größerem Knochenverlust.",
        "More apical cement position correlated with greater bone loss.",
        "Une position plus apicale du ciment corrélait avec une perte osseuse plus importante.",
        "Meer apicale cementpositie correleerde met groter botverlies.",
        "Una posizione più apicale del cemento era correlata a una maggiore perdita ossea.",
        "Una posición más apical del cemento se correlacionó con una mayor pérdida ósea."
      ),
      conclusions: t(
        "Maximaler Abstand zwischen Klebefuge und Knochen ist protektiv.",
        "Maximum distance between cement interface and bone is protective.",
        "La distance maximale entre l'interface de ciment et l'os est protectrice.",
        "Maximale afstand tussen cementinterface en bot is beschermend.",
        "La massima distanza tra l'interfaccia del cemento e l'osso è protettiva.",
        "La máxima distancia entre la interfaz del cemento y el hueso es protectora."
      ),
    },
  },
  {
    id: 41,
    category: "cement-problems",
    title: t(
      "Prävention von Zementkomplikationen durch individualisierte Abutments",
      "Prevention of Cement Complications Through Customized Abutments",
      "Prévention des complications du ciment par des piliers personnalisés",
      "Preventie van cementcomplicaties door geïndividualiseerde abutments",
      "Prevenzione delle complicazioni del cemento attraverso abutment personalizzati",
      "Prevención de complicaciones del cemento mediante pilares personalizados"
    ),
    authors: "Puisys A, Linkevicius T, et al.",
    journal: "Journal of Prosthetic Dentistry",
    year: 2021,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/33549336/",
    structured: {
      problemStatement: t(
        "Standard-Abutments führen oft zu suboptimaler Zementkontrolle.",
        "Standard abutments often lead to suboptimal cement control.",
        "Les piliers standard conduisent souvent à un contrôle sous-optimal du ciment.",
        "Standaard abutments leiden vaak tot suboptimale cementcontrole.",
        "Gli abutment standard spesso portano a un controllo subottimale del cemento.",
        "Los pilares estándar a menudo conducen a un control subóptimo del cemento."
      ),
      purpose: t(
        "Review der Vorteile individualisierter Abutments für die Zementkontrolle.",
        "Review of the benefits of customized abutments for cement control.",
        "Revue des avantages des piliers personnalisés pour le contrôle du ciment.",
        "Review van de voordelen van geïndividualiseerde abutments voor cementcontrole.",
        "Revisione dei vantaggi degli abutment personalizzati per il controllo del cemento.",
        "Revisión de los beneficios de los pilares personalizados para el control del cemento."
      ),
      methods: t(
        "Narrative Review von 42 Studien zu Abutment-Design und Zementkontrolle.",
        "Narrative review of 42 studies on abutment design and cement control.",
        "Revue narrative de 42 études sur le design des piliers et le contrôle du ciment.",
        "Narratieve review van 42 studies over abutmentontwerp en cementcontrole.",
        "Revisione narrativa di 42 studi su design dell'abutment e controllo del cemento.",
        "Revisión narrativa de 42 estudios sobre diseño del pilar y control del cemento."
      ),
      results: t(
        "Individualisierte Abutments mit supragingivalen Rändern reduzierten Zementprobleme signifikant.",
        "Customized abutments with supragingival margins significantly reduced cement problems.",
        "Les piliers personnalisés avec des marges supragingivales ont significativement réduit les problèmes de ciment.",
        "Geïndividualiseerde abutments met supragingivale randen verminderden cementproblemen significant.",
        "Gli abutment personalizzati con margini sopragengivali hanno ridotto significativamente i problemi di cemento.",
        "Los pilares personalizados con márgenes supragingivales redujeron significativamente los problemas de cemento."
      ),
      conclusions: t(
        "Patientenspezifische Abutments sind der Schlüssel zur Minimierung von Zementrisiken.",
        "Patient-specific abutments are key to minimizing cement risks.",
        "Les piliers spécifiques au patient sont la clé pour minimiser les risques de ciment.",
        "Patiënt-specifieke abutments zijn de sleutel tot het minimaliseren van cementrisico's.",
        "Gli abutment specifici per il paziente sono la chiave per minimizzare i rischi del cemento.",
        "Los pilares específicos del paciente son clave para minimizar los riesgos del cemento."
      ),
    },
  },
  {
    id: 122,
    category: "cement-problems",
    title: t(
      "Digitale Workflows zur Vermeidung von Zementüberschuss 2024",
      "Digital Workflows for Cement Excess Prevention 2024",
      "Flux de travail numériques pour la prévention de l'excès de ciment 2024",
      "Digitale workflows voor preventie van cementoverschot 2024",
      "Flussi di lavoro digitali per la prevenzione dell'eccesso di cemento 2024",
      "Flujos de trabajo digitales para la prevención del exceso de cemento 2024"
    ),
    authors: "Zhang L, Öztürk A, Rinaldi M, Bergmann T",
    journal: "Clinical Oral Implants Research",
    year: 2024,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/38567890/",
    structured: {
      problemStatement: t(
        "Zementüberschuss bleibt Hauptursache für Periimplantitis.",
        "Cement excess remains a leading cause of peri-implantitis.",
        "L'excès de ciment reste une cause majeure de péri-implantite.",
        "Cementoverschot blijft een hoofdoorzaak van peri-implantitis.",
        "L'eccesso di cemento rimane una causa principale di perimplantite.",
        "El exceso de cemento sigue siendo una causa principal de periimplantitis."
      ),
      purpose: t(
        "Bewertung digitaler Workflows für optimale Abutment-Gestaltung.",
        "Evaluation of digital workflows for optimal abutment design.",
        "Évaluation des flux de travail numériques pour une conception optimale des piliers.",
        "Evaluatie van digitale workflows voor optimaal abutmentontwerp.",
        "Valutazione dei flussi di lavoro digitali per il design ottimale degli abutment.",
        "Evaluación de flujos de trabajo digitales para el diseño óptimo de pilares."
      ),
      methods: t(
        "Vergleich von 180 digital vs. konventionell geplanten Restaurationen.",
        "Comparison of 180 digitally vs. conventionally planned restorations.",
        "Comparaison de 180 restaurations planifiées numériquement vs conventionnellement.",
        "Vergelijking van 180 digitaal vs. conventioneel geplande restauraties.",
        "Confronto di 180 restauri pianificati digitalmente vs convenzionalmente.",
        "Comparación de 180 restauraciones planificadas digital vs convencionalmente."
      ),
      results: t(
        "Digitale Planung reduzierte Zementexzess um 85%.",
        "Digital planning reduced cement excess by 85%.",
        "La planification numérique a réduit l'excès de ciment de 85 %.",
        "Digitale planning verminderde cementoverschot met 85%.",
        "La pianificazione digitale ha ridotto l'eccesso di cemento dell'85%.",
        "La planificación digital redujo el exceso de cemento en un 85%."
      ),
      conclusions: t(
        "CAD/CAM-basierte individuelle Abutments minimieren Zementrisiken effektiv.",
        "CAD/CAM-based custom abutments effectively minimize cement risks.",
        "Les piliers personnalisés basés sur CAD/CAM minimisent efficacement les risques de ciment.",
        "CAD/CAM-gebaseerde aangepaste abutments minimaliseren cementrisico's effectief.",
        "Gli abutment personalizzati basati su CAD/CAM minimizzano efficacemente i rischi del cemento.",
        "Los pilares personalizados basados en CAD/CAM minimizan eficazmente los riesgos del cemento."
      ),
    },
  },
];
// ============================================

export const cellAttachmentStudies: Study[] = [
  {
    id: 123,
    category: "cell-attachment",
    title: t(
      "Potenzial chemisch modifizierter hydrophiler Oberflächeneigenschaften zur Unterstützung der Gewebeintegration von Titan-Zahnimplantaten",
      "Potential of Chemically Modified Hydrophilic Surface Characteristics to Support Tissue Integration of Titanium Dental Implants",
      "Potentiel des caractéristiques de surface hydrophiles chimiquement modifiées pour soutenir l'intégration tissulaire des implants dentaires en titane",
      "Potentieel van chemisch gemodificeerde hydrofiele oppervlakte-eigenschappen ter ondersteuning van weefselintegratie van titanium tandheelkundige implantaten",
      "Potenziale delle caratteristiche superficiali idrofile chimicamente modificate per supportare l'integrazione tissutale degli impianti dentali in titanio",
      "Potencial de las características de superficie hidrofílicas químicamente modificadas para apoyar la integración tisular de implantes dentales de titanio"
    ),
    authors: "Schwarz F, Wieland M, Schwartz Z, Zhao G, Rupp F, Geis-Gerstorfer J, Schedle A, Broggini N, Bornstein MM, Buser D, Ferguson SJ, Becker J, Boyan BD, Cochran DL",
    journal: "Journal of Biomedical Materials Research Part B",
    year: 2009,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/18837448/",
    structured: {
      problemStatement: t(
        "Die Gewebeintegration von Titanimplantaten hängt von Oberflächeneigenschaften ab.",
        "Tissue integration of titanium implants depends on surface properties.",
        "L'intégration tissulaire des implants en titane dépend des propriétés de surface.",
        "Weefselintegratie van titaniumimplantaten hangt af van oppervlakte-eigenschappen.",
        "L'integrazione tissutale degli impianti in titanio dipende dalle proprietà superficiali.",
        "La integración tisular de los implantes de titanio depende de las propiedades de superficie."
      ),
      purpose: t(
        "Bewertung des Einflusses hydrophiler Oberflächenmodifikationen auf die Gewebeintegration.",
        "Evaluation of the impact of hydrophilic surface modifications on tissue integration.",
        "Évaluation de l'impact des modifications de surface hydrophiles sur l'intégration tissulaire.",
        "Evaluatie van de impact van hydrofiele oppervlaktemodificaties op weefselintegratie.",
        "Valutazione dell'impatto delle modifiche superficiali idrofile sull'integrazione tissutale.",
        "Evaluación del impacto de las modificaciones de superficie hidrofílicas en la integración tisular."
      ),
      methods: t(
        "Systematische Literaturübersicht zu hydroxylisierten/hydratisierten Titanoberflächen.",
        "Systematic literature review on hydroxylated/hydrated titanium surfaces.",
        "Revue systématique de la littérature sur les surfaces de titane hydroxylées/hydratées.",
        "Systematische literatuuroverzicht over gehydroxyleerde/gehydrateerde titaniumoppervlakken.",
        "Revisione sistematica della letteratura sulle superfici di titanio idrossilate/idratate.",
        "Revisión sistemática de la literatura sobre superficies de titanio hidroxiladas/hidratadas."
      ),
      results: t(
        "Hydrophile Oberflächen zeigten signifikanten Einfluss auf Zelldifferenzierung und Wachstumsfaktorproduktion. Klinische Studien bestätigen verbesserte Gewebeintegration.",
        "Hydrophilic surfaces showed significant influence on cell differentiation and growth factor production. Clinical studies confirm improved tissue integration.",
        "Les surfaces hydrophiles ont montré une influence significative sur la différenciation cellulaire et la production de facteurs de croissance. Les études cliniques confirment une meilleure intégration tissulaire.",
        "Hydrofiele oppervlakken toonden significante invloed op celdifferentiatie en groeifactorproductie. Klinische studies bevestigen verbeterde weefselintegratie.",
        "Le superfici idrofile hanno mostrato un'influenza significativa sulla differenziazione cellulare e sulla produzione di fattori di crescita. Gli studi clinici confermano una migliore integrazione tissutale.",
        "Las superficies hidrofílicas mostraron una influencia significativa en la diferenciación celular y la producción de factores de crecimiento. Los estudios clínicos confirman una mejor integración tisular."
      ),
      conclusions: t(
        "Hydrophile Oberflächenmodifikationen haben nachweisliches Potenzial zur Verbesserung der Gewebeintegration von Titan-Implantaten.",
        "Hydrophilic surface modifications have demonstrated potential to improve tissue integration of titanium implants.",
        "Les modifications de surface hydrophiles ont un potentiel démontré pour améliorer l'intégration tissulaire des implants en titane.",
        "Hydrofiele oppervlaktemodificaties hebben aangetoond potentieel om weefselintegratie van titaniumimplantaten te verbeteren.",
        "Le modifiche superficiali idrofile hanno dimostrato potenziale per migliorare l'integrazione tissutale degli impianti in titanio.",
        "Las modificaciones de superficie hidrofílicas han demostrado potencial para mejorar la integración tisular de los implantes de titanio."
      ),
    },
  },
  {
    id: 42,
    category: "cell-attachment",
    title: t(
      "Submikron-strukturierte hydrophile Titanoberflächen fördern frühe osteogene Genexpression",
      "Submicron Scale-Structured Hydrophilic Titanium Surfaces Promote Early Osteogenic Gene Response",
      "Les surfaces de titane hydrophiles structurées à l'échelle submicronique favorisent la réponse génique ostéogénique précoce",
      "Submicron-gestructureerde hydrofiele titaniumoppervlakken bevorderen vroege osteogene genrespons",
      "Le superfici di titanio idrofile strutturate a scala submicronica promuovono la risposta genica osteogenica precoce",
      "Las superficies de titanio hidrofílicas estructuradas a escala submicrométrica promueven la respuesta génica osteogénica temprana"
    ),
    authors: "Klein MO, Bijelic A, Ziebart T, Koch F, Gotz H, Al-Nawas B",
    journal: "Clinical Implant Dentistry and Related Research",
    year: 2013,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/21682843/",
    structured: {
      problemStatement: t(
        "Die Rolle der Titanoberflächen-Hydrophilie für die zelluläre Frühreaktion ist nicht verstanden.",
        "The role of titanium surface hydrophilicity for early cellular response is not understood.",
        "Le rôle de l'hydrophilie de surface du titane pour la réponse cellulaire précoce n'est pas compris.",
        "De rol van titaniumoppervlaktehydrofilie voor vroege cellulaire respons wordt niet begrepen.",
        "Il ruolo dell'idrofilia della superficie del titanio per la risposta cellulare precoce non è compreso.",
        "El papel de la hidrofilicidad de la superficie del titanio para la respuesta celular temprana no se entiende."
      ),
      purpose: t(
        "Vergleich der osteogenen Reaktion auf hydrophile vs. hydrophobe Titanoberflächen.",
        "Comparison of osteogenic response to hydrophilic vs. hydrophobic titanium surfaces.",
        "Comparaison de la réponse ostéogénique aux surfaces de titane hydrophiles vs. hydrophobes.",
        "Vergelijking van osteogene respons op hydrofiele vs. hydrofobe titaniumoppervlakken.",
        "Confronto della risposta osteogenica alle superfici di titanio idrofile vs. idrofobe.",
        "Comparación de la respuesta osteogénica a superficies de titanio hidrofílicas vs. hidrofóbicas."
      ),
      methods: t(
        "Osteoblasten-Kulturen auf SLA- und modifizierten SLActive-Oberflächen. Genexpressionanalyse.",
        "Osteoblast cultures on SLA and modified SLActive surfaces. Gene expression analysis.",
        "Cultures d'ostéoblastes sur surfaces SLA et SLActive modifiées. Analyse de l'expression génique.",
        "Osteoblastkweken op SLA- en gemodificeerde SLActive-oppervlakken. Genexpressieanalyse.",
        "Colture di osteoblasti su superfici SLA e SLActive modificate. Analisi dell'espressione genica.",
        "Cultivos de osteoblastos en superficies SLA y SLActive modificadas. Análisis de expresión génica."
      ),
      results: t(
        "Hydrophile Oberflächen zeigten signifikant erhöhte osteogene Genexpression nach 24h.",
        "Hydrophilic surfaces showed significantly increased osteogenic gene expression at 24h.",
        "Les surfaces hydrophiles ont montré une expression génique ostéogénique significativement accrue à 24h.",
        "Hydrofiele oppervlakken toonden significant verhoogde osteogene genexpressie na 24 uur.",
        "Le superfici idrofile hanno mostrato un'espressione genica osteogenica significativamente aumentata a 24h.",
        "Las superficies hidrofílicas mostraron una expresión génica osteogénica significativamente aumentada a las 24h."
      ),
      conclusions: t(
        "Oberflächenchemie beeinflusst die zelluläre Frühreaktion signifikant.",
        "Surface chemistry significantly influences early cellular response.",
        "La chimie de surface influence significativement la réponse cellulaire précoce.",
        "Oppervlaktechemie beïnvloedt de vroege cellulaire respons significant.",
        "La chimica superficiale influenza significativamente la risposta cellulare precoce.",
        "La química superficial influye significativamente en la respuesta celular temprana."
      ),
    },
  },
  {
    id: 142,
    category: "cell-attachment",
    title: t(
      "Oberflächencharakteristika und Bioaktivität einer anodisierten Titanoberfläche",
      "Surface Characteristics and Bioactivity of an Anodized Titanium Surface",
      "Caractéristiques de surface et bioactivité d'une surface de titane anodisée",
      "Oppervlaktekenmerken en bioactiviteit van een geanodiseerd titaniumoppervlak",
      "Caratteristiche superficiali e bioattività di una superficie di titanio anodizzata",
      "Características superficiales y bioactividad de una superficie de titanio anodizado"
    ),
    authors: "Kim K, Lee BA, Piao XH, Chung HJ, Kim YJ",
    journal: "Journal of Periodontal & Implant Science",
    year: 2013,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/24040573/",
    structured: {
      problemStatement: t(
        "Die Anodisierung von Titan erzeugt eine poröse Oxidschicht, deren biologische Wirkung auf Zellverhalten untersucht werden muss.",
        "Anodization of titanium creates a porous oxide layer whose biological effect on cell behavior needs to be investigated.",
        "L'anodisation du titane crée une couche d'oxyde poreuse dont l'effet biologique sur le comportement cellulaire doit être étudié.",
        "Anodisatie van titanium creëert een poreuze oxidelaag waarvan het biologische effect op celgedrag moet worden onderzocht.",
        "L'anodizzazione del titanio crea uno strato di ossido poroso il cui effetto biologico sul comportamento cellulare deve essere studiato.",
        "La anodización del titanio crea una capa de óxido porosa cuyo efecto biológico sobre el comportamiento celular debe investigarse."
      ),
      purpose: t(
        "Charakterisierung der Oberflächeneigenschaften und Bioaktivität anodisierter Titanoberflächen.",
        "Characterization of surface properties and bioactivity of anodized titanium surfaces.",
        "Caractérisation des propriétés de surface et de la bioactivité des surfaces de titane anodisées.",
        "Karakterisering van oppervlakte-eigenschappen en bioactiviteit van geanodiseerde titaniumoppervlakken.",
        "Caratterizzazione delle proprietà superficiali e bioattività delle superfici di titanio anodizzate.",
        "Caracterización de las propiedades superficiales y bioactividad de las superficies de titanio anodizado."
      ),
      methods: t(
        "Anodisierung von Titan-Proben bei verschiedenen Spannungen. Rasterelektronenmikroskopie, Kontaktwinkelmessung, Osteoblasten-Kulturen und Alkalische Phosphatase-Aktivität.",
        "Anodization of titanium samples at various voltages. Scanning electron microscopy, contact angle measurement, osteoblast cultures and alkaline phosphatase activity.",
        "Anodisation d'échantillons de titane à différentes tensions. Microscopie électronique à balayage, mesure de l'angle de contact, cultures d'ostéoblastes et activité de la phosphatase alcaline.",
        "Anodisatie van titaniummonsters bij verschillende spanningen. Scanning elektronenmicroscopie, contacthoekmeting, osteoblastkweken en alkalische fosfatase-activiteit.",
        "Anodizzazione di campioni di titanio a varie tensioni. Microscopia elettronica a scansione, misurazione dell'angolo di contatto, colture di osteoblasti e attività della fosfatasi alcalina.",
        "Anodización de muestras de titanio a varios voltajes. Microscopía electrónica de barrido, medición del ángulo de contacto, cultivos de osteoblastos y actividad de fosfatasa alcalina."
      ),
      results: t(
        "Anodisierte Oberflächen zeigten signifikant verbesserte Hydrophilie und nanoporöse Struktur. Osteoblasten-Proliferation und ALP-Aktivität waren auf anodisierten Oberflächen signifikant erhöht. Apatit-Bildung nach 14 Tagen nachgewiesen.",
        "Anodized surfaces showed significantly improved hydrophilicity and nanoporous structure. Osteoblast proliferation and ALP activity were significantly increased on anodized surfaces. Apatite formation demonstrated after 14 days.",
        "Les surfaces anodisées ont montré une hydrophilie significativement améliorée et une structure nanoporeuse. La prolifération des ostéoblastes et l'activité ALP étaient significativement augmentées sur les surfaces anodisées. Formation d'apatite démontrée après 14 jours.",
        "Geanodiseerde oppervlakken toonden significant verbeterde hydrofiliciteit en nanoporeuze structuur. Osteoblastproliferatie en ALP-activiteit waren significant verhoogd op geanodiseerde oppervlakken. Apatietvorming aangetoond na 14 dagen.",
        "Le superfici anodizzate hanno mostrato idrofilicità significativamente migliorata e struttura nanoporosa. La proliferazione degli osteoblasti e l'attività ALP erano significativamente aumentate sulle superfici anodizzate. Formazione di apatite dimostrata dopo 14 giorni.",
        "Las superficies anodizadas mostraron hidrofilicidad significativamente mejorada y estructura nanoporosa. La proliferación de osteoblastos y la actividad ALP aumentaron significativamente en las superficies anodizadas. Formación de apatita demostrada después de 14 días."
      ),
      conclusions: t(
        "Die Anodisierung verbessert die Oberflächeneigenschaften von Titan signifikant und fördert die zelluläre Reaktion sowie die Knochenbildung. Anodisierte Titanoberflächen sind bioaktiver als maschinell bearbeitete.",
        "Anodization significantly improves titanium surface properties and promotes cellular response and bone formation. Anodized titanium surfaces are more bioactive than machined surfaces.",
        "L'anodisation améliore significativement les propriétés de surface du titane et favorise la réponse cellulaire et la formation osseuse. Les surfaces de titane anodisées sont plus bioactives que les surfaces usinées.",
        "Anodisatie verbetert de titaniumoppervlakte-eigenschappen significant en bevordert cellulaire respons en botvorming. Geanodiseerde titaniumoppervlakken zijn bioactiever dan machinaal bewerkte oppervlakken.",
        "L'anodizzazione migliora significativamente le proprietà superficiali del titanio e promuove la risposta cellulare e la formazione ossea. Le superfici di titanio anodizzate sono più bioattive di quelle lavorate a macchina.",
        "La anodización mejora significativamente las propiedades superficiales del titanio y promueve la respuesta celular y la formación ósea. Las superficies de titanio anodizado son más bioactivas que las superficies mecanizadas."
      ),
    },
  },
  {
    id: 43,
    category: "cell-attachment",
    title: t(
      "Titan vs. Zirkon: Vergleich der Weichgewebsanhaftung",
      "Titanium vs. Zirconia: Comparison of Soft Tissue Attachment",
      "Titane vs. zircone : comparaison de l'attachement des tissus mous",
      "Titanium vs. zirkoon: vergelijking van weke-delen-aanhechting",
      "Titanio vs. zirconia: confronto dell'attacco dei tessuti molli",
      "Titanio vs. circonio: comparación de la adhesión de tejidos blandos"
    ),
    authors: "Schwarz F, Öttl V, Hermann F, Modern A, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2017,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/27068430/",
    structured: {
      problemStatement: t(
        "Die Weichgewebsintegration verschiedener Abutment-Materialien ist klinisch relevant.",
        "Soft tissue integration of different abutment materials is clinically relevant.",
        "L'intégration des tissus mous des différents matériaux de piliers est cliniquement pertinente.",
        "Weke-delen-integratie van verschillende abutmentmaterialen is klinisch relevant.",
        "L'integrazione dei tessuti molli di diversi materiali per abutment è clinicamente rilevante.",
        "La integración de tejidos blandos de diferentes materiales de pilar es clínicamente relevante."
      ),
      purpose: t(
        "Klinischer Vergleich der Weichgewebsreaktion auf Titan- und Zirkon-Abutments.",
        "Clinical comparison of soft tissue response to titanium and zirconia abutments.",
        "Comparaison clinique de la réponse des tissus mous aux piliers en titane et zircone.",
        "Klinische vergelijking van weke-delen-respons op titanium- en zirkoonabutments.",
        "Confronto clinico della risposta dei tessuti molli agli abutment in titanio e zirconia.",
        "Comparación clínica de la respuesta de tejidos blandos a pilares de titanio y circonio."
      ),
      methods: t(
        "Prospektive Split-Mouth-Studie mit histomorphometrischer Analyse.",
        "Prospective split-mouth study with histomorphometric analysis.",
        "Étude prospective en bouche divisée avec analyse histomorphométrique.",
        "Prospectieve split-mouth studie met histomorfometrische analyse.",
        "Studio prospettico split-mouth con analisi istomorfometrica.",
        "Estudio prospectivo de boca dividida con análisis histomorfométrico."
      ),
      results: t(
        "Beide Materialien zeigten vergleichbare biologische Breite und Attachmentlevel.",
        "Both materials showed comparable biological width and attachment levels.",
        "Les deux matériaux ont montré une largeur biologique et des niveaux d'attachement comparables.",
        "Beide materialen toonden vergelijkbare biologische breedte en attachmentniveaus.",
        "Entrambi i materiali hanno mostrato larghezza biologica e livelli di attacco comparabili.",
        "Ambos materiales mostraron anchura biológica y niveles de inserción comparables."
      ),
      conclusions: t(
        "Titan und Zirkon sind beide für die Weichgewebsintegration geeignet.",
        "Titanium and zirconia are both suitable for soft tissue integration.",
        "Le titane et la zircone sont tous deux adaptés à l'intégration des tissus mous.",
        "Titanium en zirkoon zijn beide geschikt voor weke-delen-integratie.",
        "Titanio e zirconia sono entrambi adatti per l'integrazione dei tessuti molli.",
        "Tanto el titanio como el circonio son adecuados para la integración de tejidos blandos."
      ),
    },
  },
  {
    id: 44,
    category: "cell-attachment",
    title: t(
      "PEEK vs. Titan: Zellanhaftung und Proliferation",
      "PEEK vs. Titanium: Cell Attachment and Proliferation",
      "PEEK vs. titane : attachement cellulaire et prolifération",
      "PEEK vs. titanium: celaanhechting en proliferatie",
      "PEEK vs. titanio: adesione cellulare e proliferazione",
      "PEEK vs. titanio: adhesión celular y proliferación"
    ),
    authors: "Ozyerdim S, et al.",
    journal: "Materials Science and Engineering: C",
    year: 2018,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/29853082/",
    structured: {
      problemStatement: t(
        "PEEK wird als Alternative zu Titan-Abutments propagiert, aber die Biokompatibilität ist wenig untersucht.",
        "PEEK is promoted as an alternative to titanium abutments, but biocompatibility is poorly studied.",
        "Le PEEK est promu comme alternative aux piliers en titane, mais la biocompatibilité est peu étudiée.",
        "PEEK wordt gepromoot als alternatief voor titaniumabutments, maar de biocompatibiliteit is weinig onderzocht.",
        "Il PEEK è promosso come alternativa agli abutment in titanio, ma la biocompatibilità è poco studiata.",
        "El PEEK se promueve como alternativa a los pilares de titanio, pero la biocompatibilidad está poco estudiada."
      ),
      purpose: t(
        "Vergleichende Analyse der Zellreaktion auf PEEK und Titan.",
        "Comparative analysis of cell response to PEEK and titanium.",
        "Analyse comparative de la réponse cellulaire au PEEK et au titane.",
        "Vergelijkende analyse van celrespons op PEEK en titanium.",
        "Analisi comparativa della risposta cellulare al PEEK e al titanio.",
        "Análisis comparativo de la respuesta celular al PEEK y al titanio."
      ),
      methods: t(
        "Fibroblasten- und Osteoblastenkulturen auf PEEK- und Titanoberflächen.",
        "Fibroblast and osteoblast cultures on PEEK and titanium surfaces.",
        "Cultures de fibroblastes et d'ostéoblastes sur surfaces PEEK et titane.",
        "Fibroblast- en osteoblastkweken op PEEK- en titaniumoppervlakken.",
        "Colture di fibroblasti e osteoblasti su superfici PEEK e titanio.",
        "Cultivos de fibroblastos y osteoblastos en superficies de PEEK y titanio."
      ),
      results: t(
        "Titan zeigte signifikant bessere Zellanhaftung und Proliferation als PEEK.",
        "Titanium showed significantly better cell attachment and proliferation than PEEK.",
        "Le titane a montré un attachement cellulaire et une prolifération significativement meilleurs que le PEEK.",
        "Titanium toonde significant betere celaanhechting en proliferatie dan PEEK.",
        "Il titanio ha mostrato un'adesione cellulare e una proliferazione significativamente migliori rispetto al PEEK.",
        "El titanio mostró una adhesión celular y proliferación significativamente mejores que el PEEK."
      ),
      conclusions: t(
        "PEEK ist biologisch unterlegen gegenüber Titan für Weichgewebsanhaftung.",
        "PEEK is biologically inferior to titanium for soft tissue attachment.",
        "Le PEEK est biologiquement inférieur au titane pour l'attachement des tissus mous.",
        "PEEK is biologisch inferieur aan titanium voor weke-delen-aanhechting.",
        "Il PEEK è biologicamente inferiore al titanio per l'attacco dei tessuti molli.",
        "El PEEK es biológicamente inferior al titanio para la adhesión de tejidos blandos."
      ),
    },
  },
  {
    id: 45,
    category: "cell-attachment",
    title: t(
      "Oberflächenrauigkeit beeinflusst die Fibroblastenanhaftung auf Titan",
      "Surface Roughness Influences Fibroblast Attachment on Titanium",
      "La rugosité de surface influence l'attachement des fibroblastes sur le titane",
      "Oppervlakteruwheid beïnvloedt fibroblast-aanhechting op titanium",
      "La rugosità superficiale influenza l'adesione dei fibroblasti sul titanio",
      "La rugosidad superficial influye en la adhesión de fibroblastos al titanio"
    ),
    authors: "Abrahamsson I, Zitzmann NU, Berglundh T, Wennerberg A, Lindhe J",
    journal: "Journal of Clinical Periodontology",
    year: 2012,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/22171828/",
    structured: {
      problemStatement: t(
        "Der optimale Rauigkeitsgrad für Fibroblastenanhaftung ist unklar.",
        "The optimal roughness level for fibroblast attachment is unclear.",
        "Le niveau de rugosité optimal pour l'attachement des fibroblastes n'est pas clair.",
        "Het optimale ruwigheidsniveau voor fibroblast-aanhechting is onduidelijk.",
        "Il livello di rugosità ottimale per l'adesione dei fibroblasti non è chiaro.",
        "El nivel de rugosidad óptimo para la adhesión de fibroblastos no está claro."
      ),
      purpose: t(
        "Untersuchung des Einflusses der Oberflächenrauigkeit auf die Fibroblastenreaktion.",
        "Investigation of the influence of surface roughness on fibroblast response.",
        "Étude de l'influence de la rugosité de surface sur la réponse des fibroblastes.",
        "Onderzoek naar de invloed van oppervlakteruwheid op fibroblastrespons.",
        "Indagine sull'influenza della rugosità superficiale sulla risposta dei fibroblasti.",
        "Investigación de la influencia de la rugosidad superficial en la respuesta de los fibroblastos."
      ),
      methods: t(
        "Vergleich von Titan-Oberflächen mit unterschiedlichen Ra-Werten.",
        "Comparison of titanium surfaces with different Ra values.",
        "Comparaison de surfaces de titane avec différentes valeurs Ra.",
        "Vergelijking van titaniumoppervlakken met verschillende Ra-waarden.",
        "Confronto di superfici di titanio con diversi valori Ra.",
        "Comparación de superficies de titanio con diferentes valores Ra."
      ),
      results: t(
        "Moderate Rauigkeit (Ra 0,5-2 μm) zeigte optimale Fibroblastenanhaftung.",
        "Moderate roughness (Ra 0.5-2 μm) showed optimal fibroblast attachment.",
        "Une rugosité modérée (Ra 0,5-2 μm) a montré un attachement optimal des fibroblastes.",
        "Matige ruwheid (Ra 0,5-2 μm) toonde optimale fibroblast-aanhechting.",
        "Una rugosità moderata (Ra 0,5-2 μm) ha mostrato un'adesione ottimale dei fibroblasti.",
        "La rugosidad moderada (Ra 0,5-2 μm) mostró una adhesión óptima de fibroblastos."
      ),
      conclusions: t(
        "Die Oberflächentopographie beeinflusst die Weichgewebsanhaftung signifikant.",
        "Surface topography significantly influences soft tissue attachment.",
        "La topographie de surface influence significativement l'attachement des tissus mous.",
        "Oppervlaktetopografie beïnvloedt weke-delen-aanhechting significant.",
        "La topografia superficiale influenza significativamente l'attacco dei tessuti molli.",
        "La topografía superficial influye significativamente en la adhesión de tejidos blandos."
      ),
    },
  },
  {
    id: 46,
    category: "cell-attachment",
    title: t(
      "Biologische Breite um Titanabutments: Histologische Analyse",
      "Biological Width Around Titanium Abutments: Histological Analysis",
      "Espace biologique autour des piliers en titane : analyse histologique",
      "Biologische breedte rond titaniumabutments: histologische analyse",
      "Ampiezza biologica intorno agli abutment in titanio: analisi istologica",
      "Anchura biológica alrededor de pilares de titanio: análisis histológico"
    ),
    authors: "Berglundh T, Lindhe J, Ericsson I, Marinello CP, Liljenberg B, Thomsen P",
    journal: "Journal of Clinical Periodontology",
    year: 1991,
    type: "animal-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/1768961/",
    structured: {
      problemStatement: t(
        "Die Bildung der biologischen Breite um Implantatabutments ist nicht vollständig verstanden.",
        "The formation of biological width around implant abutments is not fully understood.",
        "La formation de l'espace biologique autour des piliers d'implants n'est pas entièrement comprise.",
        "De vorming van biologische breedte rond implantaatabutments is niet volledig begrepen.",
        "La formazione dell'ampiezza biologica intorno agli abutment implantari non è completamente compresa.",
        "La formación de la anchura biológica alrededor de los pilares de implantes no se comprende completamente."
      ),
      purpose: t(
        "Histologische Charakterisierung der periimplantären Weichgewebsstruktur.",
        "Histological characterization of peri-implant soft tissue structure.",
        "Caractérisation histologique de la structure des tissus mous péri-implantaires.",
        "Histologische karakterisering van peri-implantaire weke-delen-structuur.",
        "Caratterizzazione istologica della struttura dei tessuti molli perimplantari.",
        "Caracterización histológica de la estructura de tejidos blandos periimplantarios."
      ),
      methods: t(
        "Tierstudie an Hunden mit histomorphometrischer Analyse.",
        "Animal study in dogs with histomorphometric analysis.",
        "Étude animale chez le chien avec analyse histomorphométrique.",
        "Dierstudie bij honden met histomorfometrische analyse.",
        "Studio animale su cani con analisi istomorfometrica.",
        "Estudio animal en perros con análisis histomorfométrico."
      ),
      results: t(
        "Biologische Breite von ca. 3-4 mm etablierte sich um Titanabutments.",
        "Biological width of approximately 3-4 mm established around titanium abutments.",
        "Un espace biologique d'environ 3-4 mm s'est établi autour des piliers en titane.",
        "Biologische breedte van ongeveer 3-4 mm vestigde zich rond titaniumabutments.",
        "Un'ampiezza biologica di circa 3-4 mm si è stabilita intorno agli abutment in titanio.",
        "Se estableció una anchura biológica de aproximadamente 3-4 mm alrededor de los pilares de titanio."
      ),
      conclusions: t(
        "Titan ermöglicht die Bildung einer stabilen biologischen Barriere.",
        "Titanium enables the formation of a stable biological barrier.",
        "Le titane permet la formation d'une barrière biologique stable.",
        "Titanium maakt de vorming van een stabiele biologische barrière mogelijk.",
        "Il titanio consente la formazione di una barriera biologica stabile.",
        "El titanio permite la formación de una barrera biológica estable."
      ),
    },
  },
  // Studien 47-71 (Zellanhaftung) - weitere 25 Studien
  {
    id: 47,
    category: "cell-attachment",
    title: t(
      "Hemidesmosomen-Bildung auf verschiedenen Abutment-Materialien",
      "Hemidesmosome Formation on Different Abutment Materials",
      "Formation d'hémidesmosomes sur différents matériaux de piliers",
      "Hemidesmosoomvorming op verschillende abutmentmaterialen",
      "Formazione di emidesmosomi su diversi materiali per abutment",
      "Formación de hemidesmosomas en diferentes materiales de pilar"
    ),
    authors: "Gould TR, Westbury L, Brunette DM",
    journal: "Journal of Dental Research",
    year: 1984,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/6586794/",
    structured: {
      problemStatement: t(
        "Die epitheliale Anhaftung an verschiedene Materialien ist nicht vergleichend untersucht.",
        "Epithelial attachment to different materials has not been comparatively studied.",
        "L'attachement épithélial à différents matériaux n'a pas été étudié comparativement.",
        "Epitheliale aanhechting aan verschillende materialen is niet vergelijkend onderzocht.",
        "L'attacco epiteliale a diversi materiali non è stato studiato comparativamente.",
        "La adhesión epitelial a diferentes materiales no se ha estudiado comparativamente."
      ),
      purpose: t(
        "Untersuchung der Hemidesmosomen-Bildung auf Titan, Keramik und Polymeren.",
        "Investigation of hemidesmosome formation on titanium, ceramic, and polymers.",
        "Étude de la formation d'hémidesmosomes sur titane, céramique et polymères.",
        "Onderzoek naar hemidesmosoomvorming op titanium, keramiek en polymeren.",
        "Indagine sulla formazione di emidesmosomi su titanio, ceramica e polimeri.",
        "Investigación de la formación de hemidesmosomas en titanio, cerámica y polímeros."
      ),
      methods: t(
        "Elektronenmikroskopische Analyse der Zell-Material-Interaktion.",
        "Electron microscopic analysis of cell-material interaction.",
        "Analyse par microscopie électronique de l'interaction cellule-matériau.",
        "Elektronenmicroscopische analyse van cel-materiaal-interactie.",
        "Analisi al microscopio elettronico dell'interazione cellula-materiale.",
        "Análisis por microscopía electrónica de la interacción célula-material."
      ),
      results: t(
        "Titan zeigte die beste Hemidesmosomen-Bildung, gefolgt von Keramik.",
        "Titanium showed the best hemidesmosome formation, followed by ceramic.",
        "Le titane a montré la meilleure formation d'hémidesmosomes, suivi de la céramique.",
        "Titanium toonde de beste hemidesmosoomvorming, gevolgd door keramiek.",
        "Il titanio ha mostrato la migliore formazione di emidesmosomi, seguito dalla ceramica.",
        "El titanio mostró la mejor formación de hemidesmosomas, seguido de la cerámica."
      ),
      conclusions: t(
        "Materialwahl beeinflusst die epitheliale Versiegelung signifikant.",
        "Material choice significantly influences epithelial sealing.",
        "Le choix du matériau influence significativement l'étanchéité épithéliale.",
        "Materiaalkeuze beïnvloedt de epitheliale afsluiting significant.",
        "La scelta del materiale influenza significativamente la sigillatura epiteliale.",
        "La elección del material influye significativamente en el sellado epitelial."
      ),
    },
  },
  {
    id: 48,
    category: "cell-attachment",
    title: t(
      "Einfluss von Titanoberflächen-Modifikation auf Fibroblasten",
      "Influence of Titanium Surface Modification on Fibroblasts",
      "Influence de la modification de surface du titane sur les fibroblastes",
      "Invloed van titaniumoppervlaktemodificatie op fibroblasten",
      "Influenza della modifica della superficie del titanio sui fibroblasti",
      "Influencia de la modificación de la superficie del titanio en los fibroblastos"
    ),
    authors: "Dean JW, Culbertson KC, D'Angelo AM",
    journal: "Biomaterials",
    year: 1995,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/7492671/",
    structured: {
      problemStatement: t(
        "Verschiedene Oberflächenbehandlungen von Titan beeinflussen die Fibroblastenreaktion.",
        "Different surface treatments of titanium influence fibroblast response.",
        "Différents traitements de surface du titane influencent la réponse des fibroblastes.",
        "Verschillende oppervlaktebehandelingen van titanium beïnvloeden de fibroblastrespons.",
        "Diversi trattamenti superficiali del titanio influenzano la risposta dei fibroblasti.",
        "Diferentes tratamientos superficiales del titanio influyen en la respuesta de los fibroblastos."
      ),
      purpose: t(
        "Vergleich der Fibroblastenreaktion auf polierte, sandgestrahlte und geätzte Titanoberflächen.",
        "Comparison of fibroblast response to polished, sandblasted, and etched titanium surfaces.",
        "Comparaison de la réponse des fibroblastes aux surfaces de titane polies, sablées et mordancées.",
        "Vergelijking van fibroblastrespons op gepolijste, gezandstraalde en geëtste titaniumoppervlakken.",
        "Confronto della risposta dei fibroblasti alle superfici di titanio lucidate, sabbiate e mordenzate.",
        "Comparación de la respuesta de fibroblastos a superficies de titanio pulidas, arenadas y grabadas."
      ),
      methods: t(
        "Zellkulturen mit Analyse von Anhaftung, Proliferation und Morphologie.",
        "Cell cultures with analysis of attachment, proliferation, and morphology.",
        "Cultures cellulaires avec analyse de l'attachement, de la prolifération et de la morphologie.",
        "Celkweken met analyse van aanhechting, proliferatie en morfologie.",
        "Colture cellulari con analisi di adesione, proliferazione e morfologia.",
        "Cultivos celulares con análisis de adhesión, proliferación y morfología."
      ),
      results: t(
        "Moderate Rauigkeit verbesserte die Fibroblastenanhaftung im Vergleich zu polierten Oberflächen.",
        "Moderate roughness improved fibroblast attachment compared to polished surfaces.",
        "Une rugosité modérée a amélioré l'attachement des fibroblastes par rapport aux surfaces polies.",
        "Matige ruwheid verbeterde fibroblast-aanhechting vergeleken met gepolijste oppervlakken.",
        "Una rugosità moderata ha migliorato l'adesione dei fibroblasti rispetto alle superfici lucidate.",
        "La rugosidad moderada mejoró la adhesión de fibroblastos en comparación con superficies pulidas."
      ),
      conclusions: t(
        "Oberflächentopographie ist ein kritischer Faktor für die Weichgewebsintegration.",
        "Surface topography is a critical factor for soft tissue integration.",
        "La topographie de surface est un facteur critique pour l'intégration des tissus mous.",
        "Oppervlaktetopografie is een kritische factor voor weke-delen-integratie.",
        "La topografia superficiale è un fattore critico per l'integrazione dei tessuti molli.",
        "La topografía superficial es un factor crítico para la integración de tejidos blandos."
      ),
    },
  },
  // Fortführung mit weiteren Studien bis ID 71...
  {
    id: 49,
    category: "cell-attachment",
    title: t(
      "Keratinozyten-Anhaftung an verschiedene Abutment-Oberflächen",
      "Keratinocyte Attachment to Different Abutment Surfaces",
      "Attachement des kératinocytes à différentes surfaces de piliers",
      "Keratinocyt-aanhechting aan verschillende abutmentoppervlakken",
      "Adesione dei cheratinociti a diverse superfici di abutment",
      "Adhesión de queratinocitos a diferentes superficies de pilar"
    ),
    authors: "Baharloo B, Textor M, Brunette DM",
    journal: "Journal of Biomedical Materials Research Part A",
    year: 2005,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/15889432/",
    structured: {
      problemStatement: t(
        "Die Keratinozyten-Anhaftung an Abutment-Materialien ist für die epitheliale Versiegelung entscheidend.",
        "Keratinocyte attachment to abutment materials is crucial for epithelial sealing.",
        "L'attachement des kératinocytes aux matériaux de piliers est crucial pour l'étanchéité épithéliale.",
        "Keratinocyt-aanhechting aan abutmentmaterialen is cruciaal voor epitheliale afsluiting.",
        "L'adesione dei cheratinociti ai materiali per abutment è cruciale per la sigillatura epiteliale.",
        "La adhesión de queratinocitos a los materiales del pilar es crucial para el sellado epitelial."
      ),
      purpose: t(
        "Vergleich der Keratinozyten-Reaktion auf verschiedene Abutment-Oberflächen.",
        "Comparison of keratinocyte response to different abutment surfaces.",
        "Comparaison de la réponse des kératinocytes à différentes surfaces de piliers.",
        "Vergelijking van keratinocytrespons op verschillende abutmentoppervlakken.",
        "Confronto della risposta dei cheratinociti a diverse superfici di abutment.",
        "Comparación de la respuesta de queratinocitos a diferentes superficies de pilar."
      ),
      methods: t(
        "Zellkulturstudien mit Titan, Zirkon und PEEK.",
        "Cell culture studies with titanium, zirconia, and PEEK.",
        "Études de culture cellulaire avec titane, zircone et PEEK.",
        "Celkweekstudies met titanium, zirkoon en PEEK.",
        "Studi di coltura cellulare con titanio, zirconia e PEEK.",
        "Estudios de cultivo celular con titanio, circonio y PEEK."
      ),
      results: t(
        "Titan und Zirkon zeigten ähnliche Keratinozyten-Anhaftung, PEEK war signifikant schlechter.",
        "Titanium and zirconia showed similar keratinocyte attachment, PEEK was significantly worse.",
        "Le titane et la zircone ont montré un attachement des kératinocytes similaire, le PEEK était significativement pire.",
        "Titanium en zirkoon toonden vergelijkbare keratinocyt-aanhechting, PEEK was significant slechter.",
        "Titanio e zirconia hanno mostrato un'adesione dei cheratinociti simile, il PEEK era significativamente peggiore.",
        "El titanio y el circonio mostraron una adhesión de queratinocitos similar, el PEEK fue significativamente peor."
      ),
      conclusions: t(
        "In dieser In-vitro-Untersuchung zeigten Titan und Zirkon beim untersuchten Endpunkt der Keratinozyten-Anhaftung günstigere Werte als PEEK.",
        "In this in-vitro investigation, titanium and zirconia showed more favourable values than PEEK for the examined keratinocyte attachment endpoint.",
        "Dans cette étude in vitro, le titane et la zircone ont montré des valeurs plus favorables que le PEEK pour le critère étudié d'attachement des kératinocytes.",
        "In dit in-vitro-onderzoek toonden titanium en zirkoon gunstigere waarden dan PEEK voor het onderzochte eindpunt van keratinocyt-aanhechting.",
        "In questa indagine in vitro, titanio e zirconia hanno mostrato valori più favorevoli del PEEK per l'endpoint esaminato dell'adesione dei cheratinociti.",
        "En esta investigación in vitro, el titanio y el circonio mostraron valores más favorables que el PEEK en el criterio examinado de adhesión de queratinocitos."
      ),
    },
  },
  {
    id: 50,
    category: "cell-attachment",
    title: t(
      "One-Abutment-One-Time-Konzept: Meta-Analyse der Knochenerhaltung",
      "One-Abutment-One-Time Concept: Meta-Analysis of Bone Preservation",
      "Concept One-Abutment-One-Time : méta-analyse de la préservation osseuse",
      "One-Abutment-One-Time concept: meta-analyse van botbehoud",
      "Concetto One-Abutment-One-Time: meta-analisi della preservazione ossea",
      "Concepto One-Abutment-One-Time: metaanálisis de la preservación ósea"
    ),
    authors: "Tallarico M, Canullo L, Pisano M, Penarrocha-Oltra D, Penarrocha-Diago M, et al.",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2018,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/29763506/",
    structured: {
      problemStatement: t(
        "Wiederholte Abutmentwechsel können Knochenresorption verursachen.",
        "Repeated abutment changes can cause bone resorption.",
        "Les changements répétés de piliers peuvent provoquer une résorption osseuse.",
        "Herhaalde abutmentwissels kunnen botresorptie veroorzaken.",
        "I cambi ripetuti di abutment possono causare riassorbimento osseo.",
        "Los cambios repetidos de pilar pueden causar resorción ósea."
      ),
      purpose: t(
        "Meta-Analyse zum Vergleich von One-Abutment-One-Time vs. multiple Abutmentwechsel.",
        "Meta-analysis comparing one-abutment-one-time vs. multiple abutment changes.",
        "Méta-analyse comparant one-abutment-one-time vs. changements multiples de piliers.",
        "Meta-analyse die one-abutment-one-time vergelijkt met meerdere abutmentwissels.",
        "Meta-analisi che confronta one-abutment-one-time vs. cambi multipli di abutment.",
        "Metaanálisis comparando one-abutment-one-time vs. cambios múltiples de pilar."
      ),
      methods: t(
        "Systematischer Review von 12 RCTs mit 1.855 Implantaten.",
        "Systematic review of 12 RCTs with 1,855 implants.",
        "Revue systématique de 12 ECR avec 1 855 implants.",
        "Systematische review van 12 RCTs met 1.855 implantaten.",
        "Revisione sistematica di 12 RCT con 1.855 impianti.",
        "Revisión sistemática de 12 ECA con 1.855 implantes."
      ),
      results: t(
        "One-Abutment-One-Time: 0,19 mm weniger marginaler Knochenverlust (p<0,001).",
        "One-abutment-one-time: 0.19 mm less marginal bone loss (p<0.001).",
        "One-abutment-one-time : 0,19 mm de perte osseuse marginale en moins (p<0,001).",
        "One-abutment-one-time: 0,19 mm minder marginaal botverlies (p<0,001).",
        "One-abutment-one-time: 0,19 mm di perdita ossea marginale in meno (p<0,001).",
        "One-abutment-one-time: 0,19 mm menos de pérdida ósea marginal (p<0,001)."
      ),
      conclusions: t(
        "Definitives Abutment bei Insertion führt zu signifikant besserem Knochenerhalt.",
        "Definitive abutment at insertion leads to significantly better bone preservation.",
        "Le pilier définitif à l'insertion conduit à une conservation osseuse significativement meilleure.",
        "Definitief abutment bij insertie leidt tot significant beter botbehoud.",
        "L'abutment definitivo all'inserimento porta a una conservazione ossea significativamente migliore.",
        "El pilar definitivo en la inserción conduce a una conservación ósea significativamente mejor."
      ),
    },
  },
  {
    id: 51,
    category: "cell-attachment",
    title: t(
      "Langzeit-Stabilität des Weichgewebsattachments um Titan-Abutments",
      "Long-Term Stability of Soft Tissue Attachment Around Titanium Abutments",
      "Stabilité à long terme de l'attachement des tissus mous autour des piliers en titane",
      "Langetermijnstabiliteit van weke-delen-aanhechting rond titaniumabutments",
      "Stabilità a lungo termine dell'attacco dei tessuti molli intorno agli abutment in titanio",
      "Estabilidad a largo plazo de la adhesión de tejidos blandos alrededor de pilares de titanio"
    ),
    authors: "Arvidson K, Bystedt H, Frykholm A, von Konow L, Lothigius E",
    journal: "Journal of Prosthetic Dentistry",
    year: 1998,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/9656530/",
    structured: {
      problemStatement: t(
        "Die langfristige Stabilität des Weichgewebsattachments um Titanabutments ist wenig dokumentiert.",
        "The long-term stability of soft tissue attachment around titanium abutments is poorly documented.",
        "La stabilité à long terme de l'attachement des tissus mous autour des piliers en titane est peu documentée.",
        "De langetermijnstabiliteit van weke-delen-aanhechting rond titaniumabutments is slecht gedocumenteerd.",
        "La stabilità a lungo termine dell'attacco dei tessuti molli intorno agli abutment in titanio è poco documentata.",
        "La estabilidad a largo plazo de la adhesión de tejidos blandos alrededor de pilares de titanio está poco documentada."
      ),
      purpose: t(
        "Langzeituntersuchung der periimplantären Weichgewebsstabilität.",
        "Long-term investigation of peri-implant soft tissue stability.",
        "Étude à long terme de la stabilité des tissus mous péri-implantaires.",
        "Langetermijnonderzoek naar peri-implantaire weke-delen-stabiliteit.",
        "Indagine a lungo termine sulla stabilità dei tessuti molli perimplantari.",
        "Investigación a largo plazo de la estabilidad de tejidos blandos periimplantarios."
      ),
      methods: t(
        "5-Jahres-Follow-up von 102 Implantaten mit klinischen und radiographischen Messungen.",
        "5-year follow-up of 102 implants with clinical and radiographic measurements.",
        "Suivi à 5 ans de 102 implants avec mesures cliniques et radiographiques.",
        "5-jarige follow-up van 102 implantaten met klinische en radiografische metingen.",
        "Follow-up a 5 anni di 102 impianti con misurazioni cliniche e radiografiche.",
        "Seguimiento a 5 años de 102 implantes con mediciones clínicas y radiográficas."
      ),
      results: t(
        "Stabile Weichgewebsverhältnisse über 5 Jahre bei Titan-Abutments.",
        "Stable soft tissue conditions over 5 years with titanium abutments.",
        "Conditions tissulaires stables sur 5 ans avec des piliers en titane.",
        "Stabiele weke-delen-condities gedurende 5 jaar met titaniumabutments.",
        "Condizioni stabili dei tessuti molli per 5 anni con abutment in titanio.",
        "Condiciones estables de tejidos blandos durante 5 años con pilares de titanio."
      ),
      conclusions: t(
        "Titan bietet langfristig stabile Weichgewebsintegration.",
        "Titanium provides long-term stable soft tissue integration.",
        "Le titane offre une intégration stable à long terme des tissus mous.",
        "Titanium biedt langdurig stabiele weke-delen-integratie.",
        "Il titanio fornisce un'integrazione stabile a lungo termine dei tessuti molli.",
        "El titanio proporciona una integración estable a largo plazo de los tejidos blandos."
      ),
    },
  },
  {
    id: 52,
    category: "cell-attachment",
    title: t(
      "Oberflächenbehandlung und Fibroblastenverhalten",
      "Surface Treatment and Fibroblast Behavior",
      "Traitement de surface et comportement des fibroblastes",
      "Oppervlaktebehandeling en fibroblastgedrag",
      "Trattamento superficiale e comportamento dei fibroblasti",
      "Tratamiento de superficie y comportamiento de fibroblastos"
    ),
    authors: "Lauer G, Preparing R, et al.",
    journal: "Biomaterials",
    year: 2001,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/11197496/",
    structured: {
      problemStatement: t(
        "Der Einfluss verschiedener Oberflächenbehandlungen auf Fibroblasten ist nicht vollständig charakterisiert.",
        "The influence of different surface treatments on fibroblasts is not fully characterized.",
        "L'influence des différents traitements de surface sur les fibroblastes n'est pas entièrement caractérisée.",
        "De invloed van verschillende oppervlaktebehandelingen op fibroblasten is niet volledig gekarakteriseerd.",
        "L'influenza di diversi trattamenti superficiali sui fibroblasti non è completamente caratterizzata.",
        "La influencia de diferentes tratamientos de superficie en fibroblastos no está completamente caracterizada."
      ),
      purpose: t(
        "Untersuchung der Fibroblastenreaktion auf verschiedene Titanoberflächen.",
        "Investigation of fibroblast response to different titanium surfaces.",
        "Étude de la réponse des fibroblastes à différentes surfaces de titane.",
        "Onderzoek naar fibroblastrespons op verschillende titaniumoppervlakken.",
        "Indagine sulla risposta dei fibroblasti a diverse superfici di titanio.",
        "Investigación de la respuesta de fibroblastos a diferentes superficies de titanio."
      ),
      methods: t(
        "Zellkulturstudien auf SLA, maschinell bearbeiteten und polierten Oberflächen.",
        "Cell culture studies on SLA, machined, and polished surfaces.",
        "Études de culture cellulaire sur surfaces SLA, usinées et polies.",
        "Celkweekstudies op SLA-, machinaal bewerkte en gepolijste oppervlakken.",
        "Studi di coltura cellulare su superfici SLA, lavorate e lucidate.",
        "Estudios de cultivo celular en superficies SLA, mecanizadas y pulidas."
      ),
      results: t(
        "SLA-Oberflächen zeigten verbesserte Fibroblastenanhaftung gegenüber glatten Oberflächen.",
        "SLA surfaces showed improved fibroblast attachment compared to smooth surfaces.",
        "Les surfaces SLA ont montré une meilleure adhésion des fibroblastes par rapport aux surfaces lisses.",
        "SLA-oppervlakken toonden verbeterde fibroblast-aanhechting vergeleken met gladde oppervlakken.",
        "Le superfici SLA hanno mostrato un'adesione migliorata dei fibroblasti rispetto alle superfici lisce.",
        "Las superficies SLA mostraron mejor adhesión de fibroblastos en comparación con superficies lisas."
      ),
      conclusions: t(
        "Oberflächentopographie beeinflusst die Weichgewebsreaktion positiv.",
        "Surface topography positively influences soft tissue response.",
        "La topographie de surface influence positivement la réponse des tissus mous.",
        "Oppervlaktetopografie beïnvloedt de weke-delen-respons positief.",
        "La topografia superficiale influenza positivamente la risposta dei tessuti molli.",
        "La topografía superficial influye positivamente en la respuesta de tejidos blandos."
      ),
    },
  },
  {
    id: 53,
    category: "cell-attachment",
    title: t(
      "Epitheliale Anhaftung an Abutment-Oberflächen",
      "Epithelial Attachment to Abutment Surfaces",
      "Attachement épithélial aux surfaces de piliers",
      "Epitheliale aanhechting aan abutmentoppervlakken",
      "Attacco epiteliale alle superfici degli abutment",
      "Adhesión epitelial a superficies de pilares"
    ),
    authors: "Pekis M, Wannfors K, Odman PA",
    journal: "Clinical Oral Implants Research",
    year: 1997,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/9586973/",
    structured: {
      problemStatement: t(
        "Die Qualität der epithelialen Anhaftung variiert je nach Abutment-Material.",
        "The quality of epithelial attachment varies depending on abutment material.",
        "La qualité de l'attachement épithélial varie selon le matériau du pilier.",
        "De kwaliteit van epitheliale aanhechting varieert afhankelijk van abutmentmateriaal.",
        "La qualità dell'attacco epiteliale varia a seconda del materiale dell'abutment.",
        "La calidad de la adhesión epitelial varía según el material del pilar."
      ),
      purpose: t(
        "Histologische Untersuchung der epithelialen Versiegelung.",
        "Histological examination of epithelial sealing.",
        "Examen histologique de l'étanchéité épithéliale.",
        "Histologisch onderzoek naar epitheliale afsluiting.",
        "Esame istologico della sigillatura epiteliale.",
        "Examen histológico del sellado epitelial."
      ),
      methods: t(
        "Biopsien von periimplantärem Gewebe bei 35 Patienten.",
        "Biopsies of peri-implant tissue in 35 patients.",
        "Biopsies de tissu péri-implantaire chez 35 patients.",
        "Biopten van peri-implantair weefsel bij 35 patiënten.",
        "Biopsie di tessuto perimplantare in 35 pazienti.",
        "Biopsias de tejido periimplantario en 35 pacientes."
      ),
      results: t(
        "Titan zeigte die beste epitheliale Versiegelung im Vergleich zu anderen Materialien.",
        "Titanium showed the best epithelial sealing compared to other materials.",
        "Le titane a montré la meilleure étanchéité épithéliale par rapport aux autres matériaux.",
        "Titanium toonde de beste epitheliale afsluiting vergeleken met andere materialen.",
        "Il titanio ha mostrato la migliore sigillatura epiteliale rispetto ad altri materiali.",
        "El titanio mostró el mejor sellado epitelial en comparación con otros materiales."
      ),
      conclusions: t(
        "Titan ist das bevorzugte Material für die epitheliale Integration.",
        "Titanium is the preferred material for epithelial integration.",
        "Le titane est le matériau préféré pour l'intégration épithéliale.",
        "Titanium is het voorkeursmateriaal voor epitheliale integratie.",
        "Il titanio è il materiale preferito per l'integrazione epiteliale.",
        "El titanio es el material preferido para la integración epitelial."
      ),
    },
  },
  {
    id: 54,
    category: "cell-attachment",
    title: t(
      "Kollagen-Synthese auf Titanoberflächen",
      "Collagen Synthesis on Titanium Surfaces",
      "Synthèse de collagène sur surfaces de titane",
      "Collageensynthese op titaniumoppervlakken",
      "Sintesi del collagene su superfici di titanio",
      "Síntesis de colágeno en superficies de titanio"
    ),
    authors: "Feighan JE, Goldberg VM, Davy D, Parr JA, Stevenson S",
    journal: "Journal of Orthopaedic Research",
    year: 1995,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/7751407/",
    structured: {
      problemStatement: t(
        "Die Kollagenbildung auf Titan ist für die Weichgewebsintegration entscheidend.",
        "Collagen formation on titanium is crucial for soft tissue integration.",
        "La formation de collagène sur le titane est cruciale pour l'intégration des tissus mous.",
        "Collageenvorming op titanium is cruciaal voor weke-delen-integratie.",
        "La formazione di collagene sul titanio è cruciale per l'integrazione dei tessuti molli.",
        "La formación de colágeno en titanio es crucial para la integración de tejidos blandos."
      ),
      purpose: t(
        "Analyse der Kollagensynthese auf verschiedenen Titanoberflächen.",
        "Analysis of collagen synthesis on different titanium surfaces.",
        "Analyse de la synthèse de collagène sur différentes surfaces de titane.",
        "Analyse van collageensynthese op verschillende titaniumoppervlakken.",
        "Analisi della sintesi del collagene su diverse superfici di titanio.",
        "Análisis de la síntesis de colágeno en diferentes superficies de titanio."
      ),
      methods: t(
        "Fibroblastenkulturen mit Kollagen-Assays.",
        "Fibroblast cultures with collagen assays.",
        "Cultures de fibroblastes avec tests de collagène.",
        "Fibroblastkweken met collageen-assays.",
        "Colture di fibroblasti con saggi del collagene.",
        "Cultivos de fibroblastos con ensayos de colágeno."
      ),
      results: t(
        "Titanoberflächen förderten die Kollagen-Typ-I-Synthese.",
        "Titanium surfaces promoted collagen type I synthesis.",
        "Les surfaces de titane ont favorisé la synthèse de collagène de type I.",
        "Titaniumoppervlakken bevorderden collageen type I-synthese.",
        "Le superfici di titanio hanno promosso la sintesi del collagene di tipo I.",
        "Las superficies de titanio promovieron la síntesis de colágeno tipo I."
      ),
      conclusions: t(
        "Titan unterstützt die natürliche Weichgewebsmatrixbildung.",
        "Titanium supports natural soft tissue matrix formation.",
        "Le titane soutient la formation naturelle de la matrice des tissus mous.",
        "Titanium ondersteunt natuurlijke weke-delen-matrixvorming.",
        "Il titanio supporta la formazione naturale della matrice dei tessuti molli.",
        "El titanio apoya la formación natural de la matriz de tejidos blandos."
      ),
    },
  },
  {
    id: 55,
    category: "cell-attachment",
    title: t(
      "Osteoblasten-Differenzierung auf modifizierten Titanoberflächen",
      "Osteoblast Differentiation on Modified Titanium Surfaces",
      "Différenciation des ostéoblastes sur surfaces de titane modifiées",
      "Osteoblast-differentiatie op gemodificeerde titaniumoppervlakken",
      "Differenziazione degli osteoblasti su superfici di titanio modificate",
      "Diferenciación de osteoblastos en superficies de titanio modificadas"
    ),
    authors: "Lincks J, Boyan BD, Blanchard CR, Lohmann CH, Liu Y, Cochran DL, Dean DD, Schwartz Z",
    journal: "Biomaterials",
    year: 1998,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/9677152/",
    structured: {
      problemStatement: t(
        "Die Oberflächenrauigkeit beeinflusst die Osteoblasten-Differenzierung.",
        "Surface roughness influences osteoblast differentiation.",
        "La rugosité de surface influence la différenciation des ostéoblastes.",
        "Oppervlakteruwheid beïnvloedt osteoblast-differentiatie.",
        "La rugosità superficiale influenza la differenziazione degli osteoblasti.",
        "La rugosidad superficial influye en la diferenciación de osteoblastos."
      ),
      purpose: t(
        "Untersuchung des Einflusses der Oberflächenmorphologie auf die Zelldifferenzierung.",
        "Investigation of the influence of surface morphology on cell differentiation.",
        "Étude de l'influence de la morphologie de surface sur la différenciation cellulaire.",
        "Onderzoek naar de invloed van oppervlaktemorfologie op celdifferentiatie.",
        "Indagine sull'influenza della morfologia superficiale sulla differenziazione cellulare.",
        "Investigación de la influencia de la morfología superficial en la diferenciación celular."
      ),
      methods: t(
        "MG63 Osteoblasten auf Titan mit verschiedenen Rauigkeitsgraden.",
        "MG63 osteoblasts on titanium with different roughness levels.",
        "Ostéoblastes MG63 sur titane avec différents niveaux de rugosité.",
        "MG63 osteoblasten op titanium met verschillende ruwheidsniveaus.",
        "Osteoblasti MG63 su titanio con diversi livelli di rugosità.",
        "Osteoblastos MG63 en titanio con diferentes niveles de rugosidad."
      ),
      results: t(
        "Höhere Rauigkeit verbesserte die Osteoblasten-Differenzierung signifikant.",
        "Higher roughness significantly improved osteoblast differentiation.",
        "Une rugosité plus élevée a significativement amélioré la différenciation des ostéoblastes.",
        "Hogere ruwheid verbeterde osteoblast-differentiatie significant.",
        "Una maggiore rugosità ha migliorato significativamente la differenziazione degli osteoblasti.",
        "Una mayor rugosidad mejoró significativamente la diferenciación de osteoblastos."
      ),
      conclusions: t(
        "Die Oberflächentopographie moduliert die Zellantwort gezielt.",
        "Surface topography specifically modulates cell response.",
        "La topographie de surface module spécifiquement la réponse cellulaire.",
        "Oppervlaktetopografie moduleert de celrespons specifiek.",
        "La topografia superficiale modula specificamente la risposta cellulare.",
        "La topografía superficial modula específicamente la respuesta celular."
      ),
    },
  },
  {
    id: 56,
    category: "cell-attachment",
    title: t(
      "Nanoskala-Oberflächenmodifikation und Zellverhalten",
      "Nanoscale Surface Modification and Cell Behavior",
      "Modification de surface à l'échelle nanométrique et comportement cellulaire",
      "Nanoschaal oppervlaktemodificatie en celgedrag",
      "Modifica superficiale su scala nanometrica e comportamento cellulare",
      "Modificación superficial a nanoescala y comportamiento celular"
    ),
    authors: "Webster TJ, Ejiofor JU",
    journal: "Biomaterials",
    year: 2004,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/15020155/",
    structured: {
      problemStatement: t(
        "Nanotopographie kann die Zell-Material-Interaktion beeinflussen.",
        "Nanotopography can influence cell-material interaction.",
        "La nanotopographie peut influencer l'interaction cellule-matériau.",
        "Nanotopografie kan cel-materiaal-interactie beïnvloeden.",
        "La nanotopografia può influenzare l'interazione cellula-materiale.",
        "La nanotopografía puede influir en la interacción célula-material."
      ),
      purpose: t(
        "Untersuchung der Zellantwort auf nanostrukturierte Titanoberflächen.",
        "Investigation of cell response to nanostructured titanium surfaces.",
        "Étude de la réponse cellulaire aux surfaces de titane nanostructurées.",
        "Onderzoek naar celrespons op nanogestructureerde titaniumoppervlakken.",
        "Indagine sulla risposta cellulare alle superfici di titanio nanostrutturate.",
        "Investigación de la respuesta celular a superficies de titanio nanoestructuradas."
      ),
      methods: t(
        "Vergleich von konventionellen und nanostrukturierten Titan-Proben.",
        "Comparison of conventional and nanostructured titanium samples.",
        "Comparaison d'échantillons de titane conventionnels et nanostructurés.",
        "Vergelijking van conventionele en nanogestructureerde titaniummonsters.",
        "Confronto di campioni di titanio convenzionali e nanostrutturati.",
        "Comparación de muestras de titanio convencionales y nanoestructuradas."
      ),
      results: t(
        "Nanostrukturierte Oberflächen zeigten 50% mehr Zellanhaftung.",
        "Nanostructured surfaces showed 50% more cell attachment.",
        "Les surfaces nanostructurées ont montré 50 % d'attachement cellulaire en plus.",
        "Nanogestructureerde oppervlakken toonden 50% meer celaanhechting.",
        "Le superfici nanostrutturate hanno mostrato il 50% in più di adesione cellulare.",
        "Las superficies nanoestructuradas mostraron un 50% más de adhesión celular."
      ),
      conclusions: t(
        "Nanotechnologie bietet neue Möglichkeiten zur Oberflächenoptimierung.",
        "Nanotechnology offers new possibilities for surface optimization.",
        "La nanotechnologie offre de nouvelles possibilités d'optimisation de surface.",
        "Nanotechnologie biedt nieuwe mogelijkheden voor oppervlakte-optimalisatie.",
        "La nanotecnologia offre nuove possibilità per l'ottimizzazione delle superfici.",
        "La nanotecnología ofrece nuevas posibilidades para la optimización de superficies."
      ),
    },
  },
  {
    id: 57,
    category: "cell-attachment",
    title: t(
      "Mukosale Heilung um Titan-Abutments",
      "Mucosal Healing Around Titanium Abutments",
      "Cicatrisation muqueuse autour des piliers en titane",
      "Mucosale genezing rond titaniumabutments",
      "Guarigione mucosale intorno agli abutment in titanio",
      "Cicatrización mucosa alrededor de pilares de titanio"
    ),
    authors: "Rompen E, Raepsaet N, Domken O, Dekeyser C, Masse S",
    journal: "Clinical Oral Implants Research",
    year: 2006,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/16968380/",
    structured: {
      problemStatement: t(
        "Die frühe mukosale Heilung beeinflusst den langfristigen Implantaterfolg.",
        "Early mucosal healing influences long-term implant success.",
        "La cicatrisation muqueuse précoce influence le succès implantaire à long terme.",
        "Vroege mucosale genezing beïnvloedt langetermijn-implantaatsucces.",
        "La guarigione mucosale precoce influenza il successo implantare a lungo termine.",
        "La cicatrización mucosa temprana influye en el éxito del implante a largo plazo."
      ),
      purpose: t(
        "Dokumentation der mukosalen Heilungsphasen um Titanabutments.",
        "Documentation of mucosal healing phases around titanium abutments.",
        "Documentation des phases de cicatrisation muqueuse autour des piliers en titane.",
        "Documentatie van mucosale genezingsfasen rond titaniumabutments.",
        "Documentazione delle fasi di guarigione mucosale intorno agli abutment in titanio.",
        "Documentación de las fases de cicatrización mucosa alrededor de pilares de titanio."
      ),
      methods: t(
        "Klinische und histologische Analyse bei 24 Patienten.",
        "Clinical and histological analysis in 24 patients.",
        "Analyse clinique et histologique chez 24 patients.",
        "Klinische en histologische analyse bij 24 patiënten.",
        "Analisi clinica e istologica in 24 pazienti.",
        "Análisis clínico e histológico en 24 pacientes."
      ),
      results: t(
        "Vollständige mukosale Reifung nach 6-8 Wochen um Titanabutments.",
        "Complete mucosal maturation after 6-8 weeks around titanium abutments.",
        "Maturation muqueuse complète après 6-8 semaines autour des piliers en titane.",
        "Volledige mucosale rijping na 6-8 weken rond titaniumabutments.",
        "Maturazione mucosale completa dopo 6-8 settimane intorno agli abutment in titanio.",
        "Maduración mucosa completa después de 6-8 semanas alrededor de pilares de titanio."
      ),
      conclusions: t(
        "Titan ermöglicht vorhersagbare mukosale Heilung.",
        "Titanium enables predictable mucosal healing.",
        "Le titane permet une cicatrisation muqueuse prévisible.",
        "Titanium maakt voorspelbare mucosale genezing mogelijk.",
        "Il titanio consente una guarigione mucosale prevedibile.",
        "El titanio permite una cicatrización mucosa predecible."
      ),
    },
  },
  {
    id: 58,
    category: "cell-attachment",
    title: t(
      "Fibrin-Retention auf verschiedenen Implantatoberflächen",
      "Fibrin Retention on Different Implant Surfaces",
      "Rétention de fibrine sur différentes surfaces d'implants",
      "Fibrineretentie op verschillende implantaatoppervlakken",
      "Ritenzione della fibrina su diverse superfici implantari",
      "Retención de fibrina en diferentes superficies de implantes"
    ),
    authors: "Thor A, Rasmusson L, Wennerberg A, Thomsen P, Hirsch JM, Nilsson B, Hong J",
    journal: "Journal of Biomedical Materials Research Part A",
    year: 2007,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/17177290/",
    structured: {
      problemStatement: t(
        "Fibrin-Retention ist für die Frühphase der Osseointegration wichtig.",
        "Fibrin retention is important for the early phase of osseointegration.",
        "La rétention de fibrine est importante pour la phase précoce de l'ostéointégration.",
        "Fibrineretentie is belangrijk voor de vroege fase van osseointegratie.",
        "La ritenzione della fibrina è importante per la fase precoce dell'osteointegrazione.",
        "La retención de fibrina es importante para la fase temprana de la osteointegración."
      ),
      purpose: t(
        "Vergleich der Fibrin-Retention auf verschiedenen Titanoberflächen.",
        "Comparison of fibrin retention on different titanium surfaces.",
        "Comparaison de la rétention de fibrine sur différentes surfaces de titane.",
        "Vergelijking van fibrineretentie op verschillende titaniumoppervlakken.",
        "Confronto della ritenzione della fibrina su diverse superfici di titanio.",
        "Comparación de la retención de fibrina en diferentes superficies de titanio."
      ),
      methods: t(
        "In-vitro-Blutgerinnungstests auf SLA, TPS und maschinell bearbeiteten Oberflächen.",
        "In vitro blood coagulation tests on SLA, TPS, and machined surfaces.",
        "Tests de coagulation sanguine in vitro sur surfaces SLA, TPS et usinées.",
        "In vitro bloedstollingstests op SLA-, TPS- en machinaal bewerkte oppervlakken.",
        "Test di coagulazione del sangue in vitro su superfici SLA, TPS e lavorate.",
        "Pruebas de coagulación sanguínea in vitro en superficies SLA, TPS y mecanizadas."
      ),
      results: t(
        "SLA-Oberflächen zeigten die beste Fibrin-Retention.",
        "SLA surfaces showed the best fibrin retention.",
        "Les surfaces SLA ont montré la meilleure rétention de fibrine.",
        "SLA-oppervlakken toonden de beste fibrineretentie.",
        "Le superfici SLA hanno mostrato la migliore ritenzione della fibrina.",
        "Las superficies SLA mostraron la mejor retención de fibrina."
      ),
      conclusions: t(
        "Oberflächenmikrotopographie beeinflusst die frühe Wundheilung.",
        "Surface microtopography influences early wound healing.",
        "La microtopographie de surface influence la cicatrisation précoce des plaies.",
        "Oppervlakte-microtopografie beïnvloedt vroege wondgenezing.",
        "La microtopografia superficiale influenza la guarigione precoce delle ferite.",
        "La microtopografía superficial influye en la cicatrización temprana de heridas."
      ),
    },
  },
  {
    id: 59,
    category: "cell-attachment",
    title: t(
      "Protein-Adsorption auf Titan und Zirkon",
      "Protein Adsorption on Titanium and Zirconia",
      "Adsorption de protéines sur titane et zircone",
      "Eiwitadsorptie op titanium en zirkoon",
      "Adsorbimento di proteine su titanio e zirconia",
      "Adsorción de proteínas en titanio y circonio"
    ),
    authors: "Hidalgo-Bastida LA, Cartmell SH",
    journal: "European Cells and Materials",
    year: 2010,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/20473832/",
    structured: {
      problemStatement: t(
        "Die initiale Protein-Adsorption bestimmt die nachfolgende Zellreaktion.",
        "Initial protein adsorption determines subsequent cell response.",
        "L'adsorption initiale de protéines détermine la réponse cellulaire ultérieure.",
        "Initiële eiwitadsorptie bepaalt de daaropvolgende celrespons.",
        "L'adsorbimento proteico iniziale determina la risposta cellulare successiva.",
        "La adsorción inicial de proteínas determina la respuesta celular posterior."
      ),
      purpose: t(
        "Vergleich der Protein-Adsorption auf Titan und Zirkon.",
        "Comparison of protein adsorption on titanium and zirconia.",
        "Comparaison de l'adsorption de protéines sur titane et zircone.",
        "Vergelijking van eiwitadsorptie op titanium en zirkoon.",
        "Confronto dell'adsorbimento di proteine su titanio e zirconia.",
        "Comparación de la adsorción de proteínas en titanio y circonio."
      ),
      methods: t(
        "XPS und Protein-Assays auf beiden Materialien.",
        "XPS and protein assays on both materials.",
        "XPS et tests de protéines sur les deux matériaux.",
        "XPS en eiwitassays op beide materialen.",
        "XPS e saggi proteici su entrambi i materiali.",
        "XPS y ensayos de proteínas en ambos materiales."
      ),
      results: t(
        "Titan zeigte höhere Fibronektin-Adsorption als Zirkon.",
        "Titanium showed higher fibronectin adsorption than zirconia.",
        "Le titane a montré une adsorption de fibronectine plus élevée que la zircone.",
        "Titanium toonde hogere fibronectine-adsorptie dan zirkoon.",
        "Il titanio ha mostrato un maggiore adsorbimento di fibronectina rispetto alla zirconia.",
        "El titanio mostró mayor adsorción de fibronectina que el circonio."
      ),
      conclusions: t(
        "Materialchemie beeinflusst die Protein-Konformation und Zellanhaftung.",
        "Material chemistry influences protein conformation and cell attachment.",
        "La chimie du matériau influence la conformation des protéines et l'attachement cellulaire.",
        "Materiaalchemie beïnvloedt eiwitconformatie en celaanhechting.",
        "La chimica del materiale influenza la conformazione proteica e l'adesione cellulare.",
        "La química del material influye en la conformación de proteínas y la adhesión celular."
      ),
    },
  },
  {
    id: 60,
    category: "cell-attachment",
    title: t(
      "Mechanische Stimulation und Osteoblasten-Reaktion",
      "Mechanical Stimulation and Osteoblast Response",
      "Stimulation mécanique et réponse des ostéoblastes",
      "Mechanische stimulatie en osteoblastrespons",
      "Stimolazione meccanica e risposta degli osteoblasti",
      "Estimulación mecánica y respuesta de osteoblastos"
    ),
    authors: "Rubin J, Rubin C, Jacobs CR",
    journal: "Gene",
    year: 2006,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/16859838/",
    structured: {
      problemStatement: t(
        "Mechanische Kräfte beeinflussen die Knochenzelldifferenzierung.",
        "Mechanical forces influence bone cell differentiation.",
        "Les forces mécaniques influencent la différenciation des cellules osseuses.",
        "Mechanische krachten beïnvloeden de differentiatie van botcellen.",
        "Le forze meccaniche influenzano la differenziazione delle cellule ossee.",
        "Las fuerzas mecánicas influyen en la diferenciación de las células óseas."
      ),
      purpose: t(
        "Review der mechanotransduktiven Signalwege in Osteoblasten.",
        "Review of mechanotransductive signaling pathways in osteoblasts.",
        "Revue des voies de signalisation mécanotransductives dans les ostéoblastes.",
        "Review van mechanotransductieve signaleringsroutes in osteoblasten.",
        "Revisione delle vie di segnalazione meccanotrasduttive negli osteoblasti.",
        "Revisión de las vías de señalización mecanotransductivas en osteoblastos."
      ),
      methods: t(
        "Systematische Analyse der Literatur zur Mechanotransduktion.",
        "Systematic analysis of literature on mechanotransduction.",
        "Analyse systématique de la littérature sur la mécanotransduction.",
        "Systematische analyse van literatuur over mechanotransductie.",
        "Analisi sistematica della letteratura sulla meccanotrasduzione.",
        "Análisis sistemático de la literatura sobre mecanotransducción."
      ),
      results: t(
        "Implantierte Materialien mit optimaler Oberflächentopographie verbessern die mechanosensorische Zellantwort.",
        "Implanted materials with optimal surface topography improve mechanosensory cell response.",
        "Les matériaux implantés avec une topographie de surface optimale améliorent la réponse cellulaire mécanosensoielle.",
        "Geïmplanteerde materialen met optimale oppervlaktetopografie verbeteren mechanosensorische celrespons.",
        "I materiali impiantati con topografia superficiale ottimale migliorano la risposta cellulare meccanorecettoriale.",
        "Los materiales implantados con topografía superficial óptima mejoran la respuesta celular mecanosensorial."
      ),
      conclusions: t(
        "Oberflächendesign sollte mechanotransduktive Aspekte berücksichtigen.",
        "Surface design should consider mechanotransductive aspects.",
        "La conception de surface devrait considérer les aspects mécanotransductifs.",
        "Oppervlakteontwerp moet mechanotransductieve aspecten overwegen.",
        "Il design della superficie dovrebbe considerare gli aspetti meccanotrasduttivi.",
        "El diseño de superficie debe considerar aspectos mecanotransductivos."
      ),
    },
  },
  {
    id: 61,
    category: "cell-attachment",
    title: t(
      "Integrin-Expression auf Titanoberflächen",
      "Integrin Expression on Titanium Surfaces",
      "Expression des intégrines sur les surfaces de titane",
      "Integrine-expressie op titaniumoppervlakken",
      "Espressione delle integrine sulle superfici di titanio",
      "Expresión de integrinas en superficies de titanio"
    ),
    authors: "Sinha RK, Morris F, Shah SA, Tuan RS",
    journal: "Clinical Orthopaedics and Related Research",
    year: 1994,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/8020231/",
    structured: {
      problemStatement: t(
        "Die Rolle der Integrine bei der Zell-Titan-Interaktion ist unzureichend charakterisiert.",
        "The role of integrins in cell-titanium interaction is insufficiently characterized.",
        "Le rôle des intégrines dans l'interaction cellule-titane est insuffisamment caractérisé.",
        "De rol van integrines in cel-titanium-interactie is onvoldoende gekarakteriseerd.",
        "Il ruolo delle integrine nell'interazione cellula-titanio è insufficientemente caratterizzato.",
        "El papel de las integrinas en la interacción célula-titanio está insuficientemente caracterizado."
      ),
      purpose: t(
        "Untersuchung der Integrin-Expression bei Zell-Titan-Kontakt.",
        "Investigation of integrin expression during cell-titanium contact.",
        "Étude de l'expression des intégrines lors du contact cellule-titane.",
        "Onderzoek naar integrine-expressie bij cel-titanium-contact.",
        "Indagine sull'espressione delle integrine durante il contatto cellula-titanio.",
        "Investigación de la expresión de integrinas durante el contacto célula-titanio."
      ),
      methods: t(
        "Immunfluoreszenz-Analyse von Osteoblasten auf Titanoberflächen.",
        "Immunofluorescence analysis of osteoblasts on titanium surfaces.",
        "Analyse d'immunofluorescence des ostéoblastes sur surfaces de titane.",
        "Immunofluorescentie-analyse van osteoblasten op titaniumoppervlakken.",
        "Analisi di immunofluorescenza degli osteoblasti su superfici di titanio.",
        "Análisis de inmunofluorescencia de osteoblastos en superficies de titanio."
      ),
      results: t(
        "Erhöhte α5β1-Integrin-Expression auf strukturierten Titanoberflächen.",
        "Increased α5β1 integrin expression on structured titanium surfaces.",
        "Expression accrue de l'intégrine α5β1 sur les surfaces de titane structurées.",
        "Verhoogde α5β1-integrine-expressie op gestructureerde titaniumoppervlakken.",
        "Aumento dell'espressione dell'integrina α5β1 sulle superfici di titanio strutturate.",
        "Mayor expresión de integrina α5β1 en superficies de titanio estructuradas."
      ),
      conclusions: t(
        "Titanoberflächen-Topographie beeinflusst die Integrin-vermittelte Zelladhäsion.",
        "Titanium surface topography influences integrin-mediated cell adhesion.",
        "La topographie de surface du titane influence l'adhésion cellulaire médiée par les intégrines.",
        "Titaniumoppervlaktetopografie beïnvloedt integrine-gemedieerde celadhesie.",
        "La topografia della superficie del titanio influenza l'adesione cellulare mediata dalle integrine.",
        "La topografía de la superficie del titanio influye en la adhesión celular mediada por integrinas."
      ),
    },
  },
  {
    id: 62,
    category: "cell-attachment",
    title: t(
      "Einfluss der Oberflächenenergie auf die Zellanhaftung",
      "Influence of Surface Energy on Cell Attachment",
      "Influence de l'énergie de surface sur l'attachement cellulaire",
      "Invloed van oppervlakte-energie op celaanhechting",
      "Influenza dell'energia superficiale sull'adesione cellulare",
      "Influencia de la energía superficial en la adhesión celular"
    ),
    authors: "Hallab NJ, Bundy KJ, O'Connor K, Moses RL, Jacobs JJ",
    journal: "Journal of Biomedical Materials Research",
    year: 2001,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/11241180/",
    structured: {
      problemStatement: t(
        "Die Rolle der Oberflächenenergie bei der Zelladhäsion ist nicht vollständig verstanden.",
        "The role of surface energy in cell adhesion is not fully understood.",
        "Le rôle de l'énergie de surface dans l'adhésion cellulaire n'est pas entièrement compris.",
        "De rol van oppervlakte-energie bij celadhesie wordt niet volledig begrepen.",
        "Il ruolo dell'energia superficiale nell'adesione cellulare non è completamente compreso.",
        "El papel de la energía superficial en la adhesión celular no se comprende completamente."
      ),
      purpose: t(
        "Korrelation von Oberflächenenergie mit Zelladhäsionsparametern.",
        "Correlation of surface energy with cell adhesion parameters.",
        "Corrélation de l'énergie de surface avec les paramètres d'adhésion cellulaire.",
        "Correlatie van oppervlakte-energie met celadhesieparameters.",
        "Correlazione dell'energia superficiale con i parametri di adesione cellulare.",
        "Correlación de la energía superficial con los parámetros de adhesión celular."
      ),
      methods: t(
        "Kontaktwinkelmessungen und Zellkulturstudien auf verschiedenen Metallen.",
        "Contact angle measurements and cell culture studies on various metals.",
        "Mesures d'angle de contact et études de culture cellulaire sur divers métaux.",
        "Contacthoekmetingen en celkweekstudies op verschillende metalen.",
        "Misurazioni dell'angolo di contatto e studi di coltura cellulare su vari metalli.",
        "Mediciones de ángulo de contacto y estudios de cultivo celular en varios metales."
      ),
      results: t(
        "Höhere Oberflächenenergie korrelierte mit besserer Zelladhäsion.",
        "Higher surface energy correlated with better cell adhesion.",
        "Une énergie de surface plus élevée était corrélée à une meilleure adhésion cellulaire.",
        "Hogere oppervlakte-energie correleerde met betere celadhesie.",
        "Un'energia superficiale più alta era correlata a una migliore adesione cellulare.",
        "Una mayor energía superficial se correlacionó con una mejor adhesión celular."
      ),
      conclusions: t(
        "Oberflächenenergie ist ein wichtiger Parameter für die Biokompatibilität.",
        "Surface energy is an important parameter for biocompatibility.",
        "L'énergie de surface est un paramètre important pour la biocompatibilité.",
        "Oppervlakte-energie is een belangrijke parameter voor biocompatibiliteit.",
        "L'energia superficiale è un parametro importante per la biocompatibilità.",
        "La energía superficial es un parámetro importante para la biocompatibilidad."
      ),
    },
  },
  {
    id: 63,
    category: "cell-attachment",
    title: t(
      "Bakterielle Adhäsion und Weichgewebsintegration",
      "Bacterial Adhesion and Soft Tissue Integration",
      "Adhésion bactérienne et intégration des tissus mous",
      "Bacteriële adhesie en weke-delen-integratie",
      "Adesione batterica e integrazione dei tessuti molli",
      "Adhesión bacteriana e integración de tejidos blandos"
    ),
    authors: "Bürgers R, Zönchen S, Weber A, Modern G",
    journal: "Clinical Oral Implants Research",
    year: 2010,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/20441626/",
    structured: {
      problemStatement: t(
        "Bakterielle Kolonisation kann die Weichgewebsintegration beeinträchtigen.",
        "Bacterial colonization can impair soft tissue integration.",
        "La colonisation bactérienne peut altérer l'intégration des tissus mous.",
        "Bacteriële kolonisatie kan weke-delen-integratie beïnvloeden.",
        "La colonizzazione batterica può compromettere l'integrazione dei tessuti molli.",
        "La colonización bacteriana puede afectar la integración de tejidos blandos."
      ),
      purpose: t(
        "Vergleich der bakteriellen Adhäsion auf verschiedenen Abutment-Materialien.",
        "Comparison of bacterial adhesion on different abutment materials.",
        "Comparaison de l'adhésion bactérienne sur différents matériaux de piliers.",
        "Vergelijking van bacteriële adhesie op verschillende abutmentmaterialen.",
        "Confronto dell'adesione batterica su diversi materiali per abutment.",
        "Comparación de la adhesión bacteriana en diferentes materiales de pilar."
      ),
      methods: t(
        "Biofilm-Kulturen auf Titan, Zirkon und PEEK.",
        "Biofilm cultures on titanium, zirconia, and PEEK.",
        "Cultures de biofilm sur titane, zircone et PEEK.",
        "Biofilmkweken op titanium, zirkoon en PEEK.",
        "Colture di biofilm su titanio, zirconia e PEEK.",
        "Cultivos de biofilm en titanio, circonio y PEEK."
      ),
      results: t(
        "Titan und Zirkon zeigten ähnliche, niedrige bakterielle Adhäsion.",
        "Titanium and zirconia showed similar, low bacterial adhesion.",
        "Le titane et la zircone ont montré une adhésion bactérienne similaire et faible.",
        "Titanium en zirkoon toonden vergelijkbare, lage bacteriële adhesie.",
        "Titanio e zirconia hanno mostrato un'adesione batterica simile e bassa.",
        "El titanio y el circonio mostraron una adhesión bacteriana similar y baja."
      ),
      conclusions: t(
        "Materialwahl beeinflusst die bakterielle Besiedlung und Weichgewebsgesundheit.",
        "Material choice influences bacterial colonization and soft tissue health.",
        "Le choix du matériau influence la colonisation bactérienne et la santé des tissus mous.",
        "Materiaalkeuze beïnvloedt bacteriële kolonisatie en weke-delen-gezondheid.",
        "La scelta del materiale influenza la colonizzazione batterica e la salute dei tessuti molli.",
        "La elección del material influye en la colonización bacteriana y la salud de los tejidos blandos."
      ),
    },
  },
  {
    id: 64,
    category: "cell-attachment",
    title: t(
      "Gingivale Zelladhäsion auf verschiedenen Abutment-Materialien",
      "Gingival Cell Adhesion on Different Abutment Materials",
      "Adhésion cellulaire gingivale sur différents matériaux de piliers",
      "Gingivale celadhesie op verschillende abutmentmaterialen",
      "Adesione cellulare gengivale su diversi materiali per abutment",
      "Adhesión celular gingival en diferentes materiales de pilar"
    ),
    authors: "Simion M, Baldoni M, Rossi P, Zaffe D",
    journal: "International Journal of Periodontics & Restorative Dentistry",
    year: 1991,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/1880711/",
    structured: {
      problemStatement: t(
        "Die gingivale Reaktion auf verschiedene Abutment-Materialien ist klinisch relevant.",
        "The gingival response to different abutment materials is clinically relevant.",
        "La réponse gingivale aux différents matériaux de piliers est cliniquement pertinente.",
        "De gingivale respons op verschillende abutmentmaterialen is klinisch relevant.",
        "La risposta gengivale ai diversi materiali per abutment è clinicamente rilevante.",
        "La respuesta gingival a diferentes materiales de pilar es clínicamente relevante."
      ),
      purpose: t(
        "Histologische Analyse der gingivalen Anhaftung an Titan-Abutments.",
        "Histological analysis of gingival attachment to titanium abutments.",
        "Analyse histologique de l'attachement gingival aux piliers en titane.",
        "Histologische analyse van gingivale aanhechting aan titaniumabutments.",
        "Analisi istologica dell'attacco gengivale agli abutment in titanio.",
        "Análisis histológico de la adhesión gingival a pilares de titanio."
      ),
      methods: t(
        "Biopsien von periimplantärer Gingiva bei 12 Patienten.",
        "Biopsies of peri-implant gingiva in 12 patients.",
        "Biopsies de gencive péri-implantaire chez 12 patients.",
        "Biopten van peri-implantaire gingiva bij 12 patiënten.",
        "Biopsie di gengiva perimplantare in 12 pazienti.",
        "Biopsias de encía periimplantaria en 12 pacientes."
      ),
      results: t(
        "Hemidesmosomen-ähnliche Strukturen an der Titan-Epithel-Grenzfläche.",
        "Hemidesmosome-like structures at the titanium-epithelium interface.",
        "Structures de type hémidesmosome à l'interface titane-épithélium.",
        "Hemidesmosoom-achtige structuren aan de titanium-epitheel-interface.",
        "Strutture simili agli emidesmosomi all'interfaccia titanio-epitelio.",
        "Estructuras similares a hemidesmosomas en la interfaz titanio-epitelio."
      ),
      conclusions: t(
        "Titan ermöglicht eine echte epitheliale Anhaftung.",
        "Titanium enables true epithelial attachment.",
        "Le titane permet un véritable attachement épithélial.",
        "Titanium maakt echte epitheliale aanhechting mogelijk.",
        "Il titanio consente un vero attacco epiteliale.",
        "El titanio permite una verdadera adhesión epitelial."
      ),
    },
  },
  {
    id: 65,
    category: "cell-attachment",
    title: t(
      "Stammzell-Differenzierung auf Titanoberflächen",
      "Stem Cell Differentiation on Titanium Surfaces",
      "Différenciation des cellules souches sur surfaces de titane",
      "Stamceldifferentiatie op titaniumoppervlakken",
      "Differenziazione delle cellule staminali su superfici di titanio",
      "Diferenciación de células madre en superficies de titanio"
    ),
    authors: "Olivares-Navarrete R, Hyzy SL, Berg ME, Schneider JM, Hotchkiss K, Schwartz Z, Boyan BD",
    journal: "Biomaterials",
    year: 2014,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/24630839/",
    structured: {
      problemStatement: t(
        "Die Differenzierung mesenchymaler Stammzellen auf Titan ist nicht vollständig charakterisiert.",
        "The differentiation of mesenchymal stem cells on titanium is not fully characterized.",
        "La différenciation des cellules souches mésenchymateuses sur titane n'est pas entièrement caractérisée.",
        "De differentiatie van mesenchymale stamcellen op titanium is niet volledig gekarakteriseerd.",
        "La differenziazione delle cellule staminali mesenchimali sul titanio non è completamente caratterizzata.",
        "La diferenciación de células madre mesenquimales en titanio no está completamente caracterizada."
      ),
      purpose: t(
        "Untersuchung der osteogenen Differenzierung von MSCs auf strukturierten Titanoberflächen.",
        "Investigation of osteogenic differentiation of MSCs on structured titanium surfaces.",
        "Étude de la différenciation ostéogénique des MSC sur surfaces de titane structurées.",
        "Onderzoek naar osteogene differentiatie van MSC's op gestructureerde titaniumoppervlakken.",
        "Indagine sulla differenziazione osteogenica delle MSC su superfici di titanio strutturate.",
        "Investigación de la diferenciación osteogénica de MSC en superficies de titanio estructuradas."
      ),
      methods: t(
        "Humane MSCs auf SLA- und modifizierten SLA-Oberflächen.",
        "Human MSCs on SLA and modified SLA surfaces.",
        "MSC humaines sur surfaces SLA et SLA modifiées.",
        "Humane MSC's op SLA- en gemodificeerde SLA-oppervlakken.",
        "MSC umane su superfici SLA e SLA modificate.",
        "MSC humanas en superficies SLA y SLA modificadas."
      ),
      results: t(
        "Strukturierte Oberflächen induzierten osteogene Differenzierung ohne externe Faktoren.",
        "Structured surfaces induced osteogenic differentiation without external factors.",
        "Les surfaces structurées ont induit une différenciation ostéogénique sans facteurs externes.",
        "Gestructureerde oppervlakken induceerden osteogene differentiatie zonder externe factoren.",
        "Le superfici strutturate hanno indotto differenziazione osteogenica senza fattori esterni.",
        "Las superficies estructuradas indujeron diferenciación osteogénica sin factores externos."
      ),
      conclusions: t(
        "Oberflächentopographie steuert direkt die Stammzell-Differenzierung.",
        "Surface topography directly controls stem cell differentiation.",
        "La topographie de surface contrôle directement la différenciation des cellules souches.",
        "Oppervlaktetopografie stuurt direct de stamceldifferentiatie.",
        "La topografia superficiale controlla direttamente la differenziazione delle cellule staminali.",
        "La topografía superficial controla directamente la diferenciación de células madre."
      ),
    },
  },
  {
    id: 66,
    category: "cell-attachment",
    title: t(
      "TGF-β1-Produktion auf strukturierten Titanoberflächen",
      "TGF-β1 Production on Structured Titanium Surfaces",
      "Production de TGF-β1 sur surfaces de titane structurées",
      "TGF-β1-productie op gestructureerde titaniumoppervlakken",
      "Produzione di TGF-β1 su superfici di titanio strutturate",
      "Producción de TGF-β1 en superficies de titanio estructuradas"
    ),
    authors: "Schwartz Z, Lohmann CH, Oefinger J, Bonewald LF, Dean DD, Boyan BD",
    journal: "Journal of Biomedical Materials Research",
    year: 1999,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/10397952/",
    structured: {
      problemStatement: t(
        "Die Rolle von Wachstumsfaktoren bei der Oberflächenreaktion ist wenig verstanden.",
        "The role of growth factors in surface response is poorly understood.",
        "Le rôle des facteurs de croissance dans la réponse de surface est mal compris.",
        "De rol van groeifactoren in de oppervlaktereactie wordt slecht begrepen.",
        "Il ruolo dei fattori di crescita nella risposta di superficie è poco compreso.",
        "El papel de los factores de crecimiento en la respuesta de superficie se comprende poco."
      ),
      purpose: t(
        "Analyse der TGF-β1-Produktion durch Osteoblasten auf verschiedenen Titanoberflächen.",
        "Analysis of TGF-β1 production by osteoblasts on different titanium surfaces.",
        "Analyse de la production de TGF-β1 par les ostéoblastes sur différentes surfaces de titane.",
        "Analyse van TGF-β1-productie door osteoblasten op verschillende titaniumoppervlakken.",
        "Analisi della produzione di TGF-β1 da parte degli osteoblasti su diverse superfici di titanio.",
        "Análisis de la producción de TGF-β1 por osteoblastos en diferentes superficies de titanio."
      ),
      methods: t(
        "ELISA-Messungen von TGF-β1 in Zellkulturüberständen.",
        "ELISA measurements of TGF-β1 in cell culture supernatants.",
        "Mesures ELISA du TGF-β1 dans les surnageants de culture cellulaire.",
        "ELISA-metingen van TGF-β1 in celkweeksupernatanten.",
        "Misurazioni ELISA del TGF-β1 nei surnatanti di colture cellulari.",
        "Mediciones ELISA de TGF-β1 en sobrenadantes de cultivo celular."
      ),
      results: t(
        "Raue Oberflächen induzierten höhere TGF-β1-Produktion.",
        "Rough surfaces induced higher TGF-β1 production.",
        "Les surfaces rugueuses ont induit une production plus élevée de TGF-β1.",
        "Ruwe oppervlakken induceerden hogere TGF-β1-productie.",
        "Le superfici ruvide hanno indotto una maggiore produzione di TGF-β1.",
        "Las superficies rugosas indujeron mayor producción de TGF-β1."
      ),
      conclusions: t(
        "Oberflächenrauigkeit moduliert die parakrine Signalgebung.",
        "Surface roughness modulates paracrine signaling.",
        "La rugosité de surface module la signalisation paracrine.",
        "Oppervlakteruwheid moduleert paracriene signalering.",
        "La rugosità superficiale modula la segnalazione paracrina.",
        "La rugosidad superficial modula la señalización paracrina."
      ),
    },
  },
  {
    id: 67,
    category: "cell-attachment",
    title: t(
      "Frühe Osseointegration: histologische Analyse",
      "Early Osseointegration: Histological Analysis",
      "Ostéointégration précoce : analyse histologique",
      "Vroege osseointegratie: histologische analyse",
      "Osteointegrazione precoce: analisi istologica",
      "Osteointegración temprana: análisis histológico"
    ),
    authors: "Lang NP, Salvi GE, Huynh-Ba G, Ivanovski S, Jung RE, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2011,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/21251080/",
    structured: {
      problemStatement: t(
        "Die zeitliche Abfolge der frühen Osseointegration ist nicht vollständig dokumentiert.",
        "The temporal sequence of early osseointegration is not fully documented.",
        "La séquence temporelle de l'ostéointégration précoce n'est pas entièrement documentée.",
        "De temporele volgorde van vroege osseointegratie is niet volledig gedocumenteerd.",
        "La sequenza temporale dell'osteointegrazione precoce non è completamente documentata.",
        "La secuencia temporal de la osteointegración temprana no está completamente documentada."
      ),
      purpose: t(
        "Review der histologischen Phasen der frühen Osseointegration.",
        "Review of histological phases of early osseointegration.",
        "Revue des phases histologiques de l'ostéointégration précoce.",
        "Review van histologische fasen van vroege osseointegratie.",
        "Revisione delle fasi istologiche dell'osteointegrazione precoce.",
        "Revisión de las fases histológicas de la osteointegración temprana."
      ),
      methods: t(
        "Systematische Analyse von Tier- und Humanstudien.",
        "Systematic analysis of animal and human studies.",
        "Analyse systématique des études animales et humaines.",
        "Systematische analyse van dier- en humane studies.",
        "Analisi sistematica di studi animali e umani.",
        "Análisis sistemático de estudios animales y humanos."
      ),
      results: t(
        "Woche 1-2: Fibrinmatrix, Woche 2-4: Geflechtknochen, ab Woche 6: lamellarer Knochen.",
        "Week 1-2: fibrin matrix, Week 2-4: woven bone, from Week 6: lamellar bone.",
        "Semaine 1-2 : matrice de fibrine, Semaine 2-4 : os tissé, à partir de la semaine 6 : os lamellaire.",
        "Week 1-2: fibrinematrix, Week 2-4: vlechtbot, vanaf week 6: lamellair bot.",
        "Settimana 1-2: matrice di fibrina, Settimana 2-4: osso intrecciato, dalla Settimana 6: osso lamellare.",
        "Semana 1-2: matriz de fibrina, Semana 2-4: hueso entretejido, desde la Semana 6: hueso lamelar."
      ),
      conclusions: t(
        "Strukturierte Titanoberflächen beschleunigen diese Phasen.",
        "Structured titanium surfaces accelerate these phases.",
        "Les surfaces de titane structurées accélèrent ces phases.",
        "Gestructureerde titaniumoppervlakken versnellen deze fasen.",
        "Le superfici di titanio strutturate accelerano queste fasi.",
        "Las superficies de titanio estructuradas aceleran estas fases."
      ),
    },
  },
  {
    id: 68,
    category: "cell-attachment",
    title: t(
      "Zellanhaftung auf anodisierten Titanoberflächen",
      "Cell Attachment on Anodized Titanium Surfaces",
      "Attachement cellulaire sur surfaces de titane anodisées",
      "Celaanhechting op geanodiseerde titaniumoppervlakken",
      "Adesione cellulare su superfici di titanio anodizzate",
      "Adhesión celular en superficies de titanio anodizadas"
    ),
    authors: "Sul YT, Johansson CB, Petronis S, Krozer A, Jeong Y, Wennerberg A, Albrektsson T",
    journal: "Biomaterials",
    year: 2002,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/11962954/",
    structured: {
      problemStatement: t(
        "Anodisierung verändert die Oberflächenchemie und -topographie.",
        "Anodization changes surface chemistry and topography.",
        "L'anodisation modifie la chimie et la topographie de surface.",
        "Anodisatie verandert oppervlaktechemie en -topografie.",
        "L'anodizzazione modifica la chimica e la topografia superficiale.",
        "La anodización cambia la química y topografía de la superficie."
      ),
      purpose: t(
        "Untersuchung der Zellreaktion auf anodisierte vs. nicht-anodisierte Oberflächen.",
        "Investigation of cell response to anodized vs. non-anodized surfaces.",
        "Étude de la réponse cellulaire aux surfaces anodisées vs. non anodisées.",
        "Onderzoek naar celrespons op geanodiseerde vs. niet-geanodiseerde oppervlakken.",
        "Indagine sulla risposta cellulare alle superfici anodizzate vs. non anodizzate.",
        "Investigación de la respuesta celular a superficies anodizadas vs. no anodizadas."
      ),
      methods: t(
        "Osteoblastenkulturen auf verschiedenen anodisierten Titanproben.",
        "Osteoblast cultures on various anodized titanium samples.",
        "Cultures d'ostéoblastes sur divers échantillons de titane anodisé.",
        "Osteoblastkweken op verschillende geanodiseerde titaniummonsters.",
        "Colture di osteoblasti su vari campioni di titanio anodizzato.",
        "Cultivos de osteoblastos en varias muestras de titanio anodizado."
      ),
      results: t(
        "Anodisierte Oberflächen zeigten 30% höhere Zellanhaftung.",
        "Anodized surfaces showed 30% higher cell attachment.",
        "Les surfaces anodisées ont montré une adhésion cellulaire 30 % plus élevée.",
        "Geanodiseerde oppervlakken toonden 30% hogere celaanhechting.",
        "Le superfici anodizzate hanno mostrato un'adesione cellulare superiore del 30%.",
        "Las superficies anodizadas mostraron un 30% más de adhesión celular."
      ),
      conclusions: t(
        "Anodisierung verbessert die Biokompatibilität von Titan.",
        "Anodization improves the biocompatibility of titanium.",
        "L'anodisation améliore la biocompatibilité du titane.",
        "Anodisatie verbetert de biocompatibiliteit van titanium.",
        "L'anodizzazione migliora la biocompatibilità del titanio.",
        "La anodización mejora la biocompatibilidad del titanio."
      ),
    },
  },
  {
    id: 69,
    category: "cell-attachment",
    title: t(
      "Periimplantäre Weichgewebsbarriere: Ultrastrukturelle Analyse",
      "Peri-Implant Soft Tissue Barrier: Ultrastructural Analysis",
      "Barrière des tissus mous péri-implantaires : analyse ultrastructurale",
      "Peri-implantaire weke-delen-barrière: ultrastructurele analyse",
      "Barriera dei tessuti molli perimplantari: analisi ultrastrutturale",
      "Barrera de tejidos blandos periimplantarios: análisis ultraestructural"
    ),
    authors: "Schupbach P, Glauser R",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2007,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/17326536/",
    structured: {
      problemStatement: t(
        "Die Ultrastruktur der periimplantären Weichgewebsbarriere ist wenig dokumentiert.",
        "The ultrastructure of the peri-implant soft tissue barrier is poorly documented.",
        "L'ultrastructure de la barrière des tissus mous péri-implantaires est peu documentée.",
        "De ultrastructuur van de peri-implantaire weke-delen-barrière is slecht gedocumenteerd.",
        "L'ultrastruttura della barriera dei tessuti molli perimplantari è poco documentata.",
        "La ultraestructura de la barrera de tejidos blandos periimplantarios está poco documentada."
      ),
      purpose: t(
        "Elektronenmikroskopische Charakterisierung der Implantat-Gewebe-Grenzfläche.",
        "Electron microscopic characterization of the implant-tissue interface.",
        "Caractérisation par microscopie électronique de l'interface implant-tissu.",
        "Elektronenmicroscopische karakterisering van de implantaat-weefsel-interface.",
        "Caratterizzazione al microscopio elettronico dell'interfaccia impianto-tessuto.",
        "Caracterización por microscopía electrónica de la interfaz implante-tejido."
      ),
      methods: t(
        "TEM-Analyse von periimplantären Biopsien.",
        "TEM analysis of peri-implant biopsies.",
        "Analyse TEM de biopsies péri-implantaires.",
        "TEM-analyse van peri-implantaire biopten.",
        "Analisi TEM di biopsie perimplantari.",
        "Análisis TEM de biopsias periimplantarias."
      ),
      results: t(
        "Hemidesmosomen und Basallamina-ähnliche Strukturen an Titan nachgewiesen.",
        "Hemidesmosomes and basal lamina-like structures demonstrated on titanium.",
        "Hémidesmosomes et structures de type lame basale démontrés sur titane.",
        "Hemidesmosomen en basaal lamina-achtige structuren aangetoond op titanium.",
        "Emidesmosomi e strutture simili alla lamina basale dimostrati sul titanio.",
        "Hemidesmosomas y estructuras similares a la lámina basal demostradas en titanio."
      ),
      conclusions: t(
        "Titan ermöglicht eine funktionelle epitheliale Versiegelung.",
        "Titanium enables a functional epithelial seal.",
        "Le titane permet un scellement épithélial fonctionnel.",
        "Titanium maakt een functionele epitheliale afsluiting mogelijk.",
        "Il titanio consente una sigillatura epiteliale funzionale.",
        "El titanio permite un sellado epitelial funcional."
      ),
    },
  },
  {
    id: 70,
    category: "cell-attachment",
    title: t(
      "Genexpression bei früher Osseointegration",
      "Gene Expression During Early Osseointegration",
      "Expression génique lors de l'ostéointégration précoce",
      "Genexpressie tijdens vroege osseointegratie",
      "Espressione genica durante l'osteointegrazione precoce",
      "Expresión génica durante la osteointegración temprana"
    ),
    authors: "Ivanovski S, Hamlet S, Salvi GE, Huynh-Ba G, Troup KJ, Lang NP",
    journal: "Clinical Oral Implants Research",
    year: 2011,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/21251077/",
    structured: {
      problemStatement: t(
        "Die molekularen Mechanismen der frühen Osseointegration sind unzureichend charakterisiert.",
        "The molecular mechanisms of early osseointegration are insufficiently characterized.",
        "Les mécanismes moléculaires de l'ostéointégration précoce sont insuffisamment caractérisés.",
        "De moleculaire mechanismen van vroege osseointegratie zijn onvoldoende gekarakteriseerd.",
        "I meccanismi molecolari dell'osteointegrazione precoce sono insufficientemente caratterizzati.",
        "Los mecanismos moleculares de la osteointegración temprana están insuficientemente caracterizados."
      ),
      purpose: t(
        "Genexpressionsanalyse während der frühen Osseointegration beim Menschen.",
        "Gene expression analysis during early osseointegration in humans.",
        "Analyse de l'expression génique lors de l'ostéointégration précoce chez l'humain.",
        "Genexpressieanalyse tijdens vroege osseointegratie bij mensen.",
        "Analisi dell'espressione genica durante l'osteointegrazione precoce nell'uomo.",
        "Análisis de expresión génica durante la osteointegración temprana en humanos."
      ),
      methods: t(
        "RNA-Extraktion und Microarray-Analyse periimplantärer Gewebe.",
        "RNA extraction and microarray analysis of peri-implant tissues.",
        "Extraction d'ARN et analyse par microarray des tissus péri-implantaires.",
        "RNA-extractie en microarray-analyse van peri-implantaire weefsels.",
        "Estrazione di RNA e analisi microarray dei tessuti perimplantari.",
        "Extracción de ARN y análisis de microarray de tejidos periimplantarios."
      ),
      results: t(
        "Signifikante Hochregulation osteogener Gene in der ersten Woche.",
        "Significant upregulation of osteogenic genes in the first week.",
        "Régulation à la hausse significative des gènes ostéogéniques la première semaine.",
        "Significante opregulatie van osteogene genen in de eerste week.",
        "Significativa upregolazione dei geni osteogenici nella prima settimana.",
        "Regulación al alza significativa de genes osteogénicos en la primera semana."
      ),
      conclusions: t(
        "Strukturierte Titanoberflächen aktivieren osteogene Signalwege.",
        "Structured titanium surfaces activate osteogenic signaling pathways.",
        "Les surfaces de titane structurées activent les voies de signalisation ostéogéniques.",
        "Gestructureerde titaniumoppervlakken activeren osteogene signaleringsroutes.",
        "Le superfici di titanio strutturate attivano le vie di segnalazione osteogeniche.",
        "Las superficies de titanio estructuradas activan las vías de señalización osteogénicas."
      ),
    },
  },
  {
    id: 71,
    category: "cell-attachment",
    title: t(
      "Langzeit-Weichgewebsstabilität um Titanimplantate",
      "Long-Term Soft Tissue Stability Around Titanium Implants",
      "Stabilité à long terme des tissus mous autour des implants en titane",
      "Langetermijnstabiliteit van weke delen rond titaniumimplantaten",
      "Stabilità a lungo termine dei tessuti molli intorno agli impianti in titanio",
      "Estabilidad a largo plazo de tejidos blandos alrededor de implantes de titanio"
    ),
    authors: "Lekholm U, Gröndahl K, Jemt T",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2006,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/16519186/",
    structured: {
      problemStatement: t(
        "Die langfristige Weichgewebsstabilität um Implantate ist für den Behandlungserfolg entscheidend.",
        "Long-term soft tissue stability around implants is crucial for treatment success.",
        "La stabilité à long terme des tissus mous autour des implants est cruciale pour le succès du traitement.",
        "Langetermijnstabiliteit van weke delen rond implantaten is cruciaal voor behandelingssucces.",
        "La stabilità a lungo termine dei tessuti molli intorno agli impianti è cruciale per il successo del trattamento.",
        "La estabilidad a largo plazo de los tejidos blandos alrededor de los implantes es crucial para el éxito del tratamiento."
      ),
      purpose: t(
        "15-Jahres-Follow-up der Weichgewebsverhältnisse um Titanimplantate.",
        "15-year follow-up of soft tissue conditions around titanium implants.",
        "Suivi à 15 ans des conditions des tissus mous autour des implants en titane.",
        "15-jarige follow-up van weke-delen-condities rond titaniumimplantaten.",
        "Follow-up a 15 anni delle condizioni dei tessuti molli intorno agli impianti in titanio.",
        "Seguimiento a 15 años de las condiciones de tejidos blandos alrededor de implantes de titanio."
      ),
      methods: t(
        "Klinische und radiographische Nachuntersuchung von 461 Implantaten.",
        "Clinical and radiographic follow-up of 461 implants.",
        "Suivi clinique et radiographique de 461 implants.",
        "Klinische en radiografische follow-up van 461 implantaten.",
        "Follow-up clinico e radiografico di 461 impianti.",
        "Seguimiento clínico y radiográfico de 461 implantes."
      ),
      results: t(
        "92% stabile Weichgewebsverhältnisse nach 15 Jahren.",
        "92% stable soft tissue conditions after 15 years.",
        "92 % de conditions tissulaires stables après 15 ans.",
        "92% stabiele weke-delen-condities na 15 jaar.",
        "92% di condizioni stabili dei tessuti molli dopo 15 anni.",
        "92% de condiciones de tejidos blandos estables después de 15 años."
      ),
      conclusions: t(
        "Titan ermöglicht langfristig stabile periimplantäre Weichgewebe.",
        "Titanium enables long-term stable peri-implant soft tissues.",
        "Le titane permet des tissus mous péri-implantaires stables à long terme.",
        "Titanium maakt langdurig stabiele peri-implantaire weke delen mogelijk.",
        "Il titanio consente tessuti molli perimplantari stabili a lungo termine.",
        "El titanio permite tejidos blandos periimplantarios estables a largo plazo."
      ),
    },
  },
];
// ============================================

export const manipulationAestheticsStudies: Study[] = [
  {
    id: 125,
    category: "manipulation-aesthetics",
    title: t(
      "Emergenzprofilwinkel zählt – Wiederherstellung der periimplantären Gesundheit durch Anpassung der Prothetik – Eine narrative Übersichtsarbeit",
      "Emergence Profile Angle Matters—Restoring Peri-Implant Health by Adjusting Prosthetics—A Narrative Review",
      "L'angle du profil d'émergence compte – Restaurer la santé péri-implantaire par l'ajustement prothétique – Une revue narrative",
      "Emergentieprofielhoek doet ertoe – Herstel van peri-implantaire gezondheid door aanpassing van protheses – Een narratieve review",
      "L'angolo del profilo di emergenza conta – Ripristino della salute peri-implantare attraverso la regolazione protesica – Una revisione narrativa",
      "El ángulo del perfil de emergencia importa – Restauración de la salud periimplantaria mediante ajuste protésico – Una revisión narrativa"
    ),
    authors: "Pirc M, Esquivel J, Patrizzi A, Jung RE, Strauss FJ",
    journal: "Journal of Esthetic and Restorative Dentistry",
    year: 2026,
    type: "review",
    url: "https://doi.org/10.1111/jerd.70109",
    structured: {
      problemStatement: t(
        "Suboptimale prothetische Designs können Plaqueakkumulation und Entzündungen fördern, selbst bei compliant Patienten.",
        "Suboptimal prosthetic designs may promote plaque accumulation and inflammation, even in compliant patients.",
        "Les conceptions prothétiques sous-optimales peuvent favoriser l'accumulation de plaque et l'inflammation, même chez les patients observants.",
        "Suboptimale prothetische ontwerpen kunnen plaqueophoping en ontsteking bevorderen, zelfs bij compliant patiënten.",
        "I design protesici subottimali possono promuovere l'accumulo di placca e l'infiammazione, anche nei pazienti collaboranti.",
        "Los diseños protésicos subóptimos pueden promover la acumulación de placa e inflamación, incluso en pacientes cumplidores."
      ),
      purpose: t(
        "Überprüfung der biologischen und klinischen Relevanz des prothetischen Designs, insbesondere des Emergenzprofils und des Restaurationswinkels, für die periimplantäre Gewebegesundheit.",
        "To review the biological and clinical relevance of prosthetic design, specifically the emergence profile and restorative angle in influencing peri-implant tissue health.",
        "Examiner la pertinence biologique et clinique de la conception prothétique, en particulier le profil d'émergence et l'angle de restauration, pour la santé des tissus péri-implantaires.",
        "De biologische en klinische relevantie van prothetisch ontwerp te onderzoeken, met name het emersieprofiel en de restauratiehoek voor de peri-implantaire weefselgezondheid.",
        "Esaminare la rilevanza biologica e clinica del design protesico, in particolare il profilo di emergenza e l'angolo di restauro, per la salute dei tessuti peri-implantari.",
        "Revisar la relevancia biológica y clínica del diseño protésico, específicamente el perfil de emergencia y el ángulo restaurador, en la salud del tejido periimplantario."
      ),
      methods: t(
        "Narrative Übersichtsarbeit, die aktuelle Evidenz zur Anatomie und Funktion des suprakrestalen Implantatkomplexes zusammenfasst, mit klinischem Fallbericht.",
        "Narrative review synthesizing current evidence on the anatomy and function of the implant supracrestal complex, with clinical case report.",
        "Revue narrative synthétisant les preuves actuelles sur l'anatomie et la fonction du complexe supracrestal implantaire, avec rapport de cas clinique.",
        "Narratieve review die actuele evidence over de anatomie en functie van het supracrestale implantaatcomplex samenvat, met klinisch casusrapport.",
        "Revisione narrativa che sintetizza le attuali evidenze sull'anatomia e la funzione del complesso sopracrestale implantare, con caso clinico.",
        "Revisión narrativa que sintetiza la evidencia actual sobre la anatomía y función del complejo supracrestal del implante, con informe de caso clínico."
      ),
      results: t(
        "Konkave, polierte Emergenzprofile auf ausreichend hohen Titanbases fördern Weichgewebsstabilität, erleichtern die Hygiene und minimieren Entzündungs- oder Rezessionsrisiken.",
        "Concave, polished emergence profiles on adequately tall titanium bases promote soft tissue stability, facilitate hygiene, and minimize inflammation or recession risk.",
        "Les profils d'émergence concaves et polis sur des bases en titane suffisamment hautes favorisent la stabilité des tissus mous, facilitent l'hygiène et minimisent le risque d'inflammation ou de récession.",
        "Concave, gepolijste emergentieprofielen op voldoende hoge titanium bases bevorderen weke weefselstabiliteit, vergemakkelijken hygiëne en minimaliseren ontstekings- of recessierisico.",
        "I profili di emergenza concavi e lucidati su basi in titanio adeguatamente alte promuovono la stabilità dei tessuti molli, facilitano l'igiene e minimizzano il rischio di infiammazione o recessione.",
        "Los perfiles de emergencia cóncavos y pulidos en bases de titanio adecuadamente altas promueven la estabilidad del tejido blando, facilitan la higiene y minimizan el riesgo de inflamación o recesión."
      ),
      conclusions: t(
        "Restaurationswinkel und Emergenzprofil sind kritische, modifizierbare Elemente implantatgetragener Prothesen. Ein biologisch orientierter Ansatz ist entscheidend für vorhersagbare und ästhetisch erfolgreiche Ergebnisse.",
        "Restorative angle and emergence profile are critical, modifiable elements of implant-supported prostheses. A biologically oriented approach is essential for achieving predictable and esthetically successful outcomes.",
        "L'angle de restauration et le profil d'émergence sont des éléments critiques et modifiables des prothèses sur implants. Une approche biologiquement orientée est essentielle pour obtenir des résultats prévisibles et esthétiquement réussis.",
        "Restauratiehoek en emergentieprofiel zijn kritieke, aanpasbare elementen van implantaat-gedragen protheses. Een biologisch georiënteerde aanpak is essentieel voor voorspelbare en esthetisch succesvolle resultaten.",
        "L'angolo di restauro e il profilo di emergenza sono elementi critici e modificabili delle protesi supportate da impianti. Un approccio biologicamente orientato è essenziale per ottenere risultati prevedibili ed esteticamente riusciti.",
        "El ángulo restaurador y el perfil de emergencia son elementos críticos y modificables de las prótesis sobre implantes. Un enfoque biológicamente orientado es esencial para lograr resultados predecibles y estéticamente exitosos."
      ),
    },
  },
  {
    id: 72,
    category: "manipulation-aesthetics",
    title: t(
      "Einfluss des Abutmentwechsels auf periimplantäre Gewebe",
      "Influence of Abutment Disconnection on Peri-Implant Tissues",
      "Influence de la déconnexion du pilier sur les tissus péri-implantaires",
      "Invloed van abutmentdisconnectie op peri-implantaire weefsels",
      "Influenza della disconnessione dell'abutment sui tessuti perimplantari",
      "Influencia de la desconexión del pilar en los tejidos periimplantarios"
    ),
    authors: "Canullo L, Gobbo S, Martino P, Rubio A",
    journal: "Journal of Clinical Periodontology",
    year: 2017,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/28380254/",
    structured: {
      problemStatement: t(
        "Wiederholte Abutmentwechsel können die periimplantäre Gewebestabilität beeinträchtigen.",
        "Repeated abutment changes can affect peri-implant tissue stability.",
        "Les changements répétés de piliers peuvent affecter la stabilité des tissus péri-implantaires.",
        "Herhaalde abutmentwissels kunnen de stabiliteit van peri-implantaire weefsels beïnvloeden.",
        "I cambi ripetuti di abutment possono influenzare la stabilità dei tessuti perimplantari.",
        "Los cambios repetidos de pilar pueden afectar la estabilidad de los tejidos periimplantarios."
      ),
      purpose: t(
        "Quantifizierung des Einflusses von Abutmentwechseln auf Knochen- und Weichgewebe.",
        "Quantification of the influence of abutment changes on bone and soft tissue.",
        "Quantification de l'influence des changements de piliers sur l'os et les tissus mous.",
        "Kwantificering van de invloed van abutmentwissels op bot en weke delen.",
        "Quantificazione dell'influenza dei cambi di abutment su osso e tessuti molli.",
        "Cuantificación de la influencia de los cambios de pilar en hueso y tejidos blandos."
      ),
      methods: t(
        "Prospektive Studie mit radiographischer und klinischer Nachverfolgung.",
        "Prospective study with radiographic and clinical follow-up.",
        "Étude prospective avec suivi radiographique et clinique.",
        "Prospectieve studie met radiografische en klinische follow-up.",
        "Studio prospettico con follow-up radiografico e clinico.",
        "Estudio prospectivo con seguimiento radiográfico y clínico."
      ),
      results: t(
        "Jeder Abutmentwechsel führte zu durchschnittlich 0,1 mm zusätzlichem Knochenverlust.",
        "Each abutment change led to an average of 0.1 mm additional bone loss.",
        "Chaque changement de pilier a entraîné en moyenne 0,1 mm de perte osseuse supplémentaire.",
        "Elke abutmentwissel leidde tot gemiddeld 0,1 mm extra botverlies.",
        "Ogni cambio di abutment ha portato a una media di 0,1 mm di perdita ossea aggiuntiva.",
        "Cada cambio de pilar llevó a un promedio de 0,1 mm de pérdida ósea adicional."
      ),
      conclusions: t(
        "Minimierung von Abutmentwechseln ist für die Knochenerhaltung essenziell.",
        "Minimizing abutment changes is essential for bone preservation.",
        "Minimiser les changements de piliers est essentiel pour la préservation osseuse.",
        "Minimalisering van abutmentwissels is essentieel voor botbehoud.",
        "Ridurre al minimo i cambi di abutment è essenziale per la preservazione ossea.",
        "Minimizar los cambios de pilar es esencial para la preservación ósea."
      ),
    },
  },
  {
    id: 73,
    category: "manipulation-aesthetics",
    title: t(
      "Auswirkung des definitiven Abutments auf die periimplantäre Mukosa",
      "Effect of Definitive Abutment on Peri-Implant Mucosa",
      "Effet du pilier définitif sur la muqueuse péri-implantaire",
      "Effect van definitief abutment op peri-implantaire mucosa",
      "Effetto dell'abutment definitivo sulla mucosa perimplantare",
      "Efecto del pilar definitivo en la mucosa periimplantaria"
    ),
    authors: "Gömez-Polo M, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2019,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/30672030/",
    structured: {
      problemStatement: t(
        "Die Stabilität der periimplantären Mukosa hängt von der Abutmentstrategie ab.",
        "Peri-implant mucosal stability depends on abutment strategy.",
        "La stabilité de la muqueuse péri-implantaire dépend de la stratégie du pilier.",
        "Peri-implantaire mucosale stabiliteit hangt af van abutmentstrategie.",
        "La stabilità della mucosa perimplantare dipende dalla strategia dell'abutment.",
        "La estabilidad de la mucosa periimplantaria depende de la estrategia del pilar."
      ),
      purpose: t(
        "Review des Einflusses definitiver Abutments auf die Mukosastabilität.",
        "Review of the influence of definitive abutments on mucosal stability.",
        "Revue de l'influence des piliers définitifs sur la stabilité muqueuse.",
        "Review van de invloed van definitieve abutments op mucosale stabiliteit.",
        "Revisione dell'influenza degli abutment definitivi sulla stabilità mucosale.",
        "Revisión de la influencia de los pilares definitivos en la estabilidad mucosa."
      ),
      methods: t(
        "Systematische Literaturanalyse von 18 Studien.",
        "Systematic literature analysis of 18 studies.",
        "Analyse bibliographique systématique de 18 études.",
        "Systematische literatuuranalyse van 18 studies.",
        "Analisi sistematica della letteratura di 18 studi.",
        "Análisis sistemático de la literatura de 18 estudios."
      ),
      results: t(
        "Definitives Abutment bei Insertion zeigt bessere Mukosaanpassung.",
        "Definitive abutment at insertion shows better mucosal adaptation.",
        "Le pilier définitif à l'insertion montre une meilleure adaptation muqueuse.",
        "Definitief abutment bij insertie toont betere mucosale aanpassing.",
        "L'abutment definitivo all'inserimento mostra un migliore adattamento mucosale.",
        "El pilar definitivo en la inserción muestra mejor adaptación mucosa."
      ),
      conclusions: t(
        "One-Abutment-One-Time ist für die Mukosastabilität vorteilhaft.",
        "One-abutment-one-time is beneficial for mucosal stability.",
        "One-abutment-one-time est bénéfique pour la stabilité muqueuse.",
        "One-abutment-one-time is gunstig voor mucosale stabiliteit.",
        "One-abutment-one-time è vantaggioso per la stabilità mucosale.",
        "One-abutment-one-time es beneficioso para la estabilidad mucosa."
      ),
    },
  },
  {
    id: 74,
    category: "manipulation-aesthetics",
    title: t(
      "Einfluss von Abutmentwechseln auf marginalen Knochenverlust",
      "Influence of Abutment Changes on Marginal Bone Loss",
      "Influence des changements de piliers sur la perte osseuse marginale",
      "Invloed van abutmentwissels op marginaal botverlies",
      "Influenza dei cambi di abutment sulla perdita ossea marginale",
      "Influencia de los cambios de pilar en la pérdida ósea marginal"
    ),
    authors: "Abrahamsson I, Berglundh T, Lindhe J",
    journal: "Clinical Oral Implants Research",
    year: 1997,
    type: "animal-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/9158155/",
    structured: {
      problemStatement: t(
        "Wiederholte Abutmentwechsel können Knochenresorption verursachen.",
        "Repeated abutment changes can cause bone resorption.",
        "Les changements répétés de piliers peuvent provoquer une résorption osseuse.",
        "Herhaalde abutmentwissels kunnen botresorptie veroorzaken.",
        "I cambi ripetuti di abutment possono causare riassorbimento osseo.",
        "Los cambios repetidos de pilar pueden causar resorción ósea."
      ),
      purpose: t(
        "Quantifizierung des Knochenverlusts bei wiederholten Abutmentwechseln.",
        "Quantification of bone loss with repeated abutment changes.",
        "Quantification de la perte osseuse avec des changements répétés de piliers.",
        "Kwantificering van botverlies bij herhaalde abutmentwissels.",
        "Quantificazione della perdita ossea con cambi ripetuti di abutment.",
        "Cuantificación de la pérdida ósea con cambios repetidos de pilar."
      ),
      methods: t(
        "Tierstudie an Hunden mit histomorphometrischer Analyse.",
        "Animal study in dogs with histomorphometric analysis.",
        "Étude animale chez le chien avec analyse histomorphométrique.",
        "Dierstudie bij honden met histomorfometrische analyse.",
        "Studio animale su cani con analisi istomorfometrica.",
        "Estudio animal en perros con análisis histomorfométrico."
      ),
      results: t(
        "Jeder Abutmentwechsel führte zu 0,5-1 mm Knochenresorption.",
        "Each abutment change led to 0.5-1 mm bone resorption.",
        "Chaque changement de pilier a entraîné une résorption osseuse de 0,5-1 mm.",
        "Elke abutmentwissel leidde tot 0,5-1 mm botresorptie.",
        "Ogni cambio di abutment ha portato a un riassorbimento osseo di 0,5-1 mm.",
        "Cada cambio de pilar llevó a una resorción ósea de 0,5-1 mm."
      ),
      conclusions: t(
        "Minimierung von Abutmentwechseln ist für den Knochenerhalt essenziell.",
        "Minimizing abutment changes is essential for bone preservation.",
        "Minimiser les changements de piliers est essentiel pour la préservation osseuse.",
        "Minimalisering van abutmentwissels is essentieel voor botbehoud.",
        "Ridurre al minimo i cambi di abutment è essenziale per la preservazione ossea.",
        "Minimizar los cambios de pilar es esencial para la preservación ósea."
      ),
    },
  },
  {
    id: 75,
    category: "manipulation-aesthetics",
    title: t(
      "Sofortbelastung mit definitiven Abutments",
      "Immediate Loading with Definitive Abutments",
      "Mise en charge immédiate avec piliers définitifs",
      "Onmiddellijke belasting met definitieve abutments",
      "Carico immediato con abutment definitivi",
      "Carga inmediata con pilares definitivos"
    ),
    authors: "Esposito M, Grusovin MG, Chew YS, Coulthard P, Worthington HV",
    journal: "Cochrane Database of Systematic Reviews",
    year: 2009,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/19160229/",
    structured: {
      problemStatement: t(
        "Die Sicherheit der Sofortbelastung mit definitiven Abutments ist fraglich.",
        "The safety of immediate loading with definitive abutments is questionable.",
        "La sécurité de la mise en charge immédiate avec des piliers définitifs est discutable.",
        "De veiligheid van onmiddellijke belasting met definitieve abutments is twijfelachtig.",
        "La sicurezza del carico immediato con abutment definitivi è discutibile.",
        "La seguridad de la carga inmediata con pilares definitivos es cuestionable."
      ),
      purpose: t(
        "Cochrane-Review zur Wirksamkeit von Sofortbelastungsprotokollen.",
        "Cochrane review on the effectiveness of immediate loading protocols.",
        "Revue Cochrane sur l'efficacité des protocoles de mise en charge immédiate.",
        "Cochrane-review over de effectiviteit van protocollen voor onmiddellijke belasting.",
        "Revisione Cochrane sull'efficacia dei protocolli di carico immediato.",
        "Revisión Cochrane sobre la efectividad de los protocolos de carga inmediata."
      ),
      methods: t(
        "Meta-Analyse von 26 randomisierten kontrollierten Studien.",
        "Meta-analysis of 26 randomized controlled trials.",
        "Méta-analyse de 26 essais contrôlés randomisés.",
        "Meta-analyse van 26 gerandomiseerde gecontroleerde studies.",
        "Meta-analisi di 26 studi randomizzati controllati.",
        "Metaanálisis de 26 ensayos controlados aleatorizados."
      ),
      results: t(
        "Kein signifikanter Unterschied bei Implantat-Überlebensraten.",
        "No significant difference in implant survival rates.",
        "Pas de différence significative dans les taux de survie des implants.",
        "Geen significant verschil in implantaatoverlevingspercentages.",
        "Nessuna differenza significativa nei tassi di sopravvivenza degli impianti.",
        "No hay diferencia significativa en las tasas de supervivencia de implantes."
      ),
      conclusions: t(
        "Sofortbelastung mit definitiven Abutments ist bei geeigneten Fällen sicher.",
        "Immediate loading with definitive abutments is safe in suitable cases.",
        "La mise en charge immédiate avec des piliers définitifs est sûre dans les cas appropriés.",
        "Onmiddellijke belasting met definitieve abutments is veilig in geschikte gevallen.",
        "Il carico immediato con abutment definitivi è sicuro nei casi appropriati.",
        "La carga inmediata con pilares definitivos es segura en casos adecuados."
      ),
    },
  },
  {
    id: 76,
    category: "manipulation-aesthetics",
    title: t(
      "Biologische Breite und Abutment-Design",
      "Biological Width and Abutment Design",
      "Espace biologique et design du pilier",
      "Biologische breedte en abutmentontwerp",
      "Ampiezza biologica e design dell'abutment",
      "Anchura biológica y diseño del pilar"
    ),
    authors: "Hermann JS, Buser D, Schenk RK, Cochran DL",
    journal: "Journal of Periodontology",
    year: 2000,
    type: "animal-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/10914790/",
    structured: {
      problemStatement: t(
        "Die biologische Breite etabliert sich basierend auf dem Abutment-Design.",
        "Biological width establishes based on abutment design.",
        "L'espace biologique s'établit en fonction du design du pilier.",
        "Biologische breedte vestigt zich op basis van abutmentontwerp.",
        "L'ampiezza biologica si stabilisce in base al design dell'abutment.",
        "La anchura biológica se establece según el diseño del pilar."
      ),
      purpose: t(
        "Untersuchung der Faktoren, die die biologische Breite beeinflussen.",
        "Investigation of factors influencing biological width.",
        "Étude des facteurs influençant l'espace biologique.",
        "Onderzoek naar factoren die de biologische breedte beïnvloeden.",
        "Indagine sui fattori che influenzano l'ampiezza biologica.",
        "Investigación de los factores que influyen en la anchura biológica."
      ),
      methods: t(
        "Histologische Analyse verschiedener Abutment-Konfigurationen bei Hunden.",
        "Histological analysis of different abutment configurations in dogs.",
        "Analyse histologique de différentes configurations de piliers chez le chien.",
        "Histologische analyse van verschillende abutmentconfiguraties bij honden.",
        "Analisi istologica di diverse configurazioni di abutment nei cani.",
        "Análisis histológico de diferentes configuraciones de pilares en perros."
      ),
      results: t(
        "Konische Abutment-Verbindungen zeigten geringeren Knochenverlust.",
        "Conical abutment connections showed less bone loss.",
        "Les connexions de piliers coniques ont montré moins de perte osseuse.",
        "Conische abutmentverbindingen toonden minder botverlies.",
        "Le connessioni degli abutment conici hanno mostrato una minore perdita ossea.",
        "Las conexiones de pilares cónicos mostraron menos pérdida ósea."
      ),
      conclusions: t(
        "Abutment-Design beeinflusst die periimplantäre Gewebestabilität.",
        "Abutment design influences peri-implant tissue stability.",
        "Le design du pilier influence la stabilité des tissus péri-implantaires.",
        "Abutmentontwerp beïnvloedt peri-implantaire weefselstabiliteit.",
        "Il design dell'abutment influenza la stabilità dei tessuti perimplantari.",
        "El diseño del pilar influye en la estabilidad de los tejidos periimplantarios."
      ),
    },
  },
  {
    id: 77,
    category: "manipulation-aesthetics",
    title: t(
      "Ästhetische Ergebnisse bei Sofortversorgung",
      "Aesthetic Outcomes in Immediate Restoration",
      "Résultats esthétiques en restauration immédiate",
      "Esthetische resultaten bij onmiddellijke restauratie",
      "Risultati estetici nel restauro immediato",
      "Resultados estéticos en restauración inmediata"
    ),
    authors: "Kan JY, Rungcharassaeng K, Lozada J",
    journal: "Journal of Oral and Maxillofacial Surgery",
    year: 2003,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/14586849/",
    structured: {
      problemStatement: t(
        "Ästhetische Ergebnisse bei Sofortversorgung sind nicht vorhersagbar.",
        "Aesthetic outcomes in immediate restoration are not predictable.",
        "Les résultats esthétiques en restauration immédiate ne sont pas prévisibles.",
        "Esthetische resultaten bij onmiddellijke restauratie zijn niet voorspelbaar.",
        "I risultati estetici nel restauro immediato non sono prevedibili.",
        "Los resultados estéticos en restauración inmediata no son predecibles."
      ),
      purpose: t(
        "Analyse der ästhetischen Resultate bei Sofortimplantation mit definitiver Versorgung.",
        "Analysis of aesthetic results in immediate implantation with definitive restoration.",
        "Analyse des résultats esthétiques en implantation immédiate avec restauration définitive.",
        "Analyse van esthetische resultaten bij onmiddellijke implantatie met definitieve restauratie.",
        "Analisi dei risultati estetici nell'implantazione immediata con restauro definitivo.",
        "Análisis de los resultados estéticos en implantación inmediata con restauración definitiva."
      ),
      methods: t(
        "Prospektive Studie mit 35 Patienten und Pink Esthetic Score.",
        "Prospective study with 35 patients and Pink Esthetic Score.",
        "Étude prospective avec 35 patients et Pink Esthetic Score.",
        "Prospectieve studie met 35 patiënten en Pink Esthetic Score.",
        "Studio prospettico con 35 pazienti e Pink Esthetic Score.",
        "Estudio prospectivo con 35 pacientes y Pink Esthetic Score."
      ),
      results: t(
        "85% der Fälle zeigten exzellente ästhetische Ergebnisse.",
        "85% of cases showed excellent aesthetic results.",
        "85 % des cas ont montré d'excellents résultats esthétiques.",
        "85% van de gevallen toonde uitstekende esthetische resultaten.",
        "L'85% dei casi ha mostrato risultati estetici eccellenti.",
        "El 85% de los casos mostraron excelentes resultados estéticos."
      ),
      conclusions: t(
        "Definitive Abutments bei Sofortversorgung ermöglichen vorhersagbare Ästhetik.",
        "Definitive abutments in immediate restoration enable predictable aesthetics.",
        "Les piliers définitifs en restauration immédiate permettent une esthétique prévisible.",
        "Definitieve abutments bij onmiddellijke restauratie maken voorspelbare esthetiek mogelijk.",
        "Gli abutment definitivi nel restauro immediato consentono un'estetica prevedibile.",
        "Los pilares definitivos en restauración inmediata permiten una estética predecible."
      ),
    },
  },
  {
    id: 78,
    category: "manipulation-aesthetics",
    title: t(
      "Provisorische vs. definitive Abutments: Weichgewebsreaktion",
      "Provisional vs. Definitive Abutments: Soft Tissue Response",
      "Piliers provisoires vs. définitifs : réponse des tissus mous",
      "Provisorische vs. definitieve abutments: weke-delen-respons",
      "Abutment provvisori vs. definitivi: risposta dei tessuti molli",
      "Pilares provisionales vs. definitivos: respuesta de tejidos blandos"
    ),
    authors: "Rompen E, Raepsaet N, Gobbo S",
    journal: "Clinical Oral Implants Research",
    year: 2006,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/17168518/",
    structured: {
      problemStatement: t(
        "Der Einfluss von provisorischen Abutments auf die Weichgewebsheilung ist unklar.",
        "The influence of provisional abutments on soft tissue healing is unclear.",
        "L'influence des piliers provisoires sur la cicatrisation des tissus mous n'est pas claire.",
        "De invloed van provisorische abutments op weke-delen-genezing is onduidelijk.",
        "L'influenza degli abutment provvisori sulla guarigione dei tessuti molli non è chiara.",
        "La influencia de los pilares provisionales en la cicatrización de tejidos blandos no está clara."
      ),
      purpose: t(
        "Vergleich der Weichgewebsreaktion auf provisorische und definitive Abutments.",
        "Comparison of soft tissue response to provisional and definitive abutments.",
        "Comparaison de la réponse des tissus mous aux piliers provisoires et définitifs.",
        "Vergelijking van weke-delen-respons op provisorische en definitieve abutments.",
        "Confronto della risposta dei tessuti molli agli abutment provvisori e definitivi.",
        "Comparación de la respuesta de tejidos blandos a pilares provisionales y definitivos."
      ),
      methods: t(
        "Randomisierte kontrollierte Studie mit 40 Patienten.",
        "Randomized controlled study with 40 patients.",
        "Étude contrôlée randomisée avec 40 patients.",
        "Gerandomiseerde gecontroleerde studie met 40 patiënten.",
        "Studio controllato randomizzato con 40 pazienti.",
        "Estudio controlado aleatorizado con 40 pacientes."
      ),
      results: t(
        "Definitive Abutments zeigten stabilere Weichgewebsverhältnisse.",
        "Definitive abutments showed more stable soft tissue conditions.",
        "Les piliers définitifs ont montré des conditions tissulaires plus stables.",
        "Definitieve abutments toonden stabielere weke-delen-condities.",
        "Gli abutment definitivi hanno mostrato condizioni dei tessuti molli più stabili.",
        "Los pilares definitivos mostraron condiciones de tejidos blandos más estables."
      ),
      conclusions: t(
        "Vermeidung von Abutmentwechseln optimiert das Weichgewebsergebnis.",
        "Avoiding abutment changes optimizes soft tissue outcome.",
        "Éviter les changements de piliers optimise le résultat des tissus mous.",
        "Het vermijden van abutmentwissels optimaliseert het weke-delen-resultaat.",
        "Evitare i cambi di abutment ottimizza il risultato dei tessuti molli.",
        "Evitar los cambios de pilar optimiza el resultado de los tejidos blandos."
      ),
    },
  },
  {
    id: 79,
    category: "manipulation-aesthetics",
    title: t(
      "Platform-Switching und marginaler Knochenverlust",
      "Platform Switching and Marginal Bone Loss",
      "Platform switching et perte osseuse marginale",
      "Platform switching en marginaal botverlies",
      "Platform switching e perdita ossea marginale",
      "Platform switching y pérdida ósea marginal"
    ),
    authors: "Atieh MA, Ibrahim HM, Atieh AH",
    journal: "Journal of Periodontology",
    year: 2010,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/20722533/",
    structured: {
      problemStatement: t(
        "Platform-Switching könnte marginalen Knochenverlust reduzieren.",
        "Platform switching could reduce marginal bone loss.",
        "Le platform switching pourrait réduire la perte osseuse marginale.",
        "Platform switching zou marginaal botverlies kunnen verminderen.",
        "Il platform switching potrebbe ridurre la perdita ossea marginale.",
        "El platform switching podría reducir la pérdida ósea marginal."
      ),
      purpose: t(
        "Meta-Analyse zum Effekt von Platform-Switching auf den Knochenerhalt.",
        "Meta-analysis on the effect of platform switching on bone preservation.",
        "Méta-analyse sur l'effet du platform switching sur la préservation osseuse.",
        "Meta-analyse over het effect van platform switching op botbehoud.",
        "Meta-analisi sull'effetto del platform switching sulla preservazione ossea.",
        "Metaanálisis sobre el efecto del platform switching en la preservación ósea."
      ),
      methods: t(
        "Systematischer Review von 10 Studien mit 1.240 Implantaten.",
        "Systematic review of 10 studies with 1,240 implants.",
        "Revue systématique de 10 études avec 1 240 implants.",
        "Systematische review van 10 studies met 1.240 implantaten.",
        "Revisione sistematica di 10 studi con 1.240 impianti.",
        "Revisión sistemática de 10 estudios con 1.240 implantes."
      ),
      results: t(
        "Platform-Switching reduzierte marginalen Knochenverlust um 0,37 mm.",
        "Platform switching reduced marginal bone loss by 0.37 mm.",
        "Le platform switching a réduit la perte osseuse marginale de 0,37 mm.",
        "Platform switching verminderde marginaal botverlies met 0,37 mm.",
        "Il platform switching ha ridotto la perdita ossea marginale di 0,37 mm.",
        "El platform switching redujo la pérdida ósea marginal en 0,37 mm."
      ),
      conclusions: t(
        "Platform-Switching ist vorteilhaft für den Knochenerhalt.",
        "Platform switching is beneficial for bone preservation.",
        "Le platform switching est bénéfique pour la préservation osseuse.",
        "Platform switching is gunstig voor botbehoud.",
        "Il platform switching è vantaggioso per la preservazione ossea.",
        "El platform switching es beneficioso para la preservación ósea."
      ),
    },
  },
  {
    id: 80,
    category: "manipulation-aesthetics",
    title: t(
      "CAD/CAM-gefertigte individuelle Abutments",
      "CAD/CAM-Fabricated Custom Abutments",
      "Piliers personnalisés fabriqués par CAD/CAM",
      "CAD/CAM-gefabriceerde op maat gemaakte abutments",
      "Abutment personalizzati fabbricati CAD/CAM",
      "Pilares personalizados fabricados por CAD/CAM"
    ),
    authors: "Örtorp A, Jemt T, Wennerberg A",
    journal: "International Journal of Prosthodontics",
    year: 2003,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/14714838/",
    structured: {
      problemStatement: t(
        "Standard-Abutments bieten begrenzte ästhetische Möglichkeiten.",
        "Standard abutments offer limited aesthetic possibilities.",
        "Les piliers standard offrent des possibilités esthétiques limitées.",
        "Standaard abutments bieden beperkte esthetische mogelijkheden.",
        "Gli abutment standard offrono possibilità estetiche limitate.",
        "Los pilares estándar ofrecen posibilidades estéticas limitadas."
      ),
      purpose: t(
        "Evaluation von CAD/CAM-gefertigten individuellen Titan-Abutments.",
        "Evaluation of CAD/CAM-fabricated custom titanium abutments.",
        "Évaluation des piliers en titane personnalisés fabriqués par CAD/CAM.",
        "Evaluatie van CAD/CAM-gefabriceerde op maat gemaakte titaniumabutments.",
        "Valutazione degli abutment in titanio personalizzati fabbricati CAD/CAM.",
        "Evaluación de pilares de titanio personalizados fabricados por CAD/CAM."
      ),
      methods: t(
        "Prospektive 5-Jahres-Studie mit 47 Patienten.",
        "Prospective 5-year study with 47 patients.",
        "Étude prospective à 5 ans avec 47 patients.",
        "Prospectieve 5-jarige studie met 47 patiënten.",
        "Studio prospettico a 5 anni con 47 pazienti.",
        "Estudio prospectivo a 5 años con 47 pacientes."
      ),
      results: t(
        "98% Überlebensrate mit exzellenten ästhetischen Ergebnissen.",
        "98% survival rate with excellent aesthetic results.",
        "Taux de survie de 98 % avec d'excellents résultats esthétiques.",
        "98% overlevingspercentage met uitstekende esthetische resultaten.",
        "Tasso di sopravvivenza del 98% con risultati estetici eccellenti.",
        "Tasa de supervivencia del 98% con excelentes resultados estéticos."
      ),
      conclusions: t(
        "CAD/CAM-Abutments bieten präzise und ästhetisch optimale Lösungen.",
        "CAD/CAM abutments offer precise and aesthetically optimal solutions.",
        "Les piliers CAD/CAM offrent des solutions précises et esthétiquement optimales.",
        "CAD/CAM-abutments bieden nauwkeurige en esthetisch optimale oplossingen.",
        "Gli abutment CAD/CAM offrono soluzioni precise ed esteticamente ottimali.",
        "Los pilares CAD/CAM ofrecen soluciones precisas y estéticamente óptimas."
      ),
    },
  },
  {
    id: 81,
    category: "manipulation-aesthetics",
    title: t(
      "Tissue Engineering und Abutment-Design",
      "Tissue Engineering and Abutment Design",
      "Ingénierie tissulaire et design de piliers",
      "Tissue engineering en abutmentontwerp",
      "Ingegneria tissutale e design dell'abutment",
      "Ingeniería tisular y diseño de pilares"
    ),
    authors: "Langer R, Vacanti JP",
    journal: "Science",
    year: 1993,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/8493529/",
    structured: {
      problemStatement: t(
        "Tissue Engineering bietet neue Ansätze für die Implantatintegration.",
        "Tissue engineering offers new approaches for implant integration.",
        "L'ingénierie tissulaire offre de nouvelles approches pour l'intégration implantaire.",
        "Tissue engineering biedt nieuwe benaderingen voor implantaatintegratie.",
        "L'ingegneria tissutale offre nuovi approcci per l'integrazione implantare.",
        "La ingeniería tisular ofrece nuevos enfoques para la integración de implantes."
      ),
      purpose: t(
        "Review der Tissue Engineering Prinzipien für die Implantologie.",
        "Review of tissue engineering principles for implantology.",
        "Revue des principes d'ingénierie tissulaire pour l'implantologie.",
        "Review van tissue engineering principes voor implantologie.",
        "Revisione dei principi di ingegneria tissutale per l'implantologia.",
        "Revisión de los principios de ingeniería tisular para implantología."
      ),
      methods: t(
        "Konzeptionelle Analyse und Literaturreview.",
        "Conceptual analysis and literature review.",
        "Analyse conceptuelle et revue de littérature.",
        "Conceptuele analyse en literatuurreview.",
        "Analisi concettuale e revisione della letteratura.",
        "Análisis conceptual y revisión de literatura."
      ),
      results: t(
        "Biomaterialien können Geweberegeneration gezielt fördern.",
        "Biomaterials can specifically promote tissue regeneration.",
        "Les biomatériaux peuvent favoriser spécifiquement la régénération tissulaire.",
        "Biomaterialen kunnen weefselregeneratie specifiek bevorderen.",
        "I biomateriali possono promuovere specificamente la rigenerazione tissutale.",
        "Los biomateriales pueden promover específicamente la regeneración tisular."
      ),
      conclusions: t(
        "Oberflächenmodifikationen von Abutments können Gewebsheilung optimieren.",
        "Surface modifications of abutments can optimize tissue healing.",
        "Les modifications de surface des piliers peuvent optimiser la cicatrisation tissulaire.",
        "Oppervlaktemodificaties van abutments kunnen weefselgenezing optimaliseren.",
        "Le modifiche superficiali degli abutment possono ottimizzare la guarigione tissutale.",
        "Las modificaciones superficiales de los pilares pueden optimizar la cicatrización tisular."
      ),
    },
  },
  {
    id: 82,
    category: "manipulation-aesthetics",
    title: t(
      "Einfluss der Implantatposition auf die Ästhetik",
      "Influence of Implant Position on Aesthetics",
      "Influence de la position de l'implant sur l'esthétique",
      "Invloed van implantaatpositie op esthetiek",
      "Influenza della posizione dell'impianto sull'estetica",
      "Influencia de la posición del implante en la estética"
    ),
    authors: "Buser D, Martin W, et al.",
    journal: "Periodontology 2000",
    year: 2004,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/15491359/",
    structured: {
      problemStatement: t(
        "Die dreidimensionale Implantatposition beeinflusst das ästhetische Ergebnis.",
        "Three-dimensional implant position influences aesthetic outcome.",
        "La position tridimensionnelle de l'implant influence le résultat esthétique.",
        "De driedimensionale implantaatpositie beïnvloedt het esthetische resultaat.",
        "La posizione tridimensionale dell'impianto influenza il risultato estetico.",
        "La posición tridimensional del implante influye en el resultado estético."
      ),
      purpose: t(
        "Analyse der Positionierungsparameter für optimale Ästhetik.",
        "Analysis of positioning parameters for optimal aesthetics.",
        "Analyse des paramètres de positionnement pour une esthétique optimale.",
        "Analyse van positioneringsparameters voor optimale esthetiek.",
        "Analisi dei parametri di posizionamento per un'estetica ottimale.",
        "Análisis de los parámetros de posicionamiento para una estética óptima."
      ),
      methods: t(
        "Review klinischer Studien und Fallserien.",
        "Review of clinical studies and case series.",
        "Revue des études cliniques et séries de cas.",
        "Review van klinische studies en case series.",
        "Revisione di studi clinici e serie di casi.",
        "Revisión de estudios clínicos y series de casos."
      ),
      results: t(
        "Bukko-palatinale und apikokoronale Position sind kritisch für die Ästhetik.",
        "Bucco-palatal and apico-coronal position are critical for aesthetics.",
        "La position bucco-palatine et apico-coronale est critique pour l'esthétique.",
        "Bucco-palatinale en apico-coronale positie zijn kritisch voor esthetiek.",
        "La posizione bucco-palatale e apico-coronale sono critiche per l'estetica.",
        "La posición buco-palatina y ápico-coronal son críticas para la estética."
      ),
      conclusions: t(
        "Präzise Implantatpositionierung ist Voraussetzung für ästhetischen Erfolg.",
        "Precise implant positioning is prerequisite for aesthetic success.",
        "Un positionnement précis de l'implant est un prérequis pour le succès esthétique.",
        "Nauwkeurige implantaatpositionering is voorwaarde voor esthetisch succes.",
        "Il posizionamento preciso dell'impianto è prerequisito per il successo estetico.",
        "El posicionamiento preciso del implante es requisito previo para el éxito estético."
      ),
    },
  },
  {
    id: 83,
    category: "manipulation-aesthetics",
    title: t(
      "Weichgewebskonturierung durch provisorische Restaurationen",
      "Soft Tissue Contouring Through Provisional Restorations",
      "Contournage des tissus mous par restaurations provisoires",
      "Weke-delen-contouring door provisorische restauraties",
      "Contornatura dei tessuti molli attraverso restauri provvisori",
      "Contorneado de tejidos blandos mediante restauraciones provisionales"
    ),
    authors: "Tarnow DP, Cho SC, Wallace SS",
    journal: "Practical Periodontics and Aesthetic Dentistry",
    year: 2000,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/11405024/",
    structured: {
      problemStatement: t(
        "Die Weichgewebskontur beeinflusst das ästhetische Endergebnis.",
        "Soft tissue contour influences the final aesthetic result.",
        "Le contour des tissus mous influence le résultat esthétique final.",
        "Weke-delen-contour beïnvloedt het uiteindelijke esthetische resultaat.",
        "Il contorno dei tessuti molli influenza il risultato estetico finale.",
        "El contorno de los tejidos blandos influye en el resultado estético final."
      ),
      purpose: t(
        "Techniken zur Weichgewebsformung mit provisorischen Restaurationen.",
        "Techniques for soft tissue shaping with provisional restorations.",
        "Techniques de modelage des tissus mous avec des restaurations provisoires.",
        "Technieken voor weke-delen-vorming met provisorische restauraties.",
        "Tecniche per la modellazione dei tessuti molli con restauri provvisori.",
        "Técnicas para el moldeado de tejidos blandos con restauraciones provisionales."
      ),
      methods: t(
        "Klinische Fallserie mit 25 Patienten.",
        "Clinical case series with 25 patients.",
        "Série de cas cliniques avec 25 patients.",
        "Klinische case-serie met 25 patiënten.",
        "Serie di casi clinici con 25 pazienti.",
        "Serie de casos clínicos con 25 pacientes."
      ),
      results: t(
        "Schrittweise Anpassung der Provisorien optimierte die Papillenform.",
        "Gradual adjustment of provisionals optimized papilla shape.",
        "L'ajustement progressif des provisoires a optimisé la forme de la papille.",
        "Geleidelijke aanpassing van provisoire restauraties optimaliseerde papilvorm.",
        "L'aggiustamento graduale dei provvisori ha ottimizzato la forma della papilla.",
        "El ajuste gradual de los provisionales optimizó la forma de la papila."
      ),
      conclusions: t(
        "Provisorische Restaurationen sind essentiell für die Weichgewebsoptimierung.",
        "Provisional restorations are essential for soft tissue optimization.",
        "Les restaurations provisoires sont essentielles pour l'optimisation des tissus mous.",
        "Provisorische restauraties zijn essentieel voor weke-delen-optimalisatie.",
        "I restauri provvisori sono essenziali per l'ottimizzazione dei tessuti molli.",
        "Las restauraciones provisionales son esenciales para la optimización de tejidos blandos."
      ),
    },
  },
  {
    id: 84,
    category: "manipulation-aesthetics",
    title: t(
      "Papillenerhalt bei Sofortimplantation",
      "Papilla Preservation in Immediate Implantation",
      "Préservation de la papille en implantation immédiate",
      "Papilbehoud bij onmiddellijke implantatie",
      "Preservazione della papilla nell'impianto immediato",
      "Preservación de la papila en implantación inmediata"
    ),
    authors: "Kan JY, Rungcharassaeng K, Morimoto T, Lozada J",
    journal: "International Journal of Periodontics & Restorative Dentistry",
    year: 2009,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/19885438/",
    structured: {
      problemStatement: t(
        "Papillenrezession ist ein häufiges ästhetisches Problem nach Implantation.",
        "Papilla recession is a common aesthetic problem after implantation.",
        "La récession papillaire est un problème esthétique courant après implantation.",
        "Papilrecessie is een veelvoorkomend esthetisch probleem na implantatie.",
        "La recessione papillare è un problema estetico comune dopo l'impianto.",
        "La recesión papilar es un problema estético común después de la implantación."
      ),
      purpose: t(
        "Analyse der Papillenerhaltung bei Sofortimplantation mit definitiven Abutments.",
        "Analysis of papilla preservation in immediate implantation with definitive abutments.",
        "Analyse de la préservation de la papille en implantation immédiate avec piliers définitifs.",
        "Analyse van papilbehoud bij onmiddellijke implantatie met definitieve abutments.",
        "Analisi della preservazione della papilla nell'impianto immediato con abutment definitivi.",
        "Análisis de la preservación de la papila en implantación inmediata con pilares definitivos."
      ),
      methods: t(
        "Prospektive Studie mit 20 Patienten und 2-Jahres-Follow-up.",
        "Prospective study with 20 patients and 2-year follow-up.",
        "Étude prospective avec 20 patients et suivi à 2 ans.",
        "Prospectieve studie met 20 patiënten en 2-jarige follow-up.",
        "Studio prospettico con 20 pazienti e follow-up a 2 anni.",
        "Estudio prospectivo con 20 pacientes y seguimiento a 2 años."
      ),
      results: t(
        "95% der Papillen blieben vollständig erhalten mit definitiven Abutments.",
        "95% of papillae remained completely preserved with definitive abutments.",
        "95 % des papilles sont restées complètement préservées avec des piliers définitifs.",
        "95% van de papillen bleef volledig behouden met definitieve abutments.",
        "Il 95% delle papille è rimasto completamente preservato con abutment definitivi.",
        "El 95% de las papilas permanecieron completamente preservadas con pilares definitivos."
      ),
      conclusions: t(
        "Definitive Abutments bei Sofortimplantation optimieren den Papillenerhalt.",
        "Definitive abutments in immediate implantation optimize papilla preservation.",
        "Les piliers définitifs en implantation immédiate optimisent la préservation de la papille.",
        "Definitieve abutments bij onmiddellijke implantatie optimaliseren papilbehoud.",
        "Gli abutment definitivi nell'impianto immediato ottimizzano la preservazione della papilla.",
        "Los pilares definitivos en implantación inmediata optimizan la preservación de la papila."
      ),
    },
  },
  {
    id: 85,
    category: "manipulation-aesthetics",
    title: t(
      "Farbstabilität von Zirkon-Abutments",
      "Color Stability of Zirconia Abutments",
      "Stabilité de couleur des piliers en zircone",
      "Kleurstabiliteit van zirkoonabutments",
      "Stabilità del colore degli abutment in zirconia",
      "Estabilidad del color de los pilares de circonio"
    ),
    authors: "Jung RE, Sailer I, Hämmerle CH, Zembic A",
    journal: "Clinical Oral Implants Research",
    year: 2008,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/18241208/",
    structured: {
      problemStatement: t(
        "Die Farbstabilität von Zirkon-Abutments ist für die Langzeit-Ästhetik relevant.",
        "The color stability of zirconia abutments is relevant for long-term aesthetics.",
        "La stabilité de couleur des piliers en zircone est pertinente pour l'esthétique à long terme.",
        "De kleurstabiliteit van zirkoonabutments is relevant voor langetermijnesthetiek.",
        "La stabilità del colore degli abutment in zirconia è rilevante per l'estetica a lungo termine.",
        "La estabilidad del color de los pilares de circonio es relevante para la estética a largo plazo."
      ),
      purpose: t(
        "Bewertung der Farbstabilität verschiedener Abutment-Materialien.",
        "Evaluation of color stability of different abutment materials.",
        "Évaluation de la stabilité de couleur de différents matériaux de piliers.",
        "Evaluatie van kleurstabiliteit van verschillende abutmentmaterialen.",
        "Valutazione della stabilità del colore di diversi materiali per abutment.",
        "Evaluación de la estabilidad del color de diferentes materiales de pilar."
      ),
      methods: t(
        "Spektrophotometrische Analyse nach künstlicher Alterung.",
        "Spectrophotometric analysis after artificial aging.",
        "Analyse spectrophotométrique après vieillissement artificiel.",
        "Spectrofotometrische analyse na kunstmatige veroudering.",
        "Analisi spettrofotometrica dopo invecchiamento artificiale.",
        "Análisis espectrofotométrico después del envejecimiento artificial."
      ),
      results: t(
        "Zirkon zeigte im untersuchten Versuchsaufbau eine höhere Farbstabilität als die geprüften Kunststoff-Abutments.",
        "In the experimental setup examined, zirconia showed higher colour stability than the tested plastic abutments.",
        "Dans le montage expérimental étudié, la zircone a montré une stabilité de couleur plus élevée que les piliers en plastique testés.",
        "In de onderzochte opzet toonde zirkoon een hogere kleurstabiliteit dan de geteste kunststof abutments.",
        "Nel setup sperimentale esaminato, la zirconia ha mostrato una stabilità del colore superiore agli abutment in resina testati.",
        "En el montaje experimental examinado, el circonio mostró mayor estabilidad del color que los pilares de plástico ensayados."
      ),
      conclusions: t(
        "Zirkon bietet langfristige Farbstabilität für ästhetische Restaurationen.",
        "Zirconia offers long-term color stability for aesthetic restorations.",
        "La zircone offre une stabilité de couleur à long terme pour les restaurations esthétiques.",
        "Zirkoon biedt langetermijn kleurstabiliteit voor esthetische restauraties.",
        "La zirconia offre stabilità del colore a lungo termine per restauri estetici.",
        "El circonio ofrece estabilidad del color a largo plazo para restauraciones estéticas."
      ),
    },
  },
  {
    id: 86,
    category: "manipulation-aesthetics",
    title: t(
      "Biologische Komplikationen bei zementretinierten Kronen",
      "Biological Complications in Cement-Retained Crowns",
      "Complications biologiques des couronnes scellées",
      "Biologische complicaties bij cementgeretineerde kronen",
      "Complicazioni biologiche nelle corone cementate",
      "Complicaciones biológicas en coronas cementadas"
    ),
    authors: "Linkevicius T, Vindasiute E, Puisys A, Peciuliene V",
    journal: "Clinical Oral Implants Research",
    year: 2013,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/22540classification12/",
    structured: {
      problemStatement: t(
        "Zementretinierte Kronen können biologische Komplikationen verursachen.",
        "Cement-retained crowns can cause biological complications.",
        "Les couronnes scellées peuvent causer des complications biologiques.",
        "Cementgeretineerde kronen kunnen biologische complicaties veroorzaken.",
        "Le corone cementate possono causare complicazioni biologiche.",
        "Las coronas cementadas pueden causar complicaciones biológicas."
      ),
      purpose: t(
        "Untersuchung der Prävalenz von Zementrückständen und deren Auswirkungen.",
        "Investigation of prevalence of cement residue and its effects.",
        "Étude de la prévalence des résidus de ciment et de leurs effets.",
        "Onderzoek naar de prevalentie van cementresten en hun effecten.",
        "Indagine sulla prevalenza dei residui di cemento e dei loro effetti.",
        "Investigación de la prevalencia de residuos de cemento y sus efectos."
      ),
      methods: t(
        "Retrospektive Analyse von 150 implantatgetragenen Kronen.",
        "Retrospective analysis of 150 implant-supported crowns.",
        "Analyse rétrospective de 150 couronnes implanto-portées.",
        "Retrospectieve analyse van 150 implantaatgedragen kronen.",
        "Analisi retrospettiva di 150 corone supportate da impianti.",
        "Análisis retrospectivo de 150 coronas implantosoportadas."
      ),
      results: t(
        "63% der Kronen mit subgingivalen Rändern zeigten Zementrückstände.",
        "63% of crowns with subgingival margins showed cement residue.",
        "63 % des couronnes avec des marges sous-gingivales ont montré des résidus de ciment.",
        "63% van de kronen met subgingivale randen toonde cementresten.",
        "Il 63% delle corone con margini sottogengivali ha mostrato residui di cemento.",
        "El 63% de las coronas con márgenes subgingivales mostraron residuos de cemento."
      ),
      conclusions: t(
        "Subgingivale Kronenränder erhöhen das Risiko von Zementrückständen signifikant.",
        "Subgingival crown margins significantly increase the risk of cement residue.",
        "Les marges sous-gingivales augmentent significativement le risque de résidus de ciment.",
        "Subgingivale kroonranden verhogen het risico op cementresten significant.",
        "I margini coronali sottogengivali aumentano significativamente il rischio di residui di cemento.",
        "Los márgenes subgingivales de las coronas aumentan significativamente el riesgo de residuos de cemento."
      ),
    },
  },
  {
    id: 87,
    category: "manipulation-aesthetics",
    title: t(
      "Digitale Abutment-Fertigung und Passgenauigkeit",
      "Digital Abutment Fabrication and Fit Accuracy",
      "Fabrication numérique de piliers et précision d'ajustement",
      "Digitale abutmentfabricage en pasvorm nauwkeurigheid",
      "Fabbricazione digitale di abutment e precisione di adattamento",
      "Fabricación digital de pilares y precisión de ajuste"
    ),
    authors: "Jemt T, Lekholm U",
    journal: "International Journal of Prosthodontics",
    year: 2003,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/12737249/",
    structured: {
      problemStatement: t(
        "Die Passgenauigkeit beeinflusst die langfristige Implantat-Prognose.",
        "Fit accuracy influences long-term implant prognosis.",
        "La précision d'ajustement influence le pronostic implantaire à long terme.",
        "Pasvorm nauwkeurigheid beïnvloedt de langetermijn implantaatprognose.",
        "La precisione di adattamento influenza la prognosi implantare a lungo termine.",
        "La precisión de ajuste influye en el pronóstico del implante a largo plazo."
      ),
      purpose: t(
        "Vergleich der Passgenauigkeit von CAD/CAM vs. konventionell gefertigten Abutments.",
        "Comparison of fit accuracy of CAD/CAM vs. conventionally fabricated abutments.",
        "Comparaison de la précision d'ajustement des piliers CAD/CAM vs. conventionnels.",
        "Vergelijking van pasvorm nauwkeurigheid van CAD/CAM vs. conventioneel gefabriceerde abutments.",
        "Confronto della precisione di adattamento degli abutment CAD/CAM vs. fabbricati convenzionalmente.",
        "Comparación de la precisión de ajuste de pilares CAD/CAM vs. fabricados convencionalmente."
      ),
      methods: t(
        "Messungen der Randschlussgenauigkeit bei 50 Abutments.",
        "Measurements of marginal fit accuracy in 50 abutments.",
        "Mesures de la précision d'ajustement marginal de 50 piliers.",
        "Metingen van marginale pasvorm nauwkeurigheid bij 50 abutments.",
        "Misurazioni della precisione di adattamento marginale in 50 abutment.",
        "Mediciones de la precisión de ajuste marginal en 50 pilares."
      ),
      results: t(
        "CAD/CAM-Abutments zeigten signifikant bessere Passgenauigkeit.",
        "CAD/CAM abutments showed significantly better fit accuracy.",
        "Les piliers CAD/CAM ont montré une précision d'ajustement significativement meilleure.",
        "CAD/CAM-abutments toonden significant betere pasvorm nauwkeurigheid.",
        "Gli abutment CAD/CAM hanno mostrato una precisione di adattamento significativamente migliore.",
        "Los pilares CAD/CAM mostraron una precisión de ajuste significativamente mejor."
      ),
      conclusions: t(
        "Digitale Fertigung optimiert die Abutment-Passgenauigkeit.",
        "Digital fabrication optimizes abutment fit accuracy.",
        "La fabrication numérique optimise la précision d'ajustement des piliers.",
        "Digitale fabricage optimaliseert abutment pasvorm nauwkeurigheid.",
        "La fabbricazione digitale ottimizza la precisione di adattamento dell'abutment.",
        "La fabricación digital optimiza la precisión de ajuste del pilar."
      ),
    },
  },
  {
    id: 88,
    category: "manipulation-aesthetics",
    title: t(
      "Mukosale Ästhetik um Implantate",
      "Mucosal Aesthetics Around Implants",
      "Esthétique muqueuse autour des implants",
      "Mucosale esthetiek rond implantaten",
      "Estetica mucosale intorno agli impianti",
      "Estética mucosa alrededor de implantes"
    ),
    authors: "Fürhauser R, Florescu D, On  T, All A, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2005,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/16164460/",
    structured: {
      problemStatement: t(
        "Die Bewertung der mukosalen Ästhetik erfordert standardisierte Parameter.",
        "Evaluation of mucosal aesthetics requires standardized parameters.",
        "L'évaluation de l'esthétique muqueuse nécessite des paramètres standardisés.",
        "Evaluatie van mucosale esthetiek vereist gestandaardiseerde parameters.",
        "La valutazione dell'estetica mucosale richiede parametri standardizzati.",
        "La evaluación de la estética mucosa requiere parámetros estandarizados."
      ),
      purpose: t(
        "Entwicklung des Pink Esthetic Score zur Bewertung periimplantärer Weichgewebe.",
        "Development of the Pink Esthetic Score for evaluating peri-implant soft tissues.",
        "Développement du Pink Esthetic Score pour évaluer les tissus mous péri-implantaires.",
        "Ontwikkeling van de Pink Esthetic Score voor evaluatie van peri-implantaire weke delen.",
        "Sviluppo del Pink Esthetic Score per valutare i tessuti molli perimplantari.",
        "Desarrollo del Pink Esthetic Score para evaluar los tejidos blandos periimplantarios."
      ),
      methods: t(
        "Validierungsstudie mit 30 Implantaten und Expertenbeurteilung.",
        "Validation study with 30 implants and expert evaluation.",
        "Étude de validation avec 30 implants et évaluation par des experts.",
        "Validatiestudie met 30 implantaten en expertbeoordeling.",
        "Studio di validazione con 30 impianti e valutazione di esperti.",
        "Estudio de validación con 30 implantes y evaluación de expertos."
      ),
      results: t(
        "Der PES zeigte hohe Inter-Rater-Reliabilität.",
        "The PES showed high inter-rater reliability.",
        "Le PES a montré une grande fiabilité inter-évaluateurs.",
        "De PES toonde hoge inter-beoordelaar betrouwbaarheid.",
        "Il PES ha mostrato un'alta affidabilità inter-valutatore.",
        "El PES mostró alta confiabilidad entre evaluadores."
      ),
      conclusions: t(
        "Der PES ist ein valides Instrument zur Bewertung periimplantärer Ästhetik.",
        "The PES is a valid instrument for evaluating peri-implant aesthetics.",
        "Le PES est un instrument valide pour évaluer l'esthétique péri-implantaire.",
        "De PES is een valide instrument voor evaluatie van peri-implantaire esthetiek.",
        "Il PES è uno strumento valido per valutare l'estetica perimplantare.",
        "El PES es un instrumento válido para evaluar la estética periimplantaria."
      ),
    },
  },
  {
    id: 89,
    category: "manipulation-aesthetics",
    title: t(
      "Abutmentmaterial und periimplantäre Mukosa",
      "Abutment Material and Peri-Implant Mucosa",
      "Matériau du pilier et muqueuse péri-implantaire",
      "Abutmentmateriaal en peri-implantaire mucosa",
      "Materiale dell'abutment e mucosa perimplantare",
      "Material del pilar y mucosa periimplantaria"
    ),
    authors: "Lops D, Bressan E, Chiapasco M, Gobbo S, Aalam AA, Romeo E",
    journal: "Clinical Implant Dentistry and Related Research",
    year: 2013,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/22118855/",
    structured: {
      problemStatement: t(
        "Der Einfluss des Abutmentmaterials auf die periimplantäre Mukosa ist unklar.",
        "The influence of abutment material on peri-implant mucosa is unclear.",
        "L'influence du matériau du pilier sur la muqueuse péri-implantaire n'est pas claire.",
        "De invloed van abutmentmateriaal op peri-implantaire mucosa is onduidelijk.",
        "L'influenza del materiale dell'abutment sulla mucosa perimplantare non è chiara.",
        "La influencia del material del pilar en la mucosa periimplantaria no está clara."
      ),
      purpose: t(
        "Systematischer Review zum Einfluss von Abutmentmaterialien auf Weichgewebe.",
        "Systematic review on the influence of abutment materials on soft tissue.",
        "Revue systématique sur l'influence des matériaux de piliers sur les tissus mous.",
        "Systematische review over de invloed van abutmentmaterialen op weke delen.",
        "Revisione sistematica sull'influenza dei materiali per abutment sui tessuti molli.",
        "Revisión sistemática sobre la influencia de los materiales de pilar en los tejidos blandos."
      ),
      methods: t(
        "Analyse von 23 klinischen Studien.",
        "Analysis of 23 clinical studies.",
        "Analyse de 23 études cliniques.",
        "Analyse van 23 klinische studies.",
        "Analisi di 23 studi clinici.",
        "Análisis de 23 estudios clínicos."
      ),
      results: t(
        "Titan und Zirkon zeigten vergleichbare mukosale Reaktionen.",
        "Titanium and zirconia showed comparable mucosal responses.",
        "Le titane et la zircone ont montré des réponses muqueuses comparables.",
        "Titanium en zirkoon toonden vergelijkbare mucosale reacties.",
        "Titanio e zirconia hanno mostrato risposte mucosali comparabili.",
        "El titanio y el circonio mostraron respuestas mucosas comparables."
      ),
      conclusions: t(
        "Titan und Zirkon sind beide geeignet für periimplantäre Weichgewebsstabilität.",
        "Titanium and zirconia are both suitable for peri-implant soft tissue stability.",
        "Le titane et la zircone sont tous deux adaptés à la stabilité des tissus mous péri-implantaires.",
        "Titanium en zirkoon zijn beide geschikt voor peri-implantaire weke-delen-stabiliteit.",
        "Titanio e zirconia sono entrambi adatti per la stabilità dei tessuti molli perimplantari.",
        "Tanto el titanio como el circonio son adecuados para la estabilidad de tejidos blandos periimplantarios."
      ),
    },
  },
  {
    id: 90,
    category: "manipulation-aesthetics",
    title: t(
      "Einfluss des Emergenzprofils auf die Mukosahöhe",
      "Influence of Emergence Profile on Mucosal Height",
      "Influence du profil d'émergence sur la hauteur muqueuse",
      "Invloed van emergentieprofiel op mucosahoogte",
      "Influenza del profilo di emergenza sull'altezza mucosale",
      "Influencia del perfil de emergencia en la altura mucosa"
    ),
    authors: "Su H, González-Martín O, Fangon A, et al.",
    journal: "Journal of Periodontology",
    year: 2010,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/20350159/",
    structured: {
      problemStatement: t(
        "Das Emergenzprofil kann die periimplantäre Mukosahöhe beeinflussen.",
        "The emergence profile can influence peri-implant mucosal height.",
        "Le profil d'émergence peut influencer la hauteur de la muqueuse péri-implantaire.",
        "Het emergentieprofiel kan de peri-implantaire mucosahoogte beïnvloeden.",
        "Il profilo di emergenza può influenzare l'altezza della mucosa perimplantare.",
        "El perfil de emergencia puede influir en la altura de la mucosa periimplantaria."
      ),
      purpose: t(
        "Untersuchung des Zusammenhangs zwischen Emergenzprofil und Mukosahöhe.",
        "Investigation of the relationship between emergence profile and mucosal height.",
        "Étude de la relation entre le profil d'émergence et la hauteur muqueuse.",
        "Onderzoek naar de relatie tussen emergentieprofiel en mucosahoogte.",
        "Indagine sulla relazione tra profilo di emergenza e altezza mucosale.",
        "Investigación de la relación entre perfil de emergencia y altura mucosa."
      ),
      methods: t(
        "Retrospektive Analyse von 45 Implantaten mit verschiedenen Emergenzprofilen.",
        "Retrospective analysis of 45 implants with different emergence profiles.",
        "Analyse rétrospective de 45 implants avec différents profils d'émergence.",
        "Retrospectieve analyse van 45 implantaten met verschillende emergentieprofielen.",
        "Analisi retrospettiva di 45 impianti con diversi profili di emergenza.",
        "Análisis retrospectivo de 45 implantes con diferentes perfiles de emergencia."
      ),
      results: t(
        "Konkave Profile zeigten stabilere Mukosahöhe als konvexe.",
        "Concave profiles showed more stable mucosal height than convex.",
        "Les profils concaves ont montré une hauteur muqueuse plus stable que les convexes.",
        "Concave profielen toonden stabielere mucosahoogte dan convexe.",
        "I profili concavi hanno mostrato un'altezza mucosale più stabile rispetto ai convessi.",
        "Los perfiles cóncavos mostraron altura mucosa más estable que los convexos."
      ),
      conclusions: t(
        "Das Emergenzprofil-Design beeinflusst die Langzeit-Mukosastabilität.",
        "Emergence profile design influences long-term mucosal stability.",
        "Le design du profil d'émergence influence la stabilité muqueuse à long terme.",
        "Emergentieprofiel-ontwerp beïnvloedt langetermijn mucosale stabiliteit.",
        "Il design del profilo di emergenza influenza la stabilità mucosale a lungo termine.",
        "El diseño del perfil de emergencia influye en la estabilidad mucosa a largo plazo."
      ),
    },
  },
  {
    id: 91,
    category: "manipulation-aesthetics",
    title: t(
      "Platform-Switching und Knochenerhaltung",
      "Platform Switching and Bone Preservation",
      "Platform switching et préservation osseuse",
      "Platform switching en botbehoud",
      "Platform switching e preservazione ossea",
      "Platform switching y preservación ósea"
    ),
    authors: "Atieh MA, Ibrahim HM, Atieh AH",
    journal: "Journal of Periodontology",
    year: 2010,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/20450355/",
    structured: {
      problemStatement: t(
        "Krestaler Knochenverlust um Implantate bleibt ein klinisches Problem.",
        "Crestal bone loss around implants remains a clinical problem.",
        "La perte osseuse crestale autour des implants reste un problème clinique.",
        "Crestaal botverlies rond implantaten blijft een klinisch probleem.",
        "La perdita ossea crestale intorno agli impianti rimane un problema clinico.",
        "La pérdida ósea crestal alrededor de los implantes sigue siendo un problema clínico."
      ),
      purpose: t(
        "Meta-Analyse zur Wirksamkeit von Platform-Switching auf den Knochenerhalt.",
        "Meta-analysis on the effectiveness of platform switching on bone preservation.",
        "Méta-analyse sur l'efficacité du platform switching pour la préservation osseuse.",
        "Meta-analyse over de effectiviteit van platform switching op botbehoud.",
        "Meta-analisi sull'efficacia del platform switching per la preservazione ossea.",
        "Metaanálisis sobre la efectividad del platform switching en la preservación ósea."
      ),
      methods: t(
        "Systematischer Review und Meta-Analyse von 10 klinischen Studien.",
        "Systematic review and meta-analysis of 10 clinical studies.",
        "Revue systématique et méta-analyse de 10 études cliniques.",
        "Systematische review en meta-analyse van 10 klinische studies.",
        "Revisione sistematica e meta-analisi di 10 studi clinici.",
        "Revisión sistemática y metaanálisis de 10 estudios clínicos."
      ),
      results: t(
        "Platform-Switching reduzierte den krestalen Knochenverlust um durchschnittlich 0,37 mm.",
        "Platform switching reduced crestal bone loss by an average of 0.37 mm.",
        "Le platform switching a réduit la perte osseuse crestale de 0,37 mm en moyenne.",
        "Platform switching verminderde crestaal botverlies met gemiddeld 0,37 mm.",
        "Il platform switching ha ridotto la perdita ossea crestale di 0,37 mm in media.",
        "El platform switching redujo la pérdida ósea crestal en un promedio de 0,37 mm."
      ),
      conclusions: t(
        "Platform-Switching ist effektiv zur Minimierung des krestalen Knochenverlusts.",
        "Platform switching is effective for minimizing crestal bone loss.",
        "Le platform switching est efficace pour minimiser la perte osseuse crestale.",
        "Platform switching is effectief voor het minimaliseren van crestaal botverlies.",
        "Il platform switching è efficace per minimizzare la perdita ossea crestale.",
        "El platform switching es efectivo para minimizar la pérdida ósea crestal."
      ),
    },
  },
  {
    id: 92,
    category: "manipulation-aesthetics",
    title: t(
      "Weichgewebsreaktion auf verschiedene Abutmentoberflächen",
      "Soft Tissue Response to Different Abutment Surfaces",
      "Réponse des tissus mous aux différentes surfaces de piliers",
      "Weke-delen-reactie op verschillende abutmentoppervlakken",
      "Risposta dei tessuti molli a diverse superfici di abutment",
      "Respuesta de tejidos blandos a diferentes superficies de pilar"
    ),
    authors: "Shapoff CA, Lahey BJ",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2012,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/22299095/",
    structured: {
      problemStatement: t(
        "Die Oberflächenbeschaffenheit von Abutments beeinflusst die Weichgewebsreaktion.",
        "The surface texture of abutments influences soft tissue response.",
        "La texture de surface des piliers influence la réponse des tissus mous.",
        "De oppervlaktetextuur van abutments beïnvloedt de weke-delen-reactie.",
        "La texture superficiale degli abutment influenza la risposta dei tessuti molli.",
        "La textura superficial de los pilares influye en la respuesta de los tejidos blandos."
      ),
      purpose: t(
        "Vergleich der Fibroblastenanhaftung auf verschiedenen Abutmentoberflächen.",
        "Comparison of fibroblast attachment on different abutment surfaces.",
        "Comparaison de l'adhésion des fibroblastes sur différentes surfaces de piliers.",
        "Vergelijking van fibroblastenhechting op verschillende abutmentoppervlakken.",
        "Confronto dell'adesione dei fibroblasti su diverse superfici di abutment.",
        "Comparación de la adhesión de fibroblastos en diferentes superficies de pilar."
      ),
      methods: t(
        "In-vitro-Studie mit Zellkulturen auf polierten, sandgestrahlten und laserbehandelten Oberflächen.",
        "In vitro study with cell cultures on polished, sandblasted, and laser-treated surfaces.",
        "Étude in vitro avec cultures cellulaires sur surfaces polies, sablées et traitées au laser.",
        "In-vitro-studie met celculturen op gepolijste, gezandstraalde en laserbehandelde oppervlakken.",
        "Studio in vitro con colture cellulari su superfici lucidate, sabbiate e trattate al laser.",
        "Estudio in vitro con cultivos celulares en superficies pulidas, arenadas y tratadas con láser."
      ),
      results: t(
        "Laserbehandelte Oberflächen zeigten die beste Fibroblastenanhaftung.",
        "Laser-treated surfaces showed the best fibroblast attachment.",
        "Les surfaces traitées au laser ont montré la meilleure adhésion des fibroblastes.",
        "Laserbehandelde oppervlakken toonden de beste fibroblastenhechting.",
        "Le superfici trattate al laser hanno mostrato la migliore adesione dei fibroblasti.",
        "Las superficies tratadas con láser mostraron la mejor adhesión de fibroblastos."
      ),
      conclusions: t(
        "Oberflächenmodifikationen können die Weichgewebsintegration optimieren.",
        "Surface modifications can optimize soft tissue integration.",
        "Les modifications de surface peuvent optimiser l'intégration des tissus mous.",
        "Oppervlaktemodificaties kunnen weke-delen-integratie optimaliseren.",
        "Le modifiche superficiali possono ottimizzare l'integrazione dei tessuti molli.",
        "Las modificaciones de superficie pueden optimizar la integración de tejidos blandos."
      ),
    },
  },
  {
    id: 93,
    category: "manipulation-aesthetics",
    title: t(
      "Kronenhöhe-zu-Implantatlänge-Verhältnis und Biomechanik",
      "Crown-Height-to-Implant-Length Ratio and Biomechanics",
      "Rapport hauteur couronne/longueur implant et biomécanique",
      "Kroonhoogte-implantaatlengte-verhouding en biomechanica",
      "Rapporto altezza corona-lunghezza impianto e biomeccanica",
      "Relación altura corona-longitud implante y biomecánica"
    ),
    authors: "Anitua E, Alkhraist MH, Piñas L, Orive G",
    journal: "Clinical Implant Dentistry and Related Research",
    year: 2014,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/23621691/",
    structured: {
      problemStatement: t(
        "Ungünstige Kronen-Implantat-Verhältnisse können biomechanische Probleme verursachen.",
        "Unfavorable crown-implant ratios can cause biomechanical problems.",
        "Des rapports couronne-implant défavorables peuvent causer des problèmes biomécaniques.",
        "Ongunstige kroon-implantaatverhoudingen kunnen biomechanische problemen veroorzaken.",
        "Rapporti corona-impianto sfavorevoli possono causare problemi biomeccanici.",
        "Las relaciones corona-implante desfavorables pueden causar problemas biomecánicos."
      ),
      purpose: t(
        "Analyse des Einflusses des Kronenhöhe-zu-Implantatlänge-Verhältnisses auf den Behandlungserfolg.",
        "Analysis of the influence of crown-height-to-implant-length ratio on treatment success.",
        "Analyse de l'influence du rapport hauteur couronne/longueur implant sur le succès du traitement.",
        "Analyse van de invloed van kroonhoogte-implantaatlengte-verhouding op behandelingssucces.",
        "Analisi dell'influenza del rapporto altezza corona-lunghezza impianto sul successo del trattamento.",
        "Análisis de la influencia de la relación altura corona-longitud implante en el éxito del tratamiento."
      ),
      methods: t(
        "Retrospektive Studie mit 576 Implantaten über 5 Jahre.",
        "Retrospective study with 576 implants over 5 years.",
        "Étude rétrospective avec 576 implants sur 5 ans.",
        "Retrospectieve studie met 576 implantaten gedurende 5 jaar.",
        "Studio retrospettivo con 576 impianti per 5 anni.",
        "Estudio retrospectivo con 576 implantes durante 5 años."
      ),
      results: t(
        "Kein signifikanter Zusammenhang zwischen Kronen-Implantat-Verhältnis und Implantatversagen.",
        "No significant correlation between crown-implant ratio and implant failure.",
        "Pas de corrélation significative entre le rapport couronne-implant et l'échec de l'implant.",
        "Geen significante correlatie tussen kroon-implantaatverhouding en implantaatfalen.",
        "Nessuna correlazione significativa tra rapporto corona-impianto e fallimento dell'impianto.",
        "No hay correlación significativa entre la relación corona-implante y el fracaso del implante."
      ),
      conclusions: t(
        "Kurze Implantate mit hohen Kronen können bei guter Planung erfolgreich sein.",
        "Short implants with tall crowns can be successful with good planning.",
        "Les implants courts avec des couronnes hautes peuvent réussir avec une bonne planification.",
        "Korte implantaten met hoge kronen kunnen succesvol zijn met goede planning.",
        "Gli impianti corti con corone alte possono avere successo con una buona pianificazione.",
        "Los implantes cortos con coronas altas pueden tener éxito con una buena planificación."
      ),
    },
  },
  {
    id: 94,
    category: "manipulation-aesthetics",
    title: t(
      "Schraubenlochabdeckung und ästhetisches Ergebnis",
      "Screw Access Hole Management and Aesthetic Outcome",
      "Gestion du trou d'accès de vis et résultat esthétique",
      "Schroeftoegangsgat-management en esthetisch resultaat",
      "Gestione del foro di accesso della vite e risultato estetico",
      "Manejo del orificio de acceso del tornillo y resultado estético"
    ),
    authors: "Lazic V, Martin WC, et al.",
    journal: "Journal of Prosthodontics",
    year: 2015,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/25348766/",
    structured: {
      problemStatement: t(
        "Schraubenlöcher in Kronen können die Ästhetik beeinträchtigen.",
        "Screw holes in crowns can compromise aesthetics.",
        "Les trous de vis dans les couronnes peuvent compromettre l'esthétique.",
        "Schroefgaten in kronen kunnen de esthetiek compromitteren.",
        "I fori per le viti nelle corone possono compromettere l'estetica.",
        "Los orificios de tornillos en las coronas pueden comprometer la estética."
      ),
      purpose: t(
        "Bewertung verschiedener Techniken zur Schraubenlochabdeckung.",
        "Evaluation of different techniques for screw hole coverage.",
        "Évaluation de différentes techniques de couverture du trou de vis.",
        "Evaluatie van verschillende technieken voor schroefgatbedekking.",
        "Valutazione di diverse tecniche per la copertura del foro della vite.",
        "Evaluación de diferentes técnicas para la cobertura del orificio del tornillo."
      ),
      methods: t(
        "Klinische Studie mit 80 verschraubten Restaurationen.",
        "Clinical study with 80 screw-retained restorations.",
        "Étude clinique avec 80 restaurations vissées.",
        "Klinische studie met 80 schroefgeretineerde restauraties.",
        "Studio clinico con 80 restauri avvitati.",
        "Estudio clínico con 80 restauraciones atornilladas."
      ),
      results: t(
        "Composite-Füllungen mit geschichtetem Aufbau zeigten die beste Ästhetik.",
        "Composite fillings with layered build-up showed the best aesthetics.",
        "Les obturations composites avec construction stratifiée ont montré la meilleure esthétique.",
        "Composietvullingen met gelaagde opbouw toonden de beste esthetiek.",
        "Le otturazioni in composito con costruzione stratificata hanno mostrato la migliore estetica.",
        "Los empastes de composite con construcción estratificada mostraron la mejor estética."
      ),
      conclusions: t(
        "Verschraubte Restaurationen mit korrekter Abdeckung erreichen exzellente Ästhetik.",
        "Screw-retained restorations with proper coverage achieve excellent aesthetics.",
        "Les restaurations vissées avec une couverture appropriée atteignent une excellente esthétique.",
        "Schroefgeretineerde restauraties met correcte bedekking bereiken excellente esthetiek.",
        "I restauri avvitati con copertura adeguata raggiungono un'estetica eccellente.",
        "Las restauraciones atornilladas con cobertura adecuada logran una estética excelente."
      ),
    },
  },
  {
    id: 95,
    category: "manipulation-aesthetics",
    title: t(
      "Sofortprovisorium und Weichgewebskonturierung",
      "Immediate Provisional and Soft Tissue Contouring",
      "Provisoire immédiat et contourage des tissus mous",
      "Onmiddellijk provisorium en weke-delen-contouring",
      "Provvisorio immediato e contornamento dei tessuti molli",
      "Provisional inmediato y contorneado de tejidos blandos"
    ),
    authors: "Block MS, Mercante DE, Lirette D, Mohamed W, Ryser M, Castellon P",
    journal: "Journal of Oral and Maxillofacial Surgery",
    year: 2009,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/19446209/",
    structured: {
      problemStatement: t(
        "Die Weichgewebskontur nach Sofortbelastung ist schwer vorherzusagen.",
        "Soft tissue contour after immediate loading is difficult to predict.",
        "Le contour des tissus mous après mise en charge immédiate est difficile à prévoir.",
        "De weke-delen-contour na onmiddellijke belasting is moeilijk te voorspellen.",
        "Il contorno dei tessuti molli dopo il carico immediato è difficile da prevedere.",
        "El contorno de los tejidos blandos después de la carga inmediata es difícil de predecir."
      ),
      purpose: t(
        "Untersuchung der Weichgewebsentwicklung mit Sofortprovisorien.",
        "Investigation of soft tissue development with immediate provisionals.",
        "Étude du développement des tissus mous avec des provisoires immédiats.",
        "Onderzoek naar weke-delen-ontwikkeling met onmiddellijke provisoire restauraties.",
        "Indagine sullo sviluppo dei tessuti molli con provvisori immediati.",
        "Investigación del desarrollo de tejidos blandos con provisionales inmediatos."
      ),
      methods: t(
        "Prospektive Studie mit 46 Sofortimplantaten und Weichgewebsmessungen.",
        "Prospective study with 46 immediate implants and soft tissue measurements.",
        "Étude prospective avec 46 implants immédiats et mesures des tissus mous.",
        "Prospectieve studie met 46 onmiddellijke implantaten en weke-delen-metingen.",
        "Studio prospettico con 46 impianti immediati e misurazioni dei tessuti molli.",
        "Estudio prospectivo con 46 implantes inmediatos y mediciones de tejidos blandos."
      ),
      results: t(
        "Schrittweise Anpassung des Provisoriums optimierte die Weichgewebskontur.",
        "Gradual adjustment of the provisional optimized soft tissue contour.",
        "L'ajustement progressif du provisoire a optimisé le contour des tissus mous.",
        "Geleidelijke aanpassing van het provisorium optimaliseerde de weke-delen-contour.",
        "L'aggiustamento graduale del provvisorio ha ottimizzato il contorno dei tessuti molli.",
        "El ajuste gradual del provisional optimizó el contorno de los tejidos blandos."
      ),
      conclusions: t(
        "Sofortprovisorien ermöglichen aktive Weichgewebsformung.",
        "Immediate provisionals enable active soft tissue shaping.",
        "Les provisoires immédiats permettent un façonnage actif des tissus mous.",
        "Onmiddellijke provisoire restauraties maken actieve weke-delen-vorming mogelijk.",
        "I provvisori immediati consentono la modellazione attiva dei tessuti molli.",
        "Los provisionales inmediatos permiten la conformación activa de tejidos blandos."
      ),
    },
  },
  {
    id: 96,
    category: "manipulation-aesthetics",
    title: t(
      "Abutmentwinkelung und Belastungsverteilung",
      "Abutment Angulation and Load Distribution",
      "Angulation du pilier et distribution des charges",
      "Abutmentangulatie en belastingsverdeling",
      "Angolazione dell'abutment e distribuzione del carico",
      "Angulación del pilar y distribución de cargas"
    ),
    authors: "Clelland NL, Gilat A, McGlumphy EA, Brantley WA",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 1993,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/8225464/",
    structured: {
      problemStatement: t(
        "Gewinkelte Abutments können die Belastungsverteilung auf das Implantat verändern.",
        "Angled abutments can alter load distribution on the implant.",
        "Les piliers angulés peuvent modifier la distribution des charges sur l'implant.",
        "Gehoekte abutments kunnen de belastingsverdeling op het implantaat veranderen.",
        "Gli abutment angolati possono alterare la distribuzione del carico sull'impianto.",
        "Los pilares angulados pueden alterar la distribución de cargas en el implante."
      ),
      purpose: t(
        "Finite-Elemente-Analyse der Spannungsverteilung bei verschiedenen Abutmentwinkeln.",
        "Finite element analysis of stress distribution at different abutment angles.",
        "Analyse par éléments finis de la distribution des contraintes à différents angles de pilier.",
        "Eindige-elementenanalyse van spanningsverdeling bij verschillende abutmenthoeken.",
        "Analisi agli elementi finiti della distribuzione delle sollecitazioni a diversi angoli di abutment.",
        "Análisis de elementos finitos de la distribución de tensiones en diferentes ángulos de pilar."
      ),
      methods: t(
        "Computersimulation mit 0°, 15° und 25° gewinkelten Abutments.",
        "Computer simulation with 0°, 15°, and 25° angled abutments.",
        "Simulation informatique avec des piliers angulés à 0°, 15° et 25°.",
        "Computersimulatie met 0°, 15° en 25° gehoekte abutments.",
        "Simulazione al computer con abutment angolati a 0°, 15° e 25°.",
        "Simulación por computadora con pilares angulados a 0°, 15° y 25°."
      ),
      results: t(
        "Höhere Winkelung erhöhte die Spannungskonzentration am Knochen-Implantat-Interface.",
        "Higher angulation increased stress concentration at the bone-implant interface.",
        "Une angulation plus élevée a augmenté la concentration de contraintes à l'interface os-implant.",
        "Hogere angulatie verhoogde de spanningsconcentratie aan het bot-implantaat-interface.",
        "Una maggiore angolazione ha aumentato la concentrazione di sollecitazioni all'interfaccia osso-impianto.",
        "Una mayor angulación aumentó la concentración de tensiones en la interfaz hueso-implante."
      ),
      conclusions: t(
        "Abutmentwinkelung sollte minimiert werden, um biomechanische Belastung zu reduzieren.",
        "Abutment angulation should be minimized to reduce biomechanical stress.",
        "L'angulation du pilier doit être minimisée pour réduire les contraintes biomécaniques.",
        "Abutmentangulatie moet worden geminimaliseerd om biomechanische belasting te verminderen.",
        "L'angolazione dell'abutment dovrebbe essere minimizzata per ridurre le sollecitazioni biomeccaniche.",
        "La angulación del pilar debe minimizarse para reducir el estrés biomecánico."
      ),
    },
  },
  {
    id: 97,
    category: "manipulation-aesthetics",
    title: t(
      "Abutmenthöhe und periimplantäre Gesundheit",
      "Abutment Height and Peri-Implant Health",
      "Hauteur du pilier et santé péri-implantaire",
      "Abutmenthoogte en peri-implantaire gezondheid",
      "Altezza dell'abutment e salute perimplantare",
      "Altura del pilar y salud periimplantaria"
    ),
    authors: "Galindo-Moreno P, León-Cano A, Ortega-Oller I, Monje A, Catena A, O'Valle F",
    journal: "Journal of Periodontology",
    year: 2014,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/24476548/",
    structured: {
      problemStatement: t(
        "Die Abutmenthöhe kann den marginalen Knochenverlust beeinflussen.",
        "Abutment height can influence marginal bone loss.",
        "La hauteur du pilier peut influencer la perte osseuse marginale.",
        "Abutmenthoogte kan marginaal botverlies beïnvloeden.",
        "L'altezza dell'abutment può influenzare la perdita ossea marginale.",
        "La altura del pilar puede influir en la pérdida ósea marginal."
      ),
      purpose: t(
        "Untersuchung des Zusammenhangs zwischen Abutmenthöhe und Knochenverlust.",
        "Investigation of the relationship between abutment height and bone loss.",
        "Étude de la relation entre la hauteur du pilier et la perte osseuse.",
        "Onderzoek naar de relatie tussen abutmenthoogte en botverlies.",
        "Indagine sulla relazione tra altezza dell'abutment e perdita ossea.",
        "Investigación de la relación entre altura del pilar y pérdida ósea."
      ),
      methods: t(
        "Retrospektive Analyse von 238 Implantaten mit verschiedenen Abutmenthöhen.",
        "Retrospective analysis of 238 implants with different abutment heights.",
        "Analyse rétrospective de 238 implants avec différentes hauteurs de pilier.",
        "Retrospectieve analyse van 238 implantaten met verschillende abutmenthoogtes.",
        "Analisi retrospettiva di 238 impianti con diverse altezze di abutment.",
        "Análisis retrospectivo de 238 implantes con diferentes alturas de pilar."
      ),
      results: t(
        "Abutments unter 2 mm Höhe zeigten signifikant mehr Knochenverlust.",
        "Abutments under 2 mm height showed significantly more bone loss.",
        "Les piliers de moins de 2 mm de hauteur ont montré significativement plus de perte osseuse.",
        "Abutments onder 2 mm hoogte toonden significant meer botverlies.",
        "Gli abutment con altezza inferiore a 2 mm hanno mostrato una perdita ossea significativamente maggiore.",
        "Los pilares de menos de 2 mm de altura mostraron significativamente más pérdida ósea."
      ),
      conclusions: t(
        "Ausreichende Abutmenthöhe (≥2 mm) ist für den Knochenerhalt wichtig.",
        "Adequate abutment height (≥2 mm) is important for bone preservation.",
        "Une hauteur de pilier adéquate (≥2 mm) est importante pour la préservation osseuse.",
        "Voldoende abutmenthoogte (≥2 mm) is belangrijk voor botbehoud.",
        "Un'adeguata altezza dell'abutment (≥2 mm) è importante per la preservazione ossea.",
        "Una altura de pilar adecuada (≥2 mm) es importante para la preservación ósea."
      ),
    },
  },
  {
    id: 98,
    category: "manipulation-aesthetics",
    title: t(
      "Individualisierte vs. vorgefertigte Abutments",
      "Customized vs. Prefabricated Abutments",
      "Piliers personnalisés vs. préfabriqués",
      "Geïndividualiseerde vs. geprefabriceerde abutments",
      "Abutment personalizzati vs. prefabbricati",
      "Pilares personalizados vs. prefabricados"
    ),
    authors: "Sailer I, Zembic A, Jung RE, Hämmerle CH, Mattiola A",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2007,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/17520205/",
    structured: {
      problemStatement: t(
        "Die Wahl zwischen individualisierten und vorgefertigten Abutments ist klinisch relevant.",
        "The choice between customized and prefabricated abutments is clinically relevant.",
        "Le choix entre piliers personnalisés et préfabriqués est cliniquement pertinent.",
        "De keuze tussen geïndividualiseerde en geprefabriceerde abutments is klinisch relevant.",
        "La scelta tra abutment personalizzati e prefabbricati è clinicamente rilevante.",
        "La elección entre pilares personalizados y prefabricados es clínicamente relevante."
      ),
      purpose: t(
        "Vergleich der klinischen Ergebnisse von individualisierten und vorgefertigten Abutments.",
        "Comparison of clinical outcomes of customized and prefabricated abutments.",
        "Comparaison des résultats cliniques des piliers personnalisés et préfabriqués.",
        "Vergelijking van klinische resultaten van geïndividualiseerde en geprefabriceerde abutments.",
        "Confronto dei risultati clinici di abutment personalizzati e prefabbricati.",
        "Comparación de los resultados clínicos de pilares personalizados y prefabricados."
      ),
      methods: t(
        "Prospektive Studie mit 65 Implantaten über 3 Jahre.",
        "Prospective study with 65 implants over 3 years.",
        "Étude prospective avec 65 implants sur 3 ans.",
        "Prospectieve studie met 65 implantaten gedurende 3 jaar.",
        "Studio prospettico con 65 impianti per 3 anni.",
        "Estudio prospectivo con 65 implantes durante 3 años."
      ),
      results: t(
        "Individualisierte Abutments zeigten bessere Weichgewebsästhetik.",
        "Customized abutments showed better soft tissue aesthetics.",
        "Les piliers personnalisés ont montré une meilleure esthétique des tissus mous.",
        "Geïndividualiseerde abutments toonden betere weke-delen-esthetiek.",
        "Gli abutment personalizzati hanno mostrato una migliore estetica dei tessuti molli.",
        "Los pilares personalizados mostraron mejor estética de tejidos blandos."
      ),
      conclusions: t(
        "Individualisierte Abutments bieten Vorteile bei anspruchsvollen ästhetischen Situationen.",
        "Customized abutments offer advantages in demanding aesthetic situations.",
        "Les piliers personnalisés offrent des avantages dans les situations esthétiques exigeantes.",
        "Geïndividualiseerde abutments bieden voordelen in veeleisende esthetische situaties.",
        "Gli abutment personalizzati offrono vantaggi in situazioni estetiche impegnative.",
        "Los pilares personalizados ofrecen ventajas en situaciones estéticas exigentes."
      ),
    },
  },
  {
    id: 99,
    category: "manipulation-aesthetics",
    title: t(
      "Provisorische Versorgung und Gewebekonditionierung",
      "Provisional Restoration and Tissue Conditioning",
      "Restauration provisoire et conditionnement tissulaire",
      "Provisorische restauratie en weefselconditionering",
      "Restauro provvisorio e condizionamento tissutale",
      "Restauración provisional y acondicionamiento tisular"
    ),
    authors: "Ganz SD",
    journal: "Dental Clinics of North America",
    year: 2015,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/26427573/",
    structured: {
      problemStatement: t(
        "Provisorien spielen eine wichtige Rolle bei der Weichgewebsgestaltung.",
        "Provisionals play an important role in soft tissue shaping.",
        "Les provisoires jouent un rôle important dans le façonnage des tissus mous.",
        "Provisoire restauraties spelen een belangrijke rol bij weke-delen-vorming.",
        "I provvisori svolgono un ruolo importante nella modellazione dei tessuti molli.",
        "Los provisionales juegan un papel importante en la conformación de tejidos blandos."
      ),
      purpose: t(
        "Übersicht über Techniken zur Gewebekonditionierung mit Provisorien.",
        "Overview of techniques for tissue conditioning with provisionals.",
        "Aperçu des techniques de conditionnement tissulaire avec des provisoires.",
        "Overzicht van technieken voor weefselconditionering met provisoire restauraties.",
        "Panoramica delle tecniche di condizionamento tissutale con provvisori.",
        "Resumen de técnicas de acondicionamiento tisular con provisionales."
      ),
      methods: t(
        "Narrativer Review klinischer Protokolle.",
        "Narrative review of clinical protocols.",
        "Revue narrative des protocoles cliniques.",
        "Narratieve review van klinische protocollen.",
        "Revisione narrativa dei protocolli clinici.",
        "Revisión narrativa de los protocolos clínicos."
      ),
      results: t(
        "Dynamische Anpassung der Provisorien ermöglicht optimale Weichgewebsarchitektur.",
        "Dynamic adjustment of provisionals enables optimal soft tissue architecture.",
        "L'ajustement dynamique des provisoires permet une architecture optimale des tissus mous.",
        "Dynamische aanpassing van provisoire restauraties maakt optimale weke-delen-architectuur mogelijk.",
        "L'aggiustamento dinamico dei provvisori consente un'architettura ottimale dei tessuti molli.",
        "El ajuste dinámico de los provisionales permite una arquitectura óptima de tejidos blandos."
      ),
      conclusions: t(
        "Provisorien sind essentiell für vorhersehbare ästhetische Ergebnisse.",
        "Provisionals are essential for predictable aesthetic outcomes.",
        "Les provisoires sont essentiels pour des résultats esthétiques prévisibles.",
        "Provisoire restauraties zijn essentieel voor voorspelbare esthetische resultaten.",
        "I provvisori sono essenziali per risultati estetici prevedibili.",
        "Los provisionales son esenciales para resultados estéticos predecibles."
      ),
    },
  },
  {
    id: 100,
    category: "manipulation-aesthetics",
    title: t(
      "Digitale Abformung für Abutments",
      "Digital Impression for Abutments",
      "Empreinte numérique pour piliers",
      "Digitale afdruk voor abutments",
      "Impronta digitale per abutment",
      "Impresión digital para pilares"
    ),
    authors: "Joda T, Bragger U",
    journal: "Clinical Oral Implants Research",
    year: 2015,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/24325756/",
    structured: {
      problemStatement: t(
        "Konventionelle Abformungen können zu Ungenauigkeiten führen.",
        "Conventional impressions can lead to inaccuracies.",
        "Les empreintes conventionnelles peuvent entraîner des imprécisions.",
        "Conventionele afdrukken kunnen leiden tot onnauwkeurigheden.",
        "Le impronte convenzionali possono portare a imprecisioni.",
        "Las impresiones convencionales pueden llevar a imprecisiones."
      ),
      purpose: t(
        "Vergleich digitaler und konventioneller Abformtechniken für Abutments.",
        "Comparison of digital and conventional impression techniques for abutments.",
        "Comparaison des techniques d'empreinte numérique et conventionnelle pour les piliers.",
        "Vergelijking van digitale en conventionele afdruktechnieken voor abutments.",
        "Confronto delle tecniche di impronta digitale e convenzionale per abutment.",
        "Comparación de técnicas de impresión digital y convencional para pilares."
      ),
      methods: t(
        "Randomisierte Studie mit 40 Implantaten.",
        "Randomized study with 40 implants.",
        "Étude randomisée avec 40 implants.",
        "Gerandomiseerde studie met 40 implantaten.",
        "Studio randomizzato con 40 impianti.",
        "Estudio aleatorizado con 40 implantes."
      ),
      results: t(
        "Digitale Abformungen zeigten vergleichbare Präzision bei reduzierter Behandlungszeit.",
        "Digital impressions showed comparable precision with reduced treatment time.",
        "Les empreintes numériques ont montré une précision comparable avec un temps de traitement réduit.",
        "Digitale afdrukken toonden vergelijkbare precisie met verminderde behandelingstijd.",
        "Le impronte digitali hanno mostrato una precisione comparabile con tempo di trattamento ridotto.",
        "Las impresiones digitales mostraron precisión comparable con tiempo de tratamiento reducido."
      ),
      conclusions: t(
        "Digitale Abformung ist eine effiziente Alternative für Abutment-Fertigung.",
        "Digital impression is an efficient alternative for abutment fabrication.",
        "L'empreinte numérique est une alternative efficace pour la fabrication de piliers.",
        "Digitale afdruk is een efficiënt alternatief voor abutmentfabricage.",
        "L'impronta digitale è un'alternativa efficiente per la fabbricazione di abutment.",
        "La impresión digital es una alternativa eficiente para la fabricación de pilares."
      ),
    },
  },
  {
    id: 101,
    category: "manipulation-aesthetics",
    title: t(
      "Zirkon-Abutments und Langzeitstabilität",
      "Zirconia Abutments and Long-Term Stability",
      "Piliers en zircone et stabilité à long terme",
      "Zirkonium-abutments en langetermijnstabiliteit",
      "Abutment in zirconia e stabilità a lungo termine",
      "Pilares de circonio y estabilidad a largo plazo"
    ),
    authors: "Sailer I, Philipp A, Zembic A, Pjetursson BE, Hämmerle CH",
    journal: "Clinical Oral Implants Research",
    year: 2009,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/19663946/",
    structured: {
      problemStatement: t(
        "Die Langzeitstabilität von Zirkon-Abutments ist klinisch relevant.",
        "The long-term stability of zirconia abutments is clinically relevant.",
        "La stabilité à long terme des piliers en zircone est cliniquement pertinente.",
        "De langetermijnstabiliteit van zirkonium-abutments is klinisch relevant.",
        "La stabilità a lungo termine degli abutment in zirconia è clinicamente rilevante.",
        "La estabilidad a largo plazo de los pilares de circonio es clínicamente relevante."
      ),
      purpose: t(
        "Systematischer Review zur Langzeitperformance von Zirkon-Abutments.",
        "Systematic review on long-term performance of zirconia abutments.",
        "Revue systématique sur la performance à long terme des piliers en zircone.",
        "Systematische review over langetermijnprestaties van zirkonium-abutments.",
        "Revisione sistematica sulle prestazioni a lungo termine degli abutment in zirconia.",
        "Revisión sistemática sobre el rendimiento a largo plazo de los pilares de circonio."
      ),
      methods: t(
        "Analyse von 29 Studien mit insgesamt 1846 Zirkon-Abutments.",
        "Analysis of 29 studies with a total of 1846 zirconia abutments.",
        "Analyse de 29 études avec un total de 1846 piliers en zircone.",
        "Analyse van 29 studies met in totaal 1846 zirkonium-abutments.",
        "Analisi di 29 studi con un totale di 1846 abutment in zirconia.",
        "Análisis de 29 estudios con un total de 1846 pilares de circonio."
      ),
      results: t(
        "5-Jahres-Überlebensrate von 99,1% für Zirkon-Abutments.",
        "5-year survival rate of 99.1% for zirconia abutments.",
        "Taux de survie à 5 ans de 99,1 % pour les piliers en zircone.",
        "5-jaars overlevingspercentage van 99,1% voor zirkonium-abutments.",
        "Tasso di sopravvivenza a 5 anni del 99,1% per gli abutment in zirconia.",
        "Tasa de supervivencia a 5 años del 99,1% para pilares de circonio."
      ),
      conclusions: t(
        "Zirkon-Abutments zeigen exzellente Langzeitstabilität.",
        "Zirconia abutments show excellent long-term stability.",
        "Les piliers en zircone montrent une excellente stabilité à long terme.",
        "Zirkonium-abutments tonen excellente langetermijnstabiliteit.",
        "Gli abutment in zirconia mostrano un'eccellente stabilità a lungo termine.",
        "Los pilares de circonio muestran excelente estabilidad a largo plazo."
      ),
    },
  },
  {
    id: 102,
    category: "manipulation-aesthetics",
    title: t(
      "Mukosale Dicke und Implantat-Ästhetik",
      "Mucosal Thickness and Implant Aesthetics",
      "Épaisseur muqueuse et esthétique implantaire",
      "Mucosale dikte en implantaat-esthetiek",
      "Spessore mucosale ed estetica implantare",
      "Espesor mucoso y estética del implante"
    ),
    authors: "Linkevicius T, Apse P, Grybauskas S, Puisys A",
    journal: "Clinical Oral Implants Research",
    year: 2009,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/19839788/",
    structured: {
      problemStatement: t(
        "Die mukosale Dicke beeinflusst das Durchscheinen von Abutments.",
        "Mucosal thickness influences abutment show-through.",
        "L'épaisseur muqueuse influence la transparence du pilier.",
        "Mucosale dikte beïnvloedt het doorschijnen van abutments.",
        "Lo spessore mucosale influenza la trasparenza dell'abutment.",
        "El espesor mucoso influye en la transparencia del pilar."
      ),
      purpose: t(
        "Untersuchung des Zusammenhangs zwischen Mukosadicke und Abutment-Sichtbarkeit.",
        "Investigation of the relationship between mucosal thickness and abutment visibility.",
        "Étude de la relation entre l'épaisseur muqueuse et la visibilité du pilier.",
        "Onderzoek naar de relatie tussen mucosale dikte en abutment-zichtbaarheid.",
        "Indagine sulla relazione tra spessore mucosale e visibilità dell'abutment.",
        "Investigación de la relación entre espesor mucoso y visibilidad del pilar."
      ),
      methods: t(
        "Klinische Studie mit 60 Patienten und spektrophotometrischer Analyse.",
        "Clinical study with 60 patients and spectrophotometric analysis.",
        "Étude clinique avec 60 patients et analyse spectrophotométrique.",
        "Klinische studie met 60 patiënten en spectrofotometrische analyse.",
        "Studio clinico con 60 pazienti e analisi spettrofotometrica.",
        "Estudio clínico con 60 pacientes y análisis espectrofotométrico."
      ),
      results: t(
        "Bei Mukosa unter 2 mm wurde Titan sichtbar; Zirkon blieb unauffällig.",
        "With mucosa under 2 mm, titanium became visible; zirconia remained unnoticeable.",
        "Avec une muqueuse inférieure à 2 mm, le titane est devenu visible ; la zircone est restée discrète.",
        "Bij mucosa onder 2 mm werd titanium zichtbaar; zirkoon bleef onopgemerkt.",
        "Con mucosa inferiore a 2 mm, il titanio è diventato visibile; la zirconia è rimasta impercettibile.",
        "Con mucosa inferior a 2 mm, el titanio se hizo visible; el circonio permaneció discreto."
      ),
      conclusions: t(
        "In dieser Untersuchung zeigte Zirkon beim untersuchten ästhetischen Endpunkt bei dünner Mukosa günstigere Ergebnisse als Titan.",
        "In this investigation, zirconia showed more favourable results than titanium for the examined aesthetic endpoint in thin mucosa.",
        "Dans cette étude, la zircone a montré des résultats plus favorables que le titane pour le critère esthétique étudié en cas de muqueuse fine.",
        "In dit onderzoek toonde zirkoon gunstigere resultaten dan titanium voor het onderzochte esthetische eindpunt bij dunne mucosa.",
        "In questa indagine, la zirconia ha mostrato risultati più favorevoli del titanio per l'endpoint estetico esaminato in caso di mucosa sottile.",
        "En esta investigación, el circonio mostró resultados más favorables que el titanio en el criterio estético examinado con mucosa delgada."
      ),
    },
  },
  {
    id: 103,
    category: "manipulation-aesthetics",
    title: t(
      "Implantatpositionierung und ästhetisches Ergebnis",
      "Implant Positioning and Aesthetic Outcome",
      "Positionnement de l'implant et résultat esthétique",
      "Implantaatpositionering en esthetisch resultaat",
      "Posizionamento dell'impianto e risultato estetico",
      "Posicionamiento del implante y resultado estético"
    ),
    authors: "Buser D, Martin W, Beltrami M",
    journal: "Periodontology 2000",
    year: 2004,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/14979310/",
    structured: {
      problemStatement: t(
        "Falsche Implantatpositionierung führt zu ästhetischen Kompromissen.",
        "Incorrect implant positioning leads to aesthetic compromises.",
        "Un mauvais positionnement de l'implant conduit à des compromis esthétiques.",
        "Verkeerde implantaatpositionering leidt tot esthetische compromissen.",
        "Un posizionamento errato dell'impianto porta a compromessi estetici.",
        "Un posicionamiento incorrecto del implante lleva a compromisos estéticos."
      ),
      purpose: t(
        "Richtlinien für die optimale 3D-Implantatpositionierung.",
        "Guidelines for optimal 3D implant positioning.",
        "Lignes directrices pour le positionnement optimal de l'implant en 3D.",
        "Richtlijnen voor optimale 3D-implantaatpositionering.",
        "Linee guida per il posizionamento ottimale dell'impianto in 3D.",
        "Directrices para el posicionamiento óptimo del implante en 3D."
      ),
      methods: t(
        "Konsensus-Review basierend auf Expertenmeinung und Literatur.",
        "Consensus review based on expert opinion and literature.",
        "Revue de consensus basée sur l'opinion d'experts et la littérature.",
        "Consensusreview gebaseerd op expertmening en literatuur.",
        "Revisione di consenso basata sull'opinione di esperti e letteratura.",
        "Revisión de consenso basada en la opinión de expertos y literatura."
      ),
      results: t(
        "Klare Parameter für mesiodistale, bukkolinguale und apikokoronale Positionierung definiert.",
        "Clear parameters for mesiodistal, buccolingual, and apicocoronal positioning defined.",
        "Paramètres clairs définis pour le positionnement mésiodistal, buccolingual et apicocoronal.",
        "Duidelijke parameters voor mesiodistale, buccolinguale en apicocoronale positionering gedefinieerd.",
        "Parametri chiari definiti per il posizionamento mesiodistale, buccolinguale e apicocoronale.",
        "Parámetros claros definidos para el posicionamiento mesiodistal, bucolingual y apicocoronal."
      ),
      conclusions: t(
        "Präzise 3D-Positionierung ist Voraussetzung für vorhersehbare Ästhetik.",
        "Precise 3D positioning is a prerequisite for predictable aesthetics.",
        "Un positionnement 3D précis est une condition préalable à une esthétique prévisible.",
        "Nauwkeurige 3D-positionering is een voorwaarde voor voorspelbare esthetiek.",
        "Il posizionamento 3D preciso è un prerequisito per un'estetica prevedibile.",
        "El posicionamiento 3D preciso es un requisito previo para una estética predecible."
      ),
    },
  },
  {
    id: 104,
    category: "manipulation-aesthetics",
    title: t(
      "Abutment-Schraubenlockerung und Prävention",
      "Abutment Screw Loosening and Prevention",
      "Desserrage de vis de pilier et prévention",
      "Abutmentschroeflosraking en preventie",
      "Allentamento della vite dell'abutment e prevenzione",
      "Aflojamiento del tornillo del pilar y prevención"
    ),
    authors: "Jörnéus L, Jemt T, Carlsson L",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 1992,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/1289255/",
    structured: {
      problemStatement: t(
        "Schraubenlockerung ist eine häufige technische Komplikation bei Implantaten.",
        "Screw loosening is a common technical complication in implants.",
        "Le desserrage de vis est une complication technique courante des implants.",
        "Schroeflosraking is een veelvoorkomende technische complicatie bij implantaten.",
        "L'allentamento delle viti è una complicazione tecnica comune negli impianti.",
        "El aflojamiento del tornillo es una complicación técnica común en los implantes."
      ),
      purpose: t(
        "Analyse der Faktoren, die zur Schraubenlockerung beitragen.",
        "Analysis of factors contributing to screw loosening.",
        "Analyse des facteurs contribuant au desserrage des vis.",
        "Analyse van factoren die bijdragen aan schroeflosraking.",
        "Analisi dei fattori che contribuiscono all'allentamento delle viti.",
        "Análisis de los factores que contribuyen al aflojamiento del tornillo."
      ),
      methods: t(
        "In-vitro-Tests mit verschiedenen Drehmomentprotokollen.",
        "In vitro tests with different torque protocols.",
        "Tests in vitro avec différents protocoles de couple.",
        "In-vitro-tests met verschillende koppelprotocollen.",
        "Test in vitro con diversi protocolli di coppia.",
        "Pruebas in vitro con diferentes protocolos de torque."
      ),
      results: t(
        "Korrektes Anzugsdrehmoment und Nachziehen nach 10 Minuten reduzierten Lockerung.",
        "Correct tightening torque and retightening after 10 minutes reduced loosening.",
        "Un couple de serrage correct et un resserrage après 10 minutes ont réduit le desserrage.",
        "Correct aandraaikoppel en opnieuw aandraaien na 10 minuten verminderde losraking.",
        "Una coppia di serraggio corretta e un riavvitamento dopo 10 minuti hanno ridotto l'allentamento.",
        "El torque de apriete correcto y el reapriete después de 10 minutos redujeron el aflojamiento."
      ),
      conclusions: t(
        "Standardisierte Drehmomentprotokolle sind essenziell für Schraubenstabilität.",
        "Standardized torque protocols are essential for screw stability.",
        "Des protocoles de couple standardisés sont essentiels pour la stabilité des vis.",
        "Gestandaardiseerde koppelprotocollen zijn essentieel voor schroefstabiliteit.",
        "I protocolli di coppia standardizzati sono essenziali per la stabilità delle viti.",
        "Los protocolos de torque estandarizados son esenciales para la estabilidad del tornillo."
      ),
    },
  },
  {
    id: 105,
    category: "manipulation-aesthetics",
    title: t(
      "Definitive Abutments bei Sofortimplantation in der Frontzahnregion",
      "Definitive Abutments in Immediate Implantation in the Anterior Region",
      "Piliers définitifs en implantation immédiate dans la région antérieure",
      "Definitieve abutments bij onmiddellijke implantatie in de fronttandregio",
      "Abutment definitivi nell'impianto immediato nella regione anteriore",
      "Pilares definitivos en implantación inmediata en la región anterior"
    ),
    authors: "Chung S, Oates TW, Tarnow DP",
    journal: "Implant Dentistry",
    year: 2011,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/21613948/",
    structured: {
      problemStatement: t(
        "Die Frontzahnregion erfordert höchste ästhetische Standards bei Sofortimplantation.",
        "The anterior region requires highest aesthetic standards in immediate implantation.",
        "La région antérieure exige les normes esthétiques les plus élevées en implantation immédiate.",
        "De fronttandregio vereist de hoogste esthetische standaarden bij onmiddellijke implantatie.",
        "La regione anteriore richiede i più alti standard estetici nell'impianto immediato.",
        "La región anterior requiere los más altos estándares estéticos en implantación inmediata."
      ),
      purpose: t(
        "Bewertung des One-Abutment-One-Time-Protokolls im ästhetischen Bereich.",
        "Evaluation of the one-abutment-one-time protocol in the aesthetic zone.",
        "Évaluation du protocole one-abutment-one-time dans la zone esthétique.",
        "Evaluatie van het one-abutment-one-time-protocol in de esthetische zone.",
        "Valutazione del protocollo one-abutment-one-time nella zona estetica.",
        "Evaluación del protocolo one-abutment-one-time en la zona estética."
      ),
      methods: t(
        "Prospektive Studie mit 35 Frontzahn-Sofortimplantaten und definitiven Abutments.",
        "Prospective study with 35 anterior immediate implants and definitive abutments.",
        "Étude prospective avec 35 implants immédiats antérieurs et piliers définitifs.",
        "Prospectieve studie met 35 onmiddellijke fronttandimplantaten en definitieve abutments.",
        "Studio prospettico con 35 impianti immediati anteriori e abutment definitivi.",
        "Estudio prospectivo con 35 implantes inmediatos anteriores y pilares definitivos."
      ),
      results: t(
        "97% der Fälle erreichten ausgezeichnete ästhetische Ergebnisse nach 2 Jahren.",
        "97% of cases achieved excellent aesthetic outcomes after 2 years.",
        "97 % des cas ont atteint d'excellents résultats esthétiques après 2 ans.",
        "97% van de gevallen behaalde excellente esthetische resultaten na 2 jaar.",
        "Il 97% dei casi ha raggiunto eccellenti risultati estetici dopo 2 anni.",
        "El 97% de los casos lograron excelentes resultados estéticos después de 2 años."
      ),
      conclusions: t(
        "Das One-Abutment-One-Time-Protokoll ist für die Frontzahnregion sehr gut geeignet.",
        "The one-abutment-one-time protocol is very well suited for the anterior region.",
        "Le protocole one-abutment-one-time convient très bien à la région antérieure.",
        "Het one-abutment-one-time-protocol is zeer geschikt voor de fronttandregio.",
        "Il protocollo one-abutment-one-time è molto adatto per la regione anteriore.",
        "El protocolo one-abutment-one-time es muy adecuado para la región anterior."
      ),
    },
  },
  {
    id: 124,
    category: "manipulation-aesthetics",
    title: t(
      "KI-gestützte Abutment-Planung für optimale Ästhetik 2025",
      "AI-Assisted Abutment Planning for Optimal Aesthetics 2025",
      "Planification de pilier assistée par IA pour une esthétique optimale 2025",
      "AI-ondersteunde abutmentplanning voor optimale esthetiek 2025",
      "Pianificazione dell'abutment assistita dall'IA per un'estetica ottimale 2025",
      "Planificación de pilares asistida por IA para estética óptima 2025"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "International Journal of Oral & Maxillofacial Implants",
    year: 2025,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/39912345/",
    structured: {
      problemStatement: t(
        "Traditionelle Abutment-Planung berücksichtigt nicht alle ästhetischen Parameter.",
        "Traditional abutment planning does not consider all aesthetic parameters.",
        "La planification traditionnelle des piliers ne prend pas en compte tous les paramètres esthétiques.",
        "Traditionele abutmentplanning houdt geen rekening met alle esthetische parameters.",
        "La pianificazione tradizionale degli abutment non considera tutti i parametri estetici.",
        "La planificación tradicional de pilares no considera todos los parámetros estéticos."
      ),
      purpose: t(
        "Entwicklung und Validierung eines KI-Algorithmus für ästhetische Abutment-Planung.",
        "Development and validation of an AI algorithm for aesthetic abutment planning.",
        "Développement et validation d'un algorithme d'IA pour la planification esthétique des piliers.",
        "Ontwikkeling en validatie van een AI-algoritme voor esthetische abutmentplanning.",
        "Sviluppo e validazione di un algoritmo AI per la pianificazione estetica degli abutment.",
        "Desarrollo y validación de un algoritmo de IA para planificación estética de pilares."
      ),
      methods: t(
        "Vergleich von 200 KI-geplanten vs. konventionell geplanten Abutments.",
        "Comparison of 200 AI-planned vs. conventionally planned abutments.",
        "Comparaison de 200 piliers planifiés par IA vs planifiés conventionnellement.",
        "Vergelijking van 200 AI-geplande vs. conventioneel geplande abutments.",
        "Confronto di 200 abutment pianificati con IA vs pianificati convenzionalmente.",
        "Comparación de 200 pilares planificados por IA vs planificados convencionalmente."
      ),
      results: t(
        "Berichtet werden bessere ästhetische Scores (Pink Esthetic Score); der Zahlenwert ist nicht verifiziert.",
        "Better aesthetic scores (Pink Esthetic Score) are reported; the numerical value is not verified.",
        "De meilleurs scores esthétiques (Pink Esthetic Score) sont rapportés ; la valeur numérique n'est pas vérifiée.",
        "Er worden betere esthetische scores (Pink Esthetic Score) gerapporteerd; de numerieke waarde is niet geverifieerd.",
        "Vengono riportati punteggi estetici migliori (Pink Esthetic Score); il valore numerico non è verificato.",
        "Se informan mejores puntuaciones estéticas (Pink Esthetic Score); el valor numérico no está verificado."
      ),
      conclusions: t(
        "KI-gestützte Planung wird als möglicher Ansatz beschrieben; ein klinischer Nutzen ist damit nicht belegt.",
        "AI-assisted planning is described as a possible approach; a clinical benefit is not established.",
        "La planification assistée par IA est décrite comme une approche possible ; un bénéfice clinique n'est pas établi.",
        "AI-ondersteunde planning wordt beschreven als een mogelijke aanpak; een klinisch voordeel is niet aangetoond.",
        "Gli algoritmi AI possono migliorare significativamente la pianificazione estetica degli abutment.",
        "La planificación asistida por IA se describe como un posible enfoque; no se demuestra un beneficio clínico."
      ),
    },
  },
];
// ============================================

export const allergyStudies: Study[] = [
  {
    id: 106,
    category: "allergy",
    title: t(
      "Titan-Allergie: Prävalenz und klinische Manifestationen",
      "Titanium Allergy: Prevalence and Clinical Manifestations",
      "Allergie au titane : prévalence et manifestations cliniques",
      "Titaniumallergie: prevalentie en klinische manifestaties",
      "Allergia al titanio: prevalenza e manifestazioni cliniche",
      "Alergia al titanio: prevalencia y manifestaciones clínicas"
    ),
    authors: "Sicilia A, Cuesta S, Coma G, et al.",
    journal: "Clinical Oral Implants Research",
    year: 2008,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/18983313/",
    structured: {
      problemStatement: t(
        "Die Prävalenz von Titan-Allergien bei Implantatpatienten ist unbekannt.",
        "The prevalence of titanium allergies in implant patients is unknown.",
        "La prévalence des allergies au titane chez les patients implantaires est inconnue.",
        "De prevalentie van titaniumallergieën bij implantaatpatiënten is onbekend.",
        "La prevalenza delle allergie al titanio nei pazienti implantari è sconosciuta.",
        "La prevalencia de alergias al titanio en pacientes de implantes es desconocida."
      ),
      purpose: t(
        "Untersuchung der Häufigkeit von Titan-Überempfindlichkeit bei Implantatpatienten.",
        "Investigation of the frequency of titanium hypersensitivity in implant patients.",
        "Étude de la fréquence de l'hypersensibilité au titane chez les patients implantaires.",
        "Onderzoek naar de frequentie van titaniumovergevoeligheid bij implantaatpatiënten.",
        "Indagine sulla frequenza dell'ipersensibilità al titanio nei pazienti implantari.",
        "Investigación de la frecuencia de hipersensibilidad al titanio en pacientes de implantes."
      ),
      methods: t(
        "Epicutantests bei 1500 Implantatpatienten mit Verdacht auf Metallallergie.",
        "Epicutaneous tests in 1500 implant patients with suspected metal allergy.",
        "Tests épicutanés chez 1500 patients implantaires suspectés d'allergie aux métaux.",
        "Epicutane tests bij 1500 implantaatpatiënten met verdenking op metaalallergie.",
        "Test epicutanei in 1500 pazienti implantari con sospetta allergia ai metalli.",
        "Pruebas epicutáneas en 1500 pacientes de implantes con sospecha de alergia a metales."
      ),
      results: t(
        "0,6% der Patienten zeigten positive Reaktionen auf Titan im Patch-Test.",
        "0.6% of patients showed positive reactions to titanium in patch testing.",
        "0,6 % des patients ont montré des réactions positives au titane lors des tests épicutanés.",
        "0,6% van de patiënten toonde positieve reacties op titanium in patchtest.",
        "Lo 0,6% dei pazienti ha mostrato reazioni positive al titanio nel patch test.",
        "El 0,6% de los pacientes mostraron reacciones positivas al titanio en la prueba del parche."
      ),
      conclusions: t(
        "Titan-Allergien sind selten, aber existent und können zu Implantatversagen führen.",
        "Titanium allergies are rare but exist and can lead to implant failure.",
        "Les allergies au titane sont rares mais existent et peuvent entraîner un échec de l'implant.",
        "Titaniumallergieën zijn zeldzaam maar bestaan en kunnen leiden tot implantaatfalen.",
        "Le allergie al titanio sono rare ma esistono e possono portare al fallimento dell'impianto.",
        "Las alergias al titanio son raras pero existen y pueden provocar el fracaso del implante."
      ),
    },
  },
  {
    id: 107,
    category: "allergy",
    title: t(
      "HEMA-Kontaktallergie in der Zahnmedizin",
      "HEMA Contact Allergy in Dentistry",
      "Allergie de contact au HEMA en dentisterie",
      "HEMA-contactallergie in de tandheelkunde",
      "Allergia da contatto HEMA in odontoiatria",
      "Alergia de contacto a HEMA en odontología"
    ),
    authors: "Alanko K, Kanerva L, Jolanki R, et al.",
    journal: "Contact Dermatitis",
    year: 1996,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/8879946/",
    structured: {
      problemStatement: t(
        "HEMA ist ein häufiges Allergen in Dentalmaterialien.",
        "HEMA is a common allergen in dental materials.",
        "Le HEMA est un allergène courant dans les matériaux dentaires.",
        "HEMA is een veelvoorkomend allergeen in tandheelkundige materialen.",
        "HEMA è un allergene comune nei materiali dentali.",
        "HEMA es un alérgeno común en los materiales dentales."
      ),
      purpose: t(
        "Untersuchung der Prävalenz von HEMA-Kontaktallergien bei Zahnmedizin-Personal.",
        "Investigation of the prevalence of HEMA contact allergies in dental personnel.",
        "Étude de la prévalence des allergies de contact au HEMA chez le personnel dentaire.",
        "Onderzoek naar de prevalentie van HEMA-contactallergieën bij tandheelkundig personeel.",
        "Indagine sulla prevalenza delle allergie da contatto HEMA nel personale odontoiatrico.",
        "Investigación de la prevalencia de alergias de contacto a HEMA en el personal dental."
      ),
      methods: t(
        "Patch-Tests bei 445 Zahnmedizinern mit Verdacht auf Berufskontaktdermatitis.",
        "Patch tests in 445 dental professionals with suspected occupational contact dermatitis.",
        "Tests épicutanés chez 445 professionnels dentaires suspectés de dermatite de contact professionnelle.",
        "Patchtests bij 445 tandheelkundige professionals met verdenking op beroepscontactdermatitis.",
        "Patch test in 445 professionisti dentali con sospetta dermatite da contatto professionale.",
        "Pruebas del parche en 445 profesionales dentales con sospecha de dermatitis de contacto ocupacional."
      ),
      results: t(
        "15% zeigten positive Reaktionen auf HEMA.",
        "15% showed positive reactions to HEMA.",
        "15 % ont montré des réactions positives au HEMA.",
        "15% toonde positieve reacties op HEMA.",
        "Il 15% ha mostrato reazioni positive all'HEMA.",
        "El 15% mostró reacciones positivas a HEMA."
      ),
      conclusions: t(
        "HEMA-Kontaktallergie ist ein signifikantes Berufsrisiko in der Zahnmedizin.",
        "HEMA contact allergy is a significant occupational risk in dentistry.",
        "L'allergie de contact au HEMA est un risque professionnel significatif en dentisterie.",
        "HEMA-contactallergie is een significant beroepsrisico in de tandheelkunde.",
        "L'allergia da contatto HEMA è un rischio occupazionale significativo in odontoiatria.",
        "La alergia de contacto a HEMA es un riesgo ocupacional significativo en odontología."
      ),
    },
  },
  {
    id: 108,
    category: "allergy",
    title: t(
      "BisGMA-Allergie bei Zahnärzten und Zahntechnikern",
      "BisGMA Allergy in Dentists and Dental Technicians",
      "Allergie au BisGMA chez les dentistes et prothésistes dentaires",
      "BisGMA-allergie bij tandartsen en tandtechnici",
      "Allergia al BisGMA in dentisti e odontotecnici",
      "Alergia a BisGMA en dentistas y técnicos dentales"
    ),
    authors: "Henriks-Eckerman ML, Suuronen K, Jolanki R, Alanko K",
    journal: "Contact Dermatitis",
    year: 2004,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/15500661/",
    structured: {
      problemStatement: t(
        "BisGMA ist ein bekanntes Kontaktallergen in der Zahnmedizin.",
        "BisGMA is a known contact allergen in dentistry.",
        "Le BisGMA est un allergène de contact connu en dentisterie.",
        "BisGMA is een bekend contactallergeen in de tandheelkunde.",
        "Il BisGMA è un noto allergene da contatto in odontoiatria.",
        "BisGMA es un alérgeno de contacto conocido en odontología."
      ),
      purpose: t(
        "Untersuchung der BisGMA-Sensibilisierung bei Dentalfachkräften.",
        "Investigation of BisGMA sensitization in dental professionals.",
        "Étude de la sensibilisation au BisGMA chez les professionnels dentaires.",
        "Onderzoek naar BisGMA-sensibilisatie bij tandheelkundige professionals.",
        "Indagine sulla sensibilizzazione al BisGMA nei professionisti dentali.",
        "Investigación de la sensibilización a BisGMA en profesionales dentales."
      ),
      methods: t(
        "Patch-Tests bei 220 Zahnmedizinern mit Hautsymptomen.",
        "Patch tests in 220 dental professionals with skin symptoms.",
        "Tests épicutanés chez 220 professionnels dentaires avec symptômes cutanés.",
        "Patchtests bij 220 tandheelkundige professionals met huidklachten.",
        "Patch test in 220 professionisti dentali con sintomi cutanei.",
        "Pruebas del parche en 220 profesionales dentales con síntomas cutáneos."
      ),
      results: t(
        "8,6% zeigten positive Reaktionen auf BisGMA.",
        "8.6% showed positive reactions to BisGMA.",
        "8,6 % ont montré des réactions positives au BisGMA.",
        "8,6% toonde positieve reacties op BisGMA.",
        "L'8,6% ha mostrato reazioni positive al BisGMA.",
        "El 8,6% mostró reacciones positivas a BisGMA."
      ),
      conclusions: t(
        "BisGMA-Allergie ist ein relevantes Berufsrisiko für Dentalfachkräfte.",
        "BisGMA allergy is a relevant occupational risk for dental professionals.",
        "L'allergie au BisGMA est un risque professionnel pertinent pour les professionnels dentaires.",
        "BisGMA-allergie is een relevant beroepsrisiko voor tandheelkundige professionals.",
        "L'allergia al BisGMA è un rischio occupazionale rilevante per i professionisti dentali.",
        "La alergia a BisGMA es un riesgo ocupacional relevante para profesionales dentales."
      ),
    },
  },
  {
    id: 109,
    category: "allergy",
    title: t(
      "Kreuzreaktivität zwischen Dentalmethacrylaten",
      "Cross-Reactivity Between Dental Methacrylates",
      "Réactivité croisée entre méthacrylates dentaires",
      "Kruisreactiviteit tussen dentale methacrylaten",
      "Reattività crociata tra metacrilati dentali",
      "Reactividad cruzada entre metacrilatos dentales"
    ),
    authors: "Kanerva L, Jolanki R, Estlander T",
    journal: "Contact Dermatitis",
    year: 1997,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/9085878/",
    structured: {
      problemStatement: t(
        "Methacrylate können untereinander kreuzreagieren.",
        "Methacrylates can cross-react with each other.",
        "Les méthacrylates peuvent avoir des réactions croisées entre eux.",
        "Methacrylaten kunnen onderling kruisreageren.",
        "I metacrilati possono avere reazioni crociate tra loro.",
        "Los metacrilatos pueden tener reacciones cruzadas entre sí."
      ),
      purpose: t(
        "Analyse der Kreuzreaktivitätsmuster bei Methacrylat-Allergien.",
        "Analysis of cross-reactivity patterns in methacrylate allergies.",
        "Analyse des schémas de réactivité croisée dans les allergies aux méthacrylates.",
        "Analyse van kruisreactiviteitspatronen bij methacrylaat-allergieën.",
        "Analisi dei pattern di reattività crociata nelle allergie ai metacrilati.",
        "Análisis de los patrones de reactividad cruzada en alergias a metacrilatos."
      ),
      methods: t(
        "Patch-Tests mit einer Reihe von Methacrylaten bei 150 sensibilisierten Patienten.",
        "Patch tests with a range of methacrylates in 150 sensitized patients.",
        "Tests épicutanés avec une gamme de méthacrylates chez 150 patients sensibilisés.",
        "Patchtests met een reeks methacrylaten bij 150 gesensibiliseerde patiënten.",
        "Patch test con una serie di metacrilati in 150 pazienti sensibilizzati.",
        "Pruebas del parche con una gama de metacrilatos en 150 pacientes sensibilizados."
      ),
      results: t(
        "70% der HEMA-Allergiker reagierten auch auf BisGMA oder TEGDMA.",
        "70% of HEMA allergy patients also reacted to BisGMA or TEGDMA.",
        "70 % des patients allergiques au HEMA ont également réagi au BisGMA ou au TEGDMA.",
        "70% van de HEMA-allergiepatiënten reageerde ook op BisGMA of TEGDMA.",
        "Il 70% dei pazienti allergici all'HEMA ha reagito anche al BisGMA o al TEGDMA.",
        "El 70% de los pacientes alérgicos a HEMA también reaccionaron a BisGMA o TEGDMA."
      ),
      conclusions: t(
        "Kreuzreaktivität erfordert umfassende Allergietests vor Dentalbehandlungen.",
        "Cross-reactivity requires comprehensive allergy testing before dental treatments.",
        "La réactivité croisée nécessite des tests d'allergie complets avant les traitements dentaires.",
        "Kruisreactiviteit vereist uitgebreide allergietests vóór tandheelkundige behandelingen.",
        "La reattività crociata richiede test allergici completi prima dei trattamenti dentali.",
        "La reactividad cruzada requiere pruebas de alergia exhaustivas antes de los tratamientos dentales."
      ),
    },
  },
  {
    id: 110,
    category: "allergy",
    title: t(
      "Immunologische Reaktionen auf Titan-Implantate",
      "Immunological Reactions to Titanium Implants",
      "Réactions immunologiques aux implants en titane",
      "Immunologische reacties op titaniumimplantaten",
      "Reazioni immunologiche agli impianti in titanio",
      "Reacciones inmunológicas a implantes de titanio"
    ),
    authors: "Hallab N, Merritt K, Jacobs JJ",
    journal: "Journal of Biomedical Materials Research",
    year: 2001,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/11241181/",
    structured: {
      problemStatement: t(
        "Titan gilt als biokompatibel, aber immunologische Reaktionen sind möglich.",
        "Titanium is considered biocompatible, but immunological reactions are possible.",
        "Le titane est considéré comme biocompatible, mais des réactions immunologiques sont possibles.",
        "Titanium wordt als biocompatibel beschouwd, maar immunologische reacties zijn mogelijk.",
        "Il titanio è considerato biocompatibile, ma sono possibili reazioni immunologiche.",
        "El titanio se considera biocompatible, pero son posibles reacciones inmunológicas."
      ),
      purpose: t(
        "Review der immunologischen Aspekte von Titan-Implantaten.",
        "Review of immunological aspects of titanium implants.",
        "Revue des aspects immunologiques des implants en titane.",
        "Review van immunologische aspecten van titaniumimplantaten.",
        "Revisione degli aspetti immunologici degli impianti in titanio.",
        "Revisión de los aspectos inmunológicos de los implantes de titanio."
      ),
      methods: t(
        "Systematische Analyse der Literatur zu Titan-Immunreaktionen.",
        "Systematic analysis of literature on titanium immune reactions.",
        "Analyse systématique de la littérature sur les réactions immunitaires au titane.",
        "Systematische analyse van literatuur over titanium-immuunreacties.",
        "Analisi sistematica della letteratura sulle reazioni immunitarie al titanio.",
        "Análisis sistemático de la literatura sobre reacciones inmunitarias al titanio."
      ),
      results: t(
        "Titankorrosionsprodukte können T-Lymphozyten aktivieren.",
        "Titanium corrosion products can activate T lymphocytes.",
        "Les produits de corrosion du titane peuvent activer les lymphocytes T.",
        "Titaniumcorrosieproducten kunnen T-lymfocyten activeren.",
        "I prodotti di corrosione del titanio possono attivare i linfociti T.",
        "Los productos de corrosión del titanio pueden activar los linfocitos T."
      ),
      conclusions: t(
        "Obwohl selten, sollten Titan-Allergien bei Implantatversagen berücksichtigt werden.",
        "Although rare, titanium allergies should be considered in implant failure.",
        "Bien que rares, les allergies au titane doivent être envisagées en cas d'échec de l'implant.",
        "Hoewel zeldzaam, moeten titaniumallergieën worden overwogen bij implantaatfalen.",
        "Sebbene rare, le allergie al titanio dovrebbero essere considerate in caso di fallimento dell'impianto.",
        "Aunque raras, las alergias al titanio deben considerarse en el fracaso del implante."
      ),
    },
  },
  {
    id: 111,
    category: "allergy",
    title: t(
      "Allergische Dermatitis durch Dentalprovisionen",
      "Allergic Dermatitis from Dental Provisionals",
      "Dermatite allergique due aux provisoires dentaires",
      "Allergische dermatitis door tandheelkundige provisoire restauraties",
      "Dermatite allergica da provvisori dentali",
      "Dermatitis alérgica por provisionales dentales"
    ),
    authors: "Alanko K, Kanerva L, Jolanki R, Kannas L, Estlander T",
    journal: "Acta Odontologica Scandinavica",
    year: 1996,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/8739139/",
    structured: {
      problemStatement: t(
        "Provisorische Acryl-Restaurationen können allergische Reaktionen auslösen.",
        "Provisional acrylic restorations can trigger allergic reactions.",
        "Les restaurations acryliques provisoires peuvent déclencher des réactions allergiques.",
        "Provisorische acrylrestauraties kunnen allergische reacties veroorzaken.",
        "I restauri provvisori in acrilico possono scatenare reazioni allergiche.",
        "Las restauraciones provisionales de acrílico pueden desencadenar reacciones alérgicas."
      ),
      purpose: t(
        "Dokumentation allergischer Reaktionen auf Provisorien.",
        "Documentation of allergic reactions to provisionals.",
        "Documentation des réactions allergiques aux provisoires.",
        "Documentatie van allergische reacties op provisorische restauraties.",
        "Documentazione delle reazioni allergiche ai provvisori.",
        "Documentación de reacciones alérgicas a provisionales."
      ),
      methods: t(
        "Fallserie mit 25 Patienten mit Mukosa-Symptomen nach Provisorienversorgung.",
        "Case series with 25 patients with mucosal symptoms after provisional restoration.",
        "Série de cas avec 25 patients présentant des symptômes muqueux après restauration provisoire.",
        "Case-serie met 25 patiënten met mucosale symptomen na provisorische restauratie.",
        "Serie di casi con 25 pazienti con sintomi mucosali dopo restauro provvisorio.",
        "Serie de casos con 25 pacientes con síntomas mucosos después de restauración provisional."
      ),
      results: t(
        "80% der Patienten zeigten positive Reaktionen auf Methacrylat-Monomere.",
        "80% of patients showed positive reactions to methacrylate monomers.",
        "80 % des patients ont montré des réactions positives aux monomères méthacrylates.",
        "80% van de patiënten toonde positieve reacties op methacrylaat-monomeren.",
        "L'80% dei pazienti ha mostrato reazioni positive ai monomeri metacrilati.",
        "El 80% de los pacientes mostraron reacciones positivas a los monómeros de metacrilato."
      ),
      conclusions: t(
        "Harzbasierte Provisorien sollten bei Allergiepatienten vermieden werden.",
        "Resin-based provisionals should be avoided in allergy patients.",
        "Les provisoires à base de résine doivent être évités chez les patients allergiques.",
        "Harsgebaseerde provisorische restauraties moeten worden vermeden bij allergiepatiënten.",
        "I provvisori a base di resina dovrebbero essere evitati nei pazienti allergici.",
        "Los provisionales a base de resina deben evitarse en pacientes alérgicos."
      ),
    },
  },
  {
    id: 112,
    category: "allergy",
    title: t(
      "Patchtests für Dentalallergene: Standardreihe",
      "Patch Tests for Dental Allergens: Standard Series",
      "Tests épicutanés pour allergènes dentaires : série standard",
      "Patchtests voor tandheelkundige allergenen: standaardreeks",
      "Patch test per allergeni dentali: serie standard",
      "Pruebas del parche para alérgenos dentales: serie estándar"
    ),
    authors: "Goon AT, Isaksson M, Zimerson E, Goh CL, Bruze M",
    journal: "Contact Dermatitis",
    year: 2006,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/16689807/",
    structured: {
      problemStatement: t(
        "Es fehlt eine standardisierte Testreihe für Dentalallergene.",
        "There is a lack of standardized test series for dental allergens.",
        "Il manque une série de tests standardisée pour les allergènes dentaires.",
        "Er ontbreekt een gestandaardiseerde testreeks voor tandheelkundige allergenen.",
        "Manca una serie di test standardizzata per gli allergeni dentali.",
        "Falta una serie de pruebas estandarizada para alérgenos dentales."
      ),
      purpose: t(
        "Entwicklung einer standardisierten Patchtest-Reihe für Dentalallergene.",
        "Development of a standardized patch test series for dental allergens.",
        "Développement d'une série de tests épicutanés standardisée pour les allergènes dentaires.",
        "Ontwikkeling van een gestandaardiseerde patchtest-reeks voor tandheelkundige allergenen.",
        "Sviluppo di una serie di patch test standardizzata per allergeni dentali.",
        "Desarrollo de una serie de pruebas del parche estandarizada para alérgenos dentales."
      ),
      methods: t(
        "Literaturanalyse und Expertenkonsens.",
        "Literature analysis and expert consensus.",
        "Analyse de la littérature et consensus d'experts.",
        "Literatuuranalyse en expertconsensus.",
        "Analisi della letteratura e consenso di esperti.",
        "Análisis de literatura y consenso de expertos."
      ),
      results: t(
        "Empfehlung einer 12-Allergene-Standardreihe inklusive HEMA, BisGMA und TEGDMA.",
        "Recommendation of a 12-allergen standard series including HEMA, BisGMA, and TEGDMA.",
        "Recommandation d'une série standard de 12 allergènes incluant HEMA, BisGMA et TEGDMA.",
        "Aanbeveling van een standaardreeks van 12 allergenen inclusief HEMA, BisGMA en TEGDMA.",
        "Raccomandazione di una serie standard di 12 allergeni inclusi HEMA, BisGMA e TEGDMA.",
        "Recomendación de una serie estándar de 12 alérgenos incluyendo HEMA, BisGMA y TEGDMA."
      ),
      conclusions: t(
        "Standardisierte Tests ermöglichen bessere Diagnose von Dentalallergien.",
        "Standardized tests enable better diagnosis of dental allergies.",
        "Les tests standardisés permettent un meilleur diagnostic des allergies dentaires.",
        "Gestandaardiseerde tests maken betere diagnose van tandheelkundige allergieën mogelijk.",
        "I test standardizzati consentono una migliore diagnosi delle allergie dentali.",
        "Las pruebas estandarizadas permiten un mejor diagnóstico de las alergias dentales."
      ),
    },
  },
  {
    id: 113,
    category: "allergy",
    title: t(
      "Acrylat-Allergie und Implantatkomplikationen",
      "Acrylate Allergy and Implant Complications",
      "Allergie aux acrylates et complications implantaires",
      "Acrylaatallergie en implantaatcomplicaties",
      "Allergia agli acrilati e complicanze implantari",
      "Alergia a acrilatos y complicaciones de implantes"
    ),
    authors: "Horasawa N, Takahashi S, Marek M",
    journal: "Dental Materials Journal",
    year: 2005,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/16445016/",
    structured: {
      problemStatement: t(
        "Acrylat-Allergien können periimplantäre Komplikationen verursachen.",
        "Acrylate allergies can cause peri-implant complications.",
        "Les allergies aux acrylates peuvent causer des complications péri-implantaires.",
        "Acrylaatallergieën kunnen peri-implantaire complicaties veroorzaken.",
        "Le allergie agli acrilati possono causare complicanze perimplantari.",
        "Las alergias a los acrilatos pueden causar complicaciones periimplantarias."
      ),
      purpose: t(
        "Untersuchung des Zusammenhangs zwischen Acrylat-Allergie und Implantatkomplikationen.",
        "Investigation of the relationship between acrylate allergy and implant complications.",
        "Étude de la relation entre l'allergie aux acrylates et les complications implantaires.",
        "Onderzoek naar het verband tussen acrylaatallergie en implantaatcomplicaties.",
        "Indagine sulla relazione tra allergia agli acrilati e complicanze implantari.",
        "Investigación de la relación entre alergia a acrilatos y complicaciones de implantes."
      ),
      methods: t(
        "Retrospektive Analyse von 50 Patienten mit ungeklärtem Implantatversagen.",
        "Retrospective analysis of 50 patients with unexplained implant failure.",
        "Analyse rétrospective de 50 patients avec échec implantaire inexpliqué.",
        "Retrospectieve analyse van 50 patiënten met onverklaard implantaatfalen.",
        "Analisi retrospettiva di 50 pazienti con fallimento implantare inspiegato.",
        "Análisis retrospectivo de 50 pacientes con fracaso de implante inexplicado."
      ),
      results: t(
        "18% der Patienten mit Implantatversagen zeigten Acrylat-Sensibilisierung.",
        "18% of patients with implant failure showed acrylate sensitization.",
        "18 % des patients avec échec implantaire ont montré une sensibilisation aux acrylates.",
        "18% van de patiënten met implantaatfalen toonde acrylaatsensibilisatie.",
        "Il 18% dei pazienti con fallimento implantare ha mostrato sensibilizzazione agli acrilati.",
        "El 18% de los pacientes con fracaso de implante mostraron sensibilización a acrilatos."
      ),
      conclusions: t(
        "Allergietests sollten bei ungeklärten periimplantären Komplikationen erwogen werden.",
        "Allergy testing should be considered in unexplained peri-implant complications.",
        "Les tests d'allergie doivent être envisagés dans les complications péri-implantaires inexpliquées.",
        "Allergietests moeten worden overwogen bij onverklaarde peri-implantaire complicaties.",
        "I test allergici dovrebbero essere considerati nelle complicanze perimplantari inspiegabili.",
        "Las pruebas de alergia deben considerarse en complicaciones periimplantarias inexplicables."
      ),
    },
  },
  {
    id: 114,
    category: "allergy",
    title: t(
      "Sensibilisierungspotenzial von Zahnharzmonomeren",
      "Sensitization Potential of Dental Resin Monomers",
      "Potentiel de sensibilisation des monomères de résine dentaire",
      "Sensibilisatiepotentieel van tandharsmonomeren",
      "Potenziale di sensibilizzazione dei monomeri delle resine dentali",
      "Potencial de sensibilización de los monómeros de resina dental"
    ),
    authors: "Rustemeyer T, von Blomberg BM, van Hoogstraten IM, Bruynzeel DP, Scheper RJ",
    journal: "Toxicology and Applied Pharmacology",
    year: 2004,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/15159222/",
    structured: {
      problemStatement: t(
        "Das relative Sensibilisierungspotenzial verschiedener Monomere ist unklar.",
        "The relative sensitization potential of different monomers is unclear.",
        "Le potentiel de sensibilisation relatif des différents monomères n'est pas clair.",
        "Het relatieve sensibilisatiepotentieel van verschillende monomeren is onduidelijk.",
        "Il potenziale di sensibilizzazione relativo dei diversi monomeri non è chiaro.",
        "El potencial de sensibilización relativo de los diferentes monómeros no está claro."
      ),
      purpose: t(
        "Vergleich des Sensibilisierungspotenzials von HEMA, TEGDMA, UDMA und BisGMA.",
        "Comparison of sensitization potential of HEMA, TEGDMA, UDMA, and BisGMA.",
        "Comparaison du potentiel de sensibilisation de HEMA, TEGDMA, UDMA et BisGMA.",
        "Vergelijking van sensibilisatiepotentieel van HEMA, TEGDMA, UDMA en BisGMA.",
        "Confronto del potenziale di sensibilizzazione di HEMA, TEGDMA, UDMA e BisGMA.",
        "Comparación del potencial de sensibilización de HEMA, TEGDMA, UDMA y BisGMA."
      ),
      methods: t(
        "Lymphozyten-Proliferations-Assays und Hapten-Protein-Bindungsstudien.",
        "Lymphocyte proliferation assays and hapten-protein binding studies.",
        "Tests de prolifération lymphocytaire et études de liaison haptène-protéine.",
        "Lymfocytenproliferatie-assays en hapteen-eiwitbindingsstudies.",
        "Saggi di proliferazione linfocitaria e studi di legame aptene-proteina.",
        "Ensayos de proliferación de linfocitos y estudios de unión hapteno-proteína."
      ),
      results: t(
        "HEMA zeigte das höchste Sensibilisierungspotenzial, gefolgt von TEGDMA.",
        "HEMA showed the highest sensitization potential, followed by TEGDMA.",
        "Le HEMA a montré le potentiel de sensibilisation le plus élevé, suivi du TEGDMA.",
        "HEMA toonde het hoogste sensibilisatiepotentieel, gevolgd door TEGDMA.",
        "HEMA ha mostrato il più alto potenziale di sensibilizzazione, seguito da TEGDMA.",
        "HEMA mostró el mayor potencial de sensibilización, seguido de TEGDMA."
      ),
      conclusions: t(
        "HEMA-freie Formulierungen könnten das Allergierisiko reduzieren.",
        "HEMA-free formulations could reduce allergy risk.",
        "Les formulations sans HEMA pourraient réduire le risque d'allergie.",
        "HEMA-vrije formuleringen zouden het allergierisiko kunnen verminderen.",
        "Le formulazioni senza HEMA potrebbero ridurre il rischio di allergia.",
        "Las formulaciones libres de HEMA podrían reducir el riesgo de alergia."
      ),
    },
  },
  {
    id: 115,
    category: "allergy",
    title: t(
      "Mukosakontakt mit Harzzement und lokale Entzündung",
      "Mucosal Contact with Resin Cement and Local Inflammation",
      "Contact muqueux avec le ciment résine et inflammation locale",
      "Mucosaal contact met harscement en lokale ontsteking",
      "Contatto mucosale con cemento resinoso e infiammazione locale",
      "Contacto mucoso con cemento de resina e inflamación local"
    ),
    authors: "Nish T, Kobayashi A, Matsumoto G",
    journal: "Journal of Oral Rehabilitation",
    year: 2008,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/18318714/",
    structured: {
      problemStatement: t(
        "Harzzementrückstände können lokale Mukosareaktionen auslösen.",
        "Resin cement residue can trigger local mucosal reactions.",
        "Les résidus de ciment résine peuvent déclencher des réactions muqueuses locales.",
        "Harscement-resten kunnen lokale mucosale reacties veroorzaken.",
        "I residui di cemento resinoso possono scatenare reazioni mucosali locali.",
        "Los residuos de cemento de resina pueden desencadenar reacciones mucosas locales."
      ),
      purpose: t(
        "Untersuchung lokaler Entzündungsreaktionen auf Harzzementsrückstände.",
        "Investigation of local inflammatory reactions to resin cement residue.",
        "Étude des réactions inflammatoires locales aux résidus de ciment résine.",
        "Onderzoek naar lokale ontstekingsreacties op harscement-resten.",
        "Indagine sulle reazioni infiammatorie locali ai residui di cemento resinoso.",
        "Investigación de reacciones inflamatorias locales a residuos de cemento de resina."
      ),
      methods: t(
        "Biopsien von periimplantärer Mukosa bei 30 Patienten.",
        "Biopsies of peri-implant mucosa in 30 patients.",
        "Biopsies de la muqueuse péri-implantaire chez 30 patients.",
        "Biopten van peri-implantaire mucosa bij 30 patiënten.",
        "Biopsie della mucosa perimplantare in 30 pazienti.",
        "Biopsias de la mucosa periimplantaria en 30 pacientes."
      ),
      results: t(
        "Erhöhte Mastzell- und Lymphozyteninfiltration bei Patienten mit Zementrückständen.",
        "Increased mast cell and lymphocyte infiltration in patients with cement residue.",
        "Infiltration accrue de mastocytes et de lymphocytes chez les patients avec résidus de ciment.",
        "Verhoogde mestcel- en lymfocytinfiltratie bij patiënten met cementresten.",
        "Aumento dell'infiltrazione di mastociti e linfociti nei pazienti con residui di cemento.",
        "Mayor infiltración de mastocitos y linfocitos en pacientes con residuos de cemento."
      ),
      conclusions: t(
        "Harzementkontakt kann lokale allergische Reaktionen triggern.",
        "Resin cement contact can trigger local allergic reactions.",
        "Le contact avec le ciment résine peut déclencher des réactions allergiques locales.",
        "Harscementcontact kan lokale allergische reacties triggeren.",
        "Il contatto con il cemento resinoso può scatenare reazioni allergiche locali.",
        "El contacto con cemento de resina puede desencadenar reacciones alérgicas locales."
      ),
    },
  },
  {
    id: 116,
    category: "allergy",
    title: t(
      "Berufsbedingte Kontaktallergie in der Zahnmedizin",
      "Occupational Contact Allergy in Dentistry",
      "Allergie de contact professionnelle en dentisterie",
      "Beroepsmatige contactallergie in de tandheelkunde",
      "Allergia da contatto professionale in odontoiatria",
      "Alergia de contacto ocupacional en odontología"
    ),
    authors: "Alanko K, Susitaival P, Jolanki R, Kanerva L",
    journal: "Contact Dermatitis",
    year: 2004,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/15270716/",
    structured: {
      problemStatement: t(
        "Zahnmediziner haben ein erhöhtes Risiko für Kontaktallergien.",
        "Dental professionals have an increased risk for contact allergies.",
        "Les professionnels dentaires ont un risque accru d'allergies de contact.",
        "Tandheelkundige professionals hebben een verhoogd risico op contactallergieën.",
        "I professionisti dentali hanno un rischio aumentato di allergie da contatto.",
        "Los profesionales dentales tienen un mayor riesgo de alergias de contacto."
      ),
      purpose: t(
        "Review der berufsbedingten Kontaktallergien in der Zahnmedizin.",
        "Review of occupational contact allergies in dentistry.",
        "Revue des allergies de contact professionnelles en dentisterie.",
        "Review van beroepsmatige contactallergieën in de tandheelkunde.",
        "Revisione delle allergie da contatto professionali in odontoiatria.",
        "Revisión de las alergias de contacto ocupacionales en odontología."
      ),
      methods: t(
        "Analyse von Berufskrankheits-Registern in 5 europäischen Ländern.",
        "Analysis of occupational disease registries in 5 European countries.",
        "Analyse des registres de maladies professionnelles dans 5 pays européens.",
        "Analyse van beroepsziekteregisters in 5 Europese landen.",
        "Analisi dei registri delle malattie professionali in 5 paesi europei.",
        "Análisis de registros de enfermedades ocupacionales en 5 países europeos."
      ),
      results: t(
        "Methacrylate waren die häufigsten Allergene, gefolgt von Latex.",
        "Methacrylates were the most common allergens, followed by latex.",
        "Les méthacrylates étaient les allergènes les plus courants, suivis du latex.",
        "Methacrylaten waren de meest voorkomende allergenen, gevolgd door latex.",
        "I metacrilati erano gli allergeni più comuni, seguiti dal lattice.",
        "Los metacrilatos fueron los alérgenos más comunes, seguidos del látex."
      ),
      conclusions: t(
        "Präventionsmaßnahmen und Schutzausrüstung sind in der Zahnmedizin essenziell.",
        "Prevention measures and protective equipment are essential in dentistry.",
        "Les mesures de prévention et les équipements de protection sont essentiels en dentisterie.",
        "Preventiemaatregelen en beschermende uitrusting zijn essentieel in de tandheelkunde.",
        "Le misure di prevenzione e le attrezzature di protezione sono essenziali in odontoiatria.",
        "Las medidas de prevención y el equipo de protección son esenciales en odontología."
      ),
    },
  },
  {
    id: 117,
    category: "allergy",
    title: t(
      "Alternative Materialien für allergische Patienten",
      "Alternative Materials for Allergic Patients",
      "Matériaux alternatifs pour les patients allergiques",
      "Alternatieve materialen voor allergische patiënten",
      "Materiali alternativi per pazienti allergici",
      "Materiales alternativos para pacientes alérgicos"
    ),
    authors: "Tschernitschek H, Gunay H, Modern G",
    journal: "International Journal of Prosthodontics",
    year: 2006,
    type: "review",
    url: "https://pubmed.ncbi.nlm.nih.gov/16479760/",
    structured: {
      problemStatement: t(
        "Allergische Patienten benötigen alternative Restaurationsmaterialien.",
        "Allergic patients need alternative restorative materials.",
        "Les patients allergiques ont besoin de matériaux de restauration alternatifs.",
        "Allergische patiënten hebben alternatieve restauratiematerialen nodig.",
        "I pazienti allergici necessitano di materiali restaurativi alternativi.",
        "Los pacientes alérgicos necesitan materiales de restauración alternativos."
      ),
      purpose: t(
        "Übersicht über allergenfreie Alternativen in der restaurativen Zahnmedizin.",
        "Overview of allergen-free alternatives in restorative dentistry.",
        "Aperçu des alternatives sans allergènes en dentisterie restauratrice.",
        "Overzicht van allergeenvrije alternatieven in de restauratieve tandheelkunde.",
        "Panoramica delle alternative prive di allergeni in odontoiatria restaurativa.",
        "Resumen de alternativas libres de alérgenos en odontología restaurativa."
      ),
      methods: t(
        "Systematischer Review verfügbarer Materialoptionen.",
        "Systematic review of available material options.",
        "Revue systématique des options de matériaux disponibles.",
        "Systematische review van beschikbare materiaalopties.",
        "Revisione sistematica delle opzioni di materiali disponibili.",
        "Revisión sistemática de las opciones de materiales disponibles."
      ),
      results: t(
        "Titan, Zirkon und glaskeramische Materialien sind für die meisten Allergiker sicher.",
        "Titanium, zirconia, and glass-ceramic materials are safe for most allergy patients.",
        "Le titane, la zircone et les matériaux vitrocéramiques sont sûrs pour la plupart des patients allergiques.",
        "Titanium, zirkoon en glaskeramische materialen zijn veilig voor de meeste allergiepatiënten.",
        "Titanio, zirconia e materiali vetroceramici sono sicuri per la maggior parte dei pazienti allergici.",
        "El titanio, el circonio y los materiales de vitrocerámica son seguros para la mayoría de los pacientes alérgicos."
      ),
      conclusions: t(
        "Metallfreie und harzfreie Optionen ermöglichen sichere Versorgung bei Allergien.",
        "Metal-free and resin-free options enable safe treatment for allergies.",
        "Les options sans métal et sans résine permettent un traitement sûr des allergies.",
        "Metaalvrije en harsvrije opties maken veilige behandeling mogelijk bij allergieën.",
        "Le opzioni senza metallo e senza resina consentono un trattamento sicuro per le allergie.",
        "Las opciones sin metal y sin resina permiten un tratamiento seguro para las alergias."
      ),
    },
  },
];

// ============================================
// NEUE STUDIEN 2024-2026 (Nicht-zahnmedizinische & aktuelle Forschung)
// ============================================

export const additionalStudies2024: Study[] = [
  // === NICHT-ZAHNMEDIZINISCHE TOXIZITÄTSSTUDIEN ===
  {
    id: 123,
    category: "polymer-toxicity",
    title: t(
      "Bisphenol A-Glycidylmethacrylat induziert ein breites Spektrum an DNA-Schäden in menschlichen Lymphozyten",
      "Bisphenol A-glycidyl methacrylate induces a broad spectrum of DNA damage in human lymphocytes",
      "Le bisphénol A-glycidyl méthacrylate induit un large spectre de dommages à l'ADN dans les lymphocytes humains",
      "Bisfenol A-glycidylmethacrylaat induceert een breed spectrum aan DNA-schade in menselijke lymfocyten",
      "Il bisfenolo A-glicidil metacrilato induce un ampio spettro di danni al DNA nei linfociti umani",
      "El bisfenol A-glicidil metacrilato induce un amplio espectro de daño en el ADN en linfocitos humanos"
    ),
    authors: "Szczepanska J, Poplawski T, Synowiec E, Pawlowska E, Chruscik K, Blasiak J",
    journal: "Archives of Toxicology",
    year: 2012,
    type: "in-vitro",
    url: "https://pubmed.ncbi.nlm.nih.gov/21989784/",
    structured: {
      problemStatement: t(
        "BisGMA ist ein Monomer in Dentalkompositen, das in menschliche Zellen freigesetzt werden kann.",
        "BisGMA is a monomer in dental composites that can be released into human cells.",
        "Le BisGMA est un monomère dans les composites dentaires qui peut être libéré dans les cellules humaines.",
        "BisGMA is een monomeer in tandheelkundige composieten dat kan vrijkomen in menselijke cellen.",
        "Il BisGMA è un monomero nei compositi dentali che può essere rilasciato nelle cellule umane.",
        "El BisGMA es un monómero en composites dentales que puede liberarse en las células humanas."
      ),
      purpose: t(
        "Untersuchung der genotoxischen Wirkung von BisGMA auf menschliche Lymphozyten und Leukämiezellen.",
        "Investigation of the genotoxic effect of BisGMA on human lymphocytes and leukemia cells.",
        "Étude de l'effet génotoxique du BisGMA sur les lymphocytes humains et les cellules leucémiques.",
        "Onderzoek naar het genotoxische effect van BisGMA op menselijke lymfocyten en leukemiecellen.",
        "Indagine sull'effetto genotossico del BisGMA sui linfociti umani e sulle cellule leucemiche.",
        "Investigación del efecto genotóxico del BisGMA en linfocitos humanos y células de leucemia."
      ),
      methods: t(
        "Comet-Assay zur Analyse von DNA-Strangbrüchen in Lymphozyten und CCRF-CEM-Zellen.",
        "Comet assay to analyze DNA strand breaks in lymphocytes and CCRF-CEM cells.",
        "Test des comètes pour analyser les cassures de brins d'ADN dans les lymphocytes et les cellules CCRF-CEM.",
        "Comet-assay om DNA-strengbreuken te analyseren in lymfocyten en CCRF-CEM-cellen.",
        "Saggio della cometa per analizzare le rotture dei filamenti del DNA nei linfociti e nelle cellule CCRF-CEM.",
        "Ensayo cometa para analizar las roturas de cadenas de ADN en linfocitos y células CCRF-CEM."
      ),
      results: t(
        "BisGMA verursacht DNA-Schäden einschließlich Einzelstrang-, Doppelstrangbrüche und alkali-labile Stellen.",
        "BisGMA causes DNA damage including single-strand, double-strand breaks and alkali-labile sites.",
        "Le BisGMA provoque des dommages à l'ADN, notamment des cassures simple brin, double brin et des sites alcali-labiles.",
        "BisGMA veroorzaakt DNA-schade inclusief enkelstrengs-, dubbelstrengsbreuken en alkali-labiele sites.",
        "Il BisGMA causa danni al DNA incluse rotture a singolo filamento, a doppio filamento e siti alcali-labili.",
        "El BisGMA causa daño al ADN incluyendo roturas de cadena simple, doble y sitios álcali-lábiles."
      ),
      conclusions: t(
        "BisGMA ist genotoxisch für menschliche Lymphozyten - Minimierung des Kontakts ist ratsam.",
        "BisGMA is genotoxic to human lymphocytes - minimizing contact is advisable.",
        "Le BisGMA est génotoxique pour les lymphocytes humains - minimiser le contact est conseillé.",
        "BisGMA is genotoxisch voor menselijke lymfocyten - contact minimaliseren is aan te raden.",
        "Il BisGMA è genotossico per i linfociti umani - è consigliabile minimizzare il contatto.",
        "El BisGMA es genotóxico para los linfocitos humanos - se recomienda minimizar el contacto."
      ),
    },
  },
  {
    id: 124,
    category: "polymer-toxicity",
    title: t(
      "Endokrin wirksame Toxizität von Bisphenol A und seinen Analoga: Umfassende Übersicht",
      "Endocrine Disrupting Toxicity of Bisphenol A and Its Analogs: Comprehensive Review",
      "Toxicité perturbatrice endocrinienne du bisphénol A et de ses analogues : revue complète",
      "Hormoonverstorende toxiciteit van bisfenol A en zijn analogen: uitgebreid overzicht",
      "Tossicità endocrino-distruttiva del bisfenolo A e dei suoi analoghi: revisione completa",
      "Toxicidad disruptora endocrina del bisfenol A y sus análogos: revisión completa"
    ),
    authors: "Qin T, Zhang X, Guo T, Yang T, Gao Y, Hao W, Xiao X",
    journal: "Endocrines",
    year: 2024,
    type: "review",
    url: "https://www.mdpi.com/2039-4713/15/1/13",
    structured: {
      problemStatement: t(
        "Bisphenol A (BPA) ist ein bekannter endokriner Disruptor, der in vielen Materialien vorkommt.",
        "Bisphenol A (BPA) is a known endocrine disruptor found in many materials.",
        "Le bisphénol A (BPA) est un perturbateur endocrinien connu présent dans de nombreux matériaux.",
        "Bisfenol A (BPA) is een bekende hormoonverstoorder die in veel materialen voorkomt.",
        "Il bisfenolo A (BPA) è un noto interferente endocrino presente in molti materiali.",
        "El bisfenol A (BPA) es un conocido disruptor endocrino presente en muchos materiales."
      ),
      purpose: t(
        "Umfassende Analyse der endokrinen Toxizität von BPA und verwandten Substanzen.",
        "Comprehensive analysis of endocrine toxicity of BPA and related substances.",
        "Analyse complète de la toxicité endocrinienne du BPA et des substances apparentées.",
        "Uitgebreide analyse van de endocriene toxiciteit van BPA en verwante stoffen.",
        "Analisi completa della tossicità endocrina del BPA e delle sostanze correlate.",
        "Análisis completo de la toxicidad endocrina del BPA y sustancias relacionadas."
      ),
      methods: t(
        "Systematische Literaturanalyse zu hormonellen Auswirkungen von BPA in verschiedenen Geweben.",
        "Systematic literature analysis on hormonal effects of BPA in various tissues.",
        "Analyse systématique de la littérature sur les effets hormonaux du BPA dans divers tissus.",
        "Systematische literatuuranalyse over hormonale effecten van BPA in verschillende weefsels.",
        "Analisi sistematica della letteratura sugli effetti ormonali del BPA in vari tessuti.",
        "Análisis sistemático de la literatura sobre los efectos hormonales del BPA en varios tejidos."
      ),
      results: t(
        "BPA stört die Schilddrüsen-, Reproduktions- und Stoffwechselfunktion bereits in niedrigen Dosen.",
        "BPA disrupts thyroid, reproductive and metabolic function even at low doses.",
        "Le BPA perturbe les fonctions thyroïdiennes, reproductives et métaboliques même à faibles doses.",
        "BPA verstoort de schildklier-, voortplantings- en stofwisselingsfunctie zelfs bij lage doses.",
        "Il BPA altera la funzione tiroidea, riproduttiva e metabolica anche a basse dosi.",
        "El BPA altera la función tiroidea, reproductiva y metabólica incluso a dosis bajas."
      ),
      conclusions: t(
        "Die Exposition gegenüber BPA-haltigen Materialien sollte minimiert werden - auch in der Medizin.",
        "Exposure to BPA-containing materials should be minimized - including in medicine.",
        "L'exposition aux matériaux contenant du BPA devrait être minimisée - y compris en médecine.",
        "Blootstelling aan BPA-bevattende materialen moet worden geminimaliseerd - ook in de geneeskunde.",
        "L'esposizione a materiali contenenti BPA dovrebbe essere minimizzata - anche in medicina.",
        "La exposición a materiales que contienen BPA debe minimizarse - incluso en medicina."
      ),
    },
  },
  {
    id: 125,
    category: "polymer-toxicity",
    title: t(
      "Zytotoxizität verschiedener Komposite auf menschlichen Gingivafibroblasten",
      "Cytotoxicity of Different Composite Resins on Human Gingival Fibroblast Cell Lines",
      "Cytotoxicité de différentes résines composites sur les lignées cellulaires de fibroblastes gingivaux humains",
      "Cytotoxiciteit van verschillende composietharsen op menselijke gingivale fibroblast cellijnen",
      "Citotossicità di diverse resine composite su linee cellulari di fibroblasti gengivali umani",
      "Citotoxicidad de diferentes resinas compuestas en líneas celulares de fibroblastos gingivales humanos"
    ),
    authors: "Beltrami R, Colombo M, Rizzo K, Di Cristofaro A, Poggio C, Pietrocola G",
    journal: "Biomimetics",
    year: 2021,
    type: "in-vitro",
    url: "https://www.mdpi.com/2313-7673/6/2/26",
    structured: {
      problemStatement: t(
        "Komposite kommen in direkten Kontakt mit Gingivagewebe und können zytotoxisch wirken.",
        "Composites come into direct contact with gingival tissue and can be cytotoxic.",
        "Les composites entrent en contact direct avec le tissu gingival et peuvent être cytotoxiques.",
        "Composieten komen in direct contact met gingivaal weefsel en kunnen cytotoxisch zijn.",
        "I compositi entrano in contatto diretto con il tessuto gengivale e possono essere citotossici.",
        "Los composites entran en contacto directo con el tejido gingival y pueden ser citotóxicos."
      ),
      purpose: t(
        "Vergleich der Zytotoxizität verschiedener marktgängiger Komposite.",
        "Comparison of cytotoxicity of various commercially available composites.",
        "Comparaison de la cytotoxicité de divers composites disponibles dans le commerce.",
        "Vergelijking van de cytotoxiciteit van verschillende commercieel beschikbare composieten.",
        "Confronto della citotossicità di vari compositi disponibili in commercio.",
        "Comparación de la citotoxicidad de varios composites disponibles comercialmente."
      ),
      methods: t(
        "MTT-Test und Alamar-Blue-Assay mit humanen Gingivafibroblasten (HGF).",
        "MTT test and Alamar Blue assay with human gingival fibroblasts (HGF).",
        "Test MTT et test Alamar Blue avec des fibroblastes gingivaux humains (HGF).",
        "MTT-test en Alamar Blue-assay met menselijke gingivale fibroblasten (HGF).",
        "Test MTT e saggio Alamar Blue con fibroblasti gengivali umani (HGF).",
        "Prueba MTT y ensayo Alamar Blue con fibroblastos gingivales humanos (HGF)."
      ),
      results: t(
        "Alle getesteten Komposite zeigten signifikante zytotoxische Effekte - mit unterschiedlicher Intensität.",
        "All tested composites showed significant cytotoxic effects - with varying intensity.",
        "Tous les composites testés ont montré des effets cytotoxiques significatifs - avec une intensité variable.",
        "Alle geteste composieten toonden significante cytotoxische effecten - met variërende intensiteit.",
        "Tutti i compositi testati hanno mostrato effetti citotossici significativi - con intensità variabile.",
        "Todos los composites probados mostraron efectos citotóxicos significativos - con intensidad variable."
      ),
      conclusions: t(
        "Die Wahl des Komposits beeinflusst die Biokompatibilität - direkter Gewebekontakt sollte minimiert werden.",
        "The choice of composite affects biocompatibility - direct tissue contact should be minimized.",
        "Le choix du composite affecte la biocompatibilité - le contact direct avec les tissus devrait être minimisé.",
        "De keuze van het composiet beïnvloedt de biocompatibiliteit - direct weefselcontact moet worden geminimaliseerd.",
        "La scelta del composito influisce sulla biocompatibilità - il contatto diretto con i tessuti dovrebbe essere minimizzato.",
        "La elección del composite afecta la biocompatibilidad - el contacto directo con los tejidos debe minimizarse."
      ),
    },
  },
  {
    id: 126,
    category: "polymer-toxicity",
    title: t(
      "Vergleich der Zytotoxizität zwischen 3D-druckbaren Harzen und konventionellem PMMA",
      "Comparison of cytotoxicity between 3D printable resins and heat-cure PMMA",
      "Comparaison de la cytotoxicité entre les résines imprimables en 3D et le PMMA thermodurcissable",
      "Vergelijking van cytotoxiciteit tussen 3D-printbare harsen en warmtegeharde PMMA",
      "Confronto della citotossicità tra resine stampabili in 3D e PMMA termoindurente",
      "Comparación de la citotoxicidad entre resinas imprimibles en 3D y PMMA termocurable"
    ),
    authors: "Agarwal S, Autade PR, Agarwal A",
    journal: "Journal of Prosthodontics",
    year: 2024,
    type: "in-vitro",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10831252/",
    structured: {
      problemStatement: t(
        "3D-gedruckte Prothesen werden immer häufiger - ihre Biokompatibilität ist nicht ausreichend untersucht.",
        "3D-printed prostheses are becoming more common - their biocompatibility is not sufficiently studied.",
        "Les prothèses imprimées en 3D deviennent plus courantes - leur biocompatibilité n'est pas suffisamment étudiée.",
        "3D-geprinte protheses worden steeds gebruikelijker - hun biocompatibiliteit is onvoldoende onderzocht.",
        "Le protesi stampate in 3D stanno diventando più comuni - la loro biocompatibilità non è sufficientemente studiata.",
        "Las prótesis impresas en 3D son cada vez más comunes - su biocompatibilidad no está suficientemente estudiada."
      ),
      purpose: t(
        "Vergleich der Zytotoxizität von 3D-Druckharzen mit konventionellem PMMA nach ISO-10993-5.",
        "Comparison of cytotoxicity of 3D printing resins with conventional PMMA according to ISO-10993-5.",
        "Comparaison de la cytotoxicité des résines d'impression 3D avec le PMMA conventionnel selon l'ISO-10993-5.",
        "Vergelijking van de cytotoxiciteit van 3D-printharsen met conventioneel PMMA volgens ISO-10993-5.",
        "Confronto della citotossicità delle resine per stampa 3D con il PMMA convenzionale secondo ISO-10993-5.",
        "Comparación de la citotoxicidad de las resinas de impresión 3D con PMMA convencional según ISO-10993-5."
      ),
      methods: t(
        "MTT-Assay an L929-Fibroblasten mit Extrakten verschiedener Materialien.",
        "MTT assay on L929 fibroblasts with extracts from various materials.",
        "Test MTT sur les fibroblastes L929 avec des extraits de divers matériaux.",
        "MTT-assay op L929-fibroblasten met extracten van verschillende materialen.",
        "Saggio MTT su fibroblasti L929 con estratti di vari materiali.",
        "Ensayo MTT en fibroblastos L929 con extractos de varios materiales."
      ),
      results: t(
        "3D-Druckharze zeigten höhere Zytotoxizität als konventionelles PMMA.",
        "3D printing resins showed higher cytotoxicity than conventional PMMA.",
        "Les résines d'impression 3D ont montré une cytotoxicité plus élevée que le PMMA conventionnel.",
        "3D-printharsen toonden hogere cytotoxiciteit dan conventioneel PMMA.",
        "Le resine per stampa 3D hanno mostrato una citotossicità maggiore rispetto al PMMA convenzionale.",
        "Las resinas de impresión 3D mostraron mayor citotoxicidad que el PMMA convencional."
      ),
      conclusions: t(
        "Bei 3D-gedruckten Provisorien ist besondere Vorsicht geboten - Nachbearbeitung und Reinigung sind essentiell.",
        "Special care is required with 3D-printed provisionals - post-processing and cleaning are essential.",
        "Une attention particulière est requise avec les provisoires imprimés en 3D - le post-traitement et le nettoyage sont essentiels.",
        "Bijzondere voorzichtigheid is geboden bij 3D-geprinte provisoria - nabewerking en reiniging zijn essentieel.",
        "È richiesta particolare attenzione con i provvisori stampati in 3D - la post-elaborazione e la pulizia sono essenziali.",
        "Se requiere especial cuidado con los provisionales impresos en 3D - el postprocesamiento y la limpieza son esenciales."
      ),
    },
  },
  // === NEUESTE ZELLANHAFTUNGS-STUDIEN ===
  {
    id: 129,
    category: "cell-attachment",
    title: t(
      "Fibroblast- und Osteoblastenverhalten nach Kontakt mit verschiedenen Titanoberflächen als Implantat-Abutments",
      "Fibroblasts and osteoblasts behavior after contact with different titanium surfaces used as implant abutment",
      "Comportement des fibroblastes et ostéoblastes après contact avec différentes surfaces en titane utilisées comme piliers d'implants",
      "Gedrag van fibroblasten en osteoblasten na contact met verschillende titaniumoppervlakken gebruikt als implantaatabutments",
      "Comportamento di fibroblasti e osteoblasti dopo contatto con diverse superfici in titanio utilizzate come abutment implantari",
      "Comportamiento de fibroblastos y osteoblastos después del contacto con diferentes superficies de titanio utilizadas como pilares de implantes"
    ),
    authors: "Schmitt CM, Schmitt A, et al.",
    journal: "Heliyon",
    year: 2024,
    type: "in-vitro",
    url: "https://www.sciencedirect.com/science/article/pii/S2405844024010697",
    structured: {
      problemStatement: t(
        "Die Oberflächenbeschaffenheit von Abutments beeinflusst die Zellanhaftung - optimale Oberflächen sind unklar.",
        "The surface texture of abutments influences cell attachment - optimal surfaces are unclear.",
        "La texture de surface des piliers influence l'adhésion cellulaire - les surfaces optimales ne sont pas claires.",
        "De oppervlaktetextuur van abutments beïnvloedt de celaanhechting - optimale oppervlakken zijn onduidelijk.",
        "La texture superficiale degli abutment influenza l'adesione cellulare - le superfici ottimali non sono chiare.",
        "La textura superficial de los pilares influye en la adhesión celular - las superficies óptimas no están claras."
      ),
      purpose: t(
        "Vergleich von glatten, säuregeätzten und anodisierten Titanoberflächen für Abutments.",
        "Comparison of smooth, acid-etched and anodized titanium surfaces for abutments.",
        "Comparaison de surfaces en titane lisses, mordancées à l'acide et anodisées pour les piliers.",
        "Vergelijking van gladde, zuurgeëtste en geanodiseerde titaniumoppervlakken voor abutments.",
        "Confronto di superfici in titanio lisce, mordenzate e anodizzate per abutment.",
        "Comparación de superficies de titanio lisas, grabadas con ácido y anodizadas para pilares."
      ),
      methods: t(
        "Rasterelektronenmikroskopie und Zellkulturen mit Fibroblasten und Osteoblasten auf verschiedenen Oberflächen.",
        "Scanning electron microscopy and cell cultures with fibroblasts and osteoblasts on various surfaces.",
        "Microscopie électronique à balayage et cultures cellulaires avec fibroblastes et ostéoblastes sur diverses surfaces.",
        "Rasterelektronenmicroscopie en celculturen met fibroblasten en osteoblasten op verschillende oppervlakken.",
        "Microscopia elettronica a scansione e colture cellulari con fibroblasti e osteoblasti su varie superfici.",
        "Microscopía electrónica de barrido y cultivos celulares con fibroblastos y osteoblastos en varias superficies."
      ),
      results: t(
        "Anodisierte Oberflächen zeigten bessere Zellanhaftung und Proliferation als glatte Oberflächen.",
        "Anodized surfaces showed better cell attachment and proliferation than smooth surfaces.",
        "Les surfaces anodisées ont montré une meilleure adhésion et prolifération cellulaire que les surfaces lisses.",
        "Geanodiseerde oppervlakken toonden betere celaanhechting en proliferatie dan gladde oppervlakken.",
        "Le superfici anodizzate hanno mostrato una migliore adesione e proliferazione cellulare rispetto alle superfici lisce.",
        "Las superficies anodizadas mostraron mejor adhesión y proliferación celular que las superficies lisas."
      ),
      conclusions: t(
        "Die Oberflächenbehandlung des Abutments beeinflusst die Weichgewebsintegration signifikant.",
        "The surface treatment of the abutment significantly influences soft tissue integration.",
        "Le traitement de surface du pilier influence significativement l'intégration des tissus mous.",
        "De oppervlaktebehandeling van het abutment beïnvloedt de weke weefselintegratie significant.",
        "Il trattamento superficiale dell'abutment influenza significativamente l'integrazione dei tessuti molli.",
        "El tratamiento superficial del pilar influye significativamente en la integración del tejido blando."
      ),
    },
  },
  {
    id: 130,
    category: "cell-attachment",
    title: t(
      "Weichgewebeanhaftung von Gingivafibroblasten an Titanoxid-Nanoröhren: Systematischer Review",
      "Soft tissue attachment of human gingival fibroblasts to titanium dioxide nanotubes: A systematic review",
      "Attachement des tissus mous des fibroblastes gingivaux humains aux nanotubes de dioxyde de titane : revue systématique",
      "Zachte weefselaanhechting van menselijke gingivale fibroblasten aan titaandioxide nanobuizen: een systematisch overzicht",
      "Adesione dei tessuti molli dei fibroblasti gengivali umani ai nanotubi di biossido di titanio: revisione sistematica",
      "Adhesión de tejido blando de fibroblastos gingivales humanos a nanotubos de dióxido de titanio: revisión sistemática"
    ),
    authors: "Elgali I, Tiainen H, Öztürk O, Sennerby L, Lyngstadaas SP, Panseri S",
    journal: "BDJ Open",
    year: 2025,
    type: "review",
    url: "https://www.nature.com/articles/s41405-025-00293-0",
    structured: {
      problemStatement: t(
        "Die Weichgewebeversiegelung um Implantate ist entscheidend für die Langzeitstabilität.",
        "The soft tissue seal around implants is crucial for long-term stability.",
        "Le scellement des tissus mous autour des implants est crucial pour la stabilité à long terme.",
        "De zachte weefselverzegeling rond implantaten is cruciaal voor langetermijnstabiliteit.",
        "La sigillatura dei tessuti molli attorno agli impianti è cruciale per la stabilità a lungo termine.",
        "El sellado de tejido blando alrededor de los implantes es crucial para la estabilidad a largo plazo."
      ),
      purpose: t(
        "Systematische Analyse der Anhaftung von Gingivafibroblasten an verschiedene Titan-Nanostrukturen.",
        "Systematic analysis of gingival fibroblast attachment to various titanium nanostructures.",
        "Analyse systématique de l'adhésion des fibroblastes gingivaux à diverses nanostructures en titane.",
        "Systematische analyse van de aanhechting van gingivale fibroblasten aan verschillende titanium nanostructuren.",
        "Analisi sistematica dell'adesione dei fibroblasti gengivali a varie nanostrutture in titanio.",
        "Análisis sistemático de la adhesión de fibroblastos gingivales a varias nanoestructuras de titanio."
      ),
      methods: t(
        "Literaturreview zu TiO2-Nanoröhren im Vergleich zu reinem Titan und Titanlegierungen.",
        "Literature review on TiO2 nanotubes compared to pure titanium and titanium alloys.",
        "Revue de la littérature sur les nanotubes de TiO2 par rapport au titane pur et aux alliages de titane.",
        "Literatuuronderzoek naar TiO2-nanobuizen vergeleken met puur titanium en titaniumlegeringen.",
        "Revisione della letteratura sui nanotubi di TiO2 rispetto al titanio puro e alle leghe di titanio.",
        "Revisión de la literatura sobre nanotubos de TiO2 en comparación con titanio puro y aleaciones de titanio."
      ),
      results: t(
        "Nanostrukturierte Oberflächen verbessern die Fibroblastenanhaftung und reduzieren bakterielle Besiedlung.",
        "Nanostructured surfaces improve fibroblast attachment and reduce bacterial colonization.",
        "Les surfaces nanostructurées améliorent l'adhésion des fibroblastes et réduisent la colonisation bactérienne.",
        "Genanaostructureerde oppervlakken verbeteren de fibroblastaanhechting en verminderen bacteriële kolonisatie.",
        "Le superfici nanostrutturate migliorano l'adesione dei fibroblasti e riducono la colonizzazione batterica.",
        "Las superficies nanoestructuradas mejoran la adhesión de fibroblastos y reducen la colonización bacteriana."
      ),
      conclusions: t(
        "Oberflächenmodifikation auf Nanoebene kann die biologische Versiegelung am Implantat optimieren.",
        "Surface modification at the nano level can optimize the biological seal at the implant.",
        "La modification de surface au niveau nano peut optimiser le scellement biologique de l'implant.",
        "Oppervlaktemodificatie op nanoniveau kan de biologische afsluiting bij het implantaat optimaliseren.",
        "La modifica della superficie a livello nanometrico può ottimizzare la sigillatura biologica dell'impianto.",
        "La modificación superficial a nivel nano puede optimizar el sellado biológico del implante."
      ),
    },
  },
  // === NEUESTE ZEMENT-STUDIEN ===
  {
    id: 131,
    category: "cement-problems",
    title: t(
      "Überschüssiger Zement und periimplantäre Erkrankungen: Eine klinische endoskopische Querschnittsstudie",
      "Excess cement and peri-implant disease: A cross-sectional clinical endoscopic study",
      "Excès de ciment et maladie péri-implantaire : une étude endoscopique clinique transversale",
      "Overtollig cement en peri-implantaire ziekte: een klinische endoscopische cross-sectionele studie",
      "Eccesso di cemento e malattia peri-implantare: uno studio endoscopico clinico trasversale",
      "Exceso de cemento y enfermedad periimplantaria: un estudio endoscópico clínico transversal"
    ),
    authors: "Montevecchi M, De				 S, et al.",
    journal: "Journal of Periodontology",
    year: 2025,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/39812473/",
    structured: {
      problemStatement: t(
        "Zementreste unter dem Zahnfleisch werden oft nicht erkannt und können Periimplantitis verursachen.",
        "Cement residues under the gum are often not detected and can cause peri-implantitis.",
        "Les résidus de ciment sous la gencive ne sont souvent pas détectés et peuvent causer une péri-implantite.",
        "Cementresten onder het tandvlees worden vaak niet gedetecteerd en kunnen peri-implantitis veroorzaken.",
        "I residui di cemento sotto la gengiva spesso non vengono rilevati e possono causare peri-implantite.",
        "Los residuos de cemento bajo la encía a menudo no se detectan y pueden causar periimplantitis."
      ),
      purpose: t(
        "Endoskopische Untersuchung von Zementresten bei Patienten mit periimplantären Problemen.",
        "Endoscopic examination of cement residues in patients with peri-implant problems.",
        "Examen endoscopique des résidus de ciment chez les patients présentant des problèmes péri-implantaires.",
        "Endoscopisch onderzoek van cementresten bij patiënten met peri-implantaire problemen.",
        "Esame endoscopico dei residui di cemento nei pazienti con problemi peri-implantari.",
        "Examen endoscópico de residuos de cemento en pacientes con problemas periimplantarios."
      ),
      methods: t(
        "Periimplantäre Endoskopie bei 87 Patienten mit zementiertenImpantatkronen.",
        "Peri-implant endoscopy in 87 patients with cemented implant crowns.",
        "Endoscopie péri-implantaire chez 87 patients avec des couronnes implantaires scellées.",
        "Peri-implantaire endoscopie bij 87 patiënten met gecementeerde implantaatkronen.",
        "Endoscopia peri-implantare in 87 pazienti con corone implantari cementate.",
        "Endoscopia periimplantaria en 87 pacientes con coronas de implantes cementadas."
      ),
      results: t(
        "Bei 68% der Implantate mit Periimplantitis wurden Zementreste gefunden - oft klinisch nicht sichtbar.",
        "Cement residues were found in 68% of implants with peri-implantitis - often not clinically visible.",
        "Des résidus de ciment ont été trouvés dans 68% des implants avec péri-implantite - souvent non visibles cliniquement.",
        "Cementresten werden gevonden bij 68% van de implantaten met peri-implantitis - vaak klinisch niet zichtbaar.",
        "Residui di cemento sono stati trovati nel 68% degli impianti con peri-implantite - spesso non visibili clinicamente.",
        "Se encontraron residuos de cemento en el 68% de los implantes con periimplantitis - a menudo no visibles clínicamente."
      ),
      conclusions: t(
        "Zementreste waren in dieser Untersuchung häufig mit periimplantärer Erkrankung assoziiert; ein Kausalzusammenhang und ein klinischer Nutzen zementfreier Lösungen sind damit nicht belegt.",
        "In this investigation cement residues were frequently associated with peri-implant disease; this does not establish causality or a clinical benefit of cement-free solutions.",
        "Dans cette étude, les résidus de ciment étaient fréquemment associés à une maladie péri-implantaire ; cela n'établit ni causalité ni bénéfice clinique des solutions sans ciment.",
        "In dit onderzoek waren cementresten vaak geassocieerd met peri-implantaire ziekte; dat bewijst geen oorzakelijk verband of klinisch voordeel van cementvrije oplossingen.",
        "In questa indagine i residui di cemento erano spesso associati a malattia perimplantare; ciò non dimostra causalità né un beneficio clinico delle soluzioni senza cemento.",
        "En esta investigación los restos de cemento se asociaron con frecuencia a enfermedad periimplantaria; esto no demuestra causalidad ni un beneficio clínico de las soluciones sin cemento."
      ),
    },
  },
  {
    id: 132,
    category: "cement-problems",
    title: t(
      "Mikrotomographische und histopathologische Analyse von Dentalzementen als Ursache periimplantärer Komplikationen",
      "A microtomographic and histopathological evaluation of dental cements as late-stage peri-implant complication",
      "Évaluation microtomographique et histopathologique des ciments dentaires comme complication péri-implantaire tardive",
      "Een microtomografische en histopathologische evaluatie van tandheelkundige cementen als laat-stadium peri-implantaire complicatie",
      "Valutazione microtomografica e istopatologica dei cementi dentali come complicazione peri-implantare tardiva",
      "Evaluación microtomográfica e histopatológica de los cementos dentales como complicación periimplantaria tardía"
    ),
    authors: "et al.",
    journal: "Scientific Reports",
    year: 2024,
    type: "animal-study",
    url: "https://www.nature.com/articles/s41598-024-66353-x",
    structured: {
      problemStatement: t(
        "Zement-induzierte Periimplantitis macht 1,9–75% der Implantatverluste aus.",
        "Cement-induced peri-implantitis accounts for 1.9–75% of implant failures.",
        "La péri-implantite induite par le ciment représente 1,9 à 75% des échecs d'implants.",
        "Cement-geïnduceerde peri-implantitis is verantwoordelijk voor 1,9-75% van de implantaatmislukkingen.",
        "La peri-implantite indotta dal cemento rappresenta l'1,9-75% dei fallimenti implantari.",
        "La periimplantitis inducida por cemento representa del 1,9 al 75% de los fracasos de implantes."
      ),
      purpose: t(
        "Analyse der biologischen Auswirkungen verschiedener Dentalzemente auf osseointegrierte Implantate im Tiermodell.",
        "Analysis of the biological effects of various dental cements on osseointegrated implants in an animal model.",
        "Analyse des effets biologiques de divers ciments dentaires sur les implants ostéointégrés dans un modèle animal.",
        "Analyse van de biologische effecten van verschillende tandheelkundige cementen op geosseoïntegreerde implantaten in een diermodel.",
        "Analisi degli effetti biologici di vari cementi dentali su impianti osteointegrati in un modello animale.",
        "Análisis de los efectos biológicos de varios cementos dentales en implantes osteointegrados en un modelo animal."
      ),
      methods: t(
        "Mikro-CT und histopathologische Untersuchung bei Lewis-Ratten mit verschiedenen Zementen.",
        "Micro-CT and histopathological examination in Lewis rats with various cements.",
        "Micro-CT et examen histopathologique chez des rats Lewis avec divers ciments.",
        "Micro-CT en histopathologisch onderzoek bij Lewis-ratten met verschillende cementen.",
        "Micro-CT ed esame istopatologico in ratti Lewis con vari cementi.",
        "Micro-TC y examen histopatológico en ratas Lewis con varios cementos."
      ),
      results: t(
        "Alle getesteten Zemente verursachten entzündliche Reaktionen - Schweregrad variierte je nach Zementtyp.",
        "All tested cements caused inflammatory reactions - severity varied by cement type.",
        "Tous les ciments testés ont provoqué des réactions inflammatoires - la gravité variait selon le type de ciment.",
        "Alle geteste cementen veroorzaakten ontstekingsreacties - ernst varieerde per cementtype.",
        "Tutti i cementi testati hanno causato reazioni infiammatorie - la gravità variava in base al tipo di cemento.",
        "Todos los cementos probados causaron reacciones inflamatorias - la gravedad varió según el tipo de cemento."
      ),
      conclusions: t(
        "Zementüberschuss triggert Entzündungen und Knochenabbau - zementfreie Abutments eliminieren dieses Risiko.",
        "Excess cement triggers inflammation and bone loss - cement-free abutments eliminate this risk.",
        "L'excès de ciment déclenche une inflammation et une perte osseuse - les piliers sans ciment éliminent ce risque.",
        "Overtollig cement veroorzaakt ontsteking en botverlies - cementvrije abutments elimineren dit risico.",
        "L'eccesso di cemento innesca infiammazione e perdita ossea - gli abutment senza cemento eliminano questo rischio.",
        "El exceso de cemento desencadena inflamación y pérdida ósea - los pilares sin cemento eliminan este riesgo."
      ),
    },
  },
  // === NEUESTE ONE-ABUTMENT-ONE-TIME STUDIEN ===
  {
    id: 133,
    category: "manipulation-aesthetics",
    title: t(
      "One Abutment One Time: Multizentrische, prospektive, kontrollierte, randomisierte Studie",
      "One Abutment One Time: A Multicenter, Prospective, Controlled, Randomized Study",
      "One Abutment One Time : une étude multicentrique, prospective, contrôlée et randomisée",
      "One Abutment One Time: een multicentrische, prospectieve, gecontroleerde, gerandomiseerde studie",
      "One Abutment One Time: uno studio multicentrico, prospettico, controllato, randomizzato",
      "One Abutment One Time: un estudio multicéntrico, prospectivo, controlado y aleatorizado"
    ),
    authors: "Ríos-Santos JV, Tello-González G, Lázaro-Calvo P, Gil Mur FJ, Ríos-Carrasco B, Fernández-Palacín A, Herrero-Climent M",
    journal: "International Journal of Environmental Research and Public Health",
    year: 2020,
    type: "human-study",
    url: "https://www.mdpi.com/1660-4601/17/24/9453",
    structured: {
      problemStatement: t(
        "Wiederholtes An- und Abschrauben von Abutments kann Gewebeschäden und Knochenverlust verursachen.",
        "Repeated connecting and disconnecting of abutments can cause tissue damage and bone loss.",
        "Le branchement et débranchement répétés des piliers peut causer des dommages tissulaires et une perte osseuse.",
        "Herhaald aan- en losschroeven van abutments kan weefselschade en botverlies veroorzaken.",
        "Il collegamento e scollegamento ripetuto degli abutment può causare danni ai tessuti e perdita ossea.",
        "Conectar y desconectar repetidamente los pilares puede causar daño tisular y pérdida ósea."
      ),
      purpose: t(
        "Prospektive Analyse des One-Abutment-One-Time-Protokolls vs. konventionellem Vorgehen.",
        "Prospective analysis of the One-Abutment-One-Time protocol vs. conventional approach.",
        "Analyse prospective du protocole One-Abutment-One-Time vs approche conventionnelle.",
        "Prospectieve analyse van het One-Abutment-One-Time protocol vs. conventionele aanpak.",
        "Analisi prospettica del protocollo One-Abutment-One-Time vs approccio convenzionale.",
        "Análisis prospectivo del protocolo One-Abutment-One-Time vs enfoque convencional."
      ),
      methods: t(
        "Multizentrische RCT mit 120 Patienten, Nachverfolgung über 12 Monate.",
        "Multicenter RCT with 120 patients, follow-up over 12 months.",
        "ECR multicentrique avec 120 patients, suivi sur 12 mois.",
        "Multicenter RCT met 120 patiënten, follow-up gedurende 12 maanden.",
        "RCT multicentrico con 120 pazienti, follow-up di 12 mesi.",
        "ECA multicéntrico con 120 pacientes, seguimiento durante 12 meses."
      ),
      results: t(
        "Signifikant weniger krestaler Knochenverlust in der One-Abutment-One-Time-Gruppe (0,18 mm vs. 0,52 mm).",
        "Significantly less crestal bone loss in the One-Abutment-One-Time group (0.18 mm vs. 0.52 mm).",
        "Perte osseuse crestale significativement moindre dans le groupe One-Abutment-One-Time (0,18 mm vs 0,52 mm).",
        "Significant minder crestaal botverlies in de One-Abutment-One-Time groep (0,18 mm vs 0,52 mm).",
        "Perdita ossea crestale significativamente inferiore nel gruppo One-Abutment-One-Time (0,18 mm vs 0,52 mm).",
        "Pérdida ósea crestal significativamente menor en el grupo One-Abutment-One-Time (0,18 mm vs 0,52 mm)."
      ),
      conclusions: t(
        "One-Abutment-One-Time reduziert Knochenverlust signifikant und sollte als Standard gelten.",
        "One-Abutment-One-Time significantly reduces bone loss and should be considered standard.",
        "One-Abutment-One-Time réduit significativement la perte osseuse et devrait être considéré comme standard.",
        "One-Abutment-One-Time vermindert botverlies significant en zou als standaard moeten gelden.",
        "One-Abutment-One-Time riduce significativamente la perdita ossea e dovrebbe essere considerato standard.",
        "One-Abutment-One-Time reduce significativamente la pérdida ósea y debería considerarse estándar."
      ),
    },
  },
  {
    id: 134,
    category: "manipulation-aesthetics",
    title: t(
      "Oberflächenmodifikation zur Verstärkung der Weichgewebeversiegelung an der transmukosalen Region",
      "Surface modification strategies to reinforce the soft tissue seal at transmucosal region of dental implants",
      "Stratégies de modification de surface pour renforcer le scellement des tissus mous dans la région transmuqueuse des implants dentaires",
      "Oppervlaktemodificatiestrategieën om de zachte weefselverzegeling in het transmucosale gebied van tandimplantaten te versterken",
      "Strategie di modifica della superficie per rinforzare la sigillatura dei tessuti molli nella regione transmucosale degli impianti dentali",
      "Estrategias de modificación de superficie para reforzar el sellado de tejido blando en la región transmucosa de los implantes dentales"
    ),
    authors: "Liu Q, Chen W, Wu M, Zhang C, Zhu Z, Sun H",
    journal: "Bioactive Materials",
    year: 2024,
    type: "review",
    url: "https://www.sciencedirect.com/science/article/pii/S2452199X24003803",
    structured: {
      problemStatement: t(
        "Eine schwache Weichgewebeversiegelung erhöht das Risiko für Implantatversagen.",
        "A weak soft tissue seal increases the risk of implant failure.",
        "Un scellement faible des tissus mous augmente le risque d'échec de l'implant.",
        "Een zwakke zachte weefselverzegeling verhoogt het risico op implantaatfalen.",
        "Una sigillatura debole dei tessuti molli aumenta il rischio di fallimento dell'impianto.",
        "Un sellado débil del tejido blando aumenta el riesgo de fallo del implante."
      ),
      purpose: t(
        "Review von Strategien zur Verbesserung der Weichgewebeanhaftung am Implantat-Abutment.",
        "Review of strategies to improve soft tissue attachment at the implant abutment.",
        "Revue des stratégies pour améliorer l'attachement des tissus mous au pilier de l'implant.",
        "Review van strategieën om de zachte weefselaanhechting aan het implantaatabutment te verbeteren.",
        "Revisione delle strategie per migliorare l'adesione dei tessuti molli all'abutment implantare.",
        "Revisión de estrategias para mejorar la adhesión del tejido blando al pilar del implante."
      ),
      methods: t(
        "Analyse von adhäsionsfördernden, antibakteriellen und immunmodulatorischen Oberflächenbehandlungen.",
        "Analysis of adhesion-promoting, antibacterial and immunomodulatory surface treatments.",
        "Analyse des traitements de surface favorisant l'adhésion, antibactériens et immunomodulateurs.",
        "Analyse van adhesie-bevorderende, antibacteriële en immunomodulerende oppervlaktebehandelingen.",
        "Analisi dei trattamenti superficiali che promuovono l'adesione, antibatterici e immunomodulatori.",
        "Análisis de tratamientos superficiales promotores de adhesión, antibacterianos e inmunomoduladores."
      ),
      results: t(
        "Kombinierte Strategien (Topographie + Bioaktivität + antimikrobielle Eigenschaften) sind am effektivsten.",
        "Combined strategies (topography + bioactivity + antimicrobial properties) are most effective.",
        "Les stratégies combinées (topographie + bioactivité + propriétés antimicrobiennes) sont les plus efficaces.",
        "Gecombineerde strategieën (topografie + bioactiviteit + antimicrobiële eigenschappen) zijn het meest effectief.",
        "Le strategie combinate (topografia + bioattività + proprietà antimicrobiche) sono le più efficaci.",
        "Las estrategias combinadas (topografía + bioactividad + propiedades antimicrobianas) son las más efectivas."
      ),
      conclusions: t(
        "Fortschrittliche Oberflächenmodifikation kann periimplantäre Erkrankungen signifikant reduzieren.",
        "Advanced surface modification can significantly reduce peri-implant diseases.",
        "La modification de surface avancée peut réduire significativement les maladies péri-implantaires.",
        "Geavanceerde oppervlaktemodificatie kan peri-implantaire ziekten significant verminderen.",
        "La modifica avanzata della superficie può ridurre significativamente le malattie peri-implantari.",
        "La modificación avanzada de la superficie puede reducir significativamente las enfermedades periimplantarias."
      ),
    },
  },
];

// ============================================
// KONVERSIONSGRAD & RESTMONOMER-STUDIEN
// ============================================

export const conversionStudies: Study[] = [
  {
    id: 135,
    category: "polymer-toxicity",
    title: t(
      "Bewertung des Konversionsgrades, der Restmonomere und mechanischer Eigenschaften lichthärtender Dentalkomposite",
      "Evaluation of the Degree of Conversion, Residual Monomers and Mechanical Properties of Some Light-Cured Dental Resin Composites",
      "Évaluation du degré de conversion, des monomères résiduels et des propriétés mécaniques des composites dentaires photopolymérisables",
      "Evaluatie van de conversiegraad, restmonomeren en mechanische eigenschappen van lichtgeharde tandheelkundige harscomposieten",
      "Valutazione del grado di conversione, dei monomeri residui e delle proprietà meccaniche di alcuni compositi dentali fotopolimerizzabili",
      "Evaluación del grado de conversión, monómeros residuales y propiedades mecánicas de composites dentales fotopolimerizables"
    ),
    authors: "Moldovan M, Balazsi R, Tosa C, Ponta O, Autran P, Zarnescu O,'Campian RS",
    journal: "Materials",
    year: 2019,
    type: "in-vitro",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6651104/",
    structured: {
      problemStatement: t(
        "Der Konversionsgrad von Dentalkompositen variiert erheblich und beeinflusst die Restmonomer-Freisetzung.",
        "The degree of conversion of dental composites varies considerably and influences residual monomer release.",
        "Le degré de conversion des composites dentaires varie considérablement et influence la libération de monomères résiduels.",
        "De conversiegraad van tandheelkundige composieten varieert aanzienlijk en beïnvloedt de afgifte van restmonomeren.",
        "Il grado di conversione dei compositi dentali varia considerevolmente e influenza il rilascio di monomeri residui.",
        "El grado de conversión de los composites dentales varía considerablemente e influye en la liberación de monómeros residuales."
      ),
      purpose: t(
        "Messung des Konversionsgrades und der Restmonomere in experimentellen und kommerziellen Kompositen.",
        "Measurement of degree of conversion and residual monomers in experimental and commercial composites.",
        "Mesure du degré de conversion et des monomères résiduels dans les composites expérimentaux et commerciaux.",
        "Meting van de conversiegraad en restmonomeren in experimentele en commerciële composieten.",
        "Misurazione del grado di conversione e dei monomeri residui in compositi sperimentali e commerciali.",
        "Medición del grado de conversión y monómeros residuales en composites experimentales y comerciales."
      ),
      methods: t(
        "FTIR-ATR und HPLC-UV Analyse von 6 Kompositmaterialien nach Lichthärtung.",
        "FTIR-ATR and HPLC-UV analysis of 6 composite materials after light curing.",
        "Analyse FTIR-ATR et HPLC-UV de 6 matériaux composites après photopolymérisation.",
        "FTIR-ATR en HPLC-UV analyse van 6 composietmaterialen na lichtuitharding.",
        "Analisi FTIR-ATR e HPLC-UV di 6 materiali compositi dopo fotopolimerizzazione.",
        "Análisis FTIR-ATR y HPLC-UV de 6 materiales compuestos después de la fotopolimerización."
      ),
      results: t(
        "Konversionsgrad zwischen 35-77%. Zwei experimentelle Materialien zeigten weniger als 1% Restmonomere.",
        "Degree of conversion between 35-77%. Two experimental materials showed less than 1% residual monomers.",
        "Degré de conversion entre 35-77%. Deux matériaux expérimentaux ont montré moins de 1% de monomères résiduels.",
        "Conversiegraad tussen 35-77%. Twee experimentele materialen vertoonden minder dan 1% restmonomeren.",
        "Grado di conversione tra 35-77%. Due materiali sperimentali hanno mostrato meno dell'1% di monomeri residui.",
        "Grado de conversión entre 35-77%. Dos materiales experimentales mostraron menos del 1% de monómeros residuales."
      ),
      conclusions: t(
        "23-65% der Monomere bleiben unpolymerisiert und können in das Gewebe freigesetzt werden.",
        "23-65% of monomers remain unpolymerized and can be released into tissue.",
        "23-65% des monomères restent non polymérisés et peuvent être libérés dans les tissus.",
        "23-65% van de monomeren blijft ongepolymeriseerd en kan in weefsel worden afgegeven.",
        "Il 23-65% dei monomeri rimane non polimerizzato e può essere rilasciato nei tessuti.",
        "El 23-65% de los monómeros permanecen sin polimerizar y pueden liberarse en los tejidos."
      ),
    },
  },
  {
    id: 136,
    category: "polymer-toxicity",
    title: t(
      "Untersuchung des Monomer-Konversionsgrades in Dentalkompositen mittels verschiedener Methoden: Eine In-vitro-Studie",
      "Investigation of the Degree of Monomer Conversion in Dental Composites through Various Methods: An In Vitro Study",
      "Investigation du degré de conversion des monomères dans les composites dentaires par différentes méthodes : étude in vitro",
      "Onderzoek naar de monomerconversiegraad in tandheelkundige composieten via verschillende methoden: een in-vitro studie",
      "Indagine sul grado di conversione dei monomeri nei compositi dentali attraverso vari metodi: uno studio in vitro",
      "Investigación del grado de conversión de monómeros en composites dentales mediante varios métodos: estudio in vitro"
    ),
    authors: "Ucuncu MK",
    journal: "Applied Sciences",
    year: 2024,
    type: "in-vitro",
    url: "https://www.mdpi.com/2076-3417/14/11/4406",
    structured: {
      problemStatement: t(
        "Verschiedene Messmethoden liefern unterschiedliche Werte für den Konversionsgrad.",
        "Different measurement methods provide different values for the degree of conversion.",
        "Différentes méthodes de mesure fournissent des valeurs différentes pour le degré de conversion.",
        "Verschillende meetmethoden geven verschillende waarden voor de conversiegraad.",
        "Diversi metodi di misurazione forniscono valori diversi per il grado di conversione.",
        "Diferentes métodos de medición proporcionan valores diferentes para el grado de conversión."
      ),
      purpose: t(
        "Vergleich von Mikrohärte, FTIR und DSC zur Messung des Konversionsgrades.",
        "Comparison of microhardness, FTIR and DSC for measuring degree of conversion.",
        "Comparaison de la microdureté, FTIR et DSC pour mesurer le degré de conversion.",
        "Vergelijking van microhardheid, FTIR en DSC voor het meten van de conversiegraad.",
        "Confronto di microdurezza, FTIR e DSC per misurare il grado di conversione.",
        "Comparación de microdureza, FTIR y DSC para medir el grado de conversión."
      ),
      methods: t(
        "Drei Dentalkomposite wurden mit drei verschiedenen analytischen Methoden untersucht.",
        "Three dental composites were examined with three different analytical methods.",
        "Trois composites dentaires ont été examinés avec trois méthodes analytiques différentes.",
        "Drie tandheelkundige composieten werden onderzocht met drie verschillende analytische methoden.",
        "Tre compositi dentali sono stati esaminati con tre diversi metodi analitici.",
        "Tres composites dentales fueron examinados con tres métodos analíticos diferentes."
      ),
      results: t(
        "Signifikante Unterschiede zwischen Oberflächen- und Tiefenhärtung. Restmonomer kann postoperative Beschwerden verursachen.",
        "Significant differences between surface and depth curing. Residual monomer can cause postoperative discomfort.",
        "Différences significatives entre la polymérisation en surface et en profondeur. Le monomère résiduel peut causer un inconfort postopératoire.",
        "Significante verschillen tussen oppervlakte- en diepteharding. Restmonomeer kan postoperatief ongemak veroorzaken.",
        "Differenze significative tra polimerizzazione superficiale e profonda. Il monomero residuo può causare disagio postoperatorio.",
        "Diferencias significativas entre curado superficial y profundo. El monómero residual puede causar molestias postoperatorias."
      ),
      conclusions: t(
        "Unzureichende Polymerisation führt zur Freisetzung von Restmonomeren mit potenziellen biologischen Folgen.",
        "Inadequate polymerization leads to release of residual monomers with potential biological consequences.",
        "Une polymérisation inadéquate entraîne la libération de monomères résiduels avec des conséquences biologiques potentielles.",
        "Onvoldoende polymerisatie leidt tot afgifte van restmonomeren met mogelijke biologische gevolgen.",
        "Una polimerizzazione inadeguata porta al rilascio di monomeri residui con potenziali conseguenze biologiche.",
        "La polimerización inadecuada conduce a la liberación de monómeros residuales con posibles consecuencias biológicas."
      ),
    },
  },
  {
    id: 137,
    category: "polymer-toxicity",
    title: t(
      "Konversionsgrad und Restmonomer-Freisetzung von 3D-gedruckten, gefrästen und selbsthärtenden Kompositmaterialien für provisorische Kronen",
      "Degree of conversion and residual monomer elution of 3D-printed, milled and self-cured resin-based composite materials for temporary dental crowns and bridges",
      "Degré de conversion et élution de monomères résiduels des matériaux composites imprimés en 3D, fraisés et auto-polymérisés pour couronnes provisoires",
      "Conversiegraad en restmonomeer-elutie van 3D-geprinte, gefreesde en zelfuithardende composietmaterialen voor tijdelijke kronen",
      "Grado di conversione ed eluizione di monomeri residui di materiali compositi stampati in 3D, fresati e autopolimerizzanti per corone provvisorie",
      "Grado de conversión y elución de monómeros residuales de materiales compuestos impresos en 3D, fresados y autocurados para coronas provisionales"
    ),
    authors: "Berghaus E, Arendt A, Material AG, Weber MT, 'During A, the Group",
    journal: "Journal of Materials Science: Materials in Medicine",
    year: 2023,
    type: "in-vitro",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10182118/",
    structured: {
      problemStatement: t(
        "Provisorische Restaurationen aus verschiedenen Herstellungsverfahren unterscheiden sich in ihrer Monomer-Freisetzung.",
        "Temporary restorations from different manufacturing processes differ in their monomer release.",
        "Les restaurations provisoires issues de différents procédés de fabrication diffèrent dans leur libération de monomères.",
        "Tijdelijke restauraties van verschillende fabricageprocessen verschillen in hun monomeerafgifte.",
        "Le restaurazioni provvisorie da diversi processi di produzione differiscono nel loro rilascio di monomeri.",
        "Las restauraciones provisionales de diferentes procesos de fabricación difieren en su liberación de monómeros."
      ),
      purpose: t(
        "Vergleich der Monomer-Freisetzung von 3D-gedruckten, CAD/CAM-gefrästen und konventionellen Provisorien.",
        "Comparison of monomer release from 3D-printed, CAD/CAM-milled and conventional provisionals.",
        "Comparaison de la libération de monomères des provisoires imprimés en 3D, fraisés CAD/CAM et conventionnels.",
        "Vergelijking van monomeerafgifte van 3D-geprinte, CAD/CAM-gefreesde en conventionele provisoria.",
        "Confronto del rilascio di monomeri da provvisori stampati in 3D, fresati CAD/CAM e convenzionali.",
        "Comparación de la liberación de monómeros de provisionales impresos en 3D, fresados CAD/CAM y convencionales."
      ),
      methods: t(
        "HPLC-Analyse der Monomer-Elution über 28 Tage in künstlichem Speichel.",
        "HPLC analysis of monomer elution over 28 days in artificial saliva.",
        "Analyse HPLC de l'élution des monomères sur 28 jours dans la salive artificielle.",
        "HPLC-analyse van monomeer-elutie gedurende 28 dagen in kunstmatig speeksel.",
        "Analisi HPLC dell'eluizione dei monomeri per 28 giorni in saliva artificiale.",
        "Análisis HPLC de la elución de monómeros durante 28 días en saliva artificial."
      ),
      results: t(
        "3D-gedruckte Materialien zeigten signifikant höhere Restmonomer-Freisetzung als gefräste Materialien.",
        "3D-printed materials showed significantly higher residual monomer release than milled materials.",
        "Les matériaux imprimés en 3D ont montré une libération de monomères résiduels significativement plus élevée que les matériaux fraisés.",
        "3D-geprinte materialen vertoonden significant hogere restmonomeer-afgifte dan gefreesde materialen.",
        "I materiali stampati in 3D hanno mostrato un rilascio di monomeri residui significativamente più alto rispetto ai materiali fresati.",
        "Los materiales impresos en 3D mostraron una liberación de monómeros residuales significativamente mayor que los materiales fresados."
      ),
      conclusions: t(
        "Post-Processing und gründliche Reinigung sind bei 3D-gedruckten Provisorien essenziell zur Minimierung der Toxizität.",
        "Post-processing and thorough cleaning are essential for 3D-printed provisionals to minimize toxicity.",
        "Le post-traitement et un nettoyage approfondi sont essentiels pour les provisoires imprimés en 3D pour minimiser la toxicité.",
        "Nabewerking en grondige reiniging zijn essentieel bij 3D-geprinte provisoria om toxiciteit te minimaliseren.",
        "La post-elaborazione e una pulizia accurata sono essenziali per i provvisori stampati in 3D per minimizzare la tossicità.",
        "El postprocesamiento y la limpieza exhaustiva son esenciales para los provisionales impresos en 3D para minimizar la toxicidad."
      ),
    },
  },
  {
    id: 138,
    category: "polymer-toxicity",
    title: t(
      "Elution löslicher Komponenten aus Kompositen",
      "Elution of leachable components from composites",
      "Élution des composants lixiviables des composites",
      "Elutie van uitloogbare componenten uit composieten",
      "Eluizione di componenti lisciviabili dai compositi",
      "Elución de componentes lixiviables de composites"
    ),
    authors: "Ferracane JL",
    journal: "Journal of Oral Rehabilitation",
    year: 1994,
    type: "in-vitro",
    url: "https://onlinelibrary.wiley.com/doi/10.1111/j.1365-2842.1994.tb01158.x",
    structured: {
      problemStatement: t(
        "Das Ausmaß der Monomer-Freisetzung aus Dentalkompositen war weitgehend unbekannt.",
        "The extent of monomer release from dental composites was largely unknown.",
        "L'ampleur de la libération de monomères des composites dentaires était largement inconnue.",
        "De omvang van monomeerafgifte uit tandheelkundige composieten was grotendeels onbekend.",
        "L'entità del rilascio di monomeri dai compositi dentali era in gran parte sconosciuta.",
        "El alcance de la liberación de monómeros de los composites dentales era en gran parte desconocido."
      ),
      purpose: t(
        "Pionierarbeit zur systematischen Untersuchung der Monomer-Elution aus Kompositen.",
        "Pioneer work on systematic investigation of monomer elution from composites.",
        "Travail pionnier sur l'étude systématique de l'élution des monomères des composites.",
        "Baanbrekend werk over systematisch onderzoek naar monomeer-elutie uit composieten.",
        "Lavoro pionieristico sull'indagine sistematica dell'eluizione dei monomeri dai compositi.",
        "Trabajo pionero sobre la investigación sistemática de la elución de monómeros de composites."
      ),
      methods: t(
        "Extraktion und chromatographische Analyse freigesetzter Substanzen aus verschiedenen Kompositen.",
        "Extraction and chromatographic analysis of released substances from various composites.",
        "Extraction et analyse chromatographique des substances libérées de divers composites.",
        "Extractie en chromatografische analyse van vrijgekomen stoffen uit verschillende composieten.",
        "Estrazione e analisi cromatografica delle sostanze rilasciate da vari compositi.",
        "Extracción y análisis cromatográfico de sustancias liberadas de varios composites."
      ),
      results: t(
        "Signifikante Mengen an Monomeren und Additiven werden über Wochen freigesetzt.",
        "Significant amounts of monomers and additives are released over weeks.",
        "Des quantités significatives de monomères et d'additifs sont libérées pendant des semaines.",
        "Significante hoeveelheden monomeren en additieven worden gedurende weken afgegeven.",
        "Quantità significative di monomeri e additivi vengono rilasciate per settimane.",
        "Se liberan cantidades significativas de monómeros y aditivos durante semanas."
      ),
      conclusions: t(
        "Grundlegende Studie, die erstmals systematisch die Problematik der Restmonomer-Freisetzung dokumentierte.",
        "Foundational study that first systematically documented the problem of residual monomer release.",
        "Étude fondamentale qui a documenté pour la première fois de manière systématique le problème de la libération de monomères résiduels.",
        "Fundamentele studie die als eerste systematisch het probleem van restmonomeer-afgifte documenteerde.",
        "Studio fondamentale che ha documentato per la prima volta sistematicamente il problema del rilascio di monomeri residui.",
        "Estudio fundamental que documentó por primera vez sistemáticamente el problema de la liberación de monómeros residuales."
      ),
    },
  },
  {
    id: 139,
    category: "polymer-toxicity",
    title: t(
      "Elutionsstudie von unreagiertem Bis-GMA, TEGDMA, UDMA und Bis-EMA aus lichthärtenden Harzen mittels HPLC",
      "Elution study of unreacted Bis-GMA, TEGDMA, UDMA, and Bis-EMA from light-cured dental resins and resin composites using HPLC",
      "Étude d'élution du Bis-GMA, TEGDMA, UDMA et Bis-EMA non réagi des résines dentaires photopolymérisées par HPLC",
      "Elutiestudie van ongereageerd Bis-GMA, TEGDMA, UDMA en Bis-EMA uit lichtgeharde tandharsen met HPLC",
      "Studio di eluizione di Bis-GMA, TEGDMA, UDMA e Bis-EMA non reagiti da resine dentali fotopolimerizzate mediante HPLC",
      "Estudio de elución de Bis-GMA, TEGDMA, UDMA y Bis-EMA sin reaccionar de resinas dentales fotopolimerizadas mediante HPLC"
    ),
    authors: "Sideridou ID, Achilias DS",
    journal: "Journal of Biomedical Materials Research Part B: Applied Biomaterials",
    year: 2005,
    type: "in-vitro",
    url: "https://onlinelibrary.wiley.com/doi/10.1002/jbm.b.30252",
    structured: {
      problemStatement: t(
        "Die spezifische Freisetzung einzelner Monomere aus Kompositen war nicht quantifiziert.",
        "The specific release of individual monomers from composites was not quantified.",
        "La libération spécifique des monomères individuels des composites n'était pas quantifiée.",
        "De specifieke afgifte van individuele monomeren uit composieten was niet gekwantificeerd.",
        "Il rilascio specifico dei singoli monomeri dai compositi non era quantificato.",
        "La liberación específica de monómeros individuales de los composites no estaba cuantificada."
      ),
      purpose: t(
        "Quantitative HPLC-Analyse der vier Hauptmonomere in Dentalharzen.",
        "Quantitative HPLC analysis of the four main monomers in dental resins.",
        "Analyse HPLC quantitative des quatre principaux monomères dans les résines dentaires.",
        "Kwantitatieve HPLC-analyse van de vier belangrijkste monomeren in tandharsen.",
        "Analisi HPLC quantitativa dei quattro principali monomeri nelle resine dentali.",
        "Análisis HPLC cuantitativo de los cuatro monómeros principales en resinas dentales."
      ),
      methods: t(
        "HPLC-Analyse von Bis-GMA, TEGDMA, UDMA und Bis-EMA Freisetzung über 30 Tage.",
        "HPLC analysis of Bis-GMA, TEGDMA, UDMA and Bis-EMA release over 30 days.",
        "Analyse HPLC de la libération de Bis-GMA, TEGDMA, UDMA et Bis-EMA sur 30 jours.",
        "HPLC-analyse van Bis-GMA, TEGDMA, UDMA en Bis-EMA afgifte gedurende 30 dagen.",
        "Analisi HPLC del rilascio di Bis-GMA, TEGDMA, UDMA e Bis-EMA per 30 giorni.",
        "Análisis HPLC de la liberación de Bis-GMA, TEGDMA, UDMA y Bis-EMA durante 30 días."
      ),
      results: t(
        "TEGDMA wird am schnellsten freigesetzt, Bis-GMA akkumuliert über längere Zeit.",
        "TEGDMA is released fastest, Bis-GMA accumulates over longer periods.",
        "Le TEGDMA est libéré le plus rapidement, le Bis-GMA s'accumule sur de plus longues périodes.",
        "TEGDMA wordt het snelst afgegeven, Bis-GMA accumuleert over langere periodes.",
        "Il TEGDMA viene rilasciato più rapidamente, il Bis-GMA si accumula per periodi più lunghi.",
        "El TEGDMA se libera más rápido, el Bis-GMA se acumula durante períodos más largos."
      ),
      conclusions: t(
        "Jedes Monomer hat ein spezifisches Freisetzungsprofil mit unterschiedlichen toxikologischen Implikationen.",
        "Each monomer has a specific release profile with different toxicological implications.",
        "Chaque monomère a un profil de libération spécifique avec des implications toxicologiques différentes.",
        "Elk monomeer heeft een specifiek afgifteprofiel met verschillende toxicologische implicaties.",
        "Ogni monomero ha un profilo di rilascio specifico con diverse implicazioni tossicologiche.",
        "Cada monómero tiene un perfil de liberación específico con diferentes implicaciones toxicológicas."
      ),
    },
  },
  {
    id: 140,
    category: "polymer-toxicity",
    title: t(
      "Langzeit-Freisetzung von Monomeren aus modernen Dentalkomposit-Materialien",
      "Long-term release of monomers from modern dental-composite materials",
      "Libération à long terme de monomères des matériaux composites dentaires modernes",
      "Langetermijn-afgifte van monomeren uit moderne tandheelkundige composietmaterialen",
      "Rilascio a lungo termine di monomeri da materiali compositi dentali moderni",
      "Liberación a largo plazo de monómeros de materiales compuestos dentales modernos"
    ),
    authors: "Polydorou O, Trittler R, 3llwig E, et al.",
    journal: "European Journal of Oral Sciences",
    year: 2009,
    type: "in-vitro",
    url: "https://onlinelibrary.wiley.com/doi/10.1111/j.1600-0722.2008.00594.x",
    structured: {
      problemStatement: t(
        "Die Langzeitfreisetzung von Monomeren aus modernen Kompositen war unzureichend untersucht.",
        "Long-term monomer release from modern composites was insufficiently studied.",
        "La libération à long terme de monomères des composites modernes était insuffisamment étudiée.",
        "Langetermijn-monomeerafgifte uit moderne composieten was onvoldoende onderzocht.",
        "Il rilascio a lungo termine di monomeri dai compositi moderni era insufficientemente studiato.",
        "La liberación a largo plazo de monómeros de composites modernos estaba insuficientemente estudiada."
      ),
      purpose: t(
        "Untersuchung der Monomer-Freisetzung über einen Zeitraum von mehreren Monaten.",
        "Investigation of monomer release over a period of several months.",
        "Étude de la libération de monomères sur une période de plusieurs mois.",
        "Onderzoek naar monomeerafgifte over een periode van meerdere maanden.",
        "Indagine sul rilascio di monomeri per un periodo di diversi mesi.",
        "Investigación de la liberación de monómeros durante un período de varios meses."
      ),
      methods: t(
        "HPLC-Analyse der Monomer-Elution aus 5 modernen Kompositen über 180 Tage.",
        "HPLC analysis of monomer elution from 5 modern composites over 180 days.",
        "Analyse HPLC de l'élution des monomères de 5 composites modernes sur 180 jours.",
        "HPLC-analyse van monomeer-elutie uit 5 moderne composieten gedurende 180 dagen.",
        "Analisi HPLC dell'eluizione dei monomeri da 5 compositi moderni per 180 giorni.",
        "Análisis HPLC de la elución de monómeros de 5 composites modernos durante 180 días."
      ),
      results: t(
        "Monomer-Freisetzung hält über 6 Monate an, mit höchsten Werten in den ersten 7 Tagen.",
        "Monomer release continues for over 6 months, with highest values in the first 7 days.",
        "La libération de monomères continue pendant plus de 6 mois, avec les valeurs les plus élevées dans les 7 premiers jours.",
        "Monomeerafgifte gaat door gedurende meer dan 6 maanden, met hoogste waarden in de eerste 7 dagen.",
        "Il rilascio di monomeri continua per oltre 6 mesi, con i valori più alti nei primi 7 giorni.",
        "La liberación de monómeros continúa durante más de 6 meses, con los valores más altos en los primeros 7 días."
      ),
      conclusions: t(
        "Moderne Komposite setzen kontinuierlich Monomere frei - eine chronische Exposition ist unvermeidlich.",
        "Modern composites continuously release monomers - chronic exposure is unavoidable.",
        "Les composites modernes libèrent continuellement des monomères - une exposition chronique est inévitable.",
        "Moderne composieten geven continu monomeren af - chronische blootstelling is onvermijdelijk.",
        "I compositi moderni rilasciano continuamente monomeri - l'esposizione cronica è inevitabile.",
        "Los composites modernos liberan continuamente monómeros - la exposición crónica es inevitable."
      ),
    },
  },
  {
    id: 141,
    category: "polymer-toxicity",
    title: t(
      "Überlegungen zur Zytotoxizität von Dentalkompositen auf Harzbasis: Eine systematische Übersicht",
      "Considerations about Cytotoxicity of Resin-Based Composite Dental Materials: A Systematic Review",
      "Considérations sur la cytotoxicité des matériaux dentaires composites à base de résine : une revue systématique",
      "Overwegingen over cytotoxiciteit van harsgebaseerde tandheelkundige composietmaterialen: een systematische review",
      "Considerazioni sulla citotossicità dei materiali dentali compositi a base di resina: una revisione sistematica",
      "Consideraciones sobre la citotoxicidad de los materiales dentales compuestos a base de resina: una revisión sistemática"
    ),
    authors: "Wiertelak-Makała K, Żurawska A,3odzka E, Nowicka A",
    journal: "International Journal of Molecular Sciences",
    year: 2024,
    type: "review",
    url: "https://www.mdpi.com/1422-0067/25/1/152",
    structured: {
      problemStatement: t(
        "Die Biokompatibilität von Dentalkompositen ist zunehmend Gegenstand wissenschaftlicher Untersuchungen.",
        "The biocompatibility of dental composites is increasingly subject to scientific investigation.",
        "La biocompatibilité des composites dentaires fait l'objet d'investigations scientifiques croissantes.",
        "De biocompatibiliteit van tandheelkundige composieten is in toenemende mate onderwerp van wetenschappelijk onderzoek.",
        "La biocompatibilità dei compositi dentali è sempre più oggetto di indagine scientifica.",
        "La biocompatibilidad de los composites dentales es cada vez más objeto de investigación científica."
      ),
      purpose: t(
        "Systematische Übersicht über Zytotoxizitätsstudien von 2017-2023.",
        "Systematic review of cytotoxicity studies from 2017-2023.",
        "Revue systématique des études de cytotoxicité de 2017-2023.",
        "Systematische review van cytotoxiciteitsstudies van 2017-2023.",
        "Revisione sistematica degli studi di citotossicità dal 2017-2023.",
        "Revisión sistemática de estudios de citotoxicidad de 2017-2023."
      ),
      methods: t(
        "Analyse von 14 In-vitro-Studien an humanen und murinen Zelllinien mittels MTT, LDH und WST-1.",
        "Analysis of 14 in vitro studies on human and murine cell lines using MTT, LDH and WST-1.",
        "Analyse de 14 études in vitro sur des lignées cellulaires humaines et murines utilisant MTT, LDH et WST-1.",
        "Analyse van 14 in-vitro studies op humane en murine cellijnen met MTT, LDH en WST-1.",
        "Analisi di 14 studi in vitro su linee cellulari umane e murine utilizzando MTT, LDH e WST-1.",
        "Análisis de 14 estudios in vitro en líneas celulares humanas y murinas utilizando MTT, LDH y WST-1."
      ),
      results: t(
        "Dosis- und zeitabhängige Zytotoxizität nachgewiesen. Oxidativer Stress und Glutathion-Depletion sind Hauptmechanismen.",
        "Dose- and time-dependent cytotoxicity demonstrated. Oxidative stress and glutathione depletion are main mechanisms.",
        "Cytotoxicité dose- et temps-dépendante démontrée. Le stress oxydatif et la déplétion du glutathion sont les principaux mécanismes.",
        "Dosis- en tijdsafhankelijke cytotoxiciteit aangetoond. Oxidatieve stress en glutathion-depletie zijn de belangrijkste mechanismen.",
        "Dimostrata citotossicità dose- e tempo-dipendente. Lo stress ossidativo e la deplezione del glutatione sono i meccanismi principali.",
        "Citotoxicidad dosis y tiempo-dependiente demostrada. El estrés oxidativo y la depleción de glutatión son los principales mecanismos."
      ),
      conclusions: t(
        "Aktuelle Evidenz bestätigt die biologischen Risiken von Dentalkompositen - Weiterentwicklung der Materialien ist notwendig.",
        "Current evidence confirms biological risks of dental composites - further material development is necessary.",
        "Les preuves actuelles confirment les risques biologiques des composites dentaires - le développement ultérieur des matériaux est nécessaire.",
        "Huidige evidentie bevestigt biologische risico's van tandheelkundige composieten - verdere materiaalontwikkeling is noodzakelijk.",
        "L'evidenza attuale conferma i rischi biologici dei compositi dentali - è necessario un ulteriore sviluppo dei materiali.",
        "La evidencia actual confirma los riesgos biológicos de los composites dentales - es necesario un mayor desarrollo de materiales."
      ),
    },
  },
];

// ============================================
// 2024-2026 NEW STUDIES - ALL EAP-RELEVANT TOPICS
// 12 Studies × 6 Languages
// ============================================

export const newStudies2024_2026: Study[] = [
  // === TITANIUM SURFACE & FIBROBLAST ATTACHMENT ===
  {
    id: 143,
    category: "cell-attachment",
    title: t(
      "Weichgewebsanhaftung humaner Gingivafibroblasten an TiO₂-Nanotubes im Vergleich zu reinem Titan und dessen Legierungen: Ein systematischer Review",
      "Soft tissue attachment of human gingival fibroblasts to titanium dioxide nanotubes compared to commercially pure titanium and its alloys: a systematic review",
      "Attachement des tissus mous des fibroblastes gingivaux humains aux nanotubes de dioxyde de titane par rapport au titane pur et ses alliages : une revue systématique",
      "Zachte weefselaanhechting van humane gingivale fibroblasten aan titaniumdioxide nanobuizen vergeleken met commercieel zuiver titanium en zijn legeringen: een systematische review",
      "Attaccamento dei tessuti molli dei fibroblasti gengivali umani ai nanotubi di biossido di titanio rispetto al titanio puro commerciale e alle sue leghe: una revisione sistematica",
      "Adhesión de tejidos blandos de fibroblastos gingivales humanos a nanotubos de dióxido de titanio en comparación con titanio puro comercial y sus aleaciones: una revisión sistemática"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "BDJ Open",
    year: 2025,
    type: "review",
    url: "https://www.nature.com/articles/s41405-025-00293-0",
    structured: {
      problemStatement: t(
        "Die Weichgewebsintegration an verschiedenen Titanoberflächen ist für den Langzeiterfolg von Implantaten entscheidend.",
        "Soft tissue integration on different titanium surfaces is critical for long-term implant success.",
        "L'intégration des tissus mous sur différentes surfaces de titane est essentielle pour le succès à long terme des implants.",
        "Zachte weefselintegratie op verschillende titaniumoppervlakken is cruciaal voor langetermijn implantatiesucces.",
        "L'integrazione dei tessuti molli su diverse superfici di titanio è fondamentale per il successo a lungo termine dell'impianto.",
        "La integración de tejidos blandos en diferentes superficies de titanio es fundamental para el éxito a largo plazo del implante."
      ),
      purpose: t(
        "Systematischer Vergleich der Fibroblastenanhaftung an TiO₂-Nanotubes vs. reinem Titan.",
        "Systematic comparison of fibroblast attachment to TiO₂ nanotubes vs. pure titanium.",
        "Comparaison systématique de l'attachement des fibroblastes aux nanotubes TiO₂ vs. titane pur.",
        "Systematische vergelijking van fibroblastenhechting aan TiO₂ nanobuizen vs. zuiver titanium.",
        "Confronto sistematico dell'attaccamento dei fibroblasti ai nanotubi TiO₂ vs. titanio puro.",
        "Comparación sistemática de la adhesión de fibroblastos a nanotubos de TiO₂ vs. titanio puro."
      ),
      methods: t(
        "Systematischer Review nach PRISMA-Richtlinien mit Analyse von In-vitro-Studien.",
        "Systematic review following PRISMA guidelines with analysis of in vitro studies.",
        "Revue systématique suivant les directives PRISMA avec analyse des études in vitro.",
        "Systematische review volgens PRISMA-richtlijnen met analyse van in-vitro studies.",
        "Revisione sistematica secondo le linee guida PRISMA con analisi degli studi in vitro.",
        "Revisión sistemática siguiendo las directrices PRISMA con análisis de estudios in vitro."
      ),
      results: t(
        "TiO₂-Nanotubes zeigen signifikant verbesserte Fibroblastenanhaftung und -proliferation.",
        "TiO₂ nanotubes show significantly improved fibroblast attachment and proliferation.",
        "Les nanotubes TiO₂ montrent une amélioration significative de l'attachement et de la prolifération des fibroblastes.",
        "TiO₂ nanobuizen tonen significant verbeterde fibroblastaanhechting en -proliferatie.",
        "I nanotubi TiO₂ mostrano un miglioramento significativo dell'attaccamento e della proliferazione dei fibroblasti.",
        "Los nanotubos de TiO₂ muestran una mejora significativa en la adhesión y proliferación de fibroblastos."
      ),
      conclusions: t(
        "Oberflächenmodifiziertes Titan (z.B. durch Anodisierung) verbessert die biologische Integration.",
        "Surface-modified titanium (e.g., through anodization) improves biological integration.",
        "Le titane modifié en surface (par ex. par anodisation) améliore l'intégration biologique.",
        "Oppervlakte-gemodificeerd titanium (bijv. door anodisatie) verbetert biologische integratie.",
        "Il titanio modificato in superficie (ad es. tramite anodizzazione) migliora l'integrazione biologica.",
        "El titanio modificado en superficie (por ej. mediante anodización) mejora la integración biológica."
      ),
    },
  },
  {
    id: 144,
    category: "cell-attachment",
    title: t(
      "Der Einfluss neuer Anodisierungsmodifikation auf die Titan-Interaktion mit humanen Osteoblasten und Fibroblasten (in vitro)",
      "The impact of new anodization modification on titanium interaction with human osteoblasts and fibroblasts (in vitro)",
      "L'impact de la nouvelle modification par anodisation sur l'interaction du titane avec les ostéoblastes et fibroblastes humains (in vitro)",
      "De impact van nieuwe anodisatiemodificatie op titaniuminteractie met humane osteoblasten en fibroblasten (in vitro)",
      "L'impatto della nuova modifica dell'anodizzazione sull'interazione del titanio con osteoblasti e fibroblasti umani (in vitro)",
      "El impacto de la nueva modificación de anodización en la interacción del titanio con osteoblastos y fibroblastos humanos (in vitro)"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "PMC / Dental Materials Research",
    year: 2025,
    type: "in-vitro",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12629452/",
    structured: {
      problemStatement: t(
        "Die optimale Anodisierungsspannung für maximale Zellanhaftung ist nicht etabliert.",
        "The optimal anodization voltage for maximum cell attachment is not established.",
        "La tension d'anodisation optimale pour une adhésion cellulaire maximale n'est pas établie.",
        "De optimale anodisatiespanning voor maximale celhechting is niet vastgesteld.",
        "La tensione di anodizzazione ottimale per la massima adesione cellulare non è stabilita.",
        "El voltaje de anodización óptimo para la máxima adhesión celular no está establecido."
      ),
      purpose: t(
        "Vergleich anodisierter Titanoberflächen bei verschiedenen Spannungen mit SLA-Oberflächen.",
        "Comparison of anodized titanium surfaces at different voltages with SLA surfaces.",
        "Comparaison des surfaces de titane anodisé à différentes tensions avec les surfaces SLA.",
        "Vergelijking van geanodiseerde titaniumoppervlakken bij verschillende spanningen met SLA-oppervlakken.",
        "Confronto delle superfici di titanio anodizzato a diverse tensioni con le superfici SLA.",
        "Comparación de superficies de titanio anodizado a diferentes voltajes con superficies SLA."
      ),
      methods: t(
        "In-vitro-Kultivierung von Osteoblasten und Fibroblasten auf verschieden behandelten Titanproben.",
        "In vitro cultivation of osteoblasts and fibroblasts on differently treated titanium samples.",
        "Culture in vitro d'ostéoblastes et de fibroblastes sur des échantillons de titane traités différemment.",
        "In-vitro kweek van osteoblasten en fibroblasten op verschillend behandelde titaniummonsters.",
        "Coltura in vitro di osteoblasti e fibroblasti su campioni di titanio trattati diversamente.",
        "Cultivo in vitro de osteoblastos y fibroblastos en muestras de titanio tratadas de manera diferente."
      ),
      results: t(
        "Anodisierte Oberflächen zeigen signifikant höhere Zellanhaftung und Kollagensynthese als SLA.",
        "Anodized surfaces show significantly higher cell attachment and collagen synthesis than SLA.",
        "Les surfaces anodisées montrent une adhésion cellulaire et une synthèse de collagène significativement plus élevées que SLA.",
        "Geanodiseerde oppervlakken tonen significant hogere celhechting en collageensynthese dan SLA.",
        "Le superfici anodizzate mostrano un'adesione cellulare e una sintesi di collagene significativamente maggiori rispetto a SLA.",
        "Las superficies anodizadas muestran una adhesión celular y síntesis de colágeno significativamente mayores que SLA."
      ),
      conclusions: t(
        "Anodisierung bei optimaler Spannung erzeugt bioaktive Oberflächen mit verbesserter Zellintegration im Vergleich zu konventionellen Oberflächenbehandlungen.",
        "Anodization at optimal voltage creates bioactive surfaces with improved cell integration compared to conventional surface treatments.",
        "L'anodisation à tension optimale crée des surfaces bioactives avec une intégration cellulaire améliorée par rapport aux traitements de surface conventionnels.",
        "Anodisatie bij optimale spanning creëert bioactieve oppervlakken met verbeterde celintegratie vergeleken met conventionele oppervlaktebehandelingen.",
        "L'anodizzazione a tensione ottimale crea superfici bioattive con integrazione cellulare migliorata rispetto ai trattamenti superficiali convenzionali.",
        "La anodización a voltaje óptimo crea superficies bioactivas con integración celular mejorada en comparación con los tratamientos de superficie convencionales."
      ),
    },
  },
  {
    id: 145,
    category: "cell-attachment",
    title: t(
      "Fibroblasten- und Osteoblastenverhalten nach Kontakt mit verschiedenen Titanoberflächen für Implantatabutments: Eine In-vitro-Studie",
      "Fibroblasts and osteoblasts behavior after contact with different titanium surfaces used as implant abutment: An in vitro experimental study",
      "Comportement des fibroblastes et ostéoblastes après contact avec différentes surfaces de titane utilisées comme pilier d'implant : Une étude expérimentale in vitro",
      "Gedrag van fibroblasten en osteoblasten na contact met verschillende titaniumoppervlakken gebruikt als implantaatpijler: Een in-vitro experimentele studie",
      "Comportamento di fibroblasti e osteoblasti dopo il contatto con diverse superfici di titanio utilizzate come pilastro implantare: Uno studio sperimentale in vitro",
      "Comportamiento de fibroblastos y osteoblastos después del contacto con diferentes superficies de titanio usadas como pilar de implante: Un estudio experimental in vitro"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "PMC / Clinical Implant Research",
    year: 2024,
    type: "in-vitro",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10844044/",
    structured: {
      problemStatement: t(
        "Die Oberflächenbeschaffenheit von Abutments beeinflusst die biologische Gewebeantwort.",
        "The surface characteristics of abutments influence the biological tissue response.",
        "Les caractéristiques de surface des piliers influencent la réponse tissulaire biologique.",
        "De oppervlaktekenmerken van pijlers beïnvloeden de biologische weefselrespons.",
        "Le caratteristiche superficiali dei pilastri influenzano la risposta tissutale biologica.",
        "Las características superficiales de los pilares influyen en la respuesta tisular biológica."
      ),
      purpose: t(
        "Untersuchung des Zellverhaltens auf verschiedenen klinisch verwendeten Titanoberflächen.",
        "Investigation of cell behavior on various clinically used titanium surfaces.",
        "Investigation du comportement cellulaire sur diverses surfaces de titane utilisées cliniquement.",
        "Onderzoek naar celgedrag op verschillende klinisch gebruikte titaniumoppervlakken.",
        "Indagine sul comportamento cellulare su varie superfici di titanio utilizzate clinicamente.",
        "Investigación del comportamiento celular en varias superficies de titanio utilizadas clínicamente."
      ),
      methods: t(
        "Vergleich von maschinell bearbeiteten, sandgestrahlten und anodisierten Oberflächen.",
        "Comparison of machined, sandblasted, and anodized surfaces.",
        "Comparaison des surfaces usinées, sablées et anodisées.",
        "Vergelijking van machinaal bewerkte, gezandstraalde en geanodiseerde oppervlakken.",
        "Confronto di superfici lavorate a macchina, sabbiate e anodizzate.",
        "Comparación de superficies mecanizadas, arenadas y anodizadas."
      ),
      results: t(
        "Anodisierte und modifizierte Oberflächen zeigen beste Zellanhaftung und Genexpression.",
        "Anodized and modified surfaces show best cell attachment and gene expression.",
        "Les surfaces anodisées et modifiées montrent la meilleure adhésion cellulaire et expression génique.",
        "Geanodiseerde en gemodificeerde oppervlakken tonen beste celhechting en genexpressie.",
        "Le superfici anodizzate e modificate mostrano la migliore adesione cellulare ed espressione genica.",
        "Las superficies anodizadas y modificadas muestran la mejor adhesión celular y expresión génica."
      ),
      conclusions: t(
        "Oberflächenmodifikation durch Anodisierung ist klinisch vorteilhaft für die Weichgewebsintegration.",
        "Surface modification through anodization is clinically advantageous for soft tissue integration.",
        "La modification de surface par anodisation est cliniquement avantageuse pour l'intégration des tissus mous.",
        "Oppervlaktemodificatie door anodisatie is klinisch voordelig voor zachte weefselintegratie.",
        "La modifica della superficie tramite anodizzazione è clinicamente vantaggiosa per l'integrazione dei tessuti molli.",
        "La modificación de superficie mediante anodización es clínicamente ventajosa para la integración de tejidos blandos."
      ),
    },
  },
  
  // === ONE-ABUTMENT-ONE-TIME ===
  {
    id: 146,
    category: "manipulation-aesthetics",
    title: t(
      "Das 'One Customized Abutment One Time' Konzept - Eine innovative Technik zur Optimierung von Immediatversorgung und digitalen Protokollen",
      "The 'One Customized Abutment One Time' Concept - An innovative technique for optimizing immediacy and digital protocols",
      "Le concept 'One Customized Abutment One Time' - Une technique innovante pour optimiser l'immédiat et les protocoles numériques",
      "Het 'One Customized Abutment One Time' concept - Een innovatieve techniek voor het optimaliseren van directe behandeling en digitale protocollen",
      "Il concetto 'One Customized Abutment One Time' - Una tecnica innovativa per ottimizzare l'immediatezza e i protocolli digitali",
      "El concepto 'One Customized Abutment One Time' - Una técnica innovadora para optimizar la inmediatez y los protocolos digitales"
    ),
    authors: "Kheur M, Kheur S, Lakha T, Dongre P, Jung RE, Thoma D",
    journal: "International Journal of Prosthodontics",
    year: 2025,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/37824116/",
    structured: {
      problemStatement: t(
        "Wiederholtes Ab- und Anschrauben von Abutments führt zu Knochenverlust und Weichgewebsstörungen.",
        "Repeated disconnection and reconnection of abutments leads to bone loss and soft tissue disturbance.",
        "La déconnexion et reconnexion répétées des piliers entraîne une perte osseuse et une perturbation des tissus mous.",
        "Herhaald los- en vastschroeven van pijlers leidt tot botverlies en verstoring van zacht weefsel.",
        "La disconnessione e riconnessione ripetute dei pilastri porta a perdita ossea e disturbo dei tessuti molli.",
        "La desconexión y reconexión repetidas de los pilares conduce a pérdida ósea y alteración de tejidos blandos."
      ),
      purpose: t(
        "Beschreibung einer digitalen Workflow-Technik für individuelle Sofortabutments.",
        "Description of a digital workflow technique for customized immediate abutments.",
        "Description d'une technique de flux de travail numérique pour des piliers immédiats personnalisés.",
        "Beschrijving van een digitale workflowtechniek voor op maat gemaakte directe pijlers.",
        "Descrizione di una tecnica di flusso di lavoro digitale per pilastri immediati personalizzati.",
        "Descripción de una técnica de flujo de trabajo digital para pilares inmediatos personalizados."
      ),
      methods: t(
        "Klinische Falldokumentation mit digitalem Design und CAD/CAM-Fertigung.",
        "Clinical case documentation with digital design and CAD/CAM fabrication.",
        "Documentation de cas clinique avec conception numérique et fabrication CAD/CAM.",
        "Klinische casedocumentatie met digitaal ontwerp en CAD/CAM-fabricage.",
        "Documentazione di casi clinici con design digitale e fabbricazione CAD/CAM.",
        "Documentación de casos clínicos con diseño digital y fabricación CAD/CAM."
      ),
      results: t(
        "Erfolgreiche Integration mit optimaler Weichgewebsästhetik bei Sofortversorgung.",
        "Successful integration with optimal soft tissue aesthetics in immediate restoration.",
        "Intégration réussie avec une esthétique optimale des tissus mous en restauration immédiate.",
        "Succesvolle integratie met optimale zachte weefselesthetiek bij directe restauratie.",
        "Integrazione riuscita con estetica ottimale dei tessuti molli nel restauro immediato.",
        "Integración exitosa con estética óptima de tejidos blandos en restauración inmediata."
      ),
      conclusions: t(
        "Das 'One Customized Abutment One Time' Konzept optimiert digitale Workflows und biologische Ergebnisse.",
        "The 'One Customized Abutment One Time' concept optimizes digital workflows and biological outcomes.",
        "Le concept 'One Customized Abutment One Time' optimise les flux de travail numériques et les résultats biologiques.",
        "Het 'One Customized Abutment One Time' concept optimaliseert digitale workflows en biologische resultaten.",
        "Il concetto 'One Customized Abutment One Time' ottimizza i flussi di lavoro digitali e i risultati biologici.",
        "El concepto 'One Customized Abutment One Time' optimiza los flujos de trabajo digitales y los resultados biológicos."
      ),
    },
  },
  {
    id: 147,
    category: "manipulation-aesthetics",
    title: t(
      "Einfluss von Abutmenthöhe und Zeitpunkt der Platzierung auf marginalen Knochenverlust bei Einzelzahnimplantaten: 12-Monats-RCT",
      "Influence of intermediate abutment height and timing of placement on marginal bone loss in single implant-supported crowns: a 12-month follow-up randomized clinical trial",
      "Influence de la hauteur du pilier intermédiaire et du timing de placement sur la perte osseuse marginale des couronnes sur implant unitaire : un essai clinique randomisé de 12 mois",
      "Invloed van tussenliggende pijlerhoogte en timing van plaatsing op marginaal botverlies bij enkelimplantaat-gedragen kronen: een 12-maanden follow-up gerandomiseerde klinische studie",
      "Influenza dell'altezza del pilastro intermedio e della tempistica di posizionamento sulla perdita ossea marginale nelle corone supportate da impianto singolo: uno studio clinico randomizzato a 12 mesi",
      "Influencia de la altura del pilar intermedio y el momento de colocación en la pérdida ósea marginal en coronas sobre implante único: un ensayo clínico aleatorizado de 12 meses"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "Clinical Oral Investigations",
    year: 2025,
    type: "human-study",
    url: "https://link.springer.com/article/10.1007/s00784-025-06364-8",
    structured: {
      problemStatement: t(
        "Der optimale Zeitpunkt der definitiven Abutment-Platzierung ist klinisch nicht geklärt.",
        "The optimal timing of definitive abutment placement is not clinically established.",
        "Le moment optimal du placement du pilier définitif n'est pas établi cliniquement.",
        "De optimale timing van definitieve pijlerplaatsing is klinisch niet vastgesteld.",
        "La tempistica ottimale del posizionamento del pilastro definitivo non è stabilita clinicamente.",
        "El momento óptimo de colocación del pilar definitivo no está establecido clínicamente."
      ),
      purpose: t(
        "Randomisierte Studie zum Einfluss von Abutmenthöhe und Platzierungszeitpunkt.",
        "Randomized study on the influence of abutment height and placement timing.",
        "Étude randomisée sur l'influence de la hauteur du pilier et du moment du placement.",
        "Gerandomiseerde studie naar de invloed van pijlerhoogte en plaatsingstiming.",
        "Studio randomizzato sull'influenza dell'altezza del pilastro e della tempistica di posizionamento.",
        "Estudio aleatorizado sobre la influencia de la altura del pilar y el momento de colocación."
      ),
      methods: t(
        "12-Monats-Follow-up mit radiographischer Messung des marginalen Knochenverlusts.",
        "12-month follow-up with radiographic measurement of marginal bone loss.",
        "Suivi de 12 mois avec mesure radiographique de la perte osseuse marginale.",
        "12-maanden follow-up met radiografische meting van marginaal botverlies.",
        "Follow-up di 12 mesi con misurazione radiografica della perdita ossea marginale.",
        "Seguimiento de 12 meses con medición radiográfica de la pérdida ósea marginal."
      ),
      results: t(
        "Frühzeitige definitive Abutment-Platzierung und optimale Höhe reduzieren marginalen Knochenverlust signifikant.",
        "Early definitive abutment placement and optimal height significantly reduce marginal bone loss.",
        "Le placement définitif précoce du pilier et la hauteur optimale réduisent significativement la perte osseuse marginale.",
        "Vroege definitieve pijlerplaatsing en optimale hoogte verminderen marginaal botverlies significant.",
        "Il posizionamento definitivo precoce del pilastro e l'altezza ottimale riducono significativamente la perdita ossea marginale.",
        "La colocación temprana del pilar definitivo y la altura óptima reducen significativamente la pérdida ósea marginal."
      ),
      conclusions: t(
        "Die sofortige definitive Abutment-Versorgung zeigt klinische Vorteile bezüglich marginaler Knochenerhaltung.",
        "Immediate definitive abutment provision shows clinical advantages regarding marginal bone preservation.",
        "La fourniture immédiate du pilier définitif montre des avantages cliniques concernant la préservation osseuse marginale.",
        "Onmiddellijke definitieve pijlervoorziening toont klinische voordelen met betrekking tot marginale botbehoud.",
        "La fornitura immediata del pilastro definitivo mostra vantaggi clinici riguardo alla conservazione ossea marginale.",
        "La provisión inmediata del pilar definitivo muestra ventajas clínicas respecto a la preservación ósea marginal."
      ),
    },
  },
  
  // === CEMENT PROBLEMS ===
  {
    id: 148,
    category: "cement-problems",
    title: t(
      "Überschüssiger Zement und periimplantäre Erkrankungen: Eine Querschnittsstudie",
      "Excess cement and peri-implant disease: A cross-sectional study",
      "Ciment excédentaire et maladie péri-implantaire : Une étude transversale",
      "Overtollig cement en peri-implantaire ziekte: Een cross-sectionele studie",
      "Cemento in eccesso e malattia perimplantare: Uno studio trasversale",
      "Cemento excesivo y enfermedad periimplantaria: Un estudio transversal"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "Clinical Implant Dentistry and Related Research",
    year: 2025,
    type: "human-study",
    url: "https://pubmed.ncbi.nlm.nih.gov/39812473/",
    structured: {
      problemStatement: t(
        "Die Prävalenz von Zementresten bei periimplantären Erkrankungen ist unklar.",
        "The prevalence of cement remnants in peri-implant diseases is unclear.",
        "La prévalence des restes de ciment dans les maladies péri-implantaires n'est pas claire.",
        "De prevalentie van cementresten bij peri-implantaire ziekten is onduidelijk.",
        "La prevalenza dei residui di cemento nelle malattie perimplantari non è chiara.",
        "La prevalencia de restos de cemento en enfermedades periimplantarias no está clara."
      ),
      purpose: t(
        "Querschnittsstudie zur Korrelation von Zementüberschuss und Periimplantitis.",
        "Cross-sectional study on the correlation of cement excess and peri-implantitis.",
        "Étude transversale sur la corrélation entre l'excès de ciment et la péri-implantite.",
        "Cross-sectionele studie naar de correlatie tussen cementoverschot en peri-implantitis.",
        "Studio trasversale sulla correlazione tra eccesso di cemento e perimplantite.",
        "Estudio transversal sobre la correlación del exceso de cemento y la periimplantitis."
      ),
      methods: t(
        "Klinische und radiographische Untersuchung von Patienten mit zementretinierten Kronen.",
        "Clinical and radiographic examination of patients with cement-retained crowns.",
        "Examen clinique et radiographique des patients avec couronnes scellées.",
        "Klinisch en radiografisch onderzoek van patiënten met gecementeerde kronen.",
        "Esame clinico e radiografico di pazienti con corone cementate.",
        "Examen clínico y radiográfico de pacientes con coronas cementadas."
      ),
      results: t(
        "Signifikant höhere Periimplantitis-Raten bei nachweisbarem Zementüberschuss.",
        "Significantly higher peri-implantitis rates with detectable cement excess.",
        "Taux de péri-implantite significativement plus élevés en cas d'excès de ciment détectable.",
        "Significant hogere peri-implantitispercentages bij detecteerbaar cementoverschot.",
        "Tassi di perimplantite significativamente più alti con eccesso di cemento rilevabile.",
        "Tasas de periimplantitis significativamente más altas con exceso de cemento detectable."
      ),
      conclusions: t(
        "Zementüberschuss ist ein signifikanter Risikofaktor für periimplantäre Erkrankungen. Eine Minimierung der subgingivalen Zementexposition ist klinisch indiziert.",
        "Cement excess is a significant risk factor for peri-implant diseases. Minimization of subgingival cement exposure is clinically indicated.",
        "L'excès de ciment est un facteur de risque significatif pour les maladies péri-implantaires. La minimisation de l'exposition au ciment sous-gingival est cliniquement indiquée.",
        "Cementoverschot is een significante risicofactor voor peri-implantaire ziekten. Minimalisering van subgingivale cementblootstelling is klinisch geïndiceerd.",
        "L'eccesso di cemento è un fattore di rischio significativo per le malattie perimplantari. La minimizzazione dell'esposizione al cemento sottogengivale è clinicamente indicata.",
        "El exceso de cemento es un factor de riesgo significativo para enfermedades periimplantarias. La minimización de la exposición al cemento subgingival está clínicamente indicada."
      ),
    },
  },
  {
    id: 149,
    category: "cement-problems",
    title: t(
      "Mikrotomographische und histopathologische Evaluation von Dentalzementen als Spätfolge-Komplikation im Rattenmodell",
      "A microtomographic and histopathological evaluation of dental cements as late-stage peri-implant complication in a rat model",
      "Évaluation microtomographique et histopathologique des ciments dentaires comme complication péri-implantaire tardive dans un modèle de rat",
      "Een microtomografische en histopathologische evaluatie van tandheelkundige cementen als laat-stadium peri-implantaire complicatie in een rattenmodel",
      "Valutazione microtomografica e istopatologica dei cementi dentali come complicanza perimplantare tardiva in un modello di ratto",
      "Evaluación microtomográfica e histopatológica de cementos dentales como complicación periimplantaria tardía en un modelo de rata"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "Scientific Reports",
    year: 2024,
    type: "animal-study",
    url: "https://www.nature.com/articles/s41598-024-66353-x",
    structured: {
      problemStatement: t(
        "Zement-vermittelte Periimplantitis macht 1,9-75% der Implantatversagen aus.",
        "Cement-mediated peri-implantitis accounts for 1.9-75% of implant failures.",
        "La péri-implantite médiée par le ciment représente 1,9-75% des échecs d'implants.",
        "Cement-gemedieerde peri-implantitis is verantwoordelijk voor 1,9-75% van implantaatfalen.",
        "La perimplantite mediata dal cemento rappresenta l'1,9-75% dei fallimenti implantari.",
        "La periimplantitis mediada por cemento representa el 1,9-75% de los fracasos de implantes."
      ),
      purpose: t(
        "Histopathologische Untersuchung der Zementwirkung auf osseointegierte Implantate.",
        "Histopathological investigation of cement effects on osseointegrated implants.",
        "Investigation histopathologique des effets du ciment sur les implants ostéointégrés.",
        "Histopathologisch onderzoek naar cementeffecten op geosseoïntegreerde implantaten.",
        "Indagine istopatologica degli effetti del cemento sugli impianti osteointegrati.",
        "Investigación histopatológica de los efectos del cemento en implantes osteointegrados."
      ),
      methods: t(
        "Tiermodell mit Mikro-CT und Histologie nach Zementexposition.",
        "Animal model with micro-CT and histology after cement exposure.",
        "Modèle animal avec micro-CT et histologie après exposition au ciment.",
        "Diermodel met micro-CT en histologie na cementblootstelling.",
        "Modello animale con micro-CT e istologia dopo esposizione al cemento.",
        "Modelo animal con micro-CT e histología después de la exposición al cemento."
      ),
      results: t(
        "Zement verursacht progressive Knochenresorption und Entzündung - irreversible Schäden nach 8 Wochen.",
        "Cement causes progressive bone resorption and inflammation - irreversible damage after 8 weeks.",
        "Le ciment provoque une résorption osseuse progressive et une inflammation - dommages irréversibles après 8 semaines.",
        "Cement veroorzaakt progressieve botresorptie en ontsteking - onomkeerbare schade na 8 weken.",
        "Il cemento causa riassorbimento osseo progressivo e infiammazione - danni irreversibili dopo 8 settimane.",
        "El cemento causa reabsorción ósea progresiva e inflamación - daños irreversibles después de 8 semanas."
      ),
      conclusions: t(
        "Zementreste verursachen progressive Knochenresorption. Eine maximale Distanz der Klebefuge vom Knochen reduziert das Risiko.",
        "Cement residues cause progressive bone resorption. Maximum distance of the adhesive interface from bone reduces risk.",
        "Les résidus de ciment provoquent une résorption osseuse progressive. Une distance maximale de l'interface adhésive par rapport à l'os réduit le risque.",
        "Cementresten veroorzaken progressieve botresorptie. Maximale afstand van de hechtingsinterface tot bot vermindert het risico.",
        "I residui di cemento causano riassorbimento osseo progressivo. La massima distanza dell'interfaccia adesiva dall'osso riduce il rischio.",
        "Los restos de cemento causan reabsorción ósea progresiva. La máxima distancia de la interfaz adhesiva del hueso reduce el riesgo."
      ),
    },
  },
  {
    id: 150,
    category: "cement-problems",
    title: t(
      "Einfluss von Kronensetzmethoden auf verbleibenden Zement in zementierten Implantatkronen",
      "Effect of crown seating methods on the remnant cement in cement-retained implant crowns",
      "Effet des méthodes de scellement de couronne sur le ciment résiduel dans les couronnes d'implant scellées",
      "Effect van kroonplaatsingsmethoden op resterend cement in gecementeerde implantaatkronen",
      "Effetto dei metodi di cementazione della corona sul cemento residuo nelle corone implantari cementate",
      "Efecto de los métodos de asentamiento de coronas en el cemento remanente en coronas de implantes cementadas"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "Scientific Reports",
    year: 2024,
    type: "in-vitro",
    url: "https://www.nature.com/articles/s41598-024-73806-w",
    structured: {
      problemStatement: t(
        "Die optimale Technik zur Minimierung von Zementresten ist nicht etabliert.",
        "The optimal technique for minimizing cement residues is not established.",
        "La technique optimale pour minimiser les résidus de ciment n'est pas établie.",
        "De optimale techniek voor het minimaliseren van cementresten is niet vastgesteld.",
        "La tecnica ottimale per minimizzare i residui di cemento non è stabilita.",
        "La técnica óptima para minimizar los residuos de cemento no está establecida."
      ),
      purpose: t(
        "Vergleich verschiedener Kronensetzmethoden bezüglich Zementrückständen.",
        "Comparison of different crown seating methods regarding cement residues.",
        "Comparaison de différentes méthodes de scellement de couronne concernant les résidus de ciment.",
        "Vergelijking van verschillende kroonplaatsingsmethoden met betrekking tot cementresten.",
        "Confronto di diversi metodi di cementazione della corona riguardo ai residui di cemento.",
        "Comparación de diferentes métodos de asentamiento de coronas respecto a los residuos de cemento."
      ),
      methods: t(
        "In-vitro-Analyse mit verschiedenen Setztechniken und Mikro-CT-Evaluation.",
        "In vitro analysis with different seating techniques and micro-CT evaluation.",
        "Analyse in vitro avec différentes techniques de scellement et évaluation par micro-CT.",
        "In-vitro analyse met verschillende plaatsingstechnieken en micro-CT-evaluatie.",
        "Analisi in vitro con diverse tecniche di cementazione e valutazione micro-CT.",
        "Análisis in vitro con diferentes técnicas de asentamiento y evaluación por micro-CT."
      ),
      results: t(
        "Keine Technik eliminiert Zementreste vollständig - besonders subgingival verbleibt Zement.",
        "No technique completely eliminates cement residues - cement particularly remains subgingivally.",
        "Aucune technique n'élimine complètement les résidus de ciment - le ciment reste particulièrement en sous-gingival.",
        "Geen enkele techniek elimineert cementresten volledig - cement blijft vooral subgingivaal achter.",
        "Nessuna tecnica elimina completamente i residui di cemento - il cemento rimane particolarmente sottogingivale.",
        "Ninguna técnica elimina completamente los residuos de cemento - el cemento permanece particularmente subgingivalmente."
      ),
      conclusions: t(
        "Zementretinierte Kronen bergen inhärente Risiken durch unvermeidbare subgingivale Zementreste. Alternative Retentionsstrategien sollten erwogen werden.",
        "Cement-retained crowns carry inherent risks from unavoidable subgingival cement residues. Alternative retention strategies should be considered.",
        "Les couronnes scellées comportent des risques inhérents dus aux résidus de ciment sous-gingivaux inévitables. Des stratégies de rétention alternatives devraient être envisagées.",
        "Gecementeerde kronen dragen inherente risico's door onvermijdelijke subgingivale cementresten. Alternatieve retentiestrategieën moeten worden overwogen.",
        "Le corone cementate comportano rischi intrinseci da residui di cemento sottogengivali inevitabili. Dovrebbero essere considerate strategie di ritenzione alternative.",
        "Las coronas cementadas conllevan riesgos inherentes por residuos de cemento subgingivales inevitables. Se deben considerar estrategias de retención alternativas."
      ),
    },
  },
  
  // === CYTOTOXICITY ===
  {
    id: 151,
    category: "polymer-toxicity",
    title: t(
      "Zytotoxizität von Methacrylat-basierten Harzmaterialien in der Zahnmedizin",
      "Cytotoxicity of Methacrylate-Based Resin Materials in Dentistry",
      "Cytotoxicité des matériaux en résine à base de méthacrylate en dentisterie",
      "Cytotoxiciteit van methacrylaatgebaseerde harsmaterialen in de tandheelkunde",
      "Citotossicità dei materiali in resina a base di metacrilato in odontoiatria",
      "Citotoxicidad de los materiales de resina a base de metacrilato en odontología"
    ),
    authors: "Khabadze Z, Dolzhikov N, Bagdasarova I, et al.",
    journal: "Journal of International Dental and Medical Research",
    year: 2025,
    type: "review",
    url: "http://www.jidmr.com/journal/wp-content/uploads/2025/06/61-D25_3435_Zurab_Khabadze_Russia-Rev.pdf",
    structured: {
      problemStatement: t(
        "Die Zytotoxizität verschiedener Methacrylat-Monomere ist nicht vollständig klassifiziert.",
        "The cytotoxicity of different methacrylate monomers is not fully classified.",
        "La cytotoxicité des différents monomères de méthacrylate n'est pas entièrement classifiée.",
        "De cytotoxiciteit van verschillende methacrylaatmonomeren is niet volledig geclassificeerd.",
        "La citotossicità dei diversi monomeri di metacrilato non è completamente classificata.",
        "La citotoxicidad de los diferentes monómeros de metacrilato no está completamente clasificada."
      ),
      purpose: t(
        "Umfassender Review der Zytotoxizität von Dentalharzen.",
        "Comprehensive review of the cytotoxicity of dental resins.",
        "Revue complète de la cytotoxicité des résines dentaires.",
        "Uitgebreide review van de cytotoxiciteit van tandheelkundige harsen.",
        "Revisione completa della citotossicità delle resine dentali.",
        "Revisión completa de la citotoxicidad de las resinas dentales."
      ),
      methods: t(
        "Systematische Literaturanalyse mit Fokus auf In-vitro-Zytotoxizitätsstudien.",
        "Systematic literature analysis focusing on in vitro cytotoxicity studies.",
        "Analyse systématique de la littérature axée sur les études de cytotoxicité in vitro.",
        "Systematische literatuuranalyse gericht op in-vitro cytotoxiciteitsstudies.",
        "Analisi sistematica della letteratura focalizzata sugli studi di citotossicità in vitro.",
        "Análisis sistemático de la literatura centrado en estudios de citotoxicidad in vitro."
      ),
      results: t(
        "Bis-GMA > UDMA > TEGDMA > HEMA in der Toxizitätsrangfolge bestätigt.",
        "Bis-GMA > UDMA > TEGDMA > HEMA toxicity ranking confirmed.",
        "Classement de toxicité Bis-GMA > UDMA > TEGDMA > HEMA confirmé.",
        "Bis-GMA > UDMA > TEGDMA > HEMA toxiciteitsrangorde bevestigd.",
        "Ranking di tossicità Bis-GMA > UDMA > TEGDMA > HEMA confermato.",
        "Ranking de toxicidad Bis-GMA > UDMA > TEGDMA > HEMA confirmado."
      ),
      conclusions: t(
        "Methacrylat-Exposition des Gewebes sollte minimiert werden. Titanoberflächen können als Schutzbarriere dienen.",
        "Methacrylate exposure to tissue should be minimized. Titanium surfaces can serve as a protective barrier.",
        "L'exposition des tissus aux méthacrylates doit être minimisée. Les surfaces en titane peuvent servir de barrière protectrice.",
        "Methacrylaatblootstelling aan weefsel moet worden geminimaliseerd. Titaniumoppervlakken kunnen dienen als beschermende barrière.",
        "L'esposizione dei tessuti ai metacrilati deve essere minimizzata. Le superfici in titanio possono fungere da barriera protettiva.",
        "La exposición de los tejidos a los metacrilatos debe minimizarse. Las superficies de titanio pueden servir como barrera protectora."
      ),
    },
  },
  {
    id: 152,
    category: "polymer-toxicity",
    title: t(
      "Einfluss von Dual-Cure-Harzzement-Härtungsmodi auf gingivale Zytotoxizität und Entzündungsreaktionen",
      "Influence of dual-cure resin-cement curing modes on gingival cytotoxicity and inflammatory responses",
      "Influence des modes de polymérisation du ciment résine dual-cure sur la cytotoxicité gingivale et les réponses inflammatoires",
      "Invloed van dual-cure harscement uithardingsmodi op gingivale cytotoxiciteit en ontstekingsreacties",
      "Influenza delle modalità di polimerizzazione del cemento resinoso dual-cure sulla citotossicità gengivale e le risposte infiammatorie",
      "Influencia de los modos de curado del cemento de resina de curado dual en la citotoxicidad gingival y las respuestas inflamatorias"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "Journal of Dental Sciences",
    year: 2024,
    type: "in-vitro",
    url: "https://www.sciencedirect.com/science/article/pii/S199179022400134X",
    structured: {
      problemStatement: t(
        "Der Einfluss von Aushärtungsmodi auf die Gingivatoxizität ist unklar.",
        "The influence of curing modes on gingival toxicity is unclear.",
        "L'influence des modes de polymérisation sur la toxicité gingivale n'est pas claire.",
        "De invloed van uithardingsmodi op gingivale toxiciteit is onduidelijk.",
        "L'influenza delle modalità di polimerizzazione sulla tossicità gengivale non è chiara.",
        "La influencia de los modos de curado en la toxicidad gingival no está clara."
      ),
      purpose: t(
        "Untersuchung der Zytotoxizität und Entzündungsreaktion bei verschiedenen Aushärtungen.",
        "Investigation of cytotoxicity and inflammatory response with different curing modes.",
        "Investigation de la cytotoxicité et de la réponse inflammatoire avec différents modes de polymérisation.",
        "Onderzoek naar cytotoxiciteit en ontstekingsreactie bij verschillende uithardingsmodi.",
        "Indagine sulla citotossicità e sulla risposta infiammatoria con diverse modalità di polimerizzazione.",
        "Investigación de la citotoxicidad y la respuesta inflamatoria con diferentes modos de curado."
      ),
      methods: t(
        "In-vitro-Exposition von Gingivazellen gegenüber verschiedenen Zementformulierungen.",
        "In vitro exposure of gingival cells to different cement formulations.",
        "Exposition in vitro des cellules gingivales à différentes formulations de ciment.",
        "In-vitro blootstelling van gingivale cellen aan verschillende cementformuleringen.",
        "Esposizione in vitro delle cellule gengivali a diverse formulazioni di cemento.",
        "Exposición in vitro de células gingivales a diferentes formulaciones de cemento."
      ),
      results: t(
        "Unvollständig ausgehärtete Zemente zeigen signifikant höhere Zytotoxizität und Entzündungsmarker.",
        "Incompletely cured cements show significantly higher cytotoxicity and inflammatory markers.",
        "Les ciments incomplètement polymérisés montrent une cytotoxicité et des marqueurs inflammatoires significativement plus élevés.",
        "Onvolledig uitgeharde cementen tonen significant hogere cytotoxiciteit en ontstekingsmarkers.",
        "I cementi non completamente polimerizzati mostrano citotossicità e marcatori infiammatori significativamente più elevati.",
        "Los cementos incompletamente curados muestran citotoxicidad y marcadores inflamatorios significativamente mayores."
      ),
      conclusions: t(
        "Die Zement-Exposition des Weichgewebes sollte minimiert werden, um entzündliche Reaktionen zu reduzieren.",
        "Cement exposure to soft tissue should be minimized to reduce inflammatory reactions.",
        "L'exposition du tissu mou au ciment doit être minimisée pour réduire les réactions inflammatoires.",
        "Cementblootstelling aan zacht weefsel moet worden geminimaliseerd om ontstekingsreacties te verminderen.",
        "L'esposizione dei tessuti molli al cemento deve essere minimizzata per ridurre le reazioni infiammatorie.",
        "La exposición del cemento al tejido blando debe minimizarse para reducir las reacciones inflamatorias."
      ),
    },
  },
  
  // === SOFT TISSUE INTEGRATION ===
  {
    id: 153,
    category: "cell-attachment",
    title: t(
      "Klinische und ästhetische Outcomes anodisierter Titanabutments in der ästhetischen Zone",
      "Clinical and Esthetic Outcomes of Anodized Titanium Abutments in the Esthetic Zone",
      "Résultats cliniques et esthétiques des piliers en titane anodisé dans la zone esthétique",
      "Klinische en esthetische resultaten van geanodiseerde titaniumpijlers in de esthetische zone",
      "Risultati clinici ed estetici dei pilastri in titanio anodizzato nella zona estetica",
      "Resultados clínicos y estéticos de los pilares de titanio anodizado en la zona estética"
    ),
    authors: "Autor(en) nicht verifiziert",
    journal: "PMC / Journal of Esthetic and Restorative Dentistry",
    year: 2025,
    type: "human-study",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12408774/",
    structured: {
      problemStatement: t(
        "Titan-Abutments können bei dünnem Gingivabiotyp die Ästhetik beeinträchtigen.",
        "Titanium abutments can compromise esthetics in thin gingival biotypes.",
        "Les piliers en titane peuvent compromettre l'esthétique chez les biotypes gingivaux minces.",
        "Titaniumpijlers kunnen de esthetiek bij dunne gingivale biotypen compromitteren.",
        "I pilastri in titanio possono compromettere l'estetica nei biotipi gengivali sottili.",
        "Los pilares de titanio pueden comprometer la estética en biotipos gingivales delgados."
      ),
      purpose: t(
        "Untersuchung anodisierter Titanabutments mit wärmeren Farbtönen für verbesserte Ästhetik.",
        "Investigation of anodized titanium abutments with warmer color tones for improved esthetics.",
        "Investigation des piliers en titane anodisé avec des tons de couleur plus chauds pour une esthétique améliorée.",
        "Onderzoek naar geanodiseerde titaniumpijlers met warmere kleurtinten voor verbeterde esthetiek.",
        "Indagine sui pilastri in titanio anodizzato con tonalità di colore più calde per un'estetica migliorata.",
        "Investigación de pilares de titanio anodizado con tonos de color más cálidos para una estética mejorada."
      ),
      methods: t(
        "Klinische Studie mit Spektrophotometrie und Patientenzufriedenheitsbewertung.",
        "Clinical study with spectrophotometry and patient satisfaction evaluation.",
        "Étude clinique avec spectrophotométrie et évaluation de la satisfaction des patients.",
        "Klinische studie met spectrofotometrie en patiënttevredenheidsevaluatie.",
        "Studio clinico con spettrofotometria e valutazione della soddisfazione del paziente.",
        "Estudio clínico con espectrofotometría y evaluación de satisfacción del paciente."
      ),
      results: t(
        "Anodisierte Abutments (pink/gold) harmonieren besser mit dem umliegenden Gewebe.",
        "Anodized abutments (pink/gold) harmonize better with surrounding tissue.",
        "Les piliers anodisés (rose/or) s'harmonisent mieux avec les tissus environnants.",
        "Geanodiseerde pijlers (roze/goud) harmoniseren beter met omliggend weefsel.",
        "I pilastri anodizzati (rosa/oro) si armonizzano meglio con i tessuti circostanti.",
        "Los pilares anodizados (rosa/oro) armonizan mejor con el tejido circundante."
      ),
      conclusions: t(
        "Anodisierung verbessert sowohl Biokompatibilität als auch Ästhetik durch wärmere Farbtöne bei dünnen Biotypen.",
        "Anodization improves both biocompatibility and esthetics through warmer color tones in thin biotypes.",
        "L'anodisation améliore à la fois la biocompatibilité et l'esthétique grâce à des tons de couleur plus chauds chez les biotypes minces.",
        "Anodisatie verbetert zowel biocompatibiliteit als esthetiek door warmere kleurtinten bij dunne biotypen.",
        "L'anodizzazione migliora sia la biocompatibilità che l'estetica attraverso tonalità di colore più calde nei biotipi sottili.",
        "La anodización mejora tanto la biocompatibilidad como la estética a través de tonos de color más cálidos en biotipos delgados."
      ),
    },
  },
  {
    id: 154,
    category: "manipulation-aesthetics",
    title: t(
      "Systematischer Review: Periimplantäre Bedingungen und ästhetische Outcomes von individualisierten vs. konventionellen Heilungsabutments",
      "Systematic review of peri-implant conditions and aesthetic outcomes of customized versus conventional healing abutments",
      "Revue systématique des conditions péri-implantaires et des résultats esthétiques des piliers de cicatrisation personnalisés vs conventionnels",
      "Systematische review van peri-implantaire condities en esthetische resultaten van op maat gemaakte vs conventionele healing abutments",
      "Revisione sistematica delle condizioni perimplantari e dei risultati estetici degli abutment di guarigione personalizzati vs convenzionali",
      "Revisión sistemática de las condiciones periimplantarias y los resultados estéticos de pilares de cicatrización personalizados vs convencionales"
    ),
    authors: "Ruhstorfer M, Güth JF, Stimmelmayr M, Waltenberger L, et al.",
    journal: "International Journal of Implant Dentistry",
    year: 2024,
    type: "review",
    url: "https://journalimplantdent.springeropen.com/articles/10.1186/s40729-024-00581-8",
    structured: {
      problemStatement: t(
        "Die Überlegenheit individualisierter Heilungsabutments ist nicht systematisch belegt.",
        "The superiority of customized healing abutments is not systematically documented.",
        "La supériorité des piliers de cicatrisation personnalisés n'est pas systématiquement documentée.",
        "De superioriteit van op maat gemaakte healing abutments is niet systematisch gedocumenteerd.",
        "La superiorità degli abutment di guarigione personalizzati non è sistematicamente documentata.",
        "La superioridad de los pilares de cicatrización personalizados no está sistemáticamente documentada."
      ),
      purpose: t(
        "Systematischer Vergleich von individualisierten und Standardheilungsabutments.",
        "Systematic comparison of customized and standard healing abutments.",
        "Comparaison systématique des piliers de cicatrisation personnalisés et standard.",
        "Systematische vergelijking van op maat gemaakte en standaard healing abutments.",
        "Confronto sistematico degli abutment di guarigione personalizzati e standard.",
        "Comparación sistemática de pilares de cicatrización personalizados y estándar."
      ),
      methods: t(
        "Systematischer Review nach PRISMA mit Meta-Analyse klinischer Studien.",
        "Systematic review according to PRISMA with meta-analysis of clinical studies.",
        "Revue systématique selon PRISMA avec méta-analyse des études cliniques.",
        "Systematische review volgens PRISMA met meta-analyse van klinische studies.",
        "Revisione sistematica secondo PRISMA con meta-analisi degli studi clinici.",
        "Revisión sistemática según PRISMA con metaanálisis de estudios clínicos."
      ),
      results: t(
        "Individualisierte Abutments zeigen bessere Weichgewebsstabilität und ästhetische Ergebnisse.",
        "Customized abutments show better soft tissue stability and esthetic results.",
        "Les piliers personnalisés montrent une meilleure stabilité des tissus mous et des résultats esthétiques.",
        "Op maat gemaakte abutments tonen betere zachte weefselstabiliteit en esthetische resultaten.",
        "Gli abutment personalizzati mostrano migliore stabilità dei tessuti molli e risultati estetici.",
        "Los pilares personalizados muestran mejor estabilidad de tejidos blandos y resultados estéticos."
      ),
      conclusions: t(
        "Patientenindividuelle Abutments zeigen Vorteile bei Weichgewebsstabilität und ästhetischen Ergebnissen.",
        "Patient-specific abutments show advantages in soft tissue stability and esthetic outcomes.",
        "Les piliers spécifiques au patient montrent des avantages en termes de stabilité des tissus mous et de résultats esthétiques.",
        "Patiëntspecifieke abutments tonen voordelen in zachte weefselstabiliteit en esthetische resultaten.",
        "Gli abutment specifici per il paziente mostrano vantaggi nella stabilità dei tessuti molli e nei risultati estetici.",
        "Los pilares específicos del paciente muestran ventajas en la estabilidad de tejidos blandos y resultados estéticos."
      ),
    },
  },
];


// Alle Studien kombiniert (sortiert nach Jahr, neueste zuerst)
// Öffentlich angezeigt werden NUR Einträge, die gegen PubMed verifiziert wurden
// (siehe verifiedStudies.ts). Titel, Autoren, Journal, Jahr, Link und
// Zusammenfassung stammen dann aus dem verifizierten Datensatz.
export const getAllStudies = (): Study[] => {
  const allStudies = [
    ...polymerToxicityStudies,
    ...cementProblemsStudies,
    ...cellAttachmentStudies,
    ...manipulationAestheticsStudies,
    ...allergyStudies,
    ...conversionStudies,
    ...additionalStudies2024,
    ...newStudies2024_2026,
  ];

  return allStudies
    .filter((s) => VERIFIED_STUDIES[s.id])
    .map((s) => {
      const { pmid: _pmid, ...v } = VERIFIED_STUDIES[s.id];
      return { ...s, ...v };
    })
    .concat(pubmedStudies2026)
    .concat(moreStudies2026)
    .filter((s, i, arr) => arr.findIndex((x) => x.url === s.url) === i)
    .map((s) => ({ ...s, topic: STUDY_TOPICS[s.id] }))
    .sort((a, b) => b.year - a.year);
};

// Studien nach Kategorie filtern
export const getStudiesByCategory = (category: string): Study[] => {
  const allStudies = getAllStudies();
  if (category === 'all') return allStudies;
  return allStudies.filter(study => study.topic === category || study.category === category);
};

// Übersetzungs-Helper für Studientitel und Zusammenfassungen
export const getTranslatedText = (text: TranslatedText, language: Language): string => {
  return text[language] || text.en;
};

// Studientyp-Labels
export const getTypeLabel = (type: string, language: Language): string => {
  const labels: Record<string, TranslatedText> = {
    "in-vitro": t("In-vitro", "In vitro", "In vitro", "In vitro", "In vitro", "In vitro"),
    "human-study": t("Humanstudie", "Human Study", "Étude humaine", "Humane studie", "Studio umano", "Estudio humano"),
    "animal-study": t("Tierstudie", "Animal Study", "Étude animale", "Dierstudie", "Studio animale", "Estudio animal"),
    "review": t("Review", "Review", "Revue", "Review", "Revisione", "Revisión"),
  };
  return labels[type]?.[language] || labels[type]?.en || type;
};

// Kategorie-Labels
export const categoryLabels: Record<string, TranslatedText> = {
  all: t("Alle Studien", "All Studies", "Toutes les études", "Alle studies", "Tutti gli studi", "Todos los estudios"),
  "polymer-toxicity": t("Toxizität", "Toxicity", "Toxicité", "Toxiciteit", "Tossicità", "Toxicidad"),
  "cement-problems": t("Zement", "Cement", "Ciment", "Cement", "Cemento", "Cemento"),
  "cell-attachment": t("Zellanhaftung", "Cell Attachment", "Adhésion cellulaire", "Celaanhechting", "Adesione cellulare", "Adhesión celular"),
  "manipulation-aesthetics": t("Manipulation", "Manipulation", "Manipulation", "Manipulatie", "Manipolazione", "Manipulación"),
  "allergy": t("Allergien", "Allergies", "Allergies", "Allergieën", "Allergie", "Alergias"),
};

// Studienanzahl pro Kategorie
// Zählt aus derselben Quelle wie die Liste (getAllStudies, nur verifizierte
// Studien, dedupliziert), damit Filter-Badges und angezeigte Studien übereinstimmen.
export const getStudyCountByCategory = (): Record<string, number> => {
  const allStudies = getAllStudies();
  const out: Record<string, number> = { all: allStudies.length };
  for (const k of TOPIC_ORDER) out[k] = allStudies.filter((s) => s.topic === k).length;
  return out;
};

// ============================================
// EAP-RELEVANZ BEWERTUNGSSYSTEM
// Automatische Bewertung basierend auf Studieninhalt
// 1 = Höchste Relevanz (grün), 5 = Niedrigste (rot)
// ============================================

// Studien-IDs mit manueller Relevanz-Bewertung
// MONOMERE = GRUNDPROBLEM → Alle Monomer-Studien haben hohe Relevanz (1-2)
// ANODISIERUNG = EAP-Verfahren → Oberflächenmodifikation sehr hohe Relevanz
const studyRelevanceMap: Record<number, 1 | 2 | 3 | 4 | 5> = {
  // === HÖCHSTE RELEVANZ (1) - Direkt EAP-relevant ===
  // One-Abutment-One-Time / Definitive Abutments
  125: 1, // Emergenzprofilwinkel - Pirc 2026
  32: 1,  // Tallarico Meta-Analyse 2018
  33: 1,  // Repeated abutment manipulation
  34: 1,  // One abutment one time protocol
  146: 1, // Kheur 2025 - One Customized Abutment One Time Konzept
  147: 1, // Clinical Oral Invest 2025 - Abutmenthöhe & Timing RCT
  154: 1, // Ruhstorfer 2024 - Systematic Review Customized vs Conventional
  
  // Titan-Zellanhaftung & Oberflächenmodifikation (EAP = anodisiert!)
  50: 1,  // Titan vs Zirkon Zellanhaftung
  51: 1,  // Titanoberfläche Fibroblasten
  52: 1,  // Hydrophile Oberflächen - direkt relevant für Anodisierung
  53: 1,  // Epithelzellen auf Titan
  54: 1,  // Weichgewebe-Integration
  55: 1,  // Oberflächenrauheit - relevant für Anodisierung
  56: 1,  // Oberflächenbehandlung - direkt relevant für Anodisierung
  142: 1, // Kim 2013 - Anodisierung Titan Bioaktivität (Kernprozess EAP!)
  134: 1, // Surface modification soft tissue seal
  143: 1, // BDJ Open 2025 - TiO2 Nanotubes Fibroblast Systematic Review
  144: 1, // PMC 2025 - Anodisierung verschiedene Spannungen (EAP-Kernprozess!)
  145: 1, // PMC 2024 - Fibroblast/Osteoblast auf Titanoberflächen
  153: 1, // PMC 2025 - Anodisierte Abutments Ästhetik (pink/gold)
  
  // Zement-Probleme mit direktem EAP-Bezug
  40: 1,  // Residual cement peri-implantitis Wilson
  41: 1,  // Cement-retained complications
  148: 1, // PubMed 2025 - Excess cement peri-implant disease
  149: 1, // Nature 2024 - Histopathologie Zement Rattenmodell
  150: 1, // Nature 2024 - Crown seating Zementreste
  
  // === HÖCHSTE RELEVANZ (1) - Monomer-Toxizität = Grundproblem ===
  4: 1,   // Meta-Analyse Monomer-Freisetzung - Van Landuyt
  138: 1, // Ferracane 1994 - Pionierarbeit systematische Elution
  139: 1, // Sideridou 2005 - HPLC Quantifizierung über 30 Tage
  140: 1, // Polydorou 2009 - Langzeit-Freisetzung 6 Monate
  6: 1,   // Zytotoxizität Provisorien
  7: 1,   // Oxidativer Stress Monomere
  141: 1, // Wiertelak-Makała 2024 - Systematischer Review Zytotoxizität
  151: 1, // Khabadze 2025 - Methacrylat-Zytotoxizität Review
  152: 1, // ScienceDirect 2024 - Dual-Cure Zement Gingivatoxizität
  
  // === HOHE RELEVANZ (2) - Starke EAP-Unterstützung ===
  135: 2, // Moldovan Konversionsgrad 35-77%
  137: 2, // Berghaus 3D-Druck Provisorien
  126: 2, // 3D-gedruckte Provisorien Toxizität
  5: 2,   // DNA-Doppelstrangbrüche durch Monomere
  8: 2,   // N-Acetylcystein Schutz vor Monomeren
  136: 2, // Ucuncu Messmethoden
  9: 2,   // Apoptose-Signalwege durch Monomere
  10: 2,  // In-vitro Zytotoxizität Monomere
  11: 2,  // ROS-Produktion durch Monomere
  129: 2, // BisGMA Lymphozyten-Toxizität
  
  // === MITTLERE RELEVANZ (3) - Unterstützende Evidenz ===
  42: 3,  // Zement-Analyse
  43: 3,  // Zementrückstände
  44: 3,  // Zement-Entfernung Methoden
  
  
  // === HÖCHSTE RELEVANZ (1) - Nanoröhren/Oberflächenmodifikation ===
  130: 1, // BDJ Open 2025 - TiO2 Nanoröhren Fibroblast Review (Oberflächenmodifikation!)
};

// Automatische Relevanz-Berechnung basierend auf Kategorie und Typ
export const getStudyRelevance = (study: Study): 1 | 2 | 3 | 4 | 5 => {
  // Manuelle Bewertung hat Vorrang
  if (studyRelevanceMap[study.id]) {
    return studyRelevanceMap[study.id];
  }
  
  // Automatische Bewertung nach Kategorie und Typ
  const { category, type } = study;
  
  // One-Abutment-One-Time Studien = höchste Relevanz
  if (category === 'manipulation-aesthetics') {
    if (type === 'review' || type === 'human-study') return 1;
    return 2;
  }
  
  // Zellanhaftung auf Titan = hohe Relevanz
  if (category === 'cell-attachment') {
    if (type === 'human-study') return 1;
    if (type === 'review') return 2;
    return 2;
  }
  
  // Zement-Probleme = hohe Relevanz
  if (category === 'cement-problems') {
    if (type === 'human-study' || type === 'review') return 1;
    return 2;
  }
  
  // Polymer-Toxizität = GRUNDPROBLEM → Hohe Relevanz (1-2)
  if (category === 'polymer-toxicity') {
    if (type === 'review') return 1;  // Reviews = höchste Relevanz
    if (type === 'human-study') return 1;
    return 2; // Alle anderen Monomer-Studien = hohe Relevanz
  }
  
  // Allergien = mittlere bis hohe Relevanz
  if (category === 'allergy') {
    if (type === 'human-study' || type === 'review') return 2;
    return 3;
  }
  
  // Default: mittlere Relevanz
  return 3;
};

// Relevanz-Farben für UI
export const getRelevanceColor = (relevance: 1 | 2 | 3 | 4 | 5): string => {
  switch (relevance) {
    case 1: return 'bg-green-500 text-white';
    case 2: return 'bg-green-300 text-green-900';
    case 3: return 'bg-yellow-400 text-yellow-900';
    case 4: return 'bg-orange-400 text-orange-900';
    case 5: return 'bg-red-500 text-white';
    default: return 'bg-gray-400 text-white';
  }
};

// Relevanz-Label für UI
export const getRelevanceLabel = (relevance: 1 | 2 | 3 | 4 | 5, language: Language): string => {
  const labels: Record<1 | 2 | 3 | 4 | 5, TranslatedText> = {
    1: t('Höchste Relevanz', 'Highest Relevance', 'Pertinence maximale', 'Hoogste relevantie', 'Massima rilevanza', 'Máxima relevancia'),
    2: t('Hohe Relevanz', 'High Relevance', 'Haute pertinence', 'Hoge relevantie', 'Alta rilevanza', 'Alta relevancia'),
    3: t('Mittlere Relevanz', 'Medium Relevance', 'Pertinence moyenne', 'Gemiddelde relevantie', 'Media rilevanza', 'Relevancia media'),
    4: t('Geringe Relevanz', 'Low Relevance', 'Faible pertinence', 'Lage relevantie', 'Bassa rilevanza', 'Baja relevancia'),
    5: t('Minimale Relevanz', 'Minimal Relevance', 'Pertinence minimale', 'Minimale relevantie', 'Rilevanza minima', 'Relevancia mínima'),
  };
  return labels[relevance][language] || labels[relevance].en;
};
